
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, Suspense, lazy } from 'react';
import { flushSync } from 'react-dom';
import { createPortal } from 'react-dom';
// Sistema de permisos por usuario restaurado desde App_permisos.tsx
// Filtrado de tableros por permisos implementado - solo se muestran tableros con habitaciones permitidas
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Bolt,
  Search,
  Sort,
  FilterList,
  Close,
  AccountCircle,
  GetApp,
  Settings as SettingsIcon,
  Home as HomeIcon,
  Logout as LogoutIcon,
  SwapVerticalCircleSharp,
} from '@mui/icons-material';
import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BottomNavBar from './components/bottomNav/BottomNavBar';
import type { BottomNavItemId } from './constants/bottomNav';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import io from 'socket.io-client';
import { getServerUrl, getApiBaseUrl, syncServerUrlWithCurrentOrigin } from './config';
import useSwipeHorizontal from './useSwipeHorizontal';
import { useOrientation } from './hooks/useOrientation';
import { useDeviceGridColumns } from './hooks/useDeviceGridColumns';
import { handleOverlayBack } from './hooks/useOverlayBackButton';
import LoginPage from './pages/LoginPage';
import { SoftphonePage } from './installed/phone';
import DeviceListModal from './components/DeviceListModal';
import DeviceModal from './components/DeviceModal';
import DeviceCard from './components/DeviceCard';
import { SonosSpeakerCard } from './installed/music';
import { SonosSpeakerModal } from './installed/music';
import { SamsungDeviceCard } from './installed/samsung';
import { AndroidTvDeviceCard } from './installed/androidtv';
import { LgDeviceCard } from './installed/air';
import { EvohomeDeviceCard } from './installed/heat';
import { AndroidTvDeviceModal } from './installed/androidtv';
import { SamsungDeviceModal } from './installed/samsung';
import { LgDeviceModal } from './installed/air';
import { EvohomeDeviceModal } from './installed/heat';
// Los íconos PNG ahora están en el backend y se cargan dinámicamente
import ExitMessage from './components/ExitMessage';
import { SoftphoneCallEndedScreen } from './installed/phone';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { openPWAInstallInstructions } from './components/PWAInstallPrompt';
import { useServerLocalClock } from './hooks/useServerLocalClock';
import WeatherChip from './components/meteo/WeatherChip';
import { inicializarConsumoTiempoReal, limpiarRefrescoAutomatico, refrescarEstructuraCompleta, mapaDispositivos, mapaHabitaciones, mapaTableros, isMapaDispositivosCargado, recentControlResponses } from './services/consumptionService';
import { eventBus } from './services/EventBus';
import { patchDeviceSchedulesCache } from './services/deviceSchedulesService';
import { startSolarFlowMetricsRelay } from './installed/solar';
import { usePinContext } from './contexts/PinContext';
import { isAiPolicyUnset, useAiDevicePolicy } from './contexts/AiDevicePolicyContext';
import PinInputDialog from './components/PinInputDialog';
import FloatingCameraWindow from './components/FloatingCameraWindow';
import { getUserFavorites, getUserFavoriteGroups, getUserPermissions, updateUserHomePage, getRoomDeviceOrder, updateRoomDeviceOrder, getUserRoomSortPreference, saveUserRoomSortPreference } from './services/api';
import api from './services/api';
import { getUser, isDemo, canViewCameras, canUseAI, canViewMusic, canViewAc, canViewTv, canViewAndroidTv, canViewHeating, canViewPileta, canViewSolar, canUsePhone, canAnswerDoorbell } from './services/auth';
import { bumpUsage } from './services/dashboardUsage';
import { getDemoRestrictionSnackbarProps } from './constants/demoRestrictions';
import { useFirmwareJobMonitor } from './hooks/useFirmwareJobMonitor';
import {
  clearLoginSessionFlags,
  clearLoginNotice,
  clearPendingDoorbellCamera,
  clearPreviousSessionTimeout,
  clearRecoverSessionAfterReload,
  clearSoftphoneDoorbellSession,
  clearSoftphoneQuickAnswer,
  getDoorbellCameraFromUrl,
  hasIncomingSoftphoneIntent,
  hasLoggedInDocumentSession,
  isSoftphoneAnswerDeepLink,
  isSoftphoneCallEndedGate,
  isDoorbellIncomingContext,
  isPhoneIncomingDeepLink,
  isSoftphoneDoorbellDeepLink,
  isSoftphoneDoorbellSession,
  isSoftphoneIncomingResume,
  isSoftphoneQuickAnswer,
  isStoredJwtAlive,
  markLoginNotice,
  markPreviousSessionTimeout,
  markSoftphoneBgIncomingLocal,
  markSoftphoneCallEndedGate,
  markSoftphoneDoorbellSession,
  markSoftphoneIncomingResume,
  markSoftphoneQuickAnswer,
  readIncomingResumeFromCache,
  clearSoftphoneCallEndedGate,
  consumeIncomingSoftphoneIntent,
  setLoginUiComplete,
  setPendingDoorbellCamera,
  shouldRecoverSessionAfterReload,
} from './utils/sessionFlags';
import { tryClosePwaWindow, replaceHistoryForCallEnded } from './installed/phone';
import { canControlDevicesSync, canControlDevices } from './services/permissionsService';
import { getListenerPool } from './utils/heavyInitializations';
import { getDemoActivityTracker } from './services/demoActivityTracker';
import {
  startSessionInactivity,
  stopSessionInactivity,
  touchSessionActivity,
} from './services/sessionInactivityService';
import {
  auditAssistantControl,
  type AssistantControlDeferred,
} from './services/assistantApi';
import {
  formatAssistantControlNoop,
  formatAssistantControlSuccess,
  planPowerControl,
  isDevicePowerOn,
} from './services/assistantDeviceControl';
import { loadDownloadedIconifyIcons } from './utils/artifactIconUtils';
import { updateTiposArtefactoMap } from './components/shared/DeviceTypeImage';
import { cargarSonos, mapaSonos, refrescarSonos, getSonosByHabitacion } from './installed/music';
import { cargarSamsung, mapaSamsung, getSamsungByHabitacion, upsertSamsungDevice } from './installed/samsung';
import { cargarAndroidTv, mapaAndroidTv, getAndroidTvByHabitacion, patchAndroidTvDevice } from './installed/androidtv';
import { cargarLg, mapaLg, getLgByHabitacion, upsertLgDevice } from './installed/air';
import { cargarEvohome, mapaEvohome, getEvohomeByHabitacion, upsertEvohomeDevice } from './installed/heat';
import { cargarPileta, mapaPileta, getPiletaByHabitacion, upsertPiletaDevice } from './installed/pileta';
import { collectRoomItems, sortRoomItems, type RoomSortCriterion } from './utils/roomDeviceOrder';
import { useRoomAvSync } from './hooks/useRoomAvSync';
import {
  androidTvModelKey,
  collectModelFilterGroups,
  evohomeModelKey,
  isShellyExclusiveFilterActive,
  lgModelKey,
  matchesModelFilter,
  piletaModelKey,
  samsungModelKey,
  sonosModelKey,
} from './utils/deviceModelFilters';
import ModelFilterByManufacturer from './components/shared/ModelFilterByManufacturer';
import { sonosControl, getFavorites, getSonosFavoritesOrden, addFavorite, removeFavorite } from './installed/music';
import type { SonosSpeaker } from './installed/music';
import type { SamsungDevice } from './installed/samsung';
import type { AndroidTvDevice } from './installed/androidtv';
import type { LgDevice } from './installed/air';
import type { EvohomeDevice } from './installed/heat';
import type { PiletaDevice } from './installed/pileta';
import { requirePinIfNeeded } from './services/pinGate';
import { PiletaDeviceCard } from './installed/pileta';
import { SOLAR_INSTALLED, SolarEnergy, preload_SolarEnergy } from './installed/solar';
import { ASSISTANT_INSTALLED, AssistantHubPage, preload_AssistantHubPage } from './installed/assistant';
import { MUSIC_INSTALLED, MusicSystem, preload_MusicSystem } from './installed/music';
import { PILETA_INSTALLED, PiletaPage } from './installed/pileta';
import { PHONE_INSTALLED, preload_SipSoftphone, preload_softphoneApi, preload_kickSoftphoneStart, preload_SoftphoneClient } from './installed/phone';
import { CAMERAS_INSTALLED, preload_VideoStreamViewer, loadCameraModule } from './installed/cameras';
import { AIR_INSTALLED } from './installed/air';
import { HEAT_INSTALLED } from './installed/heat';


function readQuickAnswerBootstrap(): {
  token: string | null;
  user: ReturnType<typeof getUser>;
  island: boolean;
} {
  if (typeof window === 'undefined') {
    return { token: null, user: null, island: false };
  }
  try {
    if (shouldRecoverSessionAfterReload()) {
      return { token: localStorage.getItem('token'), user: getUser(), island: false };
    }
    // App ya logueada en este documento: remount (Android start_url) no debe
    // pedir huella ni marcar quick-answer (eso manda al login al colgar).
    if (hasLoggedInDocumentSession()) {
      const stored = localStorage.getItem('token');
      const user = getUser();
      if (stored && user && isStoredJwtAlive(stored)) {
        setLoginUiComplete(true);
        if (isSoftphoneDoorbellDeepLink()) {
          markSoftphoneDoorbellSession();
          const cam = getDoorbellCameraFromUrl();
          if (cam != null) setPendingDoorbellCamera(cam);
        }
        return { token: stored, user, island: false };
      }
    }
    if (isSoftphoneAnswerDeepLink()) {
      const stored = localStorage.getItem('token');
      const user = getUser();
      if (stored && user && isStoredJwtAlive(stored)) {
        markSoftphoneQuickAnswer();
        if (isSoftphoneDoorbellDeepLink()) {
          markSoftphoneDoorbellSession();
          const cam = getDoorbellCameraFromUrl();
          if (cam != null) setPendingDoorbellCamera(cam);
        }
        setLoginUiComplete(true);
        return { token: stored, user, island: true };
      }
    }
  } catch {
    /* ignore */
  }
  return { token: null, user: null, island: false };
}

const QUICK_ANSWER_BOOT = readQuickAnswerBootstrap();

// Code splitting: Cargar componentes pesados solo cuando se necesiten
const SettingsPage = lazy(() => import('./pages/Settings'));

const GroupsModal = lazy(() => import('./components/GroupsModal'));
const HistoricalReports = lazy(() => import('./pages/HistoricalReports'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));

const AlarmSystem = lazy(() => import('./pages/AlarmSystem'));

if (typeof window !== 'undefined' && (isSoftphoneAnswerDeepLink() || hasIncomingSoftphoneIntent())) {
  void preload_SipSoftphone();
  void preload_VideoStreamViewer();
  void preload_softphoneApi().then(({ fetchSoftphoneCredentials }) => {
    void fetchSoftphoneCredentials().catch(() => { /* ignore */ });
  });
}
const ClimateSystem = lazy(() => import('./pages/ClimateSystem'));
const WidgetsDashboard = lazy(() => import('./pages/WidgetsDashboard'));

// Función para precargar todos los componentes lazy en background
const preloadLazyComponents = async () => {
  try {
    // Precargar todos los componentes lazy en paralelo
    await Promise.all([
      import('./pages/Settings'),
      preload_SolarEnergy(),
      import('./components/GroupsModal'),
      import('./pages/HistoricalReports'),
      import('./pages/FavoritesPage'),
      preload_AssistantHubPage(),
      import('./pages/AlarmSystem'),
      preload_MusicSystem(),
      import('./pages/ClimateSystem'),
      import('./pages/WidgetsDashboard'),
    ]);
    console.log('✅ [PRECARGA] Componentes lazy precargados');
  } catch (error) {
    console.error('❌ [PRECARGA] Error precargando componentes:', error);
  }
};

// Pool de listeners ahora se inicializa de forma lazy en utils/heavyInitializations.ts
// Esto mejora el tiempo de carga inicial

// Configuración de axios
axios.defaults.timeout = 10000;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common['Accept'] = 'application/json';

/** Tableros/mapas usan axios "crudo" (no el interceptor de api.ts). Sin este header, un remount queda logueado y la casa vacía. */
function bindAxiosAuthToken(token: string | null | undefined) {
  if (!token) {
    delete axios.defaults.headers.common['Authorization'];
    return;
  }
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}
if (QUICK_ANSWER_BOOT.token) {
  bindAxiosAuthToken(QUICK_ANSWER_BOOT.token);
} else if (typeof window !== 'undefined') {
  try {
    const stored = localStorage.getItem('token');
    if (stored && isStoredJwtAlive(stored)) bindAxiosAuthToken(stored);
  } catch {
    /* ignore */
  }
}
if (
  typeof window !== 'undefined'
  && QUICK_ANSWER_BOOT.token
  && (isSoftphoneAnswerDeepLink() || hasIncomingSoftphoneIntent())
  && !isSoftphoneDoorbellDeepLink()
  && !isSoftphoneDoorbellSession()
) {
  void preload_kickSoftphoneStart().then(({ kickSoftphoneStart }) => {
    void kickSoftphoneStart();
  });
}

// Función de logging consistente (comentada para evitar warning)
// const SHC_LOG = (message: string) => {
//   const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
// console.log(`[SHELLY_DEBUG] ${timestamp} - ${message}`);
// };

// Función de logging de errores
const SHC_ERROR = (_message: string, _error?: any) => {
  // Logging desactivado en producción; reactivar si hace falta depuración
};

// Función para determinar el color según el valor de consumo
const getColorForConsumo = (consumo?: number): string => {
  if (consumo === undefined || consumo === null) return '#888888';
  return consumo >= 0 ? '#2391FF' : '#00ff00';
};

// Función para formatear consumo (W o kW)
const formatConsumption = (watts: number): string => {
  if (Math.abs(watts) >= 1000) {
    return `${(watts / 1000).toFixed(2)} kW`;
  }
  return `${watts.toFixed(2)} W`;
};

// Constantes de diseño (exactamente como React Native)
const ROOM_CARD_HEIGHT = 137; // Altura de las tarjetas de habitaciones (114px + 20% = 137px)

/** Diálogos de página de inicio: dejan libre la barra inferior (~56px + safe-area). */
const HOME_PAGE_DIALOG_OVERLAY_SX = {
  position: 'fixed' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1300,
  boxSizing: 'border-box' as const,
  paddingTop: 16,
  paddingLeft: 16,
  paddingRight: 16,
  paddingBottom: 'calc(56px + env(safe-area-inset-bottom, 0px) + 8px)',
};

const HOME_PAGE_DIALOG_CARD_SX = {
  backgroundColor: '#333',
  borderRadius: 2,
  padding: 2,
  width: '100%',
  minWidth: 280,
  maxWidth: 400,
  maxHeight: '100%',
  margin: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
  boxSizing: 'border-box' as const,
};

const HOME_PAGE_DIALOG_LIST_SX = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 0.65,
  overflowY: 'auto' as const,
  flex: 1,
  minHeight: 0,
  WebkitOverflowScrolling: 'touch' as const,
};

const HOME_PAGE_OPTION_BTN_SX = {
  paddingY: 0.65,
  fontSize: '0.875rem',
  minHeight: 34,
  lineHeight: 1.2,
  textTransform: 'none' as const,
};

function HomeChoiceButton({
  label,
  onClick,
  selected = false,
  outlined = false,
}: {
  label: string;
  onClick: () => void;
  selected?: boolean;
  outlined?: boolean;
}) {
  const filled = !outlined || selected;
  return (
    <Button
      onClick={onClick}
      variant={outlined ? (selected ? 'contained' : 'outlined') : undefined}
      sx={{
        backgroundColor: filled ? '#2391FF' : 'transparent',
        color: filled ? '#fff' : '#2391FF',
        borderColor: '#2391FF',
        ...HOME_PAGE_OPTION_BTN_SX,
        '&:hover': {
          backgroundColor: filled ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
          color: filled ? '#fff' : '#2391FF',
        },
      }}
    >
      {label}{selected ? ' ✓' : ''}
    </Button>
  );
}

const HOME_PAGE_OPTION_DISABLED_SX = {
  ...HOME_PAGE_OPTION_BTN_SX,
  backgroundColor: 'transparent',
  color: 'rgba(255,255,255,0.38)',
  borderColor: '#555',
  opacity: 0.7,
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.38)',
    borderColor: '#555',
  },
};

// Mapa de imágenes de dispositivos ahora se carga de forma dinámica en utils/deviceImages.ts
// Esto evita incluir todas las imágenes en el bundle inicial

// Interfaces
interface DispositivoConConsumo {
  id: number;
  nombre: string;
  tipo: string;
  ip?: string;
  estado: boolean;
  online: boolean;
  habitacion_id?: number | null;
  consumo: number;
  ultimoConsumoEmitido?: number;
  habitacion_nombre?: string;
  firmware_version?: string;
  ultima_actualizacion?: string;
  metadata?: any;
  lights_brightness?: number;
  cover_position?: number;
  es_habitacion_especial?: boolean;
  artefacto_id?: number | null;
  tipo_artefacto?: string | null;
  timer_config?: any;
  agenda_config?: any;
  orden?: number;
  auth_required?: boolean;
  auth_configured?: boolean;
  pin_required?: boolean;
  camera_ids?: number[];
  is_favorite?: boolean;
  [key: string]: any;
}

interface HabitacionConConsumo {
  id: number;
  nombre: string;
  tablero_id: number;
  consumo: number;
  ultimoConsumoEmitido?: number;
  [key: string]: any;
}

// Servicio API (exactamente como React Native)
class ApiService {
  private static baseURL = getApiBaseUrl();
  private static token: string | null = null;

  static setBaseURL(url: string) {
    this.baseURL = url;
    // Guardar la URL del servidor en localStorage
    const serverUrl = url.replace('/api', '');
    localStorage.setItem('serverUrl', serverUrl);
  }

  static getBaseURL(): string {
    return this.baseURL;
  }

  static setToken(token: string) {
    this.token = token;
    bindAxiosAuthToken(token);
  }

  static getToken(): string | null {
    return this.token;
  }

  static async login(email: string, password: string) {
    // SHC_LOG(`Iniciando proceso de login para: ${email}`);
    try {
      // SHC_LOG(`Enviando request POST a: ${this.baseURL}/login`);
      const response = await axios.post(`${this.baseURL}/login`, {
        email,
        password,
      });
      // SHC_LOG(`Login exitoso para: ${email} | Status: ${response.status}`);
      return response.data;
    } catch (error: any) {
      SHC_ERROR(`Error en login para: ${email}`, error);
      throw error;
    }
  }

  static async getBoards() {
    // SHC_LOG('Obteniendo tableros');
    try {
      const response = await axios.get(`${this.baseURL}/tableros`);
      // SHC_LOG(`Tableros obtenidos: ${response.data.length}`);
      return response.data;
    } catch (error: any) {
      // SHC_LOG(`Error obteniendo tableros: ${error.message}`);
      throw error;
    }
  }

  static async getBoardRooms(boardId: number) {
    // SHC_LOG(`Obteniendo habitaciones de tablero ${boardId}`);
    try {
      const response = await axios.get(`${this.baseURL}/tableros/${boardId}/habitaciones`);
      // SHC_LOG(`Habitaciones de tablero ${boardId}: ${response.data.length}`);
      return response.data;
    } catch (error: any) {
      // SHC_LOG(`Error obteniendo habitaciones de tablero: ${error.message}`);
      throw error;
    }
  }

  static async getHabitaciones() {
    // SHC_LOG('Obteniendo habitaciones permitidas para el usuario');
    try {
      const response = await axios.get(`${this.baseURL}/habitaciones`);
      console.log(`[DEBUG] API /habitaciones devuelve:`, response.data.length, 'habitaciones');
      console.log(`[DEBUG] Token actual:`, this.token ? 'presente' : 'ausente');
      // SHC_LOG(`Habitaciones permitidas: ${response.data.length}`);
      return response.data;
    } catch (error: any) {
      // SHC_LOG(`Error obteniendo habitaciones permitidas: ${error.message}`);
      throw error;
    }
  }

  static async getRoomDevices(roomId: number) {
    // SHC_LOG(`Obteniendo dispositivos de habitación ${roomId}`);
    try {
      const response = await axios.get(`${this.baseURL}/habitaciones/${roomId}/dispositivos`);
      // SHC_LOG(`Dispositivos de habitación ${roomId}: ${response.data.length}`);
      return response.data;
    } catch (error: any) {
      // SHC_LOG(`Error obteniendo dispositivos de habitación: ${error.message}`);
      throw error;
    }
  }

  static async getRoomConsumption(roomId: number) {
    // SHC_LOG(`Obteniendo consumo de habitación ${roomId}`);
    try {
      const devices = await this.getRoomDevices(roomId);
      const totalConsumption = devices.reduce((total: number, device: any) => {
        return total + (device.consumo || 0);
      }, 0);
      // SHC_LOG(`Consumo total habitación ${roomId}: ${totalConsumption}W`);
      return totalConsumption;
    } catch (error: any) {
      // SHC_LOG(`Error obteniendo consumo de habitación: ${error.message}`);
      return 0;
    }
  }

  static async toggleDevice(deviceId: number) {
    // SHC_LOG(`Alternando dispositivo ${deviceId}`);
    try {
      const response = await axios.post(`${this.baseURL}/toggle_device/${deviceId}`);
      // SHC_LOG(`Dispositivo ${deviceId} alternado exitosamente`);
      return response.data;
    } catch (error: any) {
      // SHC_LOG(`Error alternando dispositivo: ${error.message}`);
      throw error;
    }
  }
}

// NOTA: recentControlResponses se importa desde consumptionService.ts
// Es un Map<number, number> que almacena deviceId -> timestamp

// Servicio WebSocket para tiempo real (exactamente como React Native)
// OPTIMIZADO: Reconexión rápida con visibilitychange
class WebSocketService {
  private static socket: any = null;
  private static lastAuthToken: string | null = null;
  private static connected: boolean = false;
  private static reconnectAttempts: number = 0;
  private static maxReconnectAttempts: number = 10; // Aumentado de 5 a 10
  private static reconnectDelay: number = 500; // OPTIMIZADO: Reducido de 1000 a 500ms
  private static maxReconnectDelay: number = 5000; // OPTIMIZADO: Reducido de 30000 a 5000ms
  private static healthCheckInterval: NodeJS.Timeout | null = null;
  private static keepAliveInterval: NodeJS.Timeout | null = null;
  private static lastHeartbeat: number = 0;
  private static lastMessageTime: number = 0; // MEJORA 4: Para detectar conexiones muertas
  private static heartbeatInterval: number = 5000; // 5 segundos para health check
  private static keepAliveIntervalTime: number = 30000; // 30 segundos para keep-alive
  private static connectionTimeout: number = 60000; // MEJORA 4: 60 segundos sin mensajes = conexión muerta
  private static connectionCheckInterval: NodeJS.Timeout | null = null; // MEJORA 4: Interval para verificar conexiones muertas
  private static isConnecting: boolean = false; // SOLUCIÓN: Flag para prevenir conexiones duplicadas
  private static connectPromise: Promise<void> | null = null; // SOLUCIÓN: Promise para esperar conexiones en progreso
  private static connectResolve: (() => void) | null = null; // SOLUCIÓN: Resolver de la promise de conexión
  private static connectReject: ((error: Error) => void) | null = null; // SOLUCIÓN: Reject de la promise de conexión
  private static visibilityHandler: (() => void) | null = null; // OPTIMIZADO: Handler para visibilitychange
  private static connectingSince: number | null = null; // WATCHDOG: marca de tiempo desde que estamos sin conexión
  private static watchdogInterval: NodeJS.Timeout | null = null; // WATCHDOG: fuerza una reconexión limpia si la conexión queda atascada

  // POOL DE LISTENERS - Usar el pool global inicializado al inicio de la sesión
  private static currentListenerIndex: number = 0;
  private static readonly POOL_SIZE: number = 30;
  
  // MEJORA 3: Flag para rastrear si el listener de respuestas está configurado
  private static controlResponseListenerConfigured: boolean = false;
  // Evita pintar rojo en el UI cuando disconnect() es parte de una reconexión planificada
  private static suppressDisconnectEvent: boolean = false;

  // OPTIMIZADO: Inicializar listener de visibilidad para reconexión rápida
  private static initVisibilityListener(): void {
    if (typeof document === 'undefined' || this.visibilityHandler) return;
    
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && !this.connected) {
        const authToken = localStorage.getItem('token');
        if (authToken) {
          this.reconnectAttempts = 0;
          this.connect(authToken).catch(() => {
            // Silenciar errores, se reintentará automáticamente
          });
        }
      }
    };
    
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  // WATCHDOG: red de seguridad que se ejecuta de forma independiente al socket.
  // Detecta el caso en que la app abre, el primer handshake falla y el cliente
  // queda atascado (sin volverse verde) sin reintentar por su cuenta. Si llevamos
  // demasiado tiempo sin conexión y socket.io tampoco está reintentando, fuerza
  // una reconexión limpia. Esto elimina la necesidad de cerrar y reabrir la app.
  private static initWatchdog(): void {
    if (this.watchdogInterval) return;

    this.watchdogInterval = setInterval(() => {
      try {
        const hasToken = !!(this.lastAuthToken || localStorage.getItem('token'));
        if (!hasToken) {
          // Sin token (deslogueado): no hay nada que vigilar
          this.connectingSince = null;
          return;
        }

        if (this.isConnected()) {
          // Conexión sana: reiniciar el cronómetro
          this.connectingSince = null;
          return;
        }

        // A partir de aquí estamos SIN conexión
        if (this.connectingSince === null) {
          this.connectingSince = Date.now();
        }

        const stuckMs = Date.now() - this.connectingSince;
        const reconnecting = !!(this.socket && this.socket.io && this.socket.io.reconnecting);

        // Si socket.io está reintentando por su cuenta, lo dejamos trabajar.
        // Solo intervenimos cuando lleva demasiado tiempo atascado y NADIE reintenta.
        if (stuckMs > 30000 && !reconnecting) {
          this.forceReconnect();
          this.connectingSince = Date.now();
        }
      } catch (e) {
        // No-op: el watchdog nunca debe romper la app
      }
    }, 10000);
  }

  // Reconexión limpia: resetea cualquier estado atascado (promesa/flag de
  // "conectando", socket muerto) y vuelve a conectar con el token vigente.
  private static forceReconnect(): void {
    const token = this.lastAuthToken || localStorage.getItem('token');

    // Resetear el estado interno que podría quedar atascado
    this.connectPromise = null;
    this.connectResolve = null;
    this.connectReject = null;
    this.isConnecting = false;
    this.reconnectAttempts = 0;

    // Detener timers para evitar duplicados (connect() los reinicia al conectar)
    this.stopHealthCheck();
    this.stopKeepAlive();
    this.stopConnectionCheck();

    // Tirar abajo el socket actual si existe (sin emitir rojo al UI)
    if (this.socket) {
      this.suppressDisconnectEvent = true;
      try { this.socket.removeAllListeners(); } catch (e) { /* no-op */ }
      try { this.socket.disconnect(); } catch (e) { /* no-op */ }
      this.socket = null;
    }
    this.connected = false;
    this.controlResponseListenerConfigured = false;

    // Indicar visualmente que estamos reintentando
    eventBus.emit('websocket:connecting');

    if (token) {
      this.connect(token).catch(() => {
        // Silenciar: el watchdog volverá a intentar si hace falta
      });
    }
  }

  static connect(token?: string): Promise<void> {
    // OPTIMIZADO: Inicializar listener de visibilidad si no está inicializado
    this.initVisibilityListener();
    // WATCHDOG: asegurar que la red de seguridad esté activa
    this.initWatchdog();
    
    // SOLUCIÓN: Si ya hay una conexión en progreso, esperar a que termine
    if (this.connectPromise) {
      return this.connectPromise.then(() => {
        // Después de que termine la conexión anterior, verificar si necesitamos reconectar
        // Solo reconectar si el token cambió o si no hay conexión activa
        const authToken = token || localStorage.getItem('token');
        if (authToken && this.lastAuthToken && authToken !== this.lastAuthToken) {
          // Token cambió, necesitamos reconectar
          return this.connect(token);
        }
        if (!this.socket || !this.socket.connected) {
          // No hay conexión activa, reconectar
          return this.connect(token);
        }
        this.ensureReadyEmitted();
        return Promise.resolve();
      });
    }

    // SOLUCIÓN: Si ya hay un socket conectado con el mismo token, no hacer nada
    if (this.socket && this.socket.connected) {
      const authToken = token || localStorage.getItem('token');
      if (authToken && this.lastAuthToken && authToken !== this.lastAuthToken) {
        // Token cambió, necesitamos rehacer el handshake
        this.disconnect({ silent: true });
        // Continuar con la nueva conexión
      } else {
        // Ya hay conexión activa con el mismo token — asegurar que el UI reciba ready
        this.ensureReadyEmitted();
        return Promise.resolve();
      }
    }

    // SOLUCIÓN: Marcar que estamos conectando y crear promise
    this.isConnecting = true;
    this.connectPromise = new Promise<void>((resolve, reject) => {
      // Guardar resolve y reject para usarlos en los listeners
      this.connectResolve = resolve;
      this.connectReject = reject;
      
      // Si hay un socket pero no está conectado, desconectarlo primero
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }

      console.log('🔌 Conectando WebSocket...');
      // Emitir evento de connecting antes de crear el socket
      eventBus.emit('websocket:connecting');
      
      // Obtener token de autenticación: usar el proporcionado o leer de localStorage
      const authToken = token || localStorage.getItem('token');
      if (!authToken) {
        // Sin token no tiene sentido abrir WebSocket de control
        this.isConnecting = false;
        this.connectPromise = null;
        this.connectResolve = null;
        this.connectReject = null;
        reject(new Error('No token provided'));
        return;
      }
      this.lastAuthToken = authToken;
    
      // Construir URL asegurando WSS si la página es HTTPS
      let serverUrl = getServerUrl();
      if (window.location.protocol === 'https:') {
        // Asegurar que use WSS (WebSocket Secure)
        serverUrl = serverUrl.replace(/^http:/, 'https:').replace(/^ws:/, 'wss:');
      }
      
      this.socket = io(serverUrl, {
        auth: {
          token: authToken
        },
        transports: ['websocket', 'polling'], // WebSocket con fallback a polling
        reconnection: true,
        reconnectionAttempts: 20, // Aumentado de 10 a 20 intentos
        reconnectionDelay: 500, // OPTIMIZADO: Reducido de 1000 a 500ms para reconexión más rápida
        reconnectionDelayMax: 5000, // Reducido de 30000 a 5000ms
        timeout: 10000, // Reducido de 20000 a 10000ms
        forceNew: false, // No forzar nueva conexión para mantener persistencia
        upgrade: true, // Permitir upgrade automático
        rememberUpgrade: true, // Recordar upgrade para futuras conexiones
      });
    
    // MEJORA: Configurar TODOS los listeners INMEDIATAMENTE después de crear el socket
    // Esto asegura que estén listos ANTES de que se establezca la conexión
    // para no perder ningún mensaje que llegue durante la conexión
    
    // CRÍTICO: Configurar listener de respuestas de control ANTES de conectar
    this.setupControlResponseListener();
    
    // Escuchar eventos de actualización de dispositivos (telemetría) - LISTO DESDE EL INICIO
    // CRITICO: Este listener procesa directamente los eventos device_update del backend
    // y actualiza mapaDispositivos inmediatamente, asegurando que no se pierdan actualizaciones
    this.socket.on('device_update', (data: any) => {
      this.lastHeartbeat = Date.now();
      this.lastMessageTime = Date.now(); // MEJORA 4: Actualizar tiempo de último mensaje
      this.handleDeviceUpdate(data);
    });

    this.socket.on('dashboard_layout_updated', (data: any) => {
      this.lastHeartbeat = Date.now();
      this.lastMessageTime = Date.now();
      eventBus.emit('dashboard:layout_updated', data);
    });

    // ✅ ROBUSTEZ WS: el servidor responde 'pong' a cada 'ping' que enviamos (cada 5s en
    // startHealthCheck). Marcamos la conexión como viva con ese pong. Sin esto, el chequeo
    // de "conexión muerta" (60s sin device_update) cortaba sockets SANOS —p.ej. mientras el
    // usuario solo mira gráficos y no hay telemetría de dispositivos, o mientras el worker
    // está ocupado sirviendo una descarga grande— provocando reconexiones innecesarias
    // (el indicador de abajo a la derecha en naranja/rojo).
    this.socket.on('pong', () => {
      this.lastHeartbeat = Date.now();
      this.lastMessageTime = Date.now();
    });

    // Escuchar eventos de timer_master_disabled
    this.socket.on('timer_master_disabled', (data: any) => {
      this.lastHeartbeat = Date.now();
      this.lastMessageTime = Date.now(); // MEJORA 4: Actualizar tiempo de último mensaje
      console.log(`📡 Evento timer_master_disabled recibido: ${JSON.stringify(data)}`);
      // Re-emitir al eventBus para que DeviceModal lo escuche
      eventBus.emit('timer_master_disabled', data);
    });

    // Escuchar eventos de timer_activated
    this.socket.on('timer_activated', (data: any) => {
      this.lastHeartbeat = Date.now();
      this.lastMessageTime = Date.now(); // MEJORA 4: Actualizar tiempo de último mensaje
      console.log(`🕐 Evento timer_activated recibido: ${JSON.stringify(data)}`);
      // Re-emitir al eventBus para que App.tsx lo maneje
      eventBus.emit('timer_activated', data);
    });

    // Escuchar eventos de timer_deactivated
    this.socket.on('timer_deactivated', (data: any) => {
      this.lastHeartbeat = Date.now();
      this.lastMessageTime = Date.now(); // MEJORA 4: Actualizar tiempo de último mensaje
      console.log(`🛑 Evento timer_deactivated recibido: ${JSON.stringify(data)}`);
      // Re-emitir al eventBus para que App.tsx lo maneje
      eventBus.emit('timer_deactivated', data);
    });

    // Escuchar eventos de device_schedules_updated
    this.socket.on('device_schedules_updated', (data: any) => {
      this.lastHeartbeat = Date.now();
      this.lastMessageTime = Date.now(); // MEJORA 4: Actualizar tiempo de último mensaje
      console.log(`🔄 Evento device_schedules_updated recibido:`, data);
      this.handleSchedulesUpdate(data);
    });

    // Escuchar evento de forzar refresco de estructura (asignar habitación, descubrimiento, etc.)
    this.socket.on('estructura:forzar_refresco', (data: any) => {
      this.lastHeartbeat = Date.now();
      this.lastMessageTime = Date.now();
      eventBus.emit('estructura:forzar_refresco', data);
    });

    this.socket.on('grupos:actualizado', (data: any) => {
      this.lastHeartbeat = Date.now();
      this.lastMessageTime = Date.now();
      eventBus.emit('grupos:ws', data);
    });
    
    // Escuchar evento de reconexión automática de socket.io
    this.socket.io.on('reconnect_attempt', () => {
      eventBus.emit('websocket:connecting');
      // Reenviar token al reconectar
      const currentToken = this.lastAuthToken || localStorage.getItem('token');
      if (currentToken && this.socket) {
        this.socket.auth = { token: currentToken };
      }
    });

    // OPTIMIZACIÓN 2: Reducir delay de reconexión fallida
    // FIX: en socket.io-client v4 los eventos de reconexión los emite el Manager
    // (this.socket.io), NO el socket. Antes esto estaba en this.socket y NUNCA se
    // disparaba, por eso al agotar los intentos la app quedaba muerta sin recuperarse.
    this.socket.io.on('reconnect_failed', () => {
      console.log('❌ Reconexión fallida después de todos los intentos, reiniciando...');
      this.reconnectAttempts = 0;
      // OPTIMIZADO: Reducir delay de 5000ms a 2000ms para reconexión más rápida
      setTimeout(() => {
        this.disconnect({ silent: true });
        this.connect();
      }, 2000); // OPTIMIZADO: Reducido de 5000ms a 2000ms
    });

    // SOLUCIÓN 3: Listener para reconnect (éxito) - actualiza estado y reinicia timers
    // MEJORA 2: Reconfigurar listeners explícitamente en 'reconnect'
    // SOLUCIÓN 5: Asegurar que todo esté listo antes de emitir 'websocket:ready'
    // FIX: 'reconnect' también lo emite el Manager (this.socket.io), no el socket.
    this.socket.io.on('reconnect', (attemptNumber: number) => {
      console.log(`✅ WebSocket reconectado exitosamente después de ${attemptNumber} intentos`);
      this.connected = true;
      this.connectingSince = null;
      this.reconnectAttempts = 0;
      this.lastHeartbeat = Date.now();
      this.lastMessageTime = Date.now(); // MEJORA 4: Inicializar tiempo de último mensaje
      
      // SOLUCIÓN 5: Reconfigurar listener de respuestas explícitamente y asegurar que se complete
      this.setupControlResponseListener();
      
      // SOLUCIÓN 5: Inicializar pool de listeners si no está inicializado
      getListenerPool();
      
      // Reiniciar health check, keep-alive y verificación de conexiones muertas
      this.startHealthCheck();
      this.startKeepAlive();
      this.startConnectionCheck(); // MEJORA 4: Iniciar verificación de conexiones muertas
      
      // Emitir evento de reconexión INMEDIATAMENTE para mostrar moving dots (connecting)
      eventBus.emit('websocket:reconnected');
      
      // CORRECCIÓN PROBLEMA 1: Verificar isReady() INMEDIATAMENTE (sin delay de 300ms)
      // setupControlResponseListener() ya se completó en línea 745, no necesita delay
      if (this.isReady()) {
        // Si está listo, emitir inmediatamente
        eventBus.emit('websocket:ready');
      } else {
        // Si no está listo, intentar varias veces más
        let attempts = 0;
        const maxAttempts = 5;
        const checkInterval = setInterval(() => {
          attempts++;
          if (this.isReady()) {
            clearInterval(checkInterval);
            eventBus.emit('websocket:ready');
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            // FALLBACK CRÍTICO: Si después de varios intentos no está listo según isReady(),
            // pero el socket está conectado y el listener está configurado, emitir de todas formas
            // Esto previene que la app quede bloqueada si isReady() tiene un problema
            if (this.socket?.connected && this.controlResponseListenerConfigured) {
              eventBus.emit('websocket:ready');
            }
          }
        }, 100);
      }
      
      // FALLBACK DE SEGURIDAD: Después de 1 segundo, si el socket está conectado y el listener configurado,
      // emitir 'websocket:ready' de todas formas para evitar que la app quede bloqueada
      setTimeout(() => {
        if (this.socket?.connected && this.controlResponseListenerConfigured) {
          // Verificar una última vez si isReady() retorna true
          if (this.isReady()) {
            eventBus.emit('websocket:ready');
          } else {
            // Si isReady() falla pero el socket está conectado y el listener configurado,
            // emitir de todas formas para que la app funcione
            eventBus.emit('websocket:ready');
          }
        }
      }, 1000);
    });

    this.socket.on('connect', () => {
      this.connected = true;
      this.connectingSince = null; // WATCHDOG: conexión sana, limpiar cronómetro
      this.reconnectAttempts = 0;
      this.lastHeartbeat = Date.now();
      this.lastMessageTime = Date.now(); // MEJORA 4: Inicializar tiempo de último mensaje
      console.log('✅ WebSocket conectado exitosamente');
      
      // SOLUCIÓN 5: Asegurar que el listener de respuestas esté configurado y se complete
      this.setupControlResponseListener();
      
      // SOLUCIÓN 5: Inicializar pool de listeners si no está inicializado
      getListenerPool();
      
      // SOLUCIÓN: Marcar que ya no estamos conectando y resolver la promise
      this.isConnecting = false;
      if (this.connectResolve) {
        const resolve = this.connectResolve;
        this.connectPromise = null;
        this.connectResolve = null;
        this.connectReject = null;
        resolve();
      }
      
      // Asegurar que el token esté actualizado en la autenticación
      const currentToken = this.lastAuthToken || localStorage.getItem('token');
      if (currentToken && this.socket) {
        this.socket.auth = { token: currentToken };
        this.lastAuthToken = currentToken;
      }
      
      // Iniciar health check
      this.startHealthCheck();
      
      // Iniciar keep-alive automático
      this.startKeepAlive();
      
      // MEJORA 4: Iniciar verificación de conexiones muertas
      this.startConnectionCheck();
      
      // Emitir evento de reconexión INMEDIATAMENTE para mostrar moving dots (connecting)
      eventBus.emit('websocket:reconnected');
      
      // CORRECCIÓN PROBLEMA 1: Verificar isReady() INMEDIATAMENTE (sin delay de 300ms)
      // setupControlResponseListener() ya se completó en línea 810, no necesita delay
      if (this.isReady()) {
        // Si está listo, emitir inmediatamente
        eventBus.emit('websocket:ready');
      } else {
        // Si no está listo, intentar varias veces más
        let attempts = 0;
        const maxAttempts = 5;
        const checkInterval = setInterval(() => {
          attempts++;
          if (this.isReady()) {
            clearInterval(checkInterval);
            eventBus.emit('websocket:ready');
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            // FALLBACK CRÍTICO: Si después de varios intentos no está listo según isReady(),
            // pero el socket está conectado y el listener está configurado, emitir de todas formas
            // Esto previene que la app quede bloqueada si isReady() tiene un problema
            if (this.socket?.connected && this.controlResponseListenerConfigured) {
              eventBus.emit('websocket:ready');
            }
          }
        }, 100);
      }
      
      // FALLBACK DE SEGURIDAD: Después de 1 segundo, si el socket está conectado y el listener configurado,
      // emitir 'websocket:ready' de todas formas para evitar que la app quede bloqueada
      setTimeout(() => {
        if (this.socket?.connected && this.controlResponseListenerConfigured) {
          // Verificar una última vez si isReady() retorna true
          if (this.isReady()) {
            eventBus.emit('websocket:ready');
          } else {
            // Si isReady() falla pero el socket está conectado y el listener configurado,
            // emitir de todas formas para que la app funcione
            eventBus.emit('websocket:ready');
          }
        }
      }, 1000);
    });

    this.socket.on('disconnect', (reason: string) => {
      this.connected = false;
      console.log(`❌ WebSocket desconectado: ${reason}`);
      
      // OPTIMIZACIÓN 2: Reducir delay de rechazo - socket.io maneja reconexión automáticamente
      // Solo rechazar si es una desconexión manual o error crítico
      if (this.isConnecting && this.connectReject && reason === 'io client disconnect') {
        // Desconexión manual - rechazar inmediatamente
        const reject = this.connectReject;
        this.connectPromise = null;
        this.connectResolve = null;
        this.connectReject = null;
        this.isConnecting = false;
        if (reject) {
          reject(new Error(`Connection cancelled: ${reason}`));
        }
      } else if (this.isConnecting && this.connectReject && reason !== 'io client disconnect') {
        // Error de conexión - socket.io intentará reconectar automáticamente
        // Reducir delay de 5000ms a 2000ms para respuesta más rápida
        setTimeout(() => {
          if (this.isConnecting && !this.socket?.connected) {
            const reject = this.connectReject;
            this.connectPromise = null;
            this.connectResolve = null;
            this.connectReject = null;
            this.isConnecting = false;
            if (reject) {
              reject(new Error(`Connection failed: ${reason}`));
            }
          }
        }, 2000); // OPTIMIZADO: Reducido de 5000ms a 2000ms
      }
      
      // Detener health check, keep-alive y verificación de conexiones muertas
      this.stopHealthCheck();
      this.stopKeepAlive();
      this.stopConnectionCheck(); // MEJORA 4: Detener verificación de conexiones muertas
      
      // Emitir desconexión al UI solo si no es un corte intencional para reconectar
      if (!this.suppressDisconnectEvent) {
        eventBus.emit('websocket:disconnected', reason);
      }
      this.suppressDisconnectEvent = false;
      
      // OPTIMIZACIÓN 2: Eliminar delay innecesario - verificar inmediatamente
      // Si socket.io tiene reconexión automática habilitada, emitir evento de connecting
      if (this.socket && this.socket.io && this.socket.io.reconnecting) {
        eventBus.emit('websocket:connecting');
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      this.connected = false;
      this.reconnectAttempts++;
      console.log(`❌ Error de conexión WebSocket (intento ${this.reconnectAttempts}): ${error.message}`);
      
      // SOLUCIÓN: Si estábamos conectando y hay un error, rechazar la promise solo si no hay reconexión automática
      // (socket.io intentará reconectar automáticamente, así que no rechazamos inmediatamente)
      // La promise se resolverá cuando se establezca la conexión o se rechazará si falla completamente
      
      // Emitir evento de error
      eventBus.emit('websocket:error', error);
      // socket.io intentará reconectar automáticamente, así que emitir connecting
      eventBus.emit('websocket:connecting');
    });

    // Escuchar heartbeat del servidor - COMENTADO: Socket.IO ya maneja ping/pong automáticamente
    // this.socket.on('heartbeat', () => {
    //   this.lastHeartbeat = Date.now();
    // console.log('💓 Heartbeat recibido del servidor');
    // });
    }); // Cerrar la promise
    
    return this.connectPromise;
  }

  static disconnect(options?: { silent?: boolean }) {
    const silent = options?.silent === true;
    if (silent) {
      this.suppressDisconnectEvent = true;
    }

    this.stopHealthCheck();
    this.stopKeepAlive();
    this.stopConnectionCheck(); // MEJORA 4: Detener verificación de conexiones muertas
    
    // OPTIMIZADO: Limpiar listener de visibilidad solo en logout real (no en reconexión)
    if (!silent && this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    
    // SOLUCIÓN: Limpiar flags de conexión pendiente
    // NO rechazar la promise si la conexión está en progreso - esto causa errores durante
    // el desarrollo con React StrictMode que ejecuta efectos dos veces
    // En su lugar, simplemente limpiar los handlers sin rechazar
    this.isConnecting = false;
    if (this.connectReject) {
      // Limpiar los handlers sin rechazar la promise
      // Esto evita el error "Connection cancelled" durante el desarrollo
      this.connectPromise = null;
      this.connectResolve = null;
      this.connectReject = null;
    }
    
    // MEJORA 3: Marcar que el listener ya no está configurado
    this.controlResponseListenerConfigured = false;
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      if (!silent) {
        this.lastAuthToken = null;
      }
      console.log('🔌 WebSocket desconectado manualmente');
    } else if (!silent) {
      this.suppressDisconnectEvent = false;
      eventBus.emit('websocket:disconnected', 'manual');
    }
  }

  // Si el socket ya está conectado, emitir websocket:ready para sincronizar el indicador UI
  static ensureReadyEmitted(): void {
    if (this.isReady()) {
      eventBus.emit('websocket:ready');
      return;
    }
    if (this.socket?.connected && this.controlResponseListenerConfigured) {
      eventBus.emit('websocket:ready');
    }
  }

  static isConnected(): boolean {
    return Boolean(this.connected && this.socket?.connected);
  }

  // SOLUCIÓN: Getter para verificar el token actual (necesario para evitar reconexiones innecesarias)
  static getLastAuthToken(): string | null {
    return this.lastAuthToken;
  }

  // Limpiar el token guardado cuando el backend lo invalida (401). Reemplaza al
  // antiguo servicio duplicado src/services/WebSocketService.ts (ya eliminado).
  static clearStoredToken() {
    this.lastAuthToken = null;
  }

  // Health check optimizado para conexión persistente
  private static startHealthCheck() {
    this.stopHealthCheck(); // Limpiar cualquier health check anterior
    
    this.healthCheckInterval = setInterval(() => {
      if (this.socket && !this.socket.connected) {
        console.log('⚠️ Socket.IO detectó conexión perdida, reconectando...');
        this.reconnect();
      } else if (this.socket && this.socket.connected) {
        // Ping proactivo para mantener conexión activa
        this.socket.emit('ping', { timestamp: Date.now() });
      }
    }, 5000); // Reducido de 30000 a 5000ms para mayor agresividad
  }

  private static stopHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  // Iniciar keep-alive automático para mantener conexión activa
  private static startKeepAlive() {
    this.stopKeepAlive(); // Limpiar cualquier keep-alive anterior
    
    this.keepAliveInterval = setInterval(() => {
      this.keepAlive();
    }, this.keepAliveIntervalTime);
  }

  private static stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  // MEJORA 4: Verificación de conexiones muertas
  private static startConnectionCheck() {
    this.stopConnectionCheck(); // Limpiar cualquier verificación anterior
    
    this.connectionCheckInterval = setInterval(() => {
      if (this.socket && this.socket.connected) {
        const timeSinceLastMessage = Date.now() - this.lastMessageTime;
        if (timeSinceLastMessage > this.connectionTimeout) {
          console.log('⚠️ Conexión muerta detectada (sin mensajes por más de 60s), forzando reconexión...');
          this.reconnect();
        }
      }
    }, 10000); // Verificar cada 10 segundos
  }

  private static stopConnectionCheck() {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
      this.connectionCheckInterval = null;
    }
  }

  // Reconexión inteligente con delay progresivo
  static reconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`🔄 Intentando reconexión ${this.reconnectAttempts}/${this.maxReconnectAttempts}...`);
      
      this.disconnect({ silent: true });
      
      // Delay progresivo pero más agresivo: 500ms, 1000ms, 1500ms, 2000ms, 2000ms...
      const delay = Math.min(500 * this.reconnectAttempts, 2000);
      setTimeout(() => {
        this.connect();
      }, delay);
    } else {
      console.log('❌ Máximo de intentos de reconexión alcanzado');
    }
  }

  // Ping para verificar que la conexión realmente funciona
  static ping() {
    if (this.socket && this.socket.connected) {
      this.socket.emit('ping', { timestamp: Date.now() });
      console.log('📡 Ping enviado al servidor');
    } else {
      throw new Error('WebSocket no está conectado');
    }
  }

  // Autenticar WebSocket con token (para pre-conexión)
  static authenticate(token: string) {
    if (this.socket && this.socket.connected) {
      this.lastAuthToken = token;
      this.socket.emit('authenticate', { token });
      console.log('🔐 WebSocket autenticado con token');
    } else {
      console.log('⚠️ WebSocket no conectado, no se puede autenticar');
    }
  }

  // Keep-alive para mantener conexión activa
  static keepAlive() {
    if (this.socket && this.socket.connected) {
      this.socket.emit('keepalive', { timestamp: Date.now() });
      console.log('💓 Keep-alive enviado');
    }
  }

  // Manejar actualización de dispositivo
  private static handleDeviceUpdate(data: any) {
    if (!data || !data.id) return;
    
    const id = Number(data.id);
    const dispositivo = mapaDispositivos[id];
    
    // LOG: Rastrear actualizaciones de WebSocket para dispositivo 10697
    if (id === 10697) {
      console.log(`[MAP-DEVICES] App.tsx.handleDeviceUpdate() - WebSocket actualizó dispositivo 10697: dispositivo existe=${!!dispositivo}, dispositivo.pin_required=${dispositivo?.pin_required}, dispositivo.tipo_artefacto=${dispositivo?.tipo_artefacto}`);
    }
    
    // CRITICO: Si el dispositivo NO existe en mapaDispositivos, NO crear un objeto nuevo
    // Esto evita crear objetos con valores incorrectos cuando las actualizaciones de WebSocket
    // llegan antes de que refrescarEstructuraCompleta() termine de cargar los datos correctos
    // Simplemente ignorar la actualización hasta que el dispositivo esté completamente cargado
    if (!dispositivo) {
      if (id === 10697) {
        console.log(`[MAP-DEVICES] App.tsx.handleDeviceUpdate() - Ignorando actualización de dispositivo 10697 porque aún no está cargado en mapaDispositivos`);
      }
      return; // No hacer nada si el dispositivo no existe aún
    }
    
    // Calcular estadoFinal: usar el estado del evento si viene, sino el estado actual del dispositivo
    // dispositivo está garantizado de no ser null aquí porque hay un return arriba si es null
    const estadoFinal: boolean | number = data.estado !== undefined ? data.estado : dispositivo.estado;
    
    if (dispositivo) {
      // Telemetría por defecto; si el evento trae pin_required (assign/remove PIN), aplicarlo.
      // CRITICO: NO usar ?? false para pin_required porque puede convertir null/undefined a false incorrectamente
      const pinRequiredValue = data.pin_required !== undefined && data.pin_required !== null
        ? Boolean(data.pin_required)
        : (dispositivo.pin_required !== undefined && dispositivo.pin_required !== null
          ? Boolean(dispositivo.pin_required)
          : dispositivo.pin_required);
      
      // camera_ids: si el evento los trae (asignación desde Settings), aplicar; si no, preservar
      const parseCameraIds = (raw: unknown): number[] | null => {
        if (raw === undefined) return null;
        if (raw === null) return [];
        if (Array.isArray(raw)) return raw.map((x) => Number(x)).filter((n) => Number.isFinite(n));
        if (typeof raw === 'string') {
          try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed)
              ? parsed.map((x) => Number(x)).filter((n) => Number.isFinite(n))
              : [];
          } catch {
            return [];
          }
        }
        return [];
      };
      const fromEventCams = parseCameraIds(data.camera_ids);
      let cameraIdsValue: number[] =
        fromEventCams !== null
          ? fromEventCams
          : (parseCameraIds(dispositivo.camera_ids) ?? mapaDispositivos[id]?.camera_ids ?? []);

      const authRequiredValue = data.auth_required !== undefined && data.auth_required !== null
        ? Boolean(data.auth_required)
        : (dispositivo.auth_required ?? false);
      
      // Detectar flanco real de potencia (no comparar raw: false vs 0/"off" dispara falso positivo)
      const estadoAnterior = dispositivo.estado;
      const nuevoEstado = data.estado !== undefined ? data.estado : dispositivo.estado;
      const wasPowerOn = isDevicePowerOn(estadoAnterior);
      const isPowerOnNow = isDevicePowerOn(nuevoEstado);
      const powerEdge = data.estado !== undefined && wasPowerOn !== isPowerOnNow;
      const estadoCambio = estadoAnterior !== nuevoEstado && data.estado !== undefined;
      
      // Si hay cambio de estado desde telemetría (control físico), registrar timestamp para protección
      // Esto protege el estado de ser sobrescrito por refrescarEstructuraCompleta que puede estar en curso
      if (estadoCambio) {
        recentControlResponses.set(id, Date.now());
      }
      
      // Actualizar dispositivo existente
      // CRITICO: Preservar configuración; si el evento trae nombre/habitación/artefacto (Settings), aplicarlos.
      const nextNombre = data.nombre !== undefined ? data.nombre : dispositivo.nombre;
      const nextHabitacionId = data.habitacion_id !== undefined ? data.habitacion_id : dispositivo.habitacion_id;
      const nextArtefactoId = data.artefacto_id !== undefined ? data.artefacto_id : dispositivo.artefacto_id;
      const nextTipoArtefacto = data.tipo_artefacto !== undefined ? data.tipo_artefacto : dispositivo.tipo_artefacto;
      mapaDispositivos[id] = {
        // PRESERVAR todos los campos de configuración del dispositivo original
        id: dispositivo.id,
        nombre: nextNombre,
        tipo: dispositivo.tipo,
        habitacion_id: nextHabitacionId,
        ip: dispositivo.ip,
        generacion: dispositivo.generacion,
        es_habitacion_especial: dispositivo.es_habitacion_especial || false,
        artefacto_id: nextArtefactoId,
        tipo_artefacto: nextTipoArtefacto,
        orden: dispositivo.orden,
        timer_config: dispositivo.timer_config,
        agenda_config: dispositivo.agenda_config,
        auth_required: authRequiredValue,
        auth_configured: data.auth_configured !== undefined
          ? Boolean(data.auth_configured)
          : (dispositivo.auth_configured ?? false),
        pin_required: pinRequiredValue,
        camera_ids: cameraIdsValue,
        is_favorite: dispositivo.is_favorite,
        // ACTUALIZAR solo campos de telemetría que vienen en WebSocket
        consumo: data.consumo ?? dispositivo.consumo,
        ultimoConsumoEmitido: data.consumo ?? dispositivo.ultimoConsumoEmitido,
        estado: Boolean(estadoFinal), // SOLUCIÓN PROBLEMA 2: Convertir a boolean para cumplir con el tipo
        online: data.online !== undefined ? data.online : dispositivo.online,
        mac: data.mac ?? dispositivo.mac,
        // Campos especiales según tipo de dispositivo - PRESERVAR siempre
        ...(dispositivo.lights_brightness !== undefined ? { lights_brightness: data.lights_brightness !== undefined ? data.lights_brightness : dispositivo.lights_brightness } : {}),
        // Preservar cover_position si el dispositivo es PLUS2PM (igual que lights_brightness)
        ...(dispositivo.tipo === 'PLUS2PM' ? { 
          cover_position: data.cover_position !== undefined ? data.cover_position : ((dispositivo as DispositivoConConsumo).cover_position) 
        } : {}),
        ...(dispositivo.metadata ? { 
          metadata: { 
            ...dispositivo.metadata, 
            coverState: estadoFinal, // SOLUCIÓN PROBLEMA 2: Usar estadoFinal también para coverState
            ...(dispositivo.tipo === 'PLUS2PM' ? { 
              cover_position: data.cover_position !== undefined ? data.cover_position : dispositivo.metadata?.cover_position 
            } : {})
          } 
        } : {})
      };
      
      // LOG: Verificar valores después de actualización WebSocket para dispositivo 10697
      if (id === 10697) {
        console.log(`[MAP-DEVICES] App.tsx.handleDeviceUpdate() - Después de actualizar dispositivo 10697: pin_required=${mapaDispositivos[id].pin_required}, tipo_artefacto=${mapaDispositivos[id].tipo_artefacto}`);
      }
      
      // Recalcular consumo de la habitación
      if (dispositivo.habitacion_id) {
        const habitacionId = dispositivo.habitacion_id;
        if (habitacionId && mapaHabitaciones[Number(habitacionId)]) {
          const ids = mapaHabitaciones[Number(habitacionId)].dispositivos;
          mapaHabitaciones[Number(habitacionId)].consumoTotal = ids.reduce(
            (suma: number, id: number) => suma + (mapaDispositivos[Number(id)]?.consumo || 0), 0
          );
          // SHC_LOG(`Consumo recalculado para habitación ${habitacionId}: ${mapaHabitaciones[Number(habitacionId)].consumoTotal}W`);
        }
      }

      // Anillo solo en flanco real: OFF→ON = auto-off; ON→OFF = auto-on.
      // No arrancar por telemetría espuria ni por “ya estaba apagado” al guardar config.
      if (powerEdge) {
        const timerCfg = mapaDispositivos[id]?.timer_config;
        if (timerCfg?.enabled) {
          if (!wasPowerOn && isPowerOnNow && timerCfg.autoOffEnabled) {
            eventBus.emit('timer_activated', {
              device_id: id,
              timer_config: timerCfg,
              isActive: true,
              startTime: Date.now()
            });
          } else if (wasPowerOn && !isPowerOnNow && timerCfg.autoOnEnabled) {
            eventBus.emit('timer_activated', {
              device_id: id,
              timer_config: timerCfg,
              isActive: true,
              startTime: Date.now()
            });
          } else {
            eventBus.emit('timer_deactivated', {
              device_id: id,
              isActive: false
            });
          }
        }
      }
    }

    // Emitir evento al EventBus para que se propague al consumptionService y DeviceList
    // SOLUCIÓN PROBLEMA 2: Emitir con el estado corregido (estadoFinal) en lugar del data original
    // Esto evita que el evento tenga el estado anterior cuando se ignoró la actualización
    const eventoEmitido = {
      ...data,
      estado: estadoFinal !== undefined ? estadoFinal : data.estado
    };
    
    eventBus.emit('device:update', eventoEmitido);
  }

  // Manejar actualización de configuraciones de timer/agenda
  private static handleSchedulesUpdate(data: any) {
    if (!data || !data.device_id) return;
    
    const id = Number(data.device_id);
    const dispositivo = mapaDispositivos[id];
    
    if (dispositivo) {
      console.log(`🔄 [SYNC] Actualizando timer/agenda para dispositivo ${id}:`, {
        timer_enabled: data.timer_config?.enabled,
        agenda_enabled: data.agenda_config?.enabled
      });
      
      // Actualizar SOLO timer_config y agenda_config en el mapa
      mapaDispositivos[id] = {
        ...dispositivo,
        timer_config: data.timer_config,
        agenda_config: data.agenda_config
      };
      // Si no, el modal abierto vuelve a leer la agenda vieja desde caché.
      patchDeviceSchedulesCache(id, {
        timer_config: data.timer_config,
        agenda_config: data.agenda_config,
      });
      
      // Emitir evento al EventBus para que los componentes se actualicen
      eventBus.emit('device:schedules_updated', {
        id: id,
        timer_config: data.timer_config,
        agenda_config: data.agenda_config
      });
      
      console.log(`✅ [SYNC] Configuraciones actualizadas en mapa para dispositivo ${id}`);
    } else {
      console.warn(`⚠️ [SYNC] Dispositivo ${id} no encontrado en mapa`);
    }
  }

  // Enviar comando de control (replicando exactamente la web)
  static sendDeviceControl(deviceId: number, action: string, value?: any, channel?: number) {
    if (!this.socket?.connected) {
      return;
    }

    const command: any = {
      device_id: deviceId,
      action: action
    };
    
    // Solo incluir value si está definido (no incluir undefined)
    if (value !== undefined) {
      command.value = value;
    }
    
    // Solo incluir channel si se proporciona (para comandos toggle)
    if (channel !== undefined) {
      command.channel = channel;
    }

    this.socket.emit('device_control', command);
  }

  // Métodos para respuestas de control de dispositivos
  static setupControlResponseListener(callback?: (data: any) => void) {
    if (this.socket) {
      // Remover listener anterior si existe
      this.socket.off('device_control_response');
      
      // Configurar el listener con la lógica estándar
      this.socket.on('device_control_response', (data: any) => {
        // Emitir evento para que App.tsx maneje la respuesta
        eventBus.emit('device_control_response', data);
        
        // Si se proporciona callback personalizado, ejecutarlo también
        if (callback) {
          callback(data);
        }
      });
      
      // MEJORA 3: Marcar que el listener está configurado
      this.controlResponseListenerConfigured = true;
      
      console.log('✅ Listener de device_control_response configurado exitosamente');
    }
  }

  static removeControlResponseListener() {
    if (this.socket) {
      this.socket.off('device_control_response');
      // MEJORA 3: Marcar que el listener ya no está configurado
      this.controlResponseListenerConfigured = false;
    }
  }
  
  // MEJORA 3: Verificar si el WebSocket está completamente listo para usar
  static isReady(): boolean {
    // Verificar que el socket exista
    if (!this.socket) {
      return false;
    }
    
    // Verificar que el socket esté conectado
    if (!this.socket.connected || !this.connected) {
      return false;
    }
    
    // Verificar que el listener de respuestas esté configurado
    if (!this.controlResponseListenerConfigured) {
      return false;
    }
    
    // Verificar que el pool de listeners esté inicializado
    try {
      const pool = getListenerPool();
      if (!pool || pool.length === 0) {
        return false;
      }
    } catch (error) {
      return false;
    }
    
    return true;
  }
  
  // MEJORA 3: Esperar hasta que el WebSocket esté listo (con timeout)
  static async waitUntilReady(timeoutMs: number = 5000): Promise<boolean> {
    if (this.isReady()) {
      return true;
    }
    
    return new Promise((resolve) => {
      const startTime = Date.now();
      
      const checkReady = () => {
        if (this.isReady()) {
          resolve(true);
          return;
        }
        
        const elapsed = Date.now() - startTime;
        if (elapsed >= timeoutMs) {
          resolve(false);
          return;
        }
        
        // Verificar nuevamente en 100ms
        setTimeout(checkReady, 100);
      };
      
      checkReady();
    });
  }

  // POOL DE LISTENERS - Verificar disponibilidad antes del primer uso
  static hasAvailableListener(): boolean {
    const pool = getListenerPool();
    return pool.some(listener => !listener.isBusy);
  }

  // POOL DE LISTENERS - Asignar listener disponible (round-robin)
  static assignListener(deviceId: number): number | null {
    // MEJORA: Verificar disponibilidad antes de intentar asignar
    if (!this.hasAvailableListener()) {
      return null;
    }
    
    const pool = getListenerPool();
    
    // Buscar listener libre
    for (let i = 0; i < this.POOL_SIZE; i++) {
      const listenerIndex = (this.currentListenerIndex + i) % this.POOL_SIZE;
      const listener = pool[listenerIndex];
      
      if (!listener.isBusy) {
        // Asignar listener
        listener.isBusy = true;
        listener.assignedDevice = deviceId;
        this.currentListenerIndex = (listenerIndex + 1) % this.POOL_SIZE;
        return listenerIndex;
      }
    }
    
    // No hay listeners disponibles (fallback, aunque ya verificamos antes)
    return null;
  }

  // POOL DE LISTENERS - Liberar listener cuando llega respuesta
  static releaseListener(deviceId: number): void {
    const pool = getListenerPool();
    const listener = pool.find(l => l.assignedDevice === deviceId);
    
    if (listener) {
      listener.isBusy = false;
      listener.assignedDevice = null;
    }
  }

  // POOL DE LISTENERS - Obtener listener por dispositivo
  static getListenerByDevice(deviceId: number): number | null {
    const pool = getListenerPool();
    const listener = pool.find(l => l.assignedDevice === deviceId);
    return listener ? listener.id : null;
  }
}

// Conecta WebSocket tras login con reintentos; usa disconnect silencioso para no pintar rojo.
async function connectWebSocketWithRetry(authToken: string, maxAttempts = 3): Promise<boolean> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const currentToken = WebSocketService.getLastAuthToken();
      const needsReconnect =
        !WebSocketService.isConnected() ||
        (currentToken != null && currentToken !== authToken);

      if (needsReconnect) {
        WebSocketService.disconnect({ silent: true });
        await WebSocketService.connect(authToken);
      } else {
        WebSocketService.ensureReadyEmitted();
      }

      getListenerPool();
      WebSocketService.setupControlResponseListener();

      const ready = await WebSocketService.waitUntilReady(5000);
      if (ready || WebSocketService.isConnected()) {
        WebSocketService.authenticate(authToken);
        eventBus.emit('websocket:ready');
        return true;
      }
    } catch {
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
      }
    }
  }
  return false;
}

/** Shelly entra con la estructura. El resto son mapas aparte.
 *  Un remount con la sesión ya abierta solo pedía Sonos: Cocina perdía
 *  TV/aire/Android TV y Piscina perdía el control Raspberry hasta cerrar la app. */
function cargarMapasNoShelly(): void {
  cargarSonos();
  cargarSamsung();
  cargarAndroidTv();
  cargarLg();
  cargarEvohome();
  cargarPileta();
}

// Hacer WebSocketService y funciones globales para que Settings pueda usarlo
(window as any).WebSocketService = WebSocketService;

// Estados globales para que Settings pueda usarlos
(window as any).confirmingDevices = new Set();
(window as any).deviceErrors = new Set();
(window as any).coverActions = {};
(window as any).deviceTimeouts = {};

// Función global para refrescar los mapas globales
(window as any).WebSocketService = WebSocketService;
(window as any).refrescarMapasGlobales = async () => {
  try {
    await refrescarEstructuraCompleta();
    // Emitir evento para que los componentes se actualicen
    eventBus.emit('estructura:actualizada');
  } catch (error) {
    console.error('❌ [GLOBAL] Error al refrescar mapas globales:', error);
  }
};

// Mapas globales ahora se importan desde consumptionService.ts
// LoginScreen movido a pages/LoginPage.tsx para code splitting

// DEBUG: Log inmediato al cargar el módulo
console.log('🚀 App.tsx MODULO CARGADO (PWA)');

// COMPONENTE MEMOIZADO: Grid unificado Shelly + Sonos por habitación
type RoomGridItem =
  | { kind: 'device'; id: number; device: any }
  | { kind: 'sonos'; id: number; speaker: SonosSpeaker }
  | { kind: 'samsung'; id: number; device: SamsungDevice }
  | { kind: 'androidtv'; id: number; device: AndroidTvDevice }
  | { kind: 'lg'; id: number; device: LgDevice }
  | { kind: 'evohome'; id: number; device: EvohomeDevice }
  | { kind: 'pileta'; id: number; device: PiletaDevice };

const ROOM_SORT_CRITERIA: RoomSortCriterion[] = [
  'defecto', 'personalizado', 'consumo-mayor', 'consumo-menor', 'estado', 'artefacto', 'nombre',
];

function roomGridItemKey(item: RoomGridItem): string {
  const prefix = item.kind === 'device' ? 'shelly' : item.kind;
  return `${prefix}:${item.id}`;
}

function SortableRoomSlot({
  id,
  children,
}: {
  id: string;
  children: (dragHandleProps: Record<string, unknown>) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
    position: 'relative',
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        marginBottom: '0px !important',
        '& .MuiCard-root': { marginBottom: '0px !important' },
      }}
    >
      {children({ ...attributes, ...listeners })}
    </Box>
  );
}

const DeviceGrid = React.memo(({ 
  items,
  userRole, 
  deviceErrors, 
  confirmingDevices, 
  confirmedDevices, 
  coverActions, 
  onDeviceClick, 
  onDeviceToggle, 
  onControlCover, 
  onDeviceImageClick, 
  deviceTimerStates,
  dashboardNumColumns,
  onOpenSonosModal,
  onSonosTransport,
  onOpenSamsungModal,
  onSamsungUpdated,
  onOpenAndroidTvModal,
  onAndroidTvUpdated,
  onOpenLgModal,
  onLgUpdated,
  onOpenEvohomeModal,
  onEvohomeUpdated,
  onOpenPileta,
  aiPolicyRevision = 0,
  reorderMode = false,
  onReorder,
}: {
  items: RoomGridItem[];
  userRole: string;
  deviceErrors: Set<number>;
  confirmingDevices: Set<number>;
  confirmedDevices: Set<number>;
  coverActions: Record<number, string>;
  onDeviceClick: (device: any) => void;
  onDeviceToggle: (device: any) => void;
  onControlCover: (deviceId: number, action: 'up' | 'down' | 'stop') => void;
  onDeviceImageClick: (ip: string) => void;
  deviceTimerStates: Record<number, { isTimerActive: boolean; timerStartTime?: number }>;
  dashboardNumColumns?: number;
  onOpenSonosModal?: (speaker: SonosSpeaker) => void;
  onSonosTransport?: (speakerId: number, action: 'play' | 'pause' | 'previous' | 'next') => void;
  onOpenSamsungModal?: (device: SamsungDevice) => void;
  onSamsungUpdated?: (device: SamsungDevice) => void;
  onOpenAndroidTvModal?: (device: AndroidTvDevice) => void;
  onAndroidTvUpdated?: (device: AndroidTvDevice) => void;
  onOpenLgModal?: (device: LgDevice) => void;
  onLgUpdated?: (device: LgDevice) => void;
  onOpenEvohomeModal?: (device: EvohomeDevice) => void;
  onEvohomeUpdated?: (device: EvohomeDevice) => void;
  onOpenPileta?: (device: PiletaDevice) => void;
  aiPolicyRevision?: number;
  reorderMode?: boolean;
  onReorder?: (orderedKeys: string[]) => void;
}) => {
  const onReorderRef = useRef(onReorder);
  onReorderRef.current = onReorder;
  const reorderSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleReorderEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = items.map(roomGridItemKey);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorderRef.current?.(arrayMove(ids, oldIndex, newIndex));
  };
  // console.log('🎴 [GRID-COMPONENT] DeviceGrid renderizando - cantidad=' + devices.length);
  // Usar minmax(340px, 1fr) para que las tarjetas se estiren si hay espacio extra
  // El cálculo de dashboardNumColumns asegura que siempre quepan columnas completas sin cortarse
  const gridTemplateColumns = dashboardNumColumns 
    ? `repeat(${dashboardNumColumns}, minmax(340px, 1fr))`
    : 'repeat(auto-fit, minmax(340px, 1fr))';
  
  console.log('[DeviceGrid] dashboardNumColumns recibido:', dashboardNumColumns);
  console.log('[DeviceGrid] gridTemplateColumns:', gridTemplateColumns);
  
  // Construir los estilos base
  const gridStyles: any = {
    display: 'grid',
    gridTemplateColumns: gridTemplateColumns,
    columnGap: '5px',
    rowGap: '5px',
    padding: '1px',
    paddingBottom: '0px', // CRÍTICO: Sin padding inferior para evitar línea gris
    marginBottom: '0px', // CRÍTICO: Sin margen inferior
    boxSizing: 'border-box',
    width: '100%',
    backgroundColor: '#000',
    position: 'relative', // CRÍTICO: Para posicionar el elemento final
    // CRÍTICO: Eliminar cualquier borde o línea visible
    border: 'none',
    outline: 'none',
    // CRÍTICO: NO bloquear eventos de touch - dejar que pasen al contenedor padre para scroll
    touchAction: 'pan-y', // Permitir scroll vertical
    // NO usar pointerEvents aquí - dejar que los eventos pasen naturalmente
  };
  
  // Solo agregar el media query si NO hay dashboardNumColumns definido
  // (para respetar el cálculo del hook y no sobrescribir el número de columnas)
  if (!dashboardNumColumns) {
    gridStyles['@media (max-width: 768px)'] = {
      gridTemplateColumns: '1fr',
    };
  }
  
  const renderCard = (item: RoomGridItem, dragHandleProps?: Record<string, unknown>) => (
    item.kind === 'sonos' ? (
      <SonosSpeakerCard
        speaker={item.speaker}
        canControl={item.speaker.can_control !== false}
        onOpenModal={onOpenSonosModal}
        onTransport={onSonosTransport}
        isRoomEditing={Boolean(dragHandleProps)}
        dragHandleProps={dragHandleProps}
      />
    ) : item.kind === 'samsung' ? (
      <SamsungDeviceCard
        device={item.device}
        onOpenModal={onOpenSamsungModal}
        onUpdated={onSamsungUpdated}
        isRoomEditing={Boolean(dragHandleProps)}
        dragHandleProps={dragHandleProps}
      />
    ) : item.kind === 'androidtv' ? (
      <AndroidTvDeviceCard
        device={item.device}
        onOpenModal={onOpenAndroidTvModal}
        onUpdated={onAndroidTvUpdated}
        isRoomEditing={Boolean(dragHandleProps)}
        dragHandleProps={dragHandleProps}
      />
    ) : item.kind === 'lg' ? (
      <LgDeviceCard
        device={item.device}
        onOpenModal={onOpenLgModal}
        onUpdated={onLgUpdated}
        isRoomEditing={Boolean(dragHandleProps)}
        dragHandleProps={dragHandleProps}
      />
    ) : item.kind === 'evohome' ? (
      <EvohomeDeviceCard
        device={item.device}
        onOpenModal={onOpenEvohomeModal}
        onUpdated={onEvohomeUpdated}
        isRoomEditing={Boolean(dragHandleProps)}
        dragHandleProps={dragHandleProps}
      />
    ) : item.kind === 'pileta' ? (
      <PiletaDeviceCard
        device={item.device}
        onOpen={onOpenPileta}
        aiPolicyRevision={aiPolicyRevision}
        isRoomEditing={Boolean(dragHandleProps)}
        dragHandleProps={dragHandleProps}
      />
    ) : (
      <DeviceCard
        device={item.device}
        userRole={userRole}
        deviceErrors={deviceErrors}
        confirmingDevices={confirmingDevices}
        confirmedDevices={confirmedDevices}
        coverActions={coverActions}
        onDeviceClick={onDeviceClick}
        onDeviceToggle={onDeviceToggle}
        onControlCover={onControlCover}
        onDeviceImageClick={onDeviceImageClick}
        deviceTimerStates={deviceTimerStates}
        isRoomEditing={Boolean(dragHandleProps)}
        dragHandleProps={dragHandleProps}
      />
    )
  );

  const grid = (
    <Box 
      sx={gridStyles}>
      {items.map((item) => (
        reorderMode ? (
          <SortableRoomSlot key={roomGridItemKey(item)} id={roomGridItemKey(item)}>
            {(dragHandleProps) => renderCard(item, dragHandleProps)}
          </SortableRoomSlot>
        ) : (
        <Box
          key={item.kind === 'sonos' ? `sonos-${item.id}` : item.kind === 'samsung' ? `samsung-${item.id}` : item.kind === 'androidtv' ? `androidtv-${item.id}` : item.kind === 'lg' ? `lg-${item.id}` : item.kind === 'evohome' ? `evohome-${item.id}` : item.kind === 'pileta' ? `pileta-${item.id}` : item.id}
          sx={{
            marginBottom: '0px !important',
            '& .MuiCard-root': {
              marginBottom: '0px !important',
            },
          }}
        >
          {renderCard(item)}
        </Box>
        )
      ))}
      {/* CRÍTICO: Elemento espaciador al final con fondo negro para cubrir cualquier espacio */}
      <Box
        className="grid-spacer"
        sx={{
          gridColumn: '1 / -1', // Ocupar todas las columnas en CSS Grid
          height: '60px', // Espacio para la barra de navegación + margen extra
          backgroundColor: '#000',
          marginTop: '-6px', // Compensar el gap del último elemento
          marginBottom: 0,
          padding: 0,
          border: 'none',
          outline: 'none',
          // CRÍTICO: Asegurar que cubra cualquier espacio visible
          position: 'relative',
          zIndex: 1,
        }}
      />
    </Box>
  );

  if (!reorderMode) return grid;
  return (
    <DndContext sensors={reorderSensors} collisionDetection={closestCenter} onDragEnd={handleReorderEnd}>
      <SortableContext items={items.map(roomGridItemKey)} strategy={rectSortingStrategy}>
        {grid}
      </SortableContext>
    </DndContext>
  );
}, (prevProps, nextProps) => {
  if (prevProps.reorderMode !== nextProps.reorderMode) {
    return false;
  }
  if (prevProps.items.length !== nextProps.items.length) {
    return false;
  }

  const prevKeys = prevProps.items.map((i) => `${i.kind}-${i.id}`).join(',');
  const nextKeys = nextProps.items.map((i) => `${i.kind}-${i.id}`).join(',');
  if (prevKeys !== nextKeys) {
    return false;
  }

  for (let i = 0; i < prevProps.items.length; i++) {
    const prevItem = prevProps.items[i];
    const nextItem = nextProps.items[i];
    if (prevItem.kind !== nextItem.kind) return false;
    if (prevItem.kind === 'device' && nextItem.kind === 'device') {
      const prevDevice = prevItem.device;
      const nextDevice = nextItem.device;
      if (
        prevDevice.estado !== nextDevice.estado
        || prevDevice.online !== nextDevice.online
        || prevDevice.consumo !== nextDevice.consumo
        || prevDevice.lights_brightness !== nextDevice.lights_brightness
      ) {
        return false;
      }
    } else if (prevItem.kind === 'sonos' && nextItem.kind === 'sonos') {
      const prevSp = prevItem.speaker;
      const nextSp = nextItem.speaker;
      if (
        prevSp.playback_state !== nextSp.playback_state
        || prevSp.current_title !== nextSp.current_title
        || prevSp.volume !== nextSp.volume
        || prevSp.online !== nextSp.online
        || prevSp.group_coordinator_uid !== nextSp.group_coordinator_uid
      ) {
        return false;
      }
    } else if (prevItem.kind === 'samsung' && nextItem.kind === 'samsung') {
      const prevDev = prevItem.device;
      const nextDev = nextItem.device;
      if (
        prevDev.power !== nextDev.power
        || prevDev.temperature !== nextDev.temperature
        || prevDev.setpoint !== nextDev.setpoint
        || prevDev.volume !== nextDev.volume
        || prevDev.online !== nextDev.online
        || prevDev.hvac_mode !== nextDev.hvac_mode
      ) {
        return false;
      }
    }
  }

  if (
    prevProps.userRole !== nextProps.userRole
    || prevProps.dashboardNumColumns !== nextProps.dashboardNumColumns
  ) {
    return false;
  }

  if (
    prevProps.onDeviceClick !== nextProps.onDeviceClick
    || prevProps.onDeviceToggle !== nextProps.onDeviceToggle
    || prevProps.onControlCover !== nextProps.onControlCover
    || prevProps.onDeviceImageClick !== nextProps.onDeviceImageClick
    || prevProps.onOpenSonosModal !== nextProps.onOpenSonosModal
    || prevProps.onSonosTransport !== nextProps.onSonosTransport
    || prevProps.onOpenSamsungModal !== nextProps.onOpenSamsungModal
    || prevProps.onSamsungUpdated !== nextProps.onSamsungUpdated
  ) {
    return false;
  }

  return true;
});

type AppScreen =
  | 'dashboard'
  | 'devices'
  | 'settings'
  | 'realtime'
  | 'reports'
  | 'favorites'
  | 'video'
  | 'assistant'
  | 'automations'
  | 'alarm'
  | 'music'
  | 'climate'
  | 'widgets'
  | 'pileta'
  | 'softphone';

const APP_SCREENS: ReadonlySet<string> = new Set([
  'dashboard', 'devices', 'settings', 'realtime', 'reports', 'favorites',
  'video', 'assistant', 'automations', 'alarm', 'music', 'climate',
  'widgets', 'pileta', 'softphone',
]);

const resolveAllowedScreen = (
  screen: string,
  user: ReturnType<typeof getUser>
): AppScreen => {
  if (screen === 'video') {
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.get('softphone') === 'answer') {
        return (canAnswerDoorbell(user) || canUsePhone(user)) ? 'softphone' : 'dashboard';
      }
    } catch { /* ignore */ }
    if (!CAMERAS_INSTALLED || !canViewCameras(user)) return 'dashboard';
  }
  if (screen === 'music' && (!MUSIC_INSTALLED || !canViewMusic(user))) return 'dashboard';
  if (screen === 'softphone' && (!PHONE_INSTALLED || (!canUsePhone(user) && !canAnswerDoorbell(user)))) return 'dashboard';
  if (screen === 'phone') {
    return (PHONE_INSTALLED && (canUsePhone(user) || canAnswerDoorbell(user))) ? 'softphone' : 'dashboard';
  }
  if (screen === 'climate' && (!(AIR_INSTALLED || HEAT_INSTALLED) || !(canViewHeating(user) || canViewAc(user)))) return 'dashboard';
  if (screen === 'assistant' && !ASSISTANT_INSTALLED) return 'dashboard';
  if (screen === 'pileta' && !PILETA_INSTALLED) return 'dashboard';
  if (screen === 'realtime' && (!SOLAR_INSTALLED || !canViewSolar(user))) return 'dashboard';
  if (screen === 'automations') return 'assistant';
  if (APP_SCREENS.has(screen)) return screen as AppScreen;
  return 'dashboard';
};

// Componente principal de la aplicación
const App: React.FC = () => {
  // Importación condicional del módulo de video
  const [VideoModule, setVideoModule] = useState<{
    Button: React.ComponentType<any>;
    Screen: React.ComponentType<any>;
  } | null>(null);

  useEffect(() => {
    if (!CAMERAS_INSTALLED || QUICK_ANSWER_BOOT.island || isSoftphoneQuickAnswer()) return;
    // Intentar cargar el módulo de video dinámicamente
    const loadVideoModule = async () => {
      try {
        // Usar import dinámico con ruta relativa
        const module = await loadCameraModule();
        if (module.VideoModuleButton && module.VideoModuleScreen) {
          setVideoModule({
            Button: module.VideoModuleButton,
            Screen: module.VideoModuleScreen
          });
        } else {
          console.error('[VideoModule] Módulo cargado pero faltan exports:', Object.keys(module));
        }
      } catch (error) {
        // Módulo no instalado, simplemente no hacer nada
        // Esto es esperado si el módulo no está instalado
        console.warn('[VideoModule] No se pudo cargar el módulo de video:', error);
      }
    };
    loadVideoModule();
  }, []);

  // Los íconos PNG ahora están en el backend y se cargan dinámicamente
  // Ya no es necesario forzar su inclusión en el bundle
  
  // DEBUG: Contador de renderizados
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  console.log('🔄 [RENDER] App.tsx Render #' + renderCountRef.current);
  
  // Limpiar caches automáticamente en desarrollo al cargar la app
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Importar y ejecutar la función de limpieza
      import('./utils/cacheCleaner').then(({ clearAllCaches }) => {
        clearAllCaches().then(() => {
          console.log('[App] ✅ Caches limpiados automáticamente en desarrollo');
        });
      });
    }
  }, []); // Solo ejecutar una vez al montar
  
  const { t } = useTranslation();
  const aiPolicy = useAiDevicePolicy();
  const isHorizontal = useOrientation();
  const { compactLabel: dashboardClockLabel } = useServerLocalClock();
  const [token, setToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    if (QUICK_ANSWER_BOOT.token) return QUICK_ANSWER_BOOT.token;
    if (!shouldRecoverSessionAfterReload()) return null;
    return localStorage.getItem('token');
  });
  /** JWT vivo + posible llamada: no mostrar login hasta leer Cache del SW. */
  const [incomingBootState, setIncomingBootState] = useState<'checking' | 'done'>(() => {
    if (QUICK_ANSWER_BOOT.token) return 'done';
    if (isSoftphoneDoorbellDeepLink() || isSoftphoneDoorbellSession()) return 'done';
    try {
      const stored = localStorage.getItem('token');
      const user = getUser();
      if (stored && user && isStoredJwtAlive(stored)) return 'checking';
    } catch {
      /* ignore */
    }
    return 'done';
  });
  const [, setEmail] = useState<string>('');
  const [username, setUsername] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    const bootUser = QUICK_ANSWER_BOOT.user as Record<string, unknown> | null;
    if (bootUser) {
      return String(bootUser.nombre_completo || bootUser.username || (bootUser.email as string)?.split('@')[0] || '');
    }
    if (!shouldRecoverSessionAfterReload()) return '';
    const user = getUser() as Record<string, unknown> | null;
    return String(user?.nombre_completo || user?.username || (user?.email as string)?.split('@')[0] || '');
  });
  const [userRole, setUserRole] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    if (QUICK_ANSWER_BOOT.user?.role) return QUICK_ANSWER_BOOT.user.role;
    if (!shouldRecoverSessionAfterReload()) return '';
    return getUser()?.role || '';
  });
  const [userData, setUserData] = useState<any>(() => {
    if (typeof window === 'undefined') return null;
    if (QUICK_ANSWER_BOOT.user) return QUICK_ANSWER_BOOT.user;
    if (!shouldRecoverSessionAfterReload()) return null;
    return getUser();
  });
  const [demoViewMode, setDemoViewMode] = useState<'user' | 'admin' | null>(null);
  const [demoModeDialogOpen, setDemoModeDialogOpen] = useState(false);
  const [loading, setLoading] = useState(
    () => !(QUICK_ANSWER_BOOT.island || isSoftphoneDoorbellDeepLink() || isSoftphoneDoorbellSession()),
  );
  const [doorbellIsland, setDoorbellIsland] = useState(
    () => isSoftphoneDoorbellDeepLink() || isSoftphoneDoorbellSession(),
  );
  /** Pantalla de la app antes de abrir la isla timbre (volver al colgar logueado). */
  const screenBeforeDoorbellRef = useRef<string | null>(null);
  const [boards, setBoards] = useState<any[]>([]);
  const [rooms, setRooms] = useState<HabitacionConConsumo[]>([]);
  const [devices, setDevices] = useState<DispositivoConConsumo[]>([]);
  const [sonosFavorites, setSonosFavorites] = useState<number[]>([]);
  const [sonosFavoriteOrden, setSonosFavoriteOrden] = useState<Record<number, number>>({});
  const [favoriteSonosSpeakers, setFavoriteSonosSpeakers] = useState<SonosSpeaker[]>([]);
  const [sonosUpdateTick, setSonosUpdateTick] = useState(0);
  const [selectedSonosSpeaker, setSelectedSonosSpeaker] = useState<SonosSpeaker | null>(null);
  const [sonosModalOpen, setSonosModalOpen] = useState(false);
  const [samsungUpdateTick, setSamsungUpdateTick] = useState(0);
  const [androidtvUpdateTick, setAndroidTvUpdateTick] = useState(0);
  const [selectedSamsungDevice, setSelectedSamsungDevice] = useState<SamsungDevice | null>(null);
  const [selectedAndroidTvDevice, setSelectedAndroidTvDevice] = useState<AndroidTvDevice | null>(null);
  const [androidtvModalOpen, setAndroidtvModalOpen] = useState(false);
  const [samsungModalOpen, setSamsungModalOpen] = useState(false);
  const [lgUpdateTick, setLgUpdateTick] = useState(0);
  const [selectedLgDevice, setSelectedLgDevice] = useState<LgDevice | null>(null);
  const [lgModalOpen, setLgModalOpen] = useState(false);
  const [evohomeUpdateTick, setEvohomeUpdateTick] = useState(0);
  const [selectedEvohomeDevice, setSelectedEvohomeDevice] = useState<EvohomeDevice | null>(null);
  const [evohomeModalOpen, setEvohomeModalOpen] = useState(false);
  const [piletaUpdateTick, setPiletaUpdateTick] = useState(0);
  /** Habitación a la que volver al salir de la pantalla pileta. */
  const [piletaReturnRoom, setPiletaReturnRoom] = useState<HabitacionConConsumo | null>(null);
  /** Título de barra = display_name / nombre de tarjeta Discovery Pileta. */
  const [piletaPageTitle, setPiletaPageTitle] = useState<string>('');
  const [, setUserFavorites] = useState<any[]>([]);
  const [, setHasFavorites] = useState<boolean>(false);
  const [favoriteDevices, setFavoriteDevices] = useState<DispositivoConConsumo[]>([]);
  const [favoriteRoomsOrder, setFavoriteRoomsOrder] = useState<(number | string)[]>([]); // Orden personalizado de habitaciones
  
  // ✅ OPTIMIZACIÓN: Cachear permisos del usuario para evitar cargarlos múltiples veces
  const [cachedUserPermissions, setCachedUserPermissions] = useState<{
    allowedRoomIds: number[];
    permissions?: any[];
    room_ids?: number[];
  } | null>(null);

  // Remount (Android start_url): el JWT está en React/localStorage pero axios no, si no pasamos por LoginPage.
  useEffect(() => {
    if (token) {
      ApiService.setToken(token);
    }
  }, [token]);

  // Re-sincronizar suscripción push al recuperar sesión (p. ej. abrir PWA en el celular).
  useEffect(() => {
    if (!token) return;
    import('./services/pushAuthStorage').then(({ savePushAuthToken }) => {
      void savePushAuthToken(token);
    });
    import('./services/pushNotificationsService').then(({ ensurePushReady, prefetchPushInfrastructure, pushSupported }) => {
      if (!pushSupported()) return;
      void prefetchPushInfrastructure();
      void ensurePushReady();
    });
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !token) return;
      import('./services/pushNotificationsService').then(({ ensurePushReady, prefetchPushInfrastructure, pushSupported }) => {
        if (!pushSupported()) return;
        void prefetchPushInfrastructure();
        void ensurePushReady();
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [token]);

  // Respaldo: si el SO no muestra el push, detectamos avisos nuevos consultando el servidor.
  const lastAutomationNotifIdRef = useRef(0);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const poll = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const { listAutomationNotifications } = await import('./services/automationFavoritesApi');
        const data = await listAutomationNotifications('mine');
        const items = data.notifications || [];
        if (lastAutomationNotifIdRef.current === 0 && items.length > 0) {
          lastAutomationNotifIdRef.current = Math.max(...items.map((n) => n.id));
          return;
        }
        const fresh = items.filter((n) => n.id > lastAutomationNotifIdRef.current);
        if (!fresh.length || cancelled) return;
        const latest = fresh.reduce((a, b) => (a.id > b.id ? a : b));
        lastAutomationNotifIdRef.current = latest.id;
        setAutomationPushSnack({ open: true, title: latest.title, body: latest.body });
      } catch { /* noop */ }
    };
    void poll();
    const id = window.setInterval(() => void poll(), 12000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [token]);
  
  // Estados para Grupos de Dispositivos
  const [showGroupsModal, setShowGroupsModal] = useState<boolean>(false);
  const [selectedRoomForGroups, setSelectedRoomForGroups] = useState<number | null>(null);
  
  // Callback estable para cerrar GroupsModal
  const handleCloseGroupsModal = useCallback(() => {
    setShowGroupsModal(false);
    setSelectedRoomForGroups(null);
  }, []);
  
  // Callback para abrir/cerrar el modal de grupos (toggle)
  const handleOpenGroupsModal = useCallback((habitacionId: number) => {
    // Si ya está abierto para la misma habitación, cerrarlo
    if (showGroupsModal && selectedRoomForGroups === habitacionId) {
      setShowGroupsModal(false);
      setSelectedRoomForGroups(null);
    } else {
      // Si está abierto para otra habitación o está cerrado, abrirlo/actualizarlo
      setSelectedRoomForGroups(habitacionId);
      setShowGroupsModal(true);
    }
  }, [showGroupsModal, selectedRoomForGroups]);
  
  const [selectedRoom, setSelectedRoom] = useState<HabitacionConConsumo | null>(null);
  
  // Ref para el contenedor del DeviceGrid en el Dashboard para calcular el ancho correcto
  const dashboardContainerRef = useRef<HTMLDivElement>(null);
  
  // Protección contra re-renderizados durante scroll en Dashboard (vista de habitación)
  const [isScrollingDashboard, setIsScrollingDashboard] = useState(false);
  const dashboardScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingDashboardDeviceUpdatesRef = useRef<boolean>(false); // Flag para indicar si hay actualizaciones pendientes
  
  // CRÍTICO: Refs para detectar si hubo scroll durante un touch
  // Esto permite distinguir entre tap y scroll en DeviceCard
  const dashboardTouchStartRef = useRef<{ x: number; y: number; time: number; element: HTMLElement | null; scrollTop?: number } | null>(null);
  const dashboardHasScrolledRef = useRef<boolean>(false);
  /** Lista inicial de habitaciones (dashboard sin sala): detectar scroll para no confundir con tap en overlay (véase doc 51 / DeviceCard). */
  const roomsListHasScrolledRef = useRef(false);
  const roomsListScrollFlagTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Refs para scroll manual en vista horizontal (solución del documento 50)
  const dashboardScrollVelocityRef = useRef<number>(0);
  const dashboardScrollAnimationFrameRef = useRef<number | null>(null);
  const dashboardIsScrollingRef = useRef<boolean>(false);

  // Barra de tabs de tableros (dashboard): en WKWebView/PWA el scroll horizontal nativo
  // a veces no recibe el gesto, así que se hace scroll manual del scroller de MUI.
  const boardTabsContainerRef = useRef<HTMLDivElement | null>(null);
  const boardTabsTouchRef = useRef<{ x: number; y: number; scrollLeft: number; lastX: number; time: number; vel: number; moved: boolean } | null>(null);
  const boardTabsMomentumRef = useRef<number | null>(null);
  
  // SOLUCIÓN: Inicializar currentScreen basado en la homePage del usuario si existe en localStorage
  // Esto evita mostrar primero el dashboard y luego cambiar a la homePage del usuario
  const getInitialHubTab = (): 'chat' | 'automations' | 'guide' => {
    const screenParam = getUrlParam('screen');
    if (screenParam === 'automations') return 'automations';
    const user = userData || getUser();
    return canUseAI(user) ? 'chat' : 'automations';
  };

  // Deep-link desde notificaciones push: /pwa/?screen=automations&rule=<id>
  const getUrlParam = (name: string): string | null => {
    try { return new URLSearchParams(window.location.search).get(name); }
    catch { return null; }
  };
  const initialAutomationRuleId: number | null = (() => {
    const r = getUrlParam('rule');
    const n = r ? parseInt(r, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  })();
  // Regla a abrir por deep-link (URL al arrancar, o postMessage del SW si la app ya estaba abierta).
  const [deepLinkRuleId, setDeepLinkRuleId] = useState<number | null>(initialAutomationRuleId);
  // Pantalla pedida por deep-link en el arranque; tiene prioridad sobre home_page (se consume 1 vez).
  const pendingDeepLinkScreenRef = useRef<string | null>(getUrlParam('screen'));
  const pendingSoftphoneSessionResumeRef = useRef<{
    url: string;
    needsSession: boolean;
    stayLoggedIn: boolean;
  } | null>(null);
  const [softphoneSessionResumeTick, setSoftphoneSessionResumeTick] = useState(0);

  const getInitialScreen = (): AppScreen => {
    try {
      const screenParam = getUrlParam('screen');
      if (screenParam) {
        return resolveAllowedScreen(screenParam, userData || getUser());
      }
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user?.home_page) {
          return resolveAllowedScreen(user.home_page, user);
        }
      }
    } catch (error) {
      // Si hay error al leer localStorage, usar dashboard por defecto
    }
    return 'dashboard';
  };
  const [currentScreen, setCurrentScreen] = useState<AppScreen>(getInitialScreen());
  const latestScreenRef = useRef(currentScreen);
  latestScreenRef.current = currentScreen;
  const [assistantHubTab, setAssistantHubTab] = useState<'chat' | 'automations' | 'guide'>(getInitialHubTab);
  const handleAssistantHubBack = useCallback(() => setCurrentScreen('dashboard'), []);

  // Redirigir si el usuario está en una pantalla restringida
  useEffect(() => {
    const user = userData || getUser();
    if (!user) return;
    if (currentScreen === 'video' && (!CAMERAS_INSTALLED || !canViewCameras(user))) {
      setCurrentScreen('dashboard');
    }
    if (currentScreen === 'music' && (!MUSIC_INSTALLED || !canViewMusic(user))) {
      setCurrentScreen('dashboard');
    }
    if (currentScreen === 'softphone' && (!PHONE_INSTALLED || (!canUsePhone(user) && !canAnswerDoorbell(user)))) {
      setCurrentScreen('dashboard');
    }
    if (currentScreen === 'climate' && (!(AIR_INSTALLED || HEAT_INSTALLED) || !(canViewHeating(user) || canViewAc(user)))) {
      setCurrentScreen('dashboard');
    }
    if ((currentScreen === 'assistant' && !ASSISTANT_INSTALLED) || (currentScreen === 'pileta' && !PILETA_INSTALLED) || (currentScreen === 'realtime' && (!SOLAR_INSTALLED || !canViewSolar(user)))) {
      setCurrentScreen('dashboard');
    }
  }, [currentScreen, userData]);

  // Deep-link desde notificaciones push cuando la PWA YA está abierta: el SW
  // manda postMessage({type:'NAVIGATE', url}) y acá cambiamos de pantalla/regla.
  useEffect(() => {
    const applyExternalNavigateUrl = (rawUrl: string) => {
      try {
        const u = new URL(rawUrl, window.location.origin);
        const screen = u.searchParams.get('screen');
        const rule = u.searchParams.get('rule');
        if (!screen) return;
        const resolved = resolveAllowedScreen(screen, userData || getUser());
        setCurrentScreen(resolved as any);
        setIsHomeActive(resolved === 'dashboard');
        setIsInfoActive(false);
        setIsSettingsActive(false);
        if (screen === 'automations') {
          setAssistantHubTab('automations');
        } else if (screen === 'assistant') {
          setAssistantHubTab(canUseAI(userData || getUser()) ? 'chat' : 'automations');
        }
        if (screen === 'automations') {
          const n = rule ? parseInt(rule, 10) : NaN;
          setDeepLinkRuleId(Number.isFinite(n) ? n : null);
        }
        const doorbellNav = (resolved === 'softphone' && (u.searchParams.get('kind') === 'doorbell' || u.searchParams.get('camera')))
          || (u.searchParams.get('softphone') === 'answer');
        if (doorbellNav && (canAnswerDoorbell(userData || getUser()) || canUsePhone(userData || getUser()))) {
          const cam = parseInt(u.searchParams.get('camera') || '23', 10);
          const cameraId = Number.isFinite(cam) ? cam : 23;
          clearSoftphoneCallEndedGate();
          setSoftphoneCallEnded(false);
          softphoneExitInFlightRef.current = false;
          if (screenBeforeDoorbellRef.current == null) {
            screenBeforeDoorbellRef.current = latestScreenRef.current;
          }
          if (!userData && !token && !hasLoggedInDocumentSession()) {
            markSoftphoneQuickAnswer();
          }
          pendingSoftphoneSessionResumeRef.current = {
            url: rawUrl,
            needsSession: !userData && !token,
            stayLoggedIn: hasLoggedInDocumentSession(),
          };
          setSoftphoneSessionResumeTick((n) => n + 1);
          markSoftphoneDoorbellSession();
          setDoorbellIsland(true);
          setPendingDoorbellCamera(cameraId);
          setCurrentScreen('softphone');
          setIsHomeActive(false);
          try { eventBus.emit('softphone:open-doorbell', { cameraId }); } catch { /* ignore */ }
        } else if (resolved === 'softphone' && u.searchParams.get('answer') === '1') {
          clearSoftphoneCallEndedGate();
          setSoftphoneCallEnded(false);
          softphoneExitInFlightRef.current = false;
          clearSoftphoneDoorbellSession();
          clearPendingDoorbellCamera();
          setDoorbellIsland(false);
          markSoftphoneIncomingResume();
          if (!userData && !token && !hasLoggedInDocumentSession()) {
            markSoftphoneQuickAnswer();
          }
          pendingSoftphoneSessionResumeRef.current = {
            url: rawUrl,
            needsSession: !userData && !token,
            stayLoggedIn: hasLoggedInDocumentSession(),
          };
          setSoftphoneSessionResumeTick((n) => n + 1);
          setCurrentScreen('softphone');
          setIsHomeActive(false);
          try { eventBus.emit('softphone:open-phone'); } catch { /* ignore */ }
        }
      } catch { /* noop */ }
    };
    type LaunchQueueLike = {
      setConsumer: (cb: (params: { targetURL?: string }) => void) => void;
    };
    const launchQueue = (window as unknown as { launchQueue?: LaunchQueueLike }).launchQueue;
    if (launchQueue && typeof launchQueue.setConsumer === 'function') {
      launchQueue.setConsumer((params) => {
        if (params?.targetURL) applyExternalNavigateUrl(params.targetURL);
      });
    }
    if (!('serviceWorker' in navigator)) return;
    const handler = (event: MessageEvent) => {
      const d = event.data;
      if (!d) return;
      if (d.type === 'PUSH_SUBSCRIPTION_CHANGED') {
        import('./services/pushNotificationsService').then(({ ensurePushReady }) => {
          void ensurePushReady();
        });
        return;
      }
      if (d.type === 'SHC_SOFTPHONE_DISMISS') {
        void import('./utils/pushRingSound').then(({ stopPushRingSound }) => {
          stopPushRingSound();
        });
        void preload_SipSoftphone().then(({ sipSoftphone }) => {
          const aborted = sipSoftphone.handleRemoteCleared(String(d.reason || 'dismiss'), {
            call_id: d.call_id ? String(d.call_id) : undefined,
          });
          if (aborted) {
            eventBus.emit('softphone:doorbell-hangup');
            eventBus.emit('softphone:call-ended');
          }
        });
        return;
      }
      if (d.type === 'SHC_PUSH') {
        // SoftPhone ring: SoftphonePage / softphone:incoming ya muestran atender.
        // No duplicar con el snack de automatizaciones («Llamada / Llamada de …»).
        const url = String(d.url || '');
        const isSoftphoneRing =
          !!d.ring
          || /screen=softphone|answer=1|softphone=answer/.test(url);
        if (isSoftphoneRing) {
          const isDoor = d.kind === 'doorbell' || /kind=doorbell|softphone=answer|screen=video/.test(url);
          if (isDoor) {
            markSoftphoneDoorbellSession();
          } else if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            // Segundo plano: si Android remonta el documento, reclaim.
            // Primer plano: el INVITE ya va al WebRTC; no marcar intent
            // (tryReclaim + Redirect cortan la llamada PWA↔PWA).
            markSoftphoneIncomingResume();
            markSoftphoneBgIncomingLocal();
          }
          // Chrome ignora Notification.sound: si la PWA sigue viva en background,
          // reproducimos nosotros doorbell vs ring (el banner del SO suena igual).
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            void import('./utils/pushRingSound').then(({ playPushRingSound }) => {
              playPushRingSound(isDoor ? 'doorbell' : 'phone');
            });
          }
          return;
        }
        setAutomationPushSnack({
          open: true,
          title: String(d.title || 'SHC'),
          body: String(d.body || ''),
        });
        return;
      }
      if (d.type !== 'NAVIGATE' || !d.url) return;
      applyExternalNavigateUrl(String(d.url));
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [userData, token]);

  // Calcular número de columnas para grids de dispositivos (después de currentScreen: remeasureKey)
  // Usar el ancho del contenedor del DeviceGrid; useLayoutEffect + readElementWidth evita clientWidth 0 en flex
  const dashboardNumColumns = useDeviceGridColumns({
    marginRight: 0,
    paddingRight: 0,
    gridPaddingX: 0.125,
    containerRef: dashboardContainerRef,
    remeasureKey: `${currentScreen}-${selectedRoom?.id ?? 0}-${isHorizontal ? 1 : 0}`,
  });
  
  // SOLUCIÓN: Flag para indicar si el tab inicial ya fue establecido desde homeTab
  // Esto previene cargar habitaciones del primer tablero cuando hay un homeTab definido
  // Inicializar en true solo si NO hay homeTab en localStorage (para usuarios sin homeTab configurado)
  const getInitialHomeTabInitialized = (): boolean => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        // Si hay homeTab definido, inicializar en false para esperar a que se establezca
        if (user?.home_page === 'dashboard' && user?.home_tab !== null && user?.home_tab !== undefined) {
          return false; // Esperar a que se establezca el tab correcto
        }
      }
    } catch (error) {
      // Si hay error, asumir que no hay homeTab y permitir carga inmediata
    }
    return true; // Si no hay homeTab, permitir carga inmediata
  };
  const [homeTabInitialized, setHomeTabInitialized] = useState(getInitialHomeTabInitialized());
  
  const [permissionSnackbar, setPermissionSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: ''
  });
  const [automationPushSnack, setAutomationPushSnack] = useState<{
    open: boolean; title: string; body: string;
  }>({ open: false, title: '', body: '' });
  
  // Estado para manejar timers activos (con persistencia en localStorage)
  const [deviceTimerStates, setDeviceTimerStates] = useState<Record<number, {
    isTimerActive: boolean;
    timerStartTime?: number;
  }>>(() => {
    // Cargar estados de timers desde localStorage al iniciar
    try {
      const saved = localStorage.getItem('deviceTimerStates');
      if (saved) {
        const parsed = JSON.parse(saved);
        console.log('📥 [TIMER] Estados de timers cargados desde localStorage:', parsed);
        
        // IMPORTANTE: Limpiar timers que ya expiraron
        const now = Date.now();
        const cleaned: Record<number, { isTimerActive: boolean; timerStartTime?: number }> = {};
        
        Object.keys(parsed).forEach(deviceIdStr => {
          const deviceId = parseInt(deviceIdStr, 10);
          const timerState = parsed[deviceIdStr];
          if (timerState.isTimerActive && timerState.timerStartTime) {
            // Verificar si el timer aún es válido (no expiró)
            // Por ahora, asumir que si pasaron más de 24 horas, el timer expiró
            const elapsed = (now - timerState.timerStartTime) / 1000; // en segundos
            const MAX_TIMER_DURATION = 24 * 3600; // 24 horas en segundos
            
            if (elapsed < MAX_TIMER_DURATION) {
              // Timer aún válido, mantener
              cleaned[deviceId] = timerState;
              console.log(`✅ [TIMER] Timer válido para dispositivo ${deviceId}, tiempo transcurrido: ${Math.floor(elapsed)}s`);
            } else {
              // Timer expirado, marcar como inactivo
              cleaned[deviceId] = {
                isTimerActive: false,
                timerStartTime: undefined
              };
              console.log(`⏰ [TIMER] Timer expirado para dispositivo ${deviceId}, limpiado`);
            }
          } else {
            // Timer inactivo o sin startTime, mantener como está
            cleaned[deviceId] = timerState;
          }
        });
        
        console.log('✅ [TIMER] Estados de timers después de limpieza:', cleaned);
        return cleaned;
      }
    } catch (error) {
      console.error('❌ [TIMER] Error cargando estados de timers:', error);
    }
    return {};
  });
  const deviceTimerStatesRef = useRef(deviceTimerStates);
  deviceTimerStatesRef.current = deviceTimerStates;
  // SOLUCIÓN: Inicializar selectedTab basado en el homeTab del usuario si existe en localStorage
  // Esto evita que se muestren las habitaciones del primer tablero cuando hay un homeTab definido
  const getInitialSelectedTab = (): number => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        // Si hay homeTab definido para dashboard, retornar -1 para indicar que debe esperar
        // El tab correcto se establecerá cuando se carguen los boards
        if (user?.home_page === 'dashboard' && user?.home_tab !== null && user?.home_tab !== undefined) {
          return -1; // Valor especial que indica "esperar a establecer el tab correcto"
        }
      }
    } catch (error) {
      // Si hay error al leer localStorage, usar 0 por defecto
    }
    return 0;
  };
  const [selectedTab, setSelectedTab] = useState(getInitialSelectedTab());
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  
  // SOLUCIÓN: Inicializar userHomePage y userHomeTab desde localStorage para que estén disponibles inmediatamente
  const getInitialUserHomePage = (): string | null => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user?.home_page || null;
      }
    } catch (error) {
      // Ignorar errores
    }
    return null;
  };
  const getInitialUserHomeTab = (): number | null => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user?.home_tab || null;
      }
    } catch (error) {
      // Ignorar errores
    }
    return null;
  };
  const getInitialUserHomeRoom = (): number | null => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user?.home_room ?? null;
      }
    } catch (error) {
      // Ignorar errores
    }
    return null;
  };
  const [userHomePage, setUserHomePage] = useState<string | null>(getInitialUserHomePage());
  const [userHomeTab, setUserHomeTab] = useState<number | null>(getInitialUserHomeTab());
  // Habitación de inicio (solo dashboard): ID de la habitación dentro del tablero home. null = grilla del tablero
  const [userHomeRoom, setUserHomeRoom] = useState<number | null>(getInitialUserHomeRoom());
  // Ref para rastrear si ya se aplicó el tab inicial (evita re-renderizados infinitos)
  const homeTabAppliedRef = useRef<boolean>(false);
  // SOLUCIÓN: Ref para rastrear si el tab correcto ya fue establecido durante handleLogin
  // Esto previene que el useEffect cargue habitaciones del tab incorrecto
  const correctTabEstablishedRef = useRef<boolean>(false);
  // SOLUCIÓN: Ref para indicar que estamos en proceso de establecer el tab correcto
  // Esto previene que el useEffect se ejecute durante ese proceso
  const isSettingCorrectTabRef = useRef<boolean>(false);
  // SOLUCIÓN: Ref para rastrear si las habitaciones ya fueron cargadas manualmente durante el login
  // Esto previene que el useEffect recargue las habitaciones después de que ya se cargaron correctamente
  const roomsLoadedManuallyRef = useRef<boolean>(false);
  // SOLUCIÓN CRÍTICA: Ref para rastrear si estamos en proceso de carga inicial
  // Esto previene que CUALQUIER useEffect cargue habitaciones durante la carga inicial
  const isInitialLoadInProgressRef = useRef<boolean>(false);
  const ensureHouseReadyRef = useRef<() => Promise<void>>(async () => {});
  const houseEnsureInFlightRef = useRef(false);
  const [showHomePageDialog, setShowHomePageDialog] = useState(false);
  const [showHomePageMenu, setShowHomePageMenu] = useState(false);
  const [showHomeTabDialog, setShowHomeTabDialog] = useState(false);
  const [selectedPageForTab, setSelectedPageForTab] = useState<string | null>(null);
  // Tablero elegido dentro del diálogo de home para luego elegir una habitación de ese tablero
  const [selectedBoardForTab, setSelectedBoardForTab] = useState<number | null>(null);
  const [homeTabDialogRooms, setHomeTabDialogRooms] = useState<HabitacionConConsumo[]>([]);
  const [homeTabDialogRoomsLoading, setHomeTabDialogRoomsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeviceListModal, setShowDeviceListModal] = useState(false);
  
  // Estados para la barra de navegación inferior
  const [isHomeActive, setIsHomeActive] = useState(true); // Home activo por defecto
  const [isInfoActive, setIsInfoActive] = useState(false);
  const [isSettingsActive, setIsSettingsActive] = useState(false);
  
  // Estado del WebSocket para indicador visual
  const [websocketStatus, setWebsocketStatus] = useState<'connected' | 'disconnected' | 'connecting'>('disconnected');
  
  // Estado para el modal de dispositivo
  const [deviceModalOpen, setDeviceModalOpen] = useState<boolean>(false);
  const [selectedDevice, setSelectedDevice] = useState<any>(null);

  // Estados para sistema de PIN
  const { isPinSessionValid, extendPinSession } = usePinContext();
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pendingControl, setPendingControl] = useState<{ device: any; action: string; value?: any } | null>(null);
  /** Evita stale closure: onPinVerified puede correr antes del re-render tras setPendingControl. */
  const pendingControlRef = useRef<{ device: any; action: string; value?: any } | null>(null);
  /** Auditoría MCP tras control WebSocket iniciado por el asistente IA. */
  const assistantAuditRef = useRef<{
    device_id: number;
    device_name: string;
    logical_tool: string;
    accion_pwa: string;
    accion_valor?: string | null;
    frase_usuario: string;
    has_cameras: boolean;
    requires_pin: boolean;
    control_action?: string;
    idioma?: 'es' | 'en';
  } | null>(null);

  // Estados para ventana flotante de cámaras
  const [floatingCameraWindow, setFloatingCameraWindow] = useState<{
    open: boolean;
    cameraIds: number[];
    displayDuration: number;
    deviceName?: string;
    deviceId?: number;
    device?: any; // Dispositivo completo para renderizar la tarjeta
    originalCardWidth?: number; // Ancho de la tarjeta original
    requiresPin?: boolean; // Si requiere PIN
  pendingAction?: string; // Acción pendiente ('toggle' o 'control_cover')
  pendingActionValue?: any; // Valor de la acción pendiente
  controlAction?: string; // encender | apagar | alternar (asistente)
  } | null>(null);
  
  // Ref para FloatingCameraWindow para acceso en handlePopState (definir ANTES del useEffect que lo usa)
  const floatingCameraWindowRef = useRef(floatingCameraWindow);
  
  // Ref para FloatingCameraWindow para acceso en handlePopState
  useEffect(() => {
    floatingCameraWindowRef.current = floatingCameraWindow;
  }, [floatingCameraWindow]);
  
  // Estado para padding dinámico del contenedor cuando se abre la ventana flotante
  const [cameraWindowPaddingTop, setCameraWindowPaddingTop] = useState(0);

  // Estados para el botón back
  const [showExitMessage, setShowExitMessage] = useState(false);
  const exitMessageTimerRef = useRef<NodeJS.Timeout | null>(null);
  const exitMessageTimeRef = useRef<number>(0);

  // MEJORA: Cache para configuración de cámaras por dispositivo
  const cameraConfigCache = useRef<Map<number, { cameraIds: number[]; displayDuration: number } | null>>(new Map());
  const cameraConfigLoading = useRef<Set<number>>(new Set());

  // Función helper para obtener cámaras de un dispositivo (con cache)
  const getDeviceCameras = useCallback(async (deviceId: number, useCache: boolean = true): Promise<{ cameraIds: number[]; displayDuration: number } | null> => {
    // MEJORA: Verificar cache primero
    if (useCache && cameraConfigCache.current.has(deviceId)) {
      return cameraConfigCache.current.get(deviceId) || null;
    }

    // MEJORA: Evitar múltiples llamadas simultáneas para el mismo dispositivo
    if (cameraConfigLoading.current.has(deviceId)) {
      // Esperar a que termine la llamada en curso
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!cameraConfigLoading.current.has(deviceId)) {
            clearInterval(checkInterval);
            resolve(cameraConfigCache.current.get(deviceId) || null);
          }
        }, 50);
      });
    }

    cameraConfigLoading.current.add(deviceId);
    try {
      const response = await api.get(`/obtener_cameras_dispositivo/${deviceId}`);
      let result: { cameraIds: number[]; displayDuration: number } | null = null;
      if (response.data.success && response.data.cameras && response.data.cameras.length > 0) {
        result = {
          cameraIds: response.data.cameras.map((c: any) => c.id),
          displayDuration: response.data.display_duration === 0 || response.data.display_duration === '0'
            ? 0
            : (Number(response.data.display_duration) || 5)
        };
      }
      // MEJORA: Guardar en cache
      cameraConfigCache.current.set(deviceId, result);
      return result;
    } catch (error) {
      // Silenciar errores (puede que no tenga cámaras configuradas)
      cameraConfigCache.current.set(deviceId, null);
      return null;
    } finally {
      cameraConfigLoading.current.delete(deviceId);
    }
  }, []);

  // MEJORA: Cache para ancho de tarjetas de dispositivos
  const deviceCardWidthCache = useRef<Map<number, number>>(new Map());

  // Función para medir el ancho de la tarjeta original del dispositivo (con cache)
  const measureDeviceCardWidth = useCallback((deviceId: number): number => {
    // MEJORA: Verificar cache primero
    if (deviceCardWidthCache.current.has(deviceId)) {
      return deviceCardWidthCache.current.get(deviceId)!;
    }

    const deviceElement = document.querySelector(`[data-device-id="${deviceId}"]`) as HTMLElement;
    let width = 600; // Fallback: usar ancho por defecto basado en el grid
    if (deviceElement) {
      const rect = deviceElement.getBoundingClientRect();
      width = rect.width;
    }
    // MEJORA: Guardar en cache
    deviceCardWidthCache.current.set(deviceId, width);
    return width;
  }, []);

  const ensureCanControlDevice = useCallback(async (deviceLike: any): Promise<boolean> => {
    const user = getUser();
    if (user && isDemo(user)) {
      setPermissionSnackbar({
        open: true,
        message: t('permissions.demoRestriction'),
      });
      return false;
    }
    const dispositivo = deviceLike?.id != null
      ? (mapaDispositivos[deviceLike.id] || deviceLike)
      : deviceLike;
    const roomId = dispositivo?.habitacion_id;
    if (!roomId) {
      return true;
    }
    if (canControlDevicesSync(roomId)) {
      return true;
    }
    const allowed = await canControlDevices(roomId);
    if (!allowed) {
      setPermissionSnackbar({
        open: true,
        message: t('permissions.noControlDevices'),
      });
      return false;
    }
    return true;
  }, [t]);

  // MEJORA: Función para abrir ventana flotante de cámaras - abre inmediatamente sin esperar HTTP
  const openFloatingCameraWindow = useCallback(async (device: any, action?: string, actionValue?: any, controlAction?: string) => {
    // DEBUG TEMPORAL: Log para diagnosticar
    console.log('[App] openFloatingCameraWindow llamado:', { deviceId: device?.id, action, actionValue, device });
    const dispositivo = mapaDispositivos[device.id] || device;
    console.log('[App] dispositivo obtenido:', { id: dispositivo?.id, nombre: dispositivo?.nombre });
    const cardWidth = measureDeviceCardWidth(device.id);
    const requiresPin = (window as any).checkPinRequired && (window as any).checkPinRequired(dispositivo);
    console.log('[App] requiresPin:', requiresPin);
    
    // MEJORA: Verificar cache primero para obtener configuración inmediatamente
    const cachedConfig = cameraConfigCache.current.get(device.id);
    
    // MEJORA: Abrir ventana INMEDIATAMENTE con configuración cacheada o valores por defecto
    // Si hay cache, usar esos valores; si no, abrir con valores por defecto y cargar en paralelo
    const initialCameraIds = cachedConfig?.cameraIds || (dispositivo.camera_ids && Array.isArray(dispositivo.camera_ids) ? dispositivo.camera_ids : []);
    const cachedDuration = cachedConfig?.displayDuration;
    const initialDisplayDuration = cachedDuration === 0
      ? 0
      : (cachedDuration || 5);
    
    // MEJORA: Abrir ventana inmediatamente (no esperar HTTP)
    console.log('[App] setFloatingCameraWindow con:', {
      open: true,
      deviceId: device.id,
      requiresPin: requiresPin || false,
      pendingAction: action || 'toggle'
    });
    setFloatingCameraWindow({
      open: true,
      cameraIds: initialCameraIds,
      displayDuration: initialDisplayDuration,
      deviceName: dispositivo.nombre || device.nombre || `Dispositivo ${device.id}`,
      deviceId: device.id,
      device: dispositivo,
      originalCardWidth: cardWidth,
      requiresPin: requiresPin || false,
      pendingAction: action || 'toggle',
      pendingActionValue: actionValue,
      controlAction,
    });
    
    // MEJORA: Cargar configuración en paralelo (no bloquea apertura)
    // Si no hay cache o si la configuración puede haber cambiado, cargar en background
    if (!cachedConfig) {
      getDeviceCameras(device.id, true).then((cameraConfig) => {
        if (cameraConfig && cameraConfig.cameraIds.length > 0) {
          // Actualizar ventana con configuración real si cambió
          setFloatingCameraWindow(prev => {
            if (prev && prev.deviceId === device.id) {
              return {
                ...prev,
                cameraIds: cameraConfig.cameraIds,
                displayDuration: cameraConfig.displayDuration
              };
            }
            return prev;
          });
        } else {
          // Si no hay cámaras, cerrar la ventana
          setFloatingCameraWindow(prev => {
            if (prev && prev.deviceId === device.id && prev.cameraIds.length === 0) {
              return null;
            }
            return prev;
          });
        }
      }).catch(() => {
        // Si falla la carga y no había cámaras iniciales, cerrar ventana
        setFloatingCameraWindow(prev => {
          if (prev && prev.deviceId === device.id && prev.cameraIds.length === 0) {
            return null;
          }
          return prev;
        });
      });
    }
  }, [getDeviceCameras, measureDeviceCardWidth]);

  // Funciones para manejo de PIN
  // executePendingControl se define después de handleDeviceToggle y handleControlCover

  // Exponer funciones de verificación de PIN y control globalmente
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // CRITICO: checkPinRequired siempre debe obtener el dispositivo actualizado desde mapaDispositivos
      (window as any).checkPinRequired = (device: any) => {
        if (!device || !device.id) return false;
        
        // CRITICO: Obtener dispositivo actualizado desde mapaDispositivos para asegurar que tenga pin_required correcto
        const dispositivoEnMapa = mapaDispositivos[device.id];
        
        // Si mapaDispositivos está cargado Y el dispositivo está en el mapa, usar ese valor (más confiable)
        if (isMapaDispositivosCargado() && dispositivoEnMapa) {
          return Boolean(dispositivoEnMapa.pin_required);
        }
        
        // Si mapaDispositivos NO está cargado O el dispositivo no está en el mapa:
        // CRITICO: Verificar desde el dispositivo pasado
        const dispositivoActualizado = dispositivoEnMapa ? { ...device, ...dispositivoEnMapa } : device;
        const pinRequired = Boolean(dispositivoActualizado?.pin_required);
        
        // CRITICO: Si mapaDispositivos NO está cargado Y el dispositivo no tiene pin_required explícito,
        // hacer una verificación conservadora: asumir que requiere PIN por seguridad
        // Esto previene que dispositivos se operen sin PIN durante la carga inicial
        if (!isMapaDispositivosCargado() && !pinRequired) {
          // Mejor prevenir que curar: si no estamos seguros durante la carga inicial, requerir PIN
          // Esto previene race conditions donde dispositivos se operan sin PIN
          return true;
        }
        
        return pinRequired;
      };
      (window as any).isPinSessionValid = isPinSessionValid;
      (window as any).beginAssistantDeviceControl = async (
        deferred: AssistantControlDeferred
      ) => {
        const device =
          mapaDispositivos[deferred.device_id] ||
          ({
            id: deferred.device_id,
            nombre: deferred.device_name,
          } as any);
        const dispositivo = mapaDispositivos[device.id] || device;
        const plan = planPowerControl(
          dispositivo,
          deferred.pending_action,
          deferred.pending_action_value,
          deferred.control_action
        );
        if (plan.kind === 'noop' && plan.noopReason) {
          const lang: 'es' | 'en' = deferred.idioma === 'en' ? 'en' : 'es';
          const msg = formatAssistantControlNoop(
            deferred.device_name,
            plan.noopReason,
            lang
          );
          void auditAssistantControl({
            dispositivo_id: deferred.device_id,
            tool_logica: deferred.logical_tool,
            accion_pwa: deferred.pending_action,
            accion_valor: deferred.pending_action_value,
            frase_usuario: deferred.user_phrase,
            exito: true,
            mensaje_resultado: msg,
          }).catch(() => {});
          eventBus.emit('assistant:control_result', {
            device_id: deferred.device_id,
            success: true,
            message: msg,
            noop: true,
          });
          return;
        }
        assistantAuditRef.current = {
          device_id: deferred.device_id,
          device_name: deferred.device_name,
          logical_tool: deferred.logical_tool,
          accion_pwa: deferred.pending_action,
          accion_valor: deferred.pending_action_value,
          frase_usuario: deferred.user_phrase,
          has_cameras: deferred.has_cameras,
          requires_pin: deferred.requires_pin,
          control_action: deferred.control_action,
          idioma: deferred.idioma === 'en' ? 'en' : 'es',
        };
        const action = deferred.pending_action;
        const value = deferred.pending_action_value ?? undefined;
        if (deferred.requires_pin) {
          await (window as any).requestPinForControl(device, action, value);
        } else if (deferred.has_cameras) {
          await openFloatingCameraWindow(
            device,
            action,
            value,
            deferred.control_action
          );
        }
      };
      (window as any).requestPinForControl = async (device: any, action: string, value?: any) => {
        // DEBUG TEMPORAL: Log para diagnosticar
        console.log('[App] requestPinForControl llamado:', { deviceId: device?.id, action, value, device });
        try {
          const dispositivo = mapaDispositivos[device.id] || device;
          if (!(await ensureCanControlDevice(dispositivo))) {
            return;
          }
          
          // MEJORA: Verificar si el dispositivo tiene cámaras
          // Si NO tiene cámaras, usar PinInputDialog directamente (más simple y eficiente)
          const cachedConfig = cameraConfigCache.current.get(device.id);
          const hasCameras = Boolean(cachedConfig?.cameraIds && cachedConfig.cameraIds.length > 0) || 
                            Boolean(dispositivo.camera_ids && Array.isArray(dispositivo.camera_ids) && dispositivo.camera_ids.length > 0);
          
          console.log('[App] Dispositivo tiene cámaras:', hasCameras);

          // 'resume' es el gate de un grupo (o Sonos/Samsung): el callback solo
          // corre desde PinInputDialog. La ventana de cámaras no guarda ese pending
          // y el grupo quedaría sin operarse después del PIN.
          const useStandalonePinDialog = action === 'resume' || !hasCameras;
          if (useStandalonePinDialog) {
            // NO tiene cámaras (o es un resume de grupo): usar PinInputDialog directamente
            console.log('[App] Sin cámaras, usando PinInputDialog directamente');
            const pending = { device, action, value };
            pendingControlRef.current = pending;
            setPendingControl(pending);
            setShowPinDialog(true);
            // Agregar entradas al historial para que el botón BACK cierre el dialog
            for (let i = 0; i < 5; i++) {
              window.history.pushState({ page: `pin-input-dialog-${i}` }, '', '');
            }
            console.log('[App] PinInputDialog abierto');
            return;
          }
          
          // Tiene cámaras: usar FloatingCameraWindow como antes
          console.log('[App] Tiene cámaras, usando FloatingCameraWindow');
          await openFloatingCameraWindow(device, action, value);
          console.log('[App] openFloatingCameraWindow completado');
          // Agregar MÚLTIPLES entradas al historial para que el botón BACK cierre el dialog
          for (let i = 0; i < 5; i++) {
            window.history.pushState({ page: `pin-input-dialog-${i}` }, '', '');
          }
          console.log('[App] requestPinForControl completado exitosamente');
        } catch (error) {
          console.error('[App] ERROR en requestPinForControl:', error);
        }
      };
      (window as any).extendPinSession = extendPinSession;
      (window as any).mapaDispositivos = mapaDispositivos;
      // CRITICO: Exponer función para verificar si mapaDispositivos está cargado
      (window as any).isMapaDispositivosCargado = isMapaDispositivosCargado;
      // Exponer función de click (se expone inmediatamente)
      (window as any).handleDeviceClickFromGroup = handleDeviceClick;
      // CRÍTICO: Exponer refs para que DeviceCard pueda verificar si hubo scroll
      (window as any).dashboardTouchStartRef = dashboardTouchStartRef;
      (window as any).dashboardHasScrolledRef = dashboardHasScrolledRef;
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).checkPinRequired;
        delete (window as any).isPinSessionValid;
        delete (window as any).requestPinForControl;
        delete (window as any).beginAssistantDeviceControl;
        delete (window as any).extendPinSession;
        delete (window as any).mapaDispositivos;
        delete (window as any).isMapaDispositivosCargado;
        delete (window as any).dashboardTouchStartRef;
        delete (window as any).dashboardHasScrolledRef;
      }
    };
    // handleDeviceClick se define más abajo; openFloatingCameraWindow y mapa se re-asignan al montar/cambiar PIN
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPinSessionValid, extendPinSession, getDeviceCameras, openFloatingCameraWindow, ensureCanControlDevice]);
  
  // Refs para obtener valores actuales
  const currentScreenRef = useRef(currentScreen);
  const leaveSoftphoneQuickIfNeededRef = useRef<(nextScreen: string) => boolean>(() => false);
  const selectedRoomRef = useRef(selectedRoom);
  const selectedTabRef = useRef(selectedTab);
  const deviceModalOpenRef = useRef(deviceModalOpen);
  const showDeviceListModalRef = useRef(showDeviceListModal);
  const showExitMessageRef = useRef(showExitMessage);
  const showPinDialogRef = useRef(showPinDialog);
  const sortAnchorElRef = useRef<null | HTMLElement>(null);
  const filterAnchorElRef = useRef<null | HTMLElement>(null);
  const dashboardContentRef = useRef<HTMLDivElement>(null);
  /** Grilla `display: grid` de tarjetas de habitación en home (solo lectura geometría: scroll según alto real vs panel). */
  const dashboardRoomGridRef = useRef<HTMLDivElement | null>(null);
  /** Home habitaciones sin sala seleccionada: true solo si hay contenido que realmente debe desplazarse (evitar “scroll fantasma”). */
  const [dashRoomGridOverflows, setDashRoomGridOverflows] = useState(false);

  // Al cambiar de pantalla: resetear scroll del panel. Si no, overflow:hidden
  // en SoftPhone/Audio/etc. deja scrollTop viejo del dashboard y “come” el SubPageHeader.
  useEffect(() => {
    const pane = dashboardContentRef.current;
    if (pane && pane.scrollTop !== 0) {
      pane.scrollTop = 0;
    }
  }, [currentScreen]);

  // Actualizar refs
  useEffect(() => {
    currentScreenRef.current = currentScreen;
  }, [currentScreen]);
  
  useEffect(() => {
    selectedRoomRef.current = selectedRoom;
  }, [selectedRoom]);
  
  useEffect(() => {
    selectedTabRef.current = selectedTab;
  }, [selectedTab]);
  

  useEffect(() => {
    deviceModalOpenRef.current = deviceModalOpen;
  }, [deviceModalOpen]);

  useEffect(() => {
    showDeviceListModalRef.current = showDeviceListModal;
  }, [showDeviceListModal]);

  useEffect(() => {
    showExitMessageRef.current = showExitMessage;
  }, [showExitMessage]);

  useEffect(() => {
    showPinDialogRef.current = showPinDialog;
  }, [showPinDialog]);

  // Tracking de navegación para usuarios Demo
  useEffect(() => {
    if (!token) return; // Solo cuando está logueado
    
    const user = getUser();
    if (user && isDemo(user)) {
      const tracker = getDemoActivityTracker();
      tracker.trackNavigation(currentScreen, {
        roomId: selectedRoom?.id || null,
        deviceId: selectedDevice?.id || null
      });
    }
  }, [currentScreen, token, selectedRoom?.id, selectedDevice?.id]);

  // useEffect para manejar el botón back del teléfono móvil - SOLO UNA VEZ
  useEffect(() => {
    if (!token) return; // Solo inicializar cuando ya estamos logueados
    
    // Agregar MÚLTIPLES estados iniciales para interceptar el back
    window.history.pushState({ page: 1 }, '', '');
    window.history.pushState({ page: 2 }, '', '');
    window.history.pushState({ page: 3 }, '', '');
    window.history.pushState({ page: 4 }, '', '');
    window.history.pushState({ page: 5 }, '', '');
    window.history.pushState({ page: 6 }, '', '');
    window.history.pushState({ page: 7 }, '', '');
    window.history.pushState({ page: 8 }, '', '');
    window.history.pushState({ page: 9 }, '', '');
    window.history.pushState({ page: 10 }, '', '');
    window.history.pushState({ page: 11 }, '', '');
    window.history.pushState({ page: 12 }, '', '');
    window.history.pushState({ page: 13 }, '', '');
    window.history.pushState({ page: 14 }, '', '');
    window.history.pushState({ page: 15 }, '', '');
    
    const handlePopState = (event: PopStateEvent) => {
      const currentScreenValue = currentScreenRef.current;
      const deviceModalHandleBackExists = !!(window as any).deviceModalHandleBack;
      const deviceListModalOpen = showDeviceListModalRef.current;
      const sortMenuOpen = sortAnchorElRef.current !== null;
      const filterMenuOpen = filterAnchorElRef.current !== null;
      const exitMessageActive = showExitMessageRef.current;
      const pinDialogOpen = showPinDialogRef.current;
      const floatingCameraWindowOpen = floatingCameraWindowRef.current?.open === true;
      
      // PRIORIDAD -1 (MÁS ALTA): Si hay FloatingCameraWindow abierto, delegar el manejo a él
      // FloatingCameraWindow maneja su propio botón back internamente
      if (floatingCameraWindowOpen) {
        // No hacer nada aquí, dejar que FloatingCameraWindow maneje el evento
        return;
      }
      
      // PRIORIDAD 0: Si hay un dialog de PIN abierto (fuera de FloatingCameraWindow), cerrarlo
      if (pinDialogOpen) {
        setShowPinDialog(false);
        setPendingControl(null); // Limpiar control pendiente
        return;
      }

      // PRIORIDAD 0.5: Overlays de Audio/Sonos y otros modales con useOverlayBackButton
      if (handleOverlayBack()) {
        return;
      }
      
      // PRIORIDAD 1: Si hay un DeviceModal abierto, delegar a él
      if (deviceModalHandleBackExists) {
        (window as any).deviceModalHandleBack();
        return;
      }
      
      // PRIORIDAD 2: Si hay un DeviceListModal abierto, cerrarlo
      if (deviceListModalOpen) {
        setShowDeviceListModal(false);
        // Resetear el icono 'i' a blanco
        setIsInfoActive(false);
        return;
      }
      
      // PRIORIDAD 2.5: Si hay un GroupsModal o GroupDetailView abierto, cerrarlo
      if ((window as any).closeGroupsModal) {
        (window as any).closeGroupsModal();
        return;
      }
      if ((window as any).closeGroupDetailModal) {
        (window as any).closeGroupDetailModal();
        return;
      }
      
      // PRIORIDAD 3: Si hay menús de orden/filtro abiertos en Devices, cerrarlos
      if (currentScreenValue === 'devices' && (sortMenuOpen || filterMenuOpen)) {
        setSortAnchorEl(null);
        setFilterAnchorEl(null);
        return;
      }
      
      // PRIORIDAD 4: Si hay diálogos de Filtrar/Ordenar abiertos en Settings, cerrarlos
      if (currentScreenValue === 'settings' && (window as any).deviceListViewHandleBack) {
        const handled = (window as any).deviceListViewHandleBack();
        if (handled) {
          return; // Si se manejó el back (cerró diálogos), no continuar
        }
        // Si no se manejó (no había diálogos), continuar con la navegación normal
      }
      
      // PRIORIDAD 4.5: Si hay diálogos de Settings abiertos (modo edición), cerrarlos
      if (currentScreenValue === 'settings' && (window as any).settingsHandleBack) {
        const handled = (window as any).settingsHandleBack();
        if (handled) {
          return; // Si se manejó el back (cerró diálogos), no continuar
        }
        // Si no se manejó (no había diálogos), continuar con la navegación normal
      }
      if ((window as any).boardManagementHandleBack) {
        const handled = (window as any).boardManagementHandleBack();
        if (handled) {
          return; // Si se manejó el back (cerró diálogos), no continuar
        }
      }
      
      // PRIORIDAD 6: Si hay diálogos de RoomManagement abiertos, cerrarlos
      if ((window as any).roomManagementHandleBack) {
        const handled = (window as any).roomManagementHandleBack();
        if (handled) {
          return; // Si se manejó el back (cerró diálogos), no continuar
        }
      }
      
      // PRIORIDAD 7: Si estamos en Devices, navegar a Dashboard
      if (currentScreenValue === 'devices') {
        setCurrentScreen('dashboard');
        setSelectedRoom(null);
        setShowExitMessage(false);
        return;
      }
      
      // PRIORIDAD 8: Si estamos en Settings, navegar a Dashboard
      if (currentScreenValue === 'settings') {
        setCurrentScreen('dashboard');
        setIsHomeActive(true);
        setIsSettingsActive(false);
        setShowExitMessage(false);
        return;
      }

      // PRIORIDAD 8.35: Pileta → volver a habitación / Settings / widgets
      if (currentScreenValue === 'pileta') {
        const returnRoom = (window as any).__piletaReturnRoom as HabitacionConConsumo | null | undefined;
        const returnScreen = ((window as any).__piletaReturnScreen as AppScreen | undefined) || 'dashboard';
        if (returnRoom) setSelectedRoom(returnRoom);
        if (returnScreen === 'settings') {
          setIsSettingsActive(true);
          setIsHomeActive(false);
        }
        setCurrentScreen(returnScreen);
        setShowExitMessage(false);
        return;
      }

      // PRIORIDAD 8.4: Clima — editor de horario / controles de sistema antes del dashboard
      if (currentScreenValue === 'climate' && (window as any).climateHandleBack) {
        const handled = (window as any).climateHandleBack();
        if (handled) {
          return;
        }
      }

      // PRIORIDAD 8.5: Pantallas de navegación inferior → volver al Dashboard (evita cerrar la app)
      if (
        currentScreenValue === 'music'
        || currentScreenValue === 'softphone'
        || currentScreenValue === 'video'
        || currentScreenValue === 'favorites'
        || currentScreenValue === 'realtime'
        || currentScreenValue === 'reports'
        || currentScreenValue === 'assistant'
        || currentScreenValue === 'automations'
        || currentScreenValue === 'alarm'
        || currentScreenValue === 'climate'
        || currentScreenValue === 'widgets'
      ) {
        if (
          (currentScreenValue === 'softphone' || currentScreenValue === 'video')
          && leaveSoftphoneQuickIfNeededRef.current('dashboard')
        ) {
          setShowExitMessage(false);
          return;
        }
        setCurrentScreen('dashboard');
        setShowExitMessage(false);
        return;
      }
      
      
        // PRIORIDAD 6: Estamos en Dashboard - Manejar doble back para salir
        if (currentScreenValue === 'dashboard') {
          const now = Date.now();
          const timeSinceLastBack = now - exitMessageTimeRef.current;
          
          // Si el mensaje ya está activo Y han pasado menos de 2.5 segundos → SALIR
          if (exitMessageActive && timeSinceLastBack < 2500) {
            window.close();
            return;
          }
          
          // Si el mensaje estaba activo pero ya pasaron más de 2.5 segundos → mostrar de nuevo
          // O si no estaba activo → mostrar por primera vez
          setShowExitMessage(true);
          exitMessageTimeRef.current = now;
          
          // Ocultar mensaje después de 2.5 segundos
          if (exitMessageTimerRef.current) {
            clearTimeout(exitMessageTimerRef.current);
          }
          exitMessageTimerRef.current = setTimeout(() => {
            setShowExitMessage(false);
          }, 2500);
          
          // Agregar entrada al historial DESPUÉS de que el evento popstate se complete
          setTimeout(() => {
            window.history.pushState({ page: 'exit-message-ready' }, '', '');
          }, 10);
          
          return;
        }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (exitMessageTimerRef.current) {
        clearTimeout(exitMessageTimerRef.current);
      }
    };
  }, [token]); // SOLO UNA VEZ cuando hay token

  // Telemetría solar (planta: PV + batería) para DeviceList y dashboard sin abrir Energía solar
  useEffect(() => {
    if (!token) {
      return;
    }
    return startSolarFlowMetricsRelay();
  }, [token]);

  // Estados para filtros avanzados (como React Native)
  const [filtroModelos, setFiltroModelos] = useState<string[]>([]);
  const [filtroOnline, setFiltroOnline] = useState<boolean>(false);
  const [filtroOffline, setFiltroOffline] = useState<boolean>(false);
  const [filtroAuth, setFiltroAuth] = useState<boolean>(false);
  const [filtroAuthConfigured, setFiltroAuthConfigured] = useState<boolean>(false);
  const [filtroPin, setFiltroPin] = useState<boolean>(false);
  const [filtroCamera, setFiltroCamera] = useState<boolean>(false);
  const [filtroEncendido, setFiltroEncendido] = useState<boolean>(false);
  const [filtroApagado, setFiltroApagado] = useState<boolean>(false);
  const [filtroConConsumo, setFiltroConConsumo] = useState<boolean>(false);
  const [filtroSinConsumo, setFiltroSinConsumo] = useState<boolean>(false);
  const [filtroArtefactos, setFiltroArtefactos] = useState<string[]>([]);
  const [filtroAiPermitido, setFiltroAiPermitido] = useState(false);
  const [filtroAiDenegado, setFiltroAiDenegado] = useState(false);
  const [filtroAiSinDefinir, setFiltroAiSinDefinir] = useState(false);
  
  // Estado para forzar actualizaciones de UI
  const [, setRefreshCounter] = useState(0);
  
  // Estados para opciones disponibles
  // Modelos de filtro: se calcula abajo con Shelly + marcas cloud (Sonos/Samsung/…).
  const [todosArtefactos, setTodosArtefactos] = useState<string[]>([]);
  const [mapaArtefactos, setMapaArtefactos] = useState<Record<number, string>>({});

  // Estados para feedback visual inmediato
  const [confirmingDevices, setConfirmingDevices] = useState<Set<number>>(new Set());
  // Estado para dispositivos confirmados (PLUS1: feedback extendido de 2 seg)
  const [confirmedDevices, setConfirmedDevices] = useState<Set<number>>(new Set());

  // Estado temporal para covers en movimiento
  const [coverActions, setCoverActions] = useState<Record<number, string>>({});
  // Estado para guardar las acciones enviadas por deviceId
  const [, setPendingCoverActions] = useState<Record<number, string>>({});
  const [, setDeviceTimeouts] = useState<Record<number, NodeJS.Timeout>>({});
  const [deviceErrors, setDeviceErrors] = useState<Set<number>>(new Set());

  // Estados para menús de ordenamiento y filtros (como React Native)

  // El manejo del botón atrás ahora se hace directamente en index.tsx
  const [sortCriterion, setSortCriterion] = useState<RoomSortCriterion>('personalizado');
  const [roomReorderMode, setRoomReorderMode] = useState(false);
  const [roomUserOrder, setRoomUserOrder] = useState<Map<string, number>>(new Map());
  const [roomUserOrderRoomId, setRoomUserOrderRoomId] = useState<number | null>(null);
  const roomSortTouchedRef = useRef(false);
  const roomUserOrderRef = useRef<Map<string, number>>(new Map());
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  
  // Actualizar refs de menús
  useEffect(() => {
    sortAnchorElRef.current = sortAnchorEl;
  }, [sortAnchorEl]);

  useEffect(() => {
    filterAnchorElRef.current = filterAnchorEl;
  }, [filterAnchorEl]);
  
  // Estado para debug de swipe
  const [, setDebugLogs] = useState<string[]>([]);
  
  
  // Estado para prevenir clicks en tarjetas inmediatamente después de cambiar de habitación
  const [preventDeviceClicks, setPreventDeviceClicks] = useState<boolean>(false);
  
  // Estado para controlar la carga inicial
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const isLoggingInRef = useRef(false);
  const pwaWsReconnectLockRef = useRef(false);
  const pwaWsDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    isLoggingInRef.current = isLoggingIn;
  }, [isLoggingIn]);

  // Efecto para eliminar el bounce y rubber band globalmente
  useEffect(() => {
    // Aplicar estilos al body y html para eliminar efectos de scroll
    const style = document.createElement('style');
    style.textContent = `
      html, body {
        overscroll-behavior: none !important;
        -webkit-overflow-scrolling: auto !important;
        overflow: hidden !important;
      }
      * {
        overscroll-behavior: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Función para manejar el login
  const handleLogin = async (token: string, userEmail: string, userData: any) => {
    setIsLoggingIn(true); // ✅ Prevenir que el useEffect de refresh interfiera
    // El aviso de inactividad ya se mostró en LoginPage; no arrastrarlo a la sesión nueva.
    clearPreviousSessionTimeout();
    clearLoginNotice();
    clearSoftphoneCallEndedGate();
    setSoftphoneCallEnded(false);
    softphoneExitInFlightRef.current = false;
    // Evitar que un snackbar de la sesión anterior (warning/tokenExpired) reaparezca al montar la UI.
    setPermissionSnackbar({ open: false, message: '' });
    if (
      isSoftphoneAnswerDeepLink()
      && (canUsePhone(userData) || canAnswerDoorbell(userData))
    ) {
      pendingDeepLinkScreenRef.current = pendingDeepLinkScreenRef.current || 'softphone';
    }

    // ✅ GUARDAR TOKEN Y USUARIO EN LOCALSTORAGE para que el frontend pueda leerlo
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    ApiService.setToken(token);
    
    // CRITICO: Limpiar caché de permisos al iniciar sesión para forzar recarga
    // Esto asegura que los permisos se carguen frescos desde el backend
    const { clearPermissionsCache } = await import('./services/permissionsService');
    clearPermissionsCache();
    setCachedUserPermissions(null); // Limpiar también el caché local de App.tsx
    
    setToken(token);
    setEmail(userEmail);
    setUsername(userData?.nombre_completo || userData?.username || userEmail.split('@')[0]);
    setUserRole(userData?.role || '');
    setUserData(userData); // Almacenar datos completos del usuario para permisos
    eventBus.emit('ai:policy-lookup-refresh');

    // Phase 2: pre-warm LLM solo si el usuario tiene permiso de IA
    if (canUseAI(userData)) {
      import('./services/assistantWarm').then(({ triggerAssistantWarmInBackground }) => {
        triggerAssistantWarmInBackground(false);
      });
    }

    // Re-sincronizar Web Push con el servidor (celular/desktop tras revocación FCM).
    import('./services/pushNotificationsService').then(({ ensurePushReady, pushSupported }) => {
      if (pushSupported()) void ensurePushReady();
    });
    
    // 🔧 FIX: Establecer la página de inicio INMEDIATAMENTE antes de mostrar la UI
    // para evitar que se renderice el dashboard primero
    const homePage = userData?.home_page;
    const homeTab = userData?.home_tab;
    const homeRoom = userData?.home_room ?? null;
    setUserHomePage(homePage);
    setUserHomeTab(homeTab);
    setUserHomeRoom(homeRoom);

    // ¿La página de inicio guardada ya no se puede honrar por permisos? (Asistente IA / Video revocados)
    const homeBlockedByPermission =
      (homePage === 'video' && !canViewCameras(userData));

    // Valores efectivos que realmente vamos a aplicar (pueden anularse si hay que volver a preguntar)
    let effectiveHomePage: string | null = homePage || null;
    let effectiveHomeTab: number | null | undefined = homeTab;
    let effectiveHomeRoom: number | null = homeRoom;
    
    if (pendingDeepLinkScreenRef.current) {
      // Deep-link desde notificación push: gana sobre la home_page en el arranque.
      const dls = pendingDeepLinkScreenRef.current;
      pendingDeepLinkScreenRef.current = null;
      const allowedDeep = resolveAllowedScreen(dls, userData);
      setCurrentScreen(allowedDeep as any);
      if (dls === 'automations') {
        setAssistantHubTab('automations');
      } else if (dls === 'assistant') {
        setAssistantHubTab(canUseAI(userData) ? 'chat' : 'automations');
      }
      setIsHomeActive(allowedDeep === 'dashboard');
      if (
        (allowedDeep === 'softphone' && (getUrlParam('kind') === 'doorbell' || getUrlParam('camera')))
        || getUrlParam('softphone') === 'answer'
      ) {
        const cam = parseInt(getUrlParam('camera') || '23', 10);
        const cameraId = Number.isFinite(cam) ? cam : 23;
        clearSoftphoneCallEndedGate();
        setSoftphoneCallEnded(false);
        softphoneExitInFlightRef.current = false;
        if (!hasLoggedInDocumentSession()) {
          markSoftphoneQuickAnswer();
        }
        markSoftphoneDoorbellSession();
        setDoorbellIsland(true);
        setPendingDoorbellCamera(cameraId);
        setCurrentScreen('softphone');
        try { eventBus.emit('softphone:open-doorbell', { cameraId }); } catch { /* ignore */ }
      } else if (allowedDeep === 'softphone' && getUrlParam('answer') === '1') {
        clearSoftphoneCallEndedGate();
        setSoftphoneCallEnded(false);
        softphoneExitInFlightRef.current = false;
        if (!hasLoggedInDocumentSession()) {
          markSoftphoneQuickAnswer();
        }
      }
    } else if (homePage && !homeBlockedByPermission) {
      const allowedHome = resolveAllowedScreen(homePage, userData);
      setCurrentScreen(allowedHome as 'dashboard' | 'favorites' | 'reports' | 'realtime' | 'video' | 'assistant');
      if (homePage === 'assistant') {
        setAssistantHubTab(canUseAI(userData) ? 'chat' : 'automations');
      }
      setIsHomeActive(allowedHome === 'dashboard');
    } else if (homePage && homeBlockedByPermission) {
      // Permiso revocado: esta sesión cae a dashboard. NO borrar home_page en BD —
      // solo el usuario puede cambiarla desde el menú (p. ej. "Ninguna").
      setCurrentScreen('dashboard');
      setIsHomeActive(true);
    } else {
      // Usuario no tiene página de inicio definida (null)
      // Esto puede ser porque:
      // 1. Nunca configuró su home page (primera vez) -> mostrar diálogo
      // 2. Eligió "Ninguna" explícitamente -> no mostrar diálogo, ir al dashboard
      // Para diferenciar, verificamos si el usuario tiene un flag en localStorage que indique que ya eligió "Ninguna"
      // NOTA: la clave es "_v2". Al renombrarla se invalidan las elecciones "Ninguna" anteriores
      // (reset masivo de home), forzando que todos vuelvan a elegir una vez. La opción "Ninguna"
      // sigue funcionando normalmente a futuro con esta nueva clave.
      const hasChosenNone = localStorage.getItem('user_chose_none_homepage_v2');
      if (hasChosenNone) {
        // Usuario eligió "Ninguna" explícitamente, ir al dashboard sin mostrar diálogo
        setCurrentScreen('dashboard');
        setIsHomeActive(true);
      } else {
        // Primera vez que el usuario inicia sesión sin home page, mostrar diálogo
        setShowHomePageDialog(true);
        // Mientras tanto, establecer dashboard como temporal
        setCurrentScreen('dashboard');
        setIsHomeActive(true);
      }
    }
    
    // ⚡ OPTIMIZACIÓN: Mostrar UI inmediatamente, cargar datos en segundo plano
    setLoading(false); // Permitir que se muestre la UI principal

    // Timbre (f95e92a): no cargar casa/WS/mapas mientras WHEP + SIP REGISTER.
    // Teléfono sí carga casa (si no, al colgar el dashboard queda vacío).
    if (isDoorbellIncomingContext()) {
      setCurrentScreen('softphone');
      setIsHomeActive(false);
      setDoorbellIsland(true);
      setIsLoggingIn(false);
      setLoginUiComplete(true);
      return;
    }

    // Cargar iconos descargados del servidor para que estén disponibles en toda la aplicación
    loadDownloadedIconifyIcons().catch(err => {
      // Silenciar errores, los iconos se cargarán cuando se necesiten
      console.error('Error cargando iconos descargados:', err);
    });
    
    // SOLUCIÓN: Solo reconectar WebSocket si es necesario (token diferente o no hay conexión)
    setWebsocketStatus('connecting');
    syncServerUrlWithCurrentOrigin();
    connectWebSocketWithRetry(token).then((ok) => {
      if (!ok) {
        // Dejar en connecting: el watchdog reintentará sin mostrar rojo permanente
        setWebsocketStatus('connecting');
        eventBus.emit('websocket:connecting');
      }
    });
    
    try {
      // ⚡ OPTIMIZACIÓN: Cargar datos en paralelo y de forma progresiva
      // 1. Iniciar carga de estructura en segundo plano (no esperar)
      // ✅ OPTIMIZACIÓN: Solo usar refrescarEstructuraCompleta (elimina duplicación con populateGlobalMaps)
      // refrescarEstructuraCompleta ya carga dispositivos, habitaciones y tableros en paralelo
      // populateGlobalMaps hace trabajo similar pero más granular y causa conflictos, así que lo eliminamos del flujo inicial
      refrescarEstructuraCompleta();
      cargarMapasNoShelly();
      
      // 2. Cargar favoritos y permisos en paralelo con estructura (no bloqueante)
      let tieneFavoritos = false;
      const favoritosPromise = (async () => {
        try {
          const sonosFavPromise = canViewMusic(userData)
            ? Promise.all([
                getFavorites().catch(() => [] as number[]),
                getSonosFavoritesOrden().catch(() => new Map<number, number>()),
              ])
            : Promise.resolve([[] as number[], new Map<number, number>()] as const);
          const [favoritesData, favoriteGroupsData, sonosFavResult] = await Promise.all([
            getUserFavorites(userData.id, true),
            getUserFavoriteGroups(userData.id, true),
            sonosFavPromise,
          ]);
          const [sonosFavIds, sonosOrdenMap] = sonosFavResult;
          
          setSonosFavorites(sonosFavIds);
          setSonosFavoriteOrden(Object.fromEntries(sonosOrdenMap));
          const favSonos = sonosFavIds
            .map((id) => mapaSonos[id])
            .filter((s): s is SonosSpeaker => !!s);
          setFavoriteSonosSpeakers(favSonos);

          tieneFavoritos = favoritesData.length > 0 || favoriteGroupsData.length > 0 || sonosFavIds.length > 0;
        setUserFavorites(favoritesData);
        setHasFavorites(tieneFavoritos);
        
        const favDevices: DispositivoConConsumo[] = favoritesData
          .map(fav => {
            const dispositivo = fav.dispositivo;
            if (!dispositivo) return null;
            // Obtener dispositivo actualizado de mapaDispositivos para asegurar propiedades actualizadas como pin_required
            const dispositivoActualizado = mapaDispositivos[fav.device_id] || dispositivo;
            return {
              ...dispositivoActualizado,
              pin_required: dispositivoActualizado.pin_required ?? false,
              auth_required: dispositivoActualizado.auth_required ?? false,
              mac: dispositivoActualizado.mac || '',
              favorite_orden: fav.orden ?? 0,
            } as DispositivoConConsumo;
          })
          .filter((d): d is DispositivoConConsumo => d !== null)
          .sort((a, b) => {
            const aFav = favoritesData.find(f => f.device_id === a.id);
            const bFav = favoritesData.find(f => f.device_id === b.id);
            return (aFav?.orden || 0) - (bFav?.orden || 0);
          });
        setFavoriteDevices(favDevices);
        
        const roomsInOrder: (number | string)[] = [];
        const seenRooms = new Set();
        favDevices.forEach(device => {
          const roomId = String(device.habitacion_id || 'sin-habitacion');
          if (!seenRooms.has(roomId)) {
            roomsInOrder.push(roomId);
            seenRooms.add(roomId);
          }
        });
        favSonos.forEach((speaker) => {
          const roomId = String(speaker.habitacion_id || 'sin-habitacion');
          if (!seenRooms.has(roomId)) {
            roomsInOrder.push(roomId);
            seenRooms.add(roomId);
          }
        });
        setFavoriteRoomsOrder(roomsInOrder);
        
          return tieneFavoritos;
      } catch (error) {
        setHasFavorites(false);
          return false;
      }
      })();
      
      // ✅ OPTIMIZACIÓN: Precargar permisos en paralelo con favoritos (solo admin no necesita permisos)
      const permisosPromise = (async () => {
        const user = getUser();
        // CRITICO: Solo admin tiene acceso total sin permisos granulares
        // Los usuarios demo (tanto en modo user como admin) necesitan permisos granulares explícitos (igual que usuarios normales)
        // La única diferencia es que demo en modo admin puede ver Settings
        if (userRole === 'admin') {
          // Admin no necesita permisos
          return null;
        }
        try {
          if (!user || !user.id) return null;
          const userPermissions = await getUserPermissions(user.id);
          // Extraer room_ids donde tiene can_view_room
          let allowedRoomIds: number[] = [];
          if (userPermissions.permissions && Array.isArray(userPermissions.permissions)) {
            allowedRoomIds = userPermissions.permissions
              .filter((p: any) => p.can_view_room)
              .map((p: any) => p.room_id);
          } else {
            // Compatibilidad: si solo hay room_ids, asumir que tiene can_view_room
            allowedRoomIds = userPermissions.room_ids || [];
          }
          // Cachear permisos
          setCachedUserPermissions({
            allowedRoomIds,
            permissions: userPermissions.permissions,
            room_ids: userPermissions.room_ids
          });
          return { allowedRoomIds, userPermissions };
        } catch (error) {
          console.error('Error cargando permisos:', error);
          return null;
        }
      })();
      
      // ✅ OPTIMIZACIÓN: Cargar boards ANTES de que termine la estructura completa
      // Solo necesita los tableros básicos, no toda la estructura
      // 3. Cargar boards en paralelo (no esperar estructura completa)
      const boardsPromise = (async () => {
        tieneFavoritos = await favoritosPromise;
        await permisosPromise;
        const skipInitialLoad = !!(effectiveHomePage && effectiveHomeTab !== null && effectiveHomeTab !== undefined && effectiveHomePage === 'dashboard');
        isInitialLoadInProgressRef.current = true;
        
        if (effectiveHomePage && effectiveHomeTab !== null && effectiveHomeTab !== undefined && effectiveHomePage === 'dashboard') {
          setRooms([]);
          setHomeTabInitialized(false);
          isSettingCorrectTabRef.current = true;
        }
        
        return await loadBoards(tieneFavoritos, skipInitialLoad);
      })();
      
      // Esperar boards para continuar con el flujo
      let loadedBoards = await boardsPromise;
      
      // 4. Aplicar el tab/habitación de inicio si está definido (la página ya se estableció arriba)
      // SOLUCIÓN CRÍTICA: Usar flushSync para fijar tab + habitaciones + habitación seleccionada en un solo commit.
      // Así nunca se ve el primer tablero ni la grilla intermedia (se elimina el doble render).
      if (effectiveHomePage && effectiveHomeTab !== null && effectiveHomeTab !== undefined) {
        if (effectiveHomePage === 'dashboard') {
          // Para dashboard, effectiveHomeTab es el ID del tablero
          let boardIndex = loadedBoards.findIndex((board: any) => board.id === effectiveHomeTab);
          // Si no aparece (posible carrera de permisos/estructura), reintentar una vez antes de rendirse
          if (boardIndex === -1) {
            loadedBoards = await loadBoards(tieneFavoritos, true);
            boardIndex = loadedBoards.findIndex((board: any) => board.id === effectiveHomeTab);
          }
          if (boardIndex !== -1) {
            // Cargar las habitaciones del tablero objetivo SIN tocar el estado todavía
            const boardRooms = await fetchRoomsForBoard(loadedBoards[boardIndex].id);
            // Resolver la habitación de inicio (si el usuario eligió una)
            let targetRoom: HabitacionConConsumo | null = null;
            let roomMissing = false;
            if (effectiveHomeRoom !== null && effectiveHomeRoom !== undefined) {
              targetRoom = boardRooms.find((r: any) => r.id === effectiveHomeRoom) || null;
              if (!targetRoom) {
                // La habitación de inicio ya no existe / sin permiso -> volver a preguntar
                roomMissing = true;
              }
            }
            // Commit atómico: tab + habitaciones + habitación seleccionada juntos
            flushSync(() => {
              isSettingCorrectTabRef.current = true;
              correctTabEstablishedRef.current = true;
              setSelectedTab(boardIndex);
              setRooms(boardRooms);
              if (targetRoom) {
                setSelectedRoom(targetRoom);
                setIsHomeActive(false);
                // En layout vertical, los dispositivos de la habitación se ven en la pantalla
                // 'devices'. En horizontal se muestran dentro del propio dashboard.
                if (!isHorizontal) {
                  setCurrentScreen('devices');
                }
              }
              homeTabAppliedRef.current = true;
              setHomeTabInitialized(true);
            });
            roomsLoadedManuallyRef.current = true;
            isSettingCorrectTabRef.current = false;
            // En vertical, apilar estados en el history para que "atrás" vuelva a la grilla
            // del tablero (mismo comportamiento que al entrar a una habitación con un tap).
            if (targetRoom && !isHorizontal) {
              for (let i = 0; i < 10; i++) {
                window.history.pushState({ page: `devices-${i}` }, '', '');
              }
            }
            if (roomMissing) {
              // Habitación de inicio no está en el tablero: abrir grilla del tablero.
              // NO borrar home_page/home_room en BD (solo el usuario desde el menú).
              setSelectedRoom(null);
              setIsHomeActive(true);
            }
          } else if (loadedBoards.length > 0) {
            // Tablero de inicio no está en la lista cargada: mostrar el primero disponible.
            // NO borrar home en BD (puede ser carrera 401/permisos; el valor sigue válido).
            correctTabEstablishedRef.current = true;
            setHomeTabInitialized(true);
            setSelectedTab(0);
            await loadRoomsForBoard(loadedBoards[0].id);
            setSelectedRoom(null);
            setIsHomeActive(true);
          }
          // Si loadedBoards está vacío (p. ej. 401 temporal), no tocar BD ni forzar diálogo.
        } else {
          // Si no es dashboard pero hay boards, cargar el primer tablero por defecto
          if (loadedBoards.length > 0) {
            correctTabEstablishedRef.current = true; // Marcar como establecido
            setHomeTabInitialized(true);
            setSelectedTab(0);
            await loadRoomsForBoard(loadedBoards[0].id);
          }
        }
        // Para reports, realtime y favorites, el tab se aplicará cuando se monte el componente
        // pasando userHomeTab como initialTab/initialView prop
      } else {
        // Si no hay homePage definida, cargar el primer tablero por defecto
        if (loadedBoards.length > 0) {
          correctTabEstablishedRef.current = true; // Marcar como establecido (no hay homeTab, así que el primer tablero es correcto)
          setHomeTabInitialized(true);
          setSelectedTab(0);
          await loadRoomsForBoard(loadedBoards[0].id);
        }
      }
      
      // ⚡ OPTIMIZACIÓN: Eliminado loop while que esperaba (ahora los mapas ya están listos)
      // SOLUCIÓN CRÍTICA: Marcar que la carga inicial está completa
      // PERO mantener isInitialLoadInProgressRef en true hasta que las habitaciones se hayan renderizado
      // Esto previene que el useEffect recargue las habitaciones inmediatamente después
      setInitialLoadComplete(true);
      // NO resetear isInitialLoadInProgressRef aquí - se reseteará después de un pequeño delay
      // para asegurar que las habitaciones se hayan renderizado completamente
      setTimeout(() => {
        isInitialLoadInProgressRef.current = false;
      }, 100); // Pequeño delay para asegurar que el renderizado se complete
    
      // ⚡ OPTIMIZACIÓN: Inicializar consumo en tiempo real (skip refresh porque ya se hizo)
      inicializarConsumoTiempoReal(true);
      
      // NOTA: El listener de device_control_response ya está configurado
      // desde la pre-conexión del WebSocket
      
      // 🚀 PRECARGA EN BACKGROUND: Después de mostrar la página de inicio, precargar todas las demás páginas
      // Usar setTimeout para asegurar que la UI de la página de inicio se renderice primero
      if (!isSoftphoneQuickAnswer()) {
        setTimeout(() => {
          preloadAllPagesInBackground();
        }, 100);
      }
      
      setIsLoggingIn(false); // ✅ Login completado
      setLoginUiComplete(true);
      
      // cuando el token cambie (useEffect línea 1811), no es necesario llamarla aquí
    } catch (error) {
      console.error('Error durante el login:', error);
      setIsLoggingIn(false); // ✅ Login completado (con error)
    }
  };

  // Restaurar sesión tras recarga intencional (SW update o ChunkLoadError) sin pedir huella otra vez.
  const sessionRecoveredRef = useRef(false);
  useEffect(() => {
    if (sessionRecoveredRef.current) {
      return;
    }
    if (!shouldRecoverSessionAfterReload()) {
      return;
    }

    const storedToken = localStorage.getItem('token');
    const storedUser = getUser();
    const storedEmail = localStorage.getItem('lastEmail') || storedUser?.email || '';

    if (!storedToken || !storedUser) {
      clearRecoverSessionAfterReload();
      return;
    }

    sessionRecoveredRef.current = true;
    clearRecoverSessionAfterReload();
    void handleLogin(storedToken, storedEmail, storedUser);
    // handleLogin definido arriba; ejecutar una sola vez al montar tras reload
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Documento nuevo (Android start_url): el SW dejó el intent en Cache.
  // JWT vivo + llamada → restaurar sin huella e ir a Teléfono.
  useEffect(() => {
    if (incomingBootState !== 'checking') return;
    let cancelled = false;
    void (async () => {
      try {
        const cacheIncoming = await readIncomingResumeFromCache();
        const doorbellBoot = isSoftphoneDoorbellDeepLink() || isSoftphoneDoorbellSession();
        const urlIncoming = isSoftphoneAnswerDeepLink() || isPhoneIncomingDeepLink();
        if (!urlIncoming && !doorbellBoot) {
          consumeIncomingSoftphoneIntent();
          setIncomingBootState('done');
          return;
        }
        const incoming = cacheIncoming || hasIncomingSoftphoneIntent() || doorbellBoot || urlIncoming;
        if (cancelled) return;
        if (!incoming) {
          setIncomingBootState('done');
          return;
        }
        const storedToken = localStorage.getItem('token');
        const storedUser = getUser();
        const storedEmail = localStorage.getItem('lastEmail') || storedUser?.email || '';
        if (!storedToken || !storedUser || !isStoredJwtAlive(storedToken)) {
          setIncomingBootState('done');
          return;
        }
        if (doorbellBoot) {
          markSoftphoneDoorbellSession();
          const cam = getDoorbellCameraFromUrl();
          if (cam != null) setPendingDoorbellCamera(cam);
          setDoorbellIsland(true);
        } else if (urlIncoming) {
          clearSoftphoneDoorbellSession();
          clearPendingDoorbellCamera();
          setDoorbellIsland(false);
          markSoftphoneIncomingResume();
        }
        pendingDeepLinkScreenRef.current = pendingDeepLinkScreenRef.current || 'softphone';
        setCurrentScreen('softphone');
        setIsHomeActive(false);
        const quick = !hasLoggedInDocumentSession() && isSoftphoneAnswerDeepLink();
        if (quick) markSoftphoneQuickAnswer();
        setLoginUiComplete(true);
        await handleLogin(storedToken, storedEmail, storedUser);
      } finally {
        if (!cancelled) setIncomingBootState('done');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingBootState]);

  // SoftPhone: deep-link atender con JWT vivo → saltar huella.
  // Importante: al soft-lock hay que sacar ?answer=1 de la URL; si no, al poner token=null
  // el efecto vuelve a auto-loguear y parece que “nunca pide huella”.
  const softphoneQuickRef = useRef(QUICK_ANSWER_BOOT.island);
  const softphoneQuickConsumedRef = useRef(false);
  const softphoneQuickSessionRef = useRef(QUICK_ANSWER_BOOT.island);
  const softphoneExitInFlightRef = useRef(false);
  const softphoneHadLiveCallRef = useRef(false);
  const softphoneWasHiddenRef = useRef(false);
  const [softphoneQuickSession, setSoftphoneQuickSession] = useState(QUICK_ANSWER_BOOT.island);
  // Solo en memoria tras colgar; no persistir en gate (evita atrapar al reabrir la PWA).
  const [softphoneCallEnded, setSoftphoneCallEnded] = useState(false);
  const softphoneRemoteEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentScreenForQuickRef = useRef(currentScreen);
  currentScreenForQuickRef.current = currentScreen;

  const stripSoftphoneAnswerFromUrl = useCallback(() => {
    try {
      const u = new URL(window.location.href);
      let changed = false;
      if (u.searchParams.get('answer') === '1') {
        u.searchParams.delete('answer');
        changed = true;
      }
      if (u.searchParams.get('softphone') === 'answer') {
        u.searchParams.delete('softphone');
        changed = true;
      }
      if (u.searchParams.has('call_id')) {
        u.searchParams.delete('call_id');
        changed = true;
      }
      if (u.searchParams.get('kind') === 'doorbell') {
        u.searchParams.delete('kind');
        changed = true;
      }
      if (u.searchParams.get('screen') === 'softphone' || u.searchParams.get('screen') === 'video') {
        u.searchParams.delete('screen');
        changed = true;
      }
      if (u.searchParams.has('camera')) {
        u.searchParams.delete('camera');
        changed = true;
      }
      if (changed) {
        const qs = u.searchParams.toString();
        window.history.replaceState({}, '', `${u.pathname}${qs ? `?${qs}` : ''}${u.hash || ''}`);
      }
    } catch { /* ignore */ }
    clearSoftphoneQuickAnswer();
    clearPendingDoorbellCamera();
    consumeIncomingSoftphoneIntent();
    pendingDeepLinkScreenRef.current = null;
  }, []);

  // URL residual ?screen=softphone (sin llamada viva) no debe pisar la home.
  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      const live = u.searchParams.get('answer') === '1' || u.searchParams.get('softphone') === 'answer';
      if (live) return;
      const leftoverSoftphone = u.searchParams.get('screen') === 'softphone'
        || u.searchParams.get('screen') === 'video';
      if (leftoverSoftphone) {
        stripSoftphoneAnswerFromUrl();
        const user = getUser();
        if (user?.home_page) {
          const home = resolveAllowedScreen(user.home_page, user);
          setCurrentScreen(home);
          setIsHomeActive(home === 'dashboard');
        } else {
          setCurrentScreen('dashboard');
          setIsHomeActive(true);
        }
      }
    } catch {
      /* ignore */
    }
  }, [stripSoftphoneAnswerFromUrl]);

  const softLockSoftphoneQuickSession = useCallback(() => {
    if (!softphoneQuickSessionRef.current && !isSoftphoneQuickAnswer()) return false;
    softphoneQuickSessionRef.current = false;
    setSoftphoneQuickSession(false);
    softphoneQuickConsumedRef.current = true;
    softphoneQuickRef.current = true; // bloquear re-entrada quick en este ciclo de página
    stripSoftphoneAnswerFromUrl();
    try {
      void preload_SoftphoneClient().then(({ stopSoftphoneClient }) => {
        stopSoftphoneClient();
      });
      void preload_SipSoftphone().then(({ sipSoftphone }) => {
        void sipSoftphone.stop();
      });
    } catch { /* ignore */ }
    WebSocketService.disconnect();
    setWebsocketStatus('disconnected');
    stopSessionInactivity();
    setToken(null);
    setUserData(null);
    setUsername('');
    setUserRole('');
    setEmail('');
    setCurrentScreen('dashboard');
    setBoards([]);
    setRooms([]);
    setDevices([]);
    setInitialLoadComplete(false);
    setLoginUiComplete(false);
    return true;
  }, [stripSoftphoneAnswerFromUrl]);

  /**
   * Fin de llamada quick-answer: pantalla negra breve.
   * Gate solo bloquea restaurar Video/timbre; no obliga a matar la PWA.
   */
  const endSoftphoneQuickCallSession = useCallback(() => {
    const inQuick =
      softphoneQuickSessionRef.current
      || isSoftphoneQuickAnswer()
      || isSoftphoneCallEndedGate();
    if (!inQuick && !softphoneCallEnded) return false;
    if (softphoneExitInFlightRef.current && softphoneCallEnded) return true;
    softphoneExitInFlightRef.current = true;

    clearPendingDoorbellCamera();
    markSoftphoneCallEndedGate();
    replaceHistoryForCallEnded();
    setSoftphoneCallEnded(true);
    softphoneQuickSessionRef.current = false;
    setSoftphoneQuickSession(false);
    softphoneHadLiveCallRef.current = false;
    softphoneQuickConsumedRef.current = true;
    softphoneQuickRef.current = true;
    softphoneWasHiddenRef.current = false;
    stripSoftphoneAnswerFromUrl();

    try {
      void preload_SoftphoneClient().then(({ stopSoftphoneClient }) => {
        stopSoftphoneClient();
      });
      void preload_SipSoftphone().then(({ sipSoftphone }) => {
        void sipSoftphone.stop();
      });
    } catch { /* ignore */ }

    tryClosePwaWindow();

    WebSocketService.disconnect();
    setWebsocketStatus('disconnected');
    stopSessionInactivity();
    setToken(null);
    setUserData(null);
    setUsername('');
    setUserRole('');
    setEmail('');
    setCurrentScreen('dashboard');
    setBoards([]);
    setRooms([]);
    setDevices([]);
    setInitialLoadComplete(false);
    setLoginUiComplete(false);

    return true;
  }, [softphoneCallEnded, stripSoftphoneAnswerFromUrl]);

  /** Salir de «Llamada finalizada» → login (usar el resto de la app sin matar la PWA). */
  const dismissSoftphoneCallEndedToLogin = useCallback(() => {
    tryClosePwaWindow();
    clearSoftphoneCallEndedGate();
    clearPendingDoorbellCamera();
    clearSoftphoneQuickAnswer();
    setSoftphoneCallEnded(false);
    softphoneExitInFlightRef.current = false;
    softphoneQuickSessionRef.current = false;
    setSoftphoneQuickSession(false);
    softphoneWasHiddenRef.current = false;
    setToken(null);
    setUserData(null);
    setLoginUiComplete(false);
  }, []);

  /** Si estamos en sesión quick en Teléfono/Video y vamos a otra pantalla → soft-lock. */
  const leaveSoftphoneQuickIfNeeded = useCallback((nextScreen: string) => {
    if (!softphoneQuickSessionRef.current && !isSoftphoneQuickAnswer()) return false;
    const cur = currentScreenForQuickRef.current;
    if (cur !== 'softphone') return false;
    if (nextScreen === 'softphone') return false;
    return softLockSoftphoneQuickSession();
  }, [softLockSoftphoneQuickSession]);
  leaveSoftphoneQuickIfNeededRef.current = leaveSoftphoneQuickIfNeeded;

  // Escape interno (X fullscreen, back, etc.) sin cambiar de screen → soft-lock.
  useEffect(() => {
    return eventBus.on('softphone:quick-escape', () => {
      if (softphoneExitInFlightRef.current || softphoneCallEnded) return;
      softLockSoftphoneQuickSession();
    });
  }, [softLockSoftphoneQuickSession, softphoneCallEnded]);

  // Timbre / teléfono: fin de llamada → close PWA (+ pantalla negra i18n si no cierra).
  useEffect(() => {
    const onEnd = () => {
      clearPendingDoorbellCamera();
      clearSoftphoneDoorbellSession();
      consumeIncomingSoftphoneIntent();
      stripSoftphoneAnswerFromUrl();
      setDoorbellIsland(false);
      if (endSoftphoneQuickCallSession()) {
        screenBeforeDoorbellRef.current = null;
        return;
      }
      const back = screenBeforeDoorbellRef.current;
      screenBeforeDoorbellRef.current = null;
      if (back && back !== 'softphone') {
        setCurrentScreen(back as typeof currentScreen);
        setIsHomeActive(back === 'dashboard');
        setIsInfoActive(false);
        setIsSettingsActive(false);
      }
      void ensureHouseReadyRef.current();
    };
    const unsub1 = eventBus.on('softphone:doorbell-hangup', onEnd);
    const unsub2 = eventBus.on('softphone:call-ended', onEnd);
    const unsub3 = eventBus.on('softphone:house-ensure', () => {
      void ensureHouseReadyRef.current();
    });
    return () => {
      unsub1();
      unsub2();
      unsub3();
    };
  }, [endSoftphoneQuickCallSession, stripSoftphoneAnswerFromUrl]);

  // Colgó el otro extremo (sesión quick). No disparar durante accept() (falso “finalizada”).
  useEffect(() => {
    if (!softphoneQuickSession) return;
    let unsub: (() => void) | undefined;
    let cancelled = false;
    void preload_SipSoftphone().then(({ sipSoftphone }) => {
      if (cancelled) return;
      unsub = sipSoftphone.subscribe(() => {
        if (sipSoftphone.acceptInFlight) {
          if (softphoneRemoteEndTimerRef.current) {
            clearTimeout(softphoneRemoteEndTimerRef.current);
            softphoneRemoteEndTimerRef.current = null;
          }
          const c = sipSoftphone.activeCall;
          if (c && (c.phase === 'ringing' || c.phase === 'active' || c.phase === 'calling')) {
            softphoneHadLiveCallRef.current = true;
          }
          return;
        }
        const c = sipSoftphone.activeCall;
        if (
          c
          && (c.phase === 'ringing' || c.phase === 'active' || c.phase === 'calling')
        ) {
          if (softphoneRemoteEndTimerRef.current) {
            clearTimeout(softphoneRemoteEndTimerRef.current);
            softphoneRemoteEndTimerRef.current = null;
          }
          softphoneHadLiveCallRef.current = true;
          return;
        }
        if (softphoneHadLiveCallRef.current && (!c || c.phase === 'ended')) {
          // accept()/handoff puede emitir null un instante: esperar antes de cerrar sesión.
          if (softphoneRemoteEndTimerRef.current) {
            clearTimeout(softphoneRemoteEndTimerRef.current);
          }
          softphoneRemoteEndTimerRef.current = setTimeout(() => {
            softphoneRemoteEndTimerRef.current = null;
            if (sipSoftphone.acceptInFlight) return;
            const now = sipSoftphone.activeCall;
            if (now && (now.phase === 'ringing' || now.phase === 'active' || now.phase === 'calling')) {
              softphoneHadLiveCallRef.current = true;
              return;
            }
            softphoneHadLiveCallRef.current = false;
            endSoftphoneQuickCallSession();
          }, 1200);
        }
      });
    });
    return () => {
      cancelled = true;
      unsub?.();
      if (softphoneRemoteEndTimerRef.current) {
        clearTimeout(softphoneRemoteEndTimerRef.current);
        softphoneRemoteEndTimerRef.current = null;
      }
    };
  }, [softphoneQuickSession, endSoftphoneQuickCallSession]);

  // Si vuelve de background con la negra abierta → login (no atrapar hasta matar la app).
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        if (softphoneCallEnded) softphoneWasHiddenRef.current = true;
        return;
      }
      if (
        document.visibilityState === 'visible'
        && softphoneWasHiddenRef.current
        && softphoneCallEnded
      ) {
        dismissSoftphoneCallEndedToLogin();
      }
    };
    const onPageShow = (ev: PageTransitionEvent) => {
      if (ev.persisted && (softphoneCallEnded || isSoftphoneCallEndedGate())) {
        dismissSoftphoneCallEndedToLogin();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [softphoneCallEnded, dismissSoftphoneCallEndedToLogin]);

  useEffect(() => {
    if (softphoneQuickRef.current || softphoneQuickConsumedRef.current || token) return;
    if (softphoneCallEnded || isSoftphoneCallEndedGate()) return;
    if (!isSoftphoneAnswerDeepLink()) return;
    const storedToken = localStorage.getItem('token');
    const storedUser = getUser();
    const storedEmail = localStorage.getItem('lastEmail') || storedUser?.email || '';
    if (!storedToken || !storedUser || !isStoredJwtAlive(storedToken)) return;
    softphoneQuickRef.current = true;
    softphoneQuickSessionRef.current = true;
    setSoftphoneQuickSession(true);
    softphoneExitInFlightRef.current = false;
    softphoneHadLiveCallRef.current = false;
    markSoftphoneQuickAnswer();
    setLoginUiComplete(true);
    void handleLogin(storedToken, storedEmail, storedUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // NAVIGATE / launchQueue con documento remontado: JWT vivo, sin huella.
  // Si la sesión ya estaba logueada en este documento, no marcar quick-answer.
  useEffect(() => {
    const pending = pendingSoftphoneSessionResumeRef.current;
    if (!pending?.needsSession) return;
    if (token) {
      pendingSoftphoneSessionResumeRef.current = null;
      return;
    }
    const storedToken = localStorage.getItem('token');
    const storedUser = getUser();
    const storedEmail = localStorage.getItem('lastEmail') || storedUser?.email || '';
    if (!storedToken || !storedUser || !isStoredJwtAlive(storedToken)) {
      pendingSoftphoneSessionResumeRef.current = null;
      return;
    }
    pendingSoftphoneSessionResumeRef.current = null;
    if (pending.stayLoggedIn || hasLoggedInDocumentSession()) {
      setLoginUiComplete(true);
    } else {
      softphoneQuickRef.current = true;
      softphoneQuickSessionRef.current = true;
      setSoftphoneQuickSession(true);
      markSoftphoneQuickAnswer();
      setLoginUiComplete(true);
    }
    softphoneExitInFlightRef.current = false;
    softphoneHadLiveCallRef.current = false;
    void handleLogin(storedToken, storedEmail, storedUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [softphoneSessionResumeTick, token]);

  // Deep-link softphone/timbre con sesión ya abierta → modo isla (sin nav, close al colgar).
  useEffect(() => {
    if (!token) return;
    if (isSoftphoneCallEndedGate()) return;
    if (!isSoftphoneQuickAnswer()) return;
    if (currentScreen !== 'softphone') return;
    softphoneQuickSessionRef.current = true;
    setSoftphoneQuickSession(true);
  }, [token, currentScreen]);
  

  // Efectos - Solo para casos donde no se hace login (ej: refresh de página)
  useEffect(() => {
    const loadBoardsOnRefresh = async () => {
      console.log('[FAVORITOS] 🔍 useEffect evaluado - isLoggingIn:', isLoggingIn, 'token:', !!token, 'boards:', boards.length, 'initialLoad:', initialLoadComplete, 'userData:', !!userData?.id);
      
      // SOLO ejecutar en refresh de página (cuando no hay login activo y no hay boards cargados)
      // SOLUCIÓN: Ejecutar también cuando hay una homePage diferente a dashboard para cargar los boards
      // pero NO cambiar el currentScreen si ya está establecido con la homePage del usuario
      const shouldLoadBoards = token && boards.length === 0 && !initialLoadComplete && userData?.id && !isLoggingIn
        && !isDoorbellIncomingContext() && !doorbellIsland;
      if (shouldLoadBoards) {
        // SOLUCIÓN CRÍTICA: Marcar que estamos en carga inicial ANTES de cualquier operación
        // Esto previene que CUALQUIER useEffect cargue habitaciones durante la carga inicial
        isInitialLoadInProgressRef.current = true;
        console.log('[FAVORITOS] ✅ useEffect refresh EJECUTADO - VERSIÓN NUEVA CON PARÁMETRO');
        const refreshToken = localStorage.getItem('token') || token;
        if (refreshToken) {
          ApiService.setToken(refreshToken);
        }
        try {
          await refrescarEstructuraCompleta();
        } catch {
          /* los tableros igual se intentan por API */
        }
        cargarMapasNoShelly();
        
        // Cargar iconos descargados del servidor al refrescar la página
        loadDownloadedIconifyIcons().catch(err => {
          // Silenciar errores, los iconos se cargarán cuando se necesiten
          console.error('Error cargando iconos descargados:', err);
        });
        
        // En caso de refresh, cargar favoritos primero (dispositivos y grupos en paralelo)
        try {
          const sonosFavPromise = canViewMusic(userData)
            ? Promise.all([
                getFavorites().catch(() => [] as number[]),
                getSonosFavoritesOrden().catch(() => new Map<number, number>()),
              ])
            : Promise.resolve([[] as number[], new Map<number, number>()] as const);
          const [favoritesData, favoriteGroupsData, sonosFavResult] = await Promise.all([
            getUserFavorites(userData.id, true),
            getUserFavoriteGroups(userData.id, true),
            sonosFavPromise,
          ]);
          const [sonosFavIds, sonosOrdenMap] = sonosFavResult;

          const tieneFavs = favoritesData.length > 0 || favoriteGroupsData.length > 0 || sonosFavIds.length > 0;
          setSonosFavorites(sonosFavIds);
          setSonosFavoriteOrden(Object.fromEntries(sonosOrdenMap));
          const favSonos = sonosFavIds
            .map((id) => mapaSonos[id])
            .filter((s): s is SonosSpeaker => !!s);
          setFavoriteSonosSpeakers(favSonos);
          setUserFavorites(favoritesData);
          setHasFavorites(tieneFavs);
          
          const favDevices = favoritesData
            .map(fav => {
              const dispositivo = fav.dispositivo;
              if (!dispositivo) return null;
              // Asegurar que pin_required, auth_required e is_favorite estén presentes y correctamente mapeados
              return {
                ...dispositivo,
                pin_required: dispositivo.pin_required ?? false,
                auth_required: dispositivo.auth_required ?? false,
                auth_configured: dispositivo.auth_configured ?? false,
                is_favorite: true,
                favorite_orden: fav.orden ?? 0,
              };
            })
            .filter(Boolean)
            .sort((a, b) => {
              const aFav = favoritesData.find(f => f.device_id === a.id);
              const bFav = favoritesData.find(f => f.device_id === b.id);
              return (aFav?.orden || 0) - (bFav?.orden || 0);
            });
          setFavoriteDevices(favDevices);
          
          // Inicializar orden de habitaciones basado en el orden de dispositivos
          const roomsInOrder: (number | string)[] = [];
          const seenRooms = new Set();
          favDevices.forEach(device => {
            const roomId = String(device.habitacion_id || 'sin-habitacion');
            if (!seenRooms.has(roomId)) {
              roomsInOrder.push(roomId);
              seenRooms.add(roomId);
            }
          });
          favSonos.forEach((speaker) => {
            const roomId = String(speaker.habitacion_id || 'sin-habitacion');
            if (!seenRooms.has(roomId)) {
              roomsInOrder.push(roomId);
              seenRooms.add(roomId);
            }
          });
          setFavoriteRoomsOrder(roomsInOrder);
          
          // SOLUCIÓN: Verificar si hay homeTab definido para evitar cargar el primer tablero
          const savedUserStr = localStorage.getItem('user');
          let savedHomePage: string | null = null;
          let savedHomeTab: number | null = null;
          try {
            if (savedUserStr) {
              const savedUser = JSON.parse(savedUserStr);
              savedHomePage = savedUser?.home_page || null;
              savedHomeTab = savedUser?.home_tab || null;
            }
          } catch (error) {
            // Ignorar errores al leer localStorage
          }
          
          // Pasar skipInitialRoomLoad=true si hay homeTab definido para dashboard
          const skipInitialLoad = !!(savedHomePage && savedHomeTab !== null && savedHomeTab !== undefined && savedHomePage === 'dashboard');
          
          // Ahora cargar boards con el parámetro correcto
          const loadedBoards = await loadBoards(tieneFavs, skipInitialLoad);
          
          // SOLUCIÓN: Aplicar el tab de inicio si está definido (similar a handleLogin)
          if (savedHomePage && savedHomeTab !== null && savedHomeTab !== undefined) {
            if (savedHomePage === 'dashboard') {
              const boardIndex = loadedBoards.findIndex((board: any) => board.id === savedHomeTab);
              if (boardIndex !== -1) {
                // SOLUCIÓN: Marcar que estamos estableciendo el tab correcto
                isSettingCorrectTabRef.current = true;
                // Limpiar habitaciones ANTES de establecer el tab para evitar mostrar las del tab anterior
                correctTabEstablishedRef.current = true; // Marcar que el tab correcto fue establecido
                setRooms([]);
                setHomeTabInitialized(true);
                setSelectedTab(boardIndex);
                homeTabAppliedRef.current = true;
                // Cargar inmediatamente las habitaciones del tablero correcto
                await loadRoomsForBoard(loadedBoards[boardIndex].id);
                // SOLUCIÓN: Marcar que las habitaciones fueron cargadas manualmente
                roomsLoadedManuallyRef.current = true;
                // Después de cargar, permitir que el useEffect funcione normalmente
                isSettingCorrectTabRef.current = false;
              } else if (loadedBoards.length > 0) {
                correctTabEstablishedRef.current = true; // Marcar como establecido
                setRooms([]);
                setHomeTabInitialized(true);
                setSelectedTab(0);
                await loadRoomsForBoard(loadedBoards[0].id);
              }
            }
          } else if (loadedBoards.length > 0) {
            // Si no hay homeTab, cargar el primer tablero
            correctTabEstablishedRef.current = true; // Marcar como establecido (no hay homeTab, así que el primer tablero es correcto)
            setRooms([]);
            setHomeTabInitialized(true);
            setSelectedTab(0);
            await loadRoomsForBoard(loadedBoards[0].id);
          }
          
          // SOLUCIÓN: Respetar la homePage del usuario en lugar de sobrescribirla
          // Solo establecer currentScreen si no hay una homePage definida o si la homePage es 'dashboard'/'favorites'
          // y queremos aplicar la lógica de favoritos
          if (!savedHomePage || savedHomePage === 'dashboard' || savedHomePage === 'favorites') {
            // Si tiene favoritos y no hay homePage específica, establecer página de favoritos como default
            if (tieneFavs && !savedHomePage) {
              setCurrentScreen('favorites');
              setIsHomeActive(false);
            } else if (!savedHomePage) {
              setCurrentScreen('dashboard');
              setIsHomeActive(true);
            }
            // Si hay homePage definida, mantenerla (ya se estableció en el estado inicial o en handleLogin)
          }
          
          // SOLUCIÓN CRÍTICA: Marcar que la carga inicial está completa
          // PERO mantener isInitialLoadInProgressRef en true hasta que las habitaciones se hayan renderizado
          // Esto previene que el useEffect recargue las habitaciones inmediatamente después
          setInitialLoadComplete(true);
          // NO resetear isInitialLoadInProgressRef aquí - se reseteará después de un pequeño delay
          // para asegurar que las habitaciones se hayan renderizado completamente
          setTimeout(() => {
            isInitialLoadInProgressRef.current = false;
          }, 100); // Pequeño delay para asegurar que el renderizado se complete
          
          // 🚀 PRECARGA EN BACKGROUND: Después de mostrar la página, precargar todas las demás
          setTimeout(() => {
            preloadAllPagesInBackground();
          }, 100);
        } catch (error) {
          // SOLUCIÓN: Verificar si hay homeTab definido incluso en caso de error
          const savedUserStr = localStorage.getItem('user');
          let savedHomePage: string | null = null;
          let savedHomeTab: number | null = null;
          try {
            if (savedUserStr) {
              const savedUser = JSON.parse(savedUserStr);
              savedHomePage = savedUser?.home_page || null;
              savedHomeTab = savedUser?.home_tab || null;
            }
          } catch (e) {
            // Ignorar errores al leer localStorage
          }
          
          // Pasar skipInitialRoomLoad=true si hay homeTab definido para dashboard
          const skipInitialLoad = !!(savedHomePage && savedHomeTab !== null && savedHomeTab !== undefined && savedHomePage === 'dashboard');
          
          // Si falla, cargar sin favoritos
          const loadedBoards = await loadBoards(false, skipInitialLoad);
          
          // SOLUCIÓN: Aplicar el tab de inicio si está definido (similar a handleLogin)
          if (savedHomePage && savedHomeTab !== null && savedHomeTab !== undefined) {
            if (savedHomePage === 'dashboard') {
              const boardIndex = loadedBoards.findIndex((board: any) => board.id === savedHomeTab);
              if (boardIndex !== -1) {
                // SOLUCIÓN: Marcar que estamos estableciendo el tab correcto
                isSettingCorrectTabRef.current = true;
                // Limpiar habitaciones ANTES de establecer el tab para evitar mostrar las del tab anterior
                correctTabEstablishedRef.current = true; // Marcar que el tab correcto fue establecido
                setRooms([]);
                setHomeTabInitialized(true);
                setSelectedTab(boardIndex);
                homeTabAppliedRef.current = true;
                // Cargar inmediatamente las habitaciones del tablero correcto
                await loadRoomsForBoard(loadedBoards[boardIndex].id);
                // SOLUCIÓN: Marcar que las habitaciones fueron cargadas manualmente
                roomsLoadedManuallyRef.current = true;
                // Después de cargar, permitir que el useEffect funcione normalmente
                isSettingCorrectTabRef.current = false;
              } else if (loadedBoards.length > 0) {
                correctTabEstablishedRef.current = true; // Marcar como establecido
                setRooms([]);
                setHomeTabInitialized(true);
                setSelectedTab(0);
                await loadRoomsForBoard(loadedBoards[0].id);
              }
            }
          } else if (loadedBoards.length > 0) {
            // Si no hay homeTab, cargar el primer tablero
            correctTabEstablishedRef.current = true; // Marcar como establecido (no hay homeTab, así que el primer tablero es correcto)
            setRooms([]);
            setHomeTabInitialized(true);
            setSelectedTab(0);
            await loadRoomsForBoard(loadedBoards[0].id);
          }
          
          // Solo establecer dashboard si no hay homePage definida
          if (!savedHomePage) {
            setCurrentScreen('dashboard');
          }
          // SOLUCIÓN CRÍTICA: Marcar que la carga inicial está completa
          // PERO mantener isInitialLoadInProgressRef en true hasta que las habitaciones se hayan renderizado
          // Esto previene que el useEffect recargue las habitaciones inmediatamente después
          setInitialLoadComplete(true);
          // NO resetear isInitialLoadInProgressRef aquí - se reseteará después de un pequeño delay
          // para asegurar que las habitaciones se hayan renderizado completamente
          setTimeout(() => {
            isInitialLoadInProgressRef.current = false;
          }, 100); // Pequeño delay para asegurar que el renderizado se complete
          
          // 🚀 PRECARGA EN BACKGROUND: También precargar en caso de error
          setTimeout(() => {
            preloadAllPagesInBackground();
          }, 100);
        }
      }
    };
    
    loadBoardsOnRefresh();
    // deps mínimas: ampliar dispara refresh duplicado (loadBoards, preload, rooms)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userData, isLoggingIn, doorbellIsland]);

  // 🔧 FIX: useEffect para resetear el ref cuando cambia el userHomeTab
  // Esto permite re-aplicar el tab si el usuario cambia su configuración de home
  useEffect(() => {
    homeTabAppliedRef.current = false;
  }, [userHomeTab]);

  // 🔧 FIX: useEffect de respaldo para aplicar el tab cuando boards cambie
  // SOLUCIÓN CRÍTICA: NO ejecutar durante la carga inicial - esto se maneja en handleLogin y refresh
  // Solo ejecutar DESPUÉS de que la carga inicial esté completa y si realmente es necesario
  useEffect(() => {
    // NO ejecutar durante la carga inicial
    if (isInitialLoadInProgressRef.current || !initialLoadComplete) {
      return;
    }
    
    // Solo aplicar el tab inicial si:
    // 1. Estamos en el dashboard
    // 2. El home page está configurado como dashboard
    // 3. Hay un userHomeTab definido
    // 4. Hay boards cargados
    // 5. Aún no se ha aplicado el tab inicial (usando ref para evitar re-aplicaciones)
    // 6. selectedTab es -1 (esperando) o 0 (primer tablero) - solo aplicar si realmente necesita corrección
    if (currentScreen === 'dashboard' && 
        userHomePage === 'dashboard' && 
        userHomeTab !== null && 
        userHomeTab !== undefined && 
        boards.length > 0 &&
        !homeTabAppliedRef.current &&
        (selectedTab === -1 || selectedTab === 0)) { // Solo aplicar si está esperando o en el primer tablero
      const boardIndex = boards.findIndex((board: any) => board.id === userHomeTab);
      if (boardIndex !== -1 && boardIndex !== selectedTab) {
        // SOLUCIÓN: Marcar que estamos estableciendo el tab correcto
        isSettingCorrectTabRef.current = true;
        correctTabEstablishedRef.current = true; // Marcar que el tab correcto fue establecido
        setRooms([]);
        setSelectedTab(boardIndex);
        setHomeTabInitialized(true);
        homeTabAppliedRef.current = true; // Marcar como aplicado
        roomsLoadedManuallyRef.current = true; // Marcar que se cargaron manualmente
        // Cargar las habitaciones del tablero correcto
        loadRoomsForBoard(boards[boardIndex].id).then(() => {
          // Después de cargar, permitir que el useEffect funcione normalmente
          isSettingCorrectTabRef.current = false;
        }).catch(() => {
          // En caso de error, también permitir que el useEffect funcione
          isSettingCorrectTabRef.current = false;
        });
      }
    }
    // loadRoomsForBoard definido más abajo; incluirlo dispara no-use-before-define y deps inestables
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boards, userHomeTab, userHomePage, currentScreen, selectedTab, initialLoadComplete]);

  // 🚀 useEffect para precargar páginas en background cuando la carga inicial esté completa
  // y los boards estén cargados
  useEffect(() => {
    if (initialLoadComplete && boards.length > 0 && !isLoggingIn) {
      // Pequeño delay para asegurar que la UI de la página de inicio se haya renderizado
      const preloadTimer = setTimeout(() => {
        preloadAllPagesInBackground();
      }, 200);
      
      return () => {
        clearTimeout(preloadTimer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoadComplete, boards.length, isLoggingIn]);

  // ⚡ OPTIMIZACIÓN: Precargar el logo para mejorar el rendimiento de renderizado
  useEffect(() => {
    const logoImg = new Image();
    logoImg.src = '/pwa/logo192.png';
  }, []);

  // Log cuando boards cambia (desactivado para reducir ruido)
  // useEffect(() => {
  // console.log('[FAVORITOS] 🔄 boards cambió - total:', boards.length, 'nombres:', boards.map(b => b.nombre).join(', '));
  // }, [boards]);

  // Función para recargar artefactos
  const fetchArtefactos = useCallback(async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/tipos-artefacto`, {
        headers: {
          'Authorization': `Bearer ${ApiService.getToken() || ''}`
        }
      });
      if (response.ok) {
        const artefactos = await response.json();
        const nombresArtefactos = artefactos.map((a: any) => a.nombre);
        setTodosArtefactos([t('deviceModal.undefined'), ...nombresArtefactos]);
        
        // Crear mapa de artefactos (id -> nombre)
        const mapa = artefactos.reduce((acc: Record<number, string>, a: any) => {
          acc[a.id] = a.nombre;
          return acc;
        }, {});
        setMapaArtefactos(mapa);
        
        // CRÍTICO: Actualizar el mapa global de tipos de artefacto con iconos y colores
        // Esto permite que los iconos se muestren correctamente en los dispositivos
        updateTiposArtefactoMap(artefactos);
        
        console.log('🔧 [PWA] Artefactos recargados:', { nombresArtefactos, mapa });
      }
    } catch (error) {
      console.error('Error al cargar artefactos:', error);
      setTodosArtefactos([t('deviceModal.undefined')]);
    }
  }, [t]);

  // Función para sincronizar boards desde los mapas globales (sin llamar a API)
  const syncBoardsFromMaps = useCallback(() => {
    // console.log('[FAVORITOS] 🔄 syncBoardsFromMaps ejecutado - hasFavorites:', hasFavorites);
    console.log('🔄 [SYNC] Sincronizando boards desde mapas globales...');
    console.log('🔄 [SYNC] userRole:', userRole);
    console.log('🔄 [SYNC] mapaTableros keys:', Object.keys(mapaTableros).length);
    
    const tablerosArray = Object.values(mapaTableros)
      .sort((a, b) => (a.orden || 0) - (b.orden || 0));

    // Mapas vacíos (remount / refresco a medio camino): no borrar tableros ya cargados.
    if (tablerosArray.length === 0) {
      return;
    }
    
    console.log('🔄 [SYNC] tablerosArray length:', tablerosArray.length);
    
    // CRITICO: Solo admin tiene acceso total sin permisos granulares
    // Los usuarios demo (tanto en modo user como admin) necesitan permisos granulares explícitos (igual que usuarios normales)
    // La única diferencia es que demo en modo admin puede ver Settings
    if (userRole === 'admin') {
      // Admin ven todos los tableros
      console.log('🔄 [SYNC] Admin: estableciendo', tablerosArray.length, 'tableros');
      console.log('🔄 [SYNC] Admin: tableros:', tablerosArray.map(t => `${t.nombre} (${t.habitaciones?.length || 0} habs)`).join(', '));
      
        setBoards(tablerosArray);
      
      // Cargar habitaciones del tablero ACTUALMENTE seleccionado (no siempre el primero).
      // CRÍTICO: durante la carga inicial NO tocar rooms; handleLogin es el dueño del estado
      // (incluida la habitación de home), así evitamos pisarlo al llegar estructura:actualizada.
      if (!isInitialLoadInProgressRef.current && tablerosArray.length > 0) {
        const idxActual = (selectedTabRef.current >= 0 && selectedTabRef.current < tablerosArray.length)
          ? selectedTabRef.current
          : 0;
        const tableroActual = tablerosArray[idxActual];
        const tableroData = mapaTableros[tableroActual.id];
        
        if (tableroData) {
          // Si el tablero tiene habitaciones
          if (tableroData.habitaciones && tableroData.habitaciones.length > 0) {
            const roomsData = tableroData.habitaciones
              .map(habId => {
                const habData = mapaHabitaciones[habId];
                if (!habData) return null;
                // Convertir al formato esperado por App.tsx
                return {
                  id: habData.datos.id,
                  nombre: habData.datos.nombre,
                  tablero_id: tableroActual.id,
                  consumo: habData.consumoTotal,
                  ultimoConsumoEmitido: habData.consumoTotal
                };
              })
              .filter(Boolean) as HabitacionConConsumo[];
            setRooms(roomsData);
          } else {
            // Tablero vacío: establecer array vacío de habitaciones
            setRooms([]);
          }
        }
      }
    } else {
      // Usuario normal: solo mostrar tableros que tienen habitaciones permitidas
      // Verificar explícitamente que el usuario tenga permisos para ver al menos una habitación del tablero
      const user = getUser();
      if (!user || !user.id) {
        return;
      }
      
      // Obtener permisos del usuario para verificar can_view_room
      getUserPermissions(user.id).then(userPermissions => {
        // Extraer room_ids donde tiene can_view_room
        let allowedRoomIds: number[] = [];
        if (userPermissions.permissions && Array.isArray(userPermissions.permissions)) {
          allowedRoomIds = userPermissions.permissions
            .filter((p: any) => p.can_view_room)
            .map((p: any) => p.room_id);
        } else {
          // Compatibilidad: si solo hay room_ids, asumir que tiene can_view_room
          allowedRoomIds = userPermissions.room_ids || [];
        }
        
        // Crear un mapa de habitaciones permitidas por ID para búsqueda rápida
        const habitacionesPermitidasMap = new Map();
        allowedRoomIds.forEach((roomId: number) => {
          habitacionesPermitidasMap.set(roomId, true);
        });
        
        // Filtrar tableros: solo mostrar aquellos que tienen al menos una habitación con permiso can_view_room
      const tablerosPermitidos = tablerosArray.filter(tablero => {
          if (!tablero.habitaciones || tablero.habitaciones.length === 0) {
            return false; // Tablero sin habitaciones, no mostrar
          }
          // Verificar si alguna habitación del tablero tiene permiso can_view_room
          return tablero.habitaciones.some(habId => habitacionesPermitidasMap.has(habId));
      });
        
      console.log('🔄 [SYNC] Usuario: estableciendo', tablerosPermitidos.length, 'tableros permitidos de', tablerosArray.length, 'totales');
        setBoards(tablerosPermitidos);
      
      // Cargar habitaciones del tablero ACTUALMENTE seleccionado (no siempre el primero).
      // CRÍTICO: durante la carga inicial NO tocar rooms; handleLogin es el dueño del estado.
      if (!isInitialLoadInProgressRef.current && tablerosPermitidos.length > 0) {
        const idxActual = (selectedTabRef.current >= 0 && selectedTabRef.current < tablerosPermitidos.length)
          ? selectedTabRef.current
          : 0;
        const tableroActual = tablerosPermitidos[idxActual];
        const tableroData = mapaTableros[tableroActual.id];
        if (tableroData && tableroData.habitaciones) {
          const roomsData = tableroData.habitaciones
            .filter(habId => habitacionesPermitidasMap.has(habId))
            .map(habId => {
              const habData = mapaHabitaciones[habId];
              if (!habData) return null;
              // Convertir al formato esperado por App.tsx
              return {
                id: habData.datos.id,
                nombre: habData.datos.nombre,
                tablero_id: tableroActual.id,
                consumo: habData.consumoTotal,
                ultimoConsumoEmitido: habData.consumoTotal
              };
            })
            .filter(Boolean) as HabitacionConConsumo[];
          setRooms(roomsData);
          } else {
            setRooms([]);
          }
        }
      }).catch(() => {
        // Error transitorio de permisos: no vaciar tableros/habitaciones ya visibles.
      });
    }
    
    console.log('🔄 [SYNC] Sincronización completada');
  }, [userRole]); // syncBoardsFromMaps no usa hasFavorites en lógica activa

  // Obtener artefactos disponibles desde la API - Solo una vez
  useEffect(() => {
    if (token && todosArtefactos.length === 0) {
      fetchArtefactos();
    }
  }, [token, todosArtefactos.length, fetchArtefactos]);

        // Escuchar eventos de actualización de tipos de artefacto
        useEffect(() => {
          const handleArtifactTypeUpdated = (data: any) => {
            console.log('🔧 [PWA] Evento device:artifact_type_updated recibido:', data);
            console.log('🔧 [PWA] Recargando artefactos...');
            fetchArtefactos();
            setRefreshCounter(prev => prev + 1);
          };

          const handleDeviceUpdate = (data: any) => {
            console.log('🔧 [PWA] Evento device:update recibido:', data);
            if (data && data.camera_ids !== undefined && data.id != null) {
              const parsedIds = Array.isArray(data.camera_ids)
                ? data.camera_ids.map((x: unknown) => Number(x)).filter((n: number) => Number.isFinite(n))
                : [];
              const rawDur = data.camera_display_duration;
              const prevCache = cameraConfigCache.current.get(Number(data.id));
              const displayDuration = rawDur === 0 || rawDur === '0'
                ? 0
                : (rawDur != null && rawDur !== ''
                    ? (Number(rawDur) || 5)
                    : (prevCache?.displayDuration === 0 ? 0 : (prevCache?.displayDuration || 5)));
              cameraConfigCache.current.set(
                Number(data.id),
                parsedIds.length > 0
                  ? { cameraIds: parsedIds, displayDuration }
                  : null
              );
            }
            if (data.tipo_artefacto) {
              console.log('🔧 [PWA] Dispositivo actualizado con tipo de artefacto, recargando artefactos...');
              fetchArtefactos();
              setRefreshCounter(prev => prev + 1);
            }
          };

          const handleEstructuraActualizada = () => {
            console.log('🔧 [PWA] Evento estructura:actualizada recibido, sincronizando desde mapas...');
            
            // Los mapas globales ya están actualizados por refrescarEstructuraCompleta()
            // Sincronizar el estado de React desde los mapas (sin llamadas API duplicadas)
            syncBoardsFromMaps();
            
            fetchArtefactos();
            setRefreshCounter(prev => prev + 1);
          };

          const unsub1 = eventBus.on('device:artifact_type_updated', handleArtifactTypeUpdated);
          const unsub2 = eventBus.on('device:update', handleDeviceUpdate);
          const unsub3 = eventBus.on('estructura:actualizada', handleEstructuraActualizada);
          
          return () => {
            unsub1();
            unsub2();
            unsub3();
          };
        }, [syncBoardsFromMaps, fetchArtefactos]); // Incluir dependencias para que el listener siempre tenga la versión actual

  // Función para limpiar todos los filtros (como React Native)
  const limpiarFiltros = () => {
    setFiltroModelos([]);
    setFiltroOnline(false);
    setFiltroOffline(false);
    setFiltroAuth(false);
    setFiltroAuthConfigured(false);
    setFiltroPin(false);
    setFiltroCamera(false);
    setFiltroEncendido(false);
    setFiltroApagado(false);
    setFiltroConConsumo(false);
    setFiltroSinConsumo(false);
    setFiltroArtefactos([]);
    setFiltroAiPermitido(false);
    setFiltroAiDenegado(false);
    setFiltroAiSinDefinir(false);
    setSearchTerm('');
  };

  /** Cierra Shelly / Sonos / Samsung / Android TV / LG para que nunca queden dos overlays a la vez. */
  const closeAllDeviceModals = useCallback(() => {
    setDeviceModalOpen(false);
    setSelectedDevice(null);
    setSonosModalOpen(false);
    setSelectedSonosSpeaker(null);
    setSamsungModalOpen(false);
    setSelectedSamsungDevice(null);
    setAndroidtvModalOpen(false);
    setSelectedAndroidTvDevice(null);
    setLgModalOpen(false);
    setSelectedLgDevice(null);
    setEvohomeModalOpen(false);
    setSelectedEvohomeDevice(null);
  }, []);

  // Funciones para manejar el modal de dispositivo
  // CRITICO: Memoizar handleDeviceClick con useCallback para mantener referencia estable
  // Esto asegura que React.memo en DeviceCard detecte cuando cambia y actualice el onClick correctamente
  const handleDeviceClick = useCallback((device: any) => {
    // Prevenir clicks inmediatamente después de cambiar de habitación (problema de doble toque en móvil)
    if (preventDeviceClicks) {
      return;
    }
    
    // Tracking para usuarios Demo
    const tracker = getDemoActivityTracker();
    const updatedDevice = (window as any).mapaDispositivos?.[device.id] || device;
    tracker.trackClick(
      'device_card',
      updatedDevice.id,
      updatedDevice.nombre || `Dispositivo ${updatedDevice.id}`,
      {
        screen: currentScreen,
        roomId: selectedRoom?.id || updatedDevice.habitacion_id,
        deviceId: updatedDevice.id
      }
    );
    
    // Buscar el dispositivo actualizado en mapaDispositivos para asegurar que tenga la MAC
    // Cerrar Sonos/Samsung (y reset Shelly) antes de abrir el modal Shelly
    closeAllDeviceModals();
    setSelectedDevice(updatedDevice);
    // Cerrar GroupsModal si está abierto al abrir DeviceModal
    setShowGroupsModal(false);
    setSelectedRoomForGroups(null);
    setDeviceModalOpen(true);
    
    // Tracking de modal abierto
    tracker.trackModal('open', 'device_modal', {
      screen: currentScreen,
      deviceId: updatedDevice.id,
      roomId: selectedRoom?.id || updatedDevice.habitacion_id
    });
    
    // Agregar MÚLTIPLES entradas al historial para el DeviceModal y sus dialogs
    for (let i = 0; i < 10; i++) {
      window.history.pushState({ page: `device-modal-${i}` }, '', '');
    }
  }, [preventDeviceClicks, currentScreen, selectedRoom, closeAllDeviceModals]);

  // Función para manejar el modal de lista de dispositivos (removida - ahora se maneja con las nuevas funciones)

  // Funciones para manejar la navegación inferior según las reglas especificadas
  const handleHomeClick = () => {
    if (leaveSoftphoneQuickIfNeeded('dashboard')) return;

    if (showDeviceListModal) {
      // Si el modal está abierto, cerrarlo y mantener estados actuales
      setShowDeviceListModal(false);
    }
    
    // Cerrar overlays Shelly/Sonos/Samsung (también si ya estamos en Home)
    closeAllDeviceModals();
    
    // Cerrar modales de grupos si están abiertos
    if ((window as any).closeGroupsModal) {
      (window as any).closeGroupsModal();
    }
    if ((window as any).closeGroupDetailModal) {
      (window as any).closeGroupDetailModal();
    }
    
    if (isSettingsActive && isInfoActive) {
      // Condición 4: Si se está en Settings+Info (ambos azules) y se presiona Home
      // Quitar azul de Settings e Info y abrir Dashboard
      setIsSettingsActive(false);
      setIsInfoActive(false);
      setIsHomeActive(true);
      setCurrentScreen('dashboard');
      setSelectedRoom(null);
    } else {
      // Navegación normal a Dashboard
      setIsHomeActive(true);
      setIsInfoActive(false);
      setIsSettingsActive(false);
      setCurrentScreen('dashboard');
      setSelectedRoom(null);
    }
    if (boards.length === 0 || !isMapaDispositivosCargado()) {
      void ensureHouseReadyRef.current();
    }
  };

  const handleInfoClick = () => {
    // Info es un overlay: no cambia currentScreen ni desactiva la página de debajo.
    if (showDeviceListModal) {
      setShowDeviceListModal(false);
      setIsInfoActive(false);
      return;
    }

    closeAllDeviceModals();
    if ((window as any).closeGroupsModal) (window as any).closeGroupsModal();
    if ((window as any).closeGroupDetailModal) (window as any).closeGroupDetailModal();

    setIsInfoActive(true);
    setShowDeviceListModal(true);
    for (let i = 0; i < 5; i++) {
      window.history.pushState({ page: `device-list-modal-${i}` }, '', '');
    }
  };

  const handleSettingsClick = () => {
    if (leaveSoftphoneQuickIfNeeded('settings')) return;
    // Solo permitir acceso a Settings si es admin o demo en modo admin
    if (!canViewSettings) {
      setPermissionSnackbar({
        open: true,
        message: t('permissions.demoSettingsRestriction')
      });
      return;
    }
    if (showDeviceListModal) {
      // Si el modal está abierto, cerrarlo
      setShowDeviceListModal(false);
    }
    
    // Cerrar overlays Shelly/Sonos/Samsung al ir a Settings
    closeAllDeviceModals();
    
    // Cerrar modales de grupos si están abiertos
    if ((window as any).closeGroupsModal) {
      (window as any).closeGroupsModal();
    }
    if ((window as any).closeGroupDetailModal) {
      (window as any).closeGroupDetailModal();
    }
    
      if (isHomeActive && isInfoActive) {
        // Condición 3: Si se está en Home+Info (ambos azules) y se presiona Settings
        // Quitar azul de Home e Info y abrir Settings
        setIsHomeActive(false);
        setIsInfoActive(false);
        setIsSettingsActive(true);
        setCurrentScreen('settings');
        // Agregar MÚLTIPLES entradas al historial para que Back funcione desde Settings
        for (let i = 0; i < 5; i++) {
          window.history.pushState({ page: `settings-${i}` }, '', '');
        }
      } else {
        // Navegación normal a Settings
        setIsHomeActive(false);
        setIsInfoActive(false);
        setIsSettingsActive(true);
        setCurrentScreen('settings');
        // Agregar MÚLTIPLES entradas al historial para que Back funcione desde Settings
        for (let i = 0; i < 5; i++) {
          window.history.pushState({ page: `settings-${i}` }, '', '');
        }
      }
  };

  const handleBottomNavNavigate = (id: BottomNavItemId) => {
    // help / info no navegan a una pantalla
    if (id === 'help' || id === 'info') return;
    if (id === 'home') {
      handleHomeClick();
      return;
    }

    const nextScreen = id === 'phone' ? 'softphone' : id === 'assistant' ? 'assistant' : (id as string);
    if (leaveSoftphoneQuickIfNeeded(nextScreen)) return;

    if ((window as any).closeGroupsModal) (window as any).closeGroupsModal();
    if ((window as any).closeGroupDetailModal) (window as any).closeGroupDetailModal();
    setShowDeviceListModal(false);
    closeAllDeviceModals();
    setShowExitMessage(false);
    setIsHomeActive(false);
    setIsInfoActive(false);
    setIsSettingsActive(false);

    if (id === 'assistant') {
      setAssistantHubTab(canUseAI(userData || getUser()) ? 'chat' : 'automations');
      setCurrentScreen('assistant');
      return;
    }

    if (id === 'phone') {
      const u = userData || getUser();
      if (!canUsePhone(u) && !canAnswerDoorbell(u)) return;
      setCurrentScreen('softphone');
      return;
    }

    setCurrentScreen(id as typeof currentScreen);
    if (
      (id === 'favorites' || id === 'reports' || id === 'realtime')
      && (boards.length === 0 || !isMapaDispositivosCargado())
    ) {
      void ensureHouseReadyRef.current();
    }
  };

  const handleDemoModeSelect = (mode: 'user' | 'admin') => {
    setDemoViewMode(mode);
    setDemoModeDialogOpen(false);
    if (mode === 'user') {
      if (currentScreen === 'settings') {
        setCurrentScreen('dashboard');
        setIsSettingsActive(false);
        setIsHomeActive(true);
      }
    }
  };

  const handleDemoDialogClose = (_event?: object, reason?: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    setDemoModeDialogOpen(false);
  };

  useEffect(() => {
    if (userRole === 'demo') {
      if (!demoViewMode) {
        setDemoModeDialogOpen(true);
      }
    } else {
      if (demoViewMode !== null) {
        setDemoViewMode(null);
      }
    }
  }, [userRole, demoViewMode]);

  const isDemoUser = userRole === 'demo';

  // Timer de desconexión automática para usuarios Demo (15 minutos)
  useEffect(() => {
    if (isDemoUser && demoViewMode && token) {
      const DEMO_SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutos en milisegundos
      
      const timeoutId = setTimeout(() => {
        // Mostrar mensaje antes de desconectar
        setPermissionSnackbar({
          open: true,
          message: t('demo.sessionExpired')
        });
        
        // Desconectar después de un breve delay para que el usuario vea el mensaje
        setTimeout(() => {
          handleLogout(true, 'demo_timeout'); // true = timeout automático, 'demo_timeout' = razón
        }, 2000);
      }, DEMO_SESSION_TIMEOUT);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
    // handleLogout se define más abajo; añadirlo aquí re-ejecutaría timers en cada render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoUser, demoViewMode, token, t]);

  // Timeout de sesión por inactividad (60 min) — no aplica a demo ni usuarios dispositivo
  useEffect(() => {
    const isDeviceUser = userData?.role === 'device' || userData?.role === 'dispositivo';
    if (isDemoUser || isDeviceUser || !token || !userData?.id) {
      stopSessionInactivity();
      return;
    }

    startSessionInactivity({
      idleTimeoutMs: 60 * 60 * 1000,
      warningBeforeMs: 5 * 60 * 1000,
      onWarning: () => {
        setPermissionSnackbar({
          open: true,
          message: t('session.warning') || 'Tu sesión se va a cortar dentro de 5 minutos por inactividad',
        });
      },
      onTimeout: () => {
        markPreviousSessionTimeout();
        handleLogout(true, 'session_timeout');
      },
    });

    return () => {
      stopSessionInactivity();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoUser, token, userData?.id, userData?.role, t]);

  // Navegación interna cuenta como actividad
  useEffect(() => {
    const isDeviceUser = userData?.role === 'device' || userData?.role === 'dispositivo';
    if (isDemoUser || isDeviceUser || !token || !userData?.id) return;

    touchSessionActivity('navigation', { force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreen, selectedTab, selectedRoom?.id, deviceModalOpen, showGroupsModal, token, userData?.id, isDemoUser]);
  const canViewSettings = useMemo(
    () => userRole === 'admin' || (isDemoUser && demoViewMode === 'admin'),
    [userRole, isDemoUser, demoViewMode]
  );

  useEffect(() => {
    setRoomReorderMode(false);
    const roomId = selectedRoom?.id;
    const uid = userData?.id;
    if (!roomId || !uid) {
      roomUserOrderRef.current = new Map();
      setRoomUserOrder(new Map());
      setRoomUserOrderRoomId(null);
      return;
    }
    let cancelled = false;
    roomSortTouchedRef.current = false;
    Promise.all([
      getUserRoomSortPreference(roomId).catch(() => ({ sort_criterion: 'personalizado', exists: false })),
      getRoomDeviceOrder(uid, roomId).catch(() => [] as { item_key: string; orden: number }[]),
    ]).then(([pref, rows]) => {
      if (cancelled) return;
      if (!roomSortTouchedRef.current) {
        const raw = pref.sort_criterion === 'modelo' ? 'artefacto' : pref.sort_criterion;
        const next = pref.exists && ROOM_SORT_CRITERIA.includes(raw as RoomSortCriterion)
          ? raw as RoomSortCriterion
          : 'personalizado';
        setSortCriterion(next);
      }
      const map = new Map<string, number>();
      rows.forEach((row) => {
        if (row?.item_key) map.set(String(row.item_key), Number(row.orden));
      });
      roomUserOrderRef.current = map;
      setRoomUserOrder(map);
      setRoomUserOrderRoomId(roomId);
    });
    return () => { cancelled = true; };
  }, [selectedRoom?.id, userData?.id]);

  const applyRoomSortCriterion = useCallback((criterion: RoomSortCriterion) => {
    roomSortTouchedRef.current = true;
    setSortCriterion(criterion);
    setSortAnchorEl(null);
    const roomId = selectedRoom?.id;
    if (!roomId || !userData?.id || isDemoUser) return;
    saveUserRoomSortPreference(roomId, criterion).catch(() => undefined);
  }, [selectedRoom?.id, userData?.id, isDemoUser]);

  const handleRoomCardsReordered = useCallback((keys: string[]) => {
    if (isDemoUser) {
      setPermissionSnackbar({ open: true, message: t('permissions.demoRestriction') });
      return;
    }
    const roomId = selectedRoom?.id;
    const uid = userData?.id;
    if (!roomId || !uid) return;
    const prev = new Map(roomUserOrderRef.current);
    const visible = new Set(keys);
    const hidden = [...prev.entries()].filter(([key]) => !visible.has(key)).sort((a, b) => a[1] - b[1]);
    const merged = new Map<string, number>();
    keys.forEach((key, index) => merged.set(key, index));
    hidden.forEach(([key], index) => merged.set(key, keys.length + index));
    roomUserOrderRef.current = merged;
    setRoomUserOrder(merged);
    setRoomUserOrderRoomId(roomId);
    roomSortTouchedRef.current = true;
    setSortCriterion('personalizado');
    const items = [...merged.entries()].map(([item_key, orden]) => ({ item_key, orden }));
    updateRoomDeviceOrder(uid, { room_id: roomId, items }).catch(() => {
      roomUserOrderRef.current = prev;
      setRoomUserOrder(prev);
    });
    saveUserRoomSortPreference(roomId, 'personalizado').catch(() => undefined);
  }, [isDemoUser, selectedRoom?.id, userData?.id, t]);

  const roomReorderHandlerRef = useRef(handleRoomCardsReordered);
  roomReorderHandlerRef.current = handleRoomCardsReordered;
  const stableRoomReorder = useCallback((keys: string[]) => {
    roomReorderHandlerRef.current(keys);
  }, []);
  const firmwareMonitor = useFirmwareJobMonitor(userRole === 'admin');

  // Efecto para redirigir al dashboard cuando cambia a horizontal en vista de dispositivos
  useEffect(() => {
    if (isHorizontal && currentScreen === 'devices' && selectedRoom) {
      // En layout horizontal, la vista de dispositivos se muestra dentro del dashboard
      setCurrentScreen('dashboard');
    }
  }, [isHorizontal, currentScreen, selectedRoom]);

  // Función para cerrar DeviceModal
  const handleCloseDeviceModal = useCallback(() => {
    setDeviceModalOpen(false);
    setSelectedDevice(null);
    setShowExitMessage(false);
  }, []);

  // Efecto para sincronizar estados de navegación con currentScreen
  useEffect(() => {
    if (currentScreen === 'dashboard') {
      setIsHomeActive(true);
      setIsSettingsActive(false);
      // NO cambiar isInfoActive aquí, se maneja por separado
    } else if (currentScreen === 'settings') {
      setIsHomeActive(false);
      setIsSettingsActive(true);
      // NO cambiar isInfoActive aquí, se maneja por separado
    } else if (currentScreen === 'favorites') {
      setIsHomeActive(false);
      setIsSettingsActive(false);
      // NO cambiar isInfoActive aquí, se maneja por separado
    }
  }, [currentScreen]);

  // Efecto para cerrar el dialog de filtro cuando se navega a otra página/tab
  useEffect(() => {
    if (filterAnchorEl) {
      setFilterAnchorEl(null);
    }
    // filterAnchorEl omitido: al incluirlo se cerraría el menú al abrirlo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreen]);

  // Efecto para manejar el estado de Home cuando se entra/sale de una habitación
  useEffect(() => {
    if (selectedRoom !== null) {
      // Si hay una habitación seleccionada, no se está en Home (matrix de habitaciones)
      setIsHomeActive(false);
    } else if (currentScreen === 'dashboard') {
      // Si no hay habitación seleccionada y estamos en dashboard, estamos en Home
      setIsHomeActive(true);
    }
  }, [selectedRoom, currentScreen]);

  // SOLUCIÓN CRÍTICA: Cuando el usuario sale de una habitación, asegurarse de que las habitaciones
  // del tablero actual estén cargadas correctamente
  useEffect(() => {
    // Solo ejecutar si:
    // 1. No estamos en carga inicial
    // 2. El usuario acaba de salir de una habitación (selectedRoom es null)
    // 3. Estamos en dashboard
    // 4. Hay boards cargados y un tab válido seleccionado
    if (isInitialLoadInProgressRef.current) {
      return;
    }
    
    if (selectedRoom === null && currentScreen === 'dashboard' && boards.length > 0 && selectedTab >= 0 && selectedTab < boards.length) {
      // SOLUCIÓN: Resetear el flag para permitir que se carguen las habitaciones
      // Esto asegura que cuando el usuario sale de una habitación, se muestren las habitaciones correctas
      roomsLoadedManuallyRef.current = false;
      
      // Cargar las habitaciones del tablero actual
      // Solo cargar si no están ya cargadas o si el tab cambió
      loadRoomsForBoard(boards[selectedTab].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoom, currentScreen, boards, selectedTab]);

  // Función para manejar la salida manual de la app - ahora se maneja en index.tsx

  // Verificar si hay filtros activos (como React Native)
  const hayFiltrosActivos = filtroModelos.length > 0 || filtroOnline || filtroOffline || filtroAuth || filtroAuthConfigured || filtroPin || filtroCamera ||
                           filtroEncendido || filtroApagado || filtroConConsumo || filtroSinConsumo ||
                           filtroArtefactos.length > 0 || searchTerm.length > 0 ||
                           filtroAiPermitido || filtroAiDenegado || filtroAiSinDefinir;

  // Función para manejar click en imagen del dispositivo (directo a IP)
  const handleDeviceImageClick = (deviceIP: string) => {
    const user = getUser();
    if (userRole === 'admin' || (user && isDemo(user))) {
      // Ir directo a la IP del dispositivo (como en la app web) - admin y demo pueden ver
      window.open(`http://${deviceIP}/#/`, '_blank', 'width=1600,height=1200,left=100,top=100');
    }
  };

  useEffect(() => {
    // SOLUCIÓN CRÍTICA: NO ejecutar durante la carga inicial
    // Durante la carga inicial, las habitaciones se cargan manualmente en handleLogin o refresh
    // Este useEffect solo debe ejecutarse DESPUÉS de que la carga inicial esté completa
    // y cuando el usuario cambia de tab manualmente
    if (isInitialLoadInProgressRef.current) {
      return;
    }
    
    // SOLUCIÓN: Solo cargar habitaciones si:
    // 1. Ya se completó la carga inicial
    // 2. Hay boards cargados
    // 3. El selectedTab es válido (>= 0 y < boards.length)
    // 4. Si selectedTab es -1, significa que hay un homeTab y debemos esperar a que se establezca
    // 5. Si hay un homeTab definido, esperar a que homeTabInitialized sea true
    // 6. Si hay un homeTab definido, esperar a que correctTabEstablishedRef sea true (tab correcto establecido)
    // 7. NO ejecutar si estamos en proceso de establecer el tab correcto
    const hasHomeTabDefined = userHomeTab !== null && userHomeTab !== undefined;
    const isWaitingForHomeTab = selectedTab === -1; // -1 significa "esperar a establecer el tab correcto"
    
    // Si estamos estableciendo el tab correcto, no cargar habitaciones todavía
    if (isSettingCorrectTabRef.current) {
      return;
    }
    
    // SOLUCIÓN CRÍTICA: Si las habitaciones ya fueron cargadas manualmente durante el login,
    // NO recargarlas a menos que el usuario haya cambiado de tab manualmente.
    // El flag se reseteará cuando el usuario cambie de tab manualmente en handleTabPress
    // O cuando salga de una habitación (en el otro useEffect)
    if (roomsLoadedManuallyRef.current) {
      // Las habitaciones ya fueron cargadas manualmente, no recargarlas
      // Solo se reseteará el flag cuando el usuario cambie de tab manualmente o salga de una habitación
      return;
    }
    
    const shouldLoadRooms = initialLoadComplete && 
                            boards.length > 0 && 
                            selectedTab >= 0 && // No cargar si es -1 (esperando homeTab)
                            selectedTab < boards.length &&
                            !isWaitingForHomeTab && // No cargar si estamos esperando el homeTab
                            (homeTabInitialized || !hasHomeTabDefined) && // Solo cargar si homeTab ya fue inicializado o no hay homeTab definido
                            (correctTabEstablishedRef.current || !hasHomeTabDefined); // Solo cargar si el tab correcto fue establecido o no hay homeTab
    
    if (shouldLoadRooms) {
      loadRoomsForBoard(boards[selectedTab].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boards, selectedTab, initialLoadComplete, homeTabInitialized, userHomeTab]);

  // Función para cargar dispositivos de la habitación seleccionada
  const loadDevices = useCallback(async () => {
    if (!selectedRoom) return;
    
    // CRÍTICO: Si estamos scrolleando en Dashboard, marcar que hay actualizaciones pendientes
    // pero NO actualizar ahora para evitar interrumpir el momentum del scroll
    if (isScrollingDashboard && currentScreen === 'dashboard') {
      pendingDashboardDeviceUpdatesRef.current = true;
      return;
    }
    
    try {
      // Usar los mapas globales en lugar de hacer llamadas HTTP
      const habitacionData = mapaHabitaciones[selectedRoom.id];
      if (habitacionData) {
        const devicesData = habitacionData.dispositivos.map(id => mapaDispositivos[id]).filter(Boolean);
        
        // LOG: Información que se pasa a la UI para renderizar
        // NOTA: tipo_artefacto es OPCIONAL (puede ser null si el dispositivo no tiene tipo de artefacto asignado)
        console.log(`[DEVICES-RENDER] loadDevices() - Origen: carga manual - Dispositivos: ${devicesData.length}`);
        // Logs de depuración por dispositivo desactivados

        setDevices(devicesData);
      } else {
        console.error('Habitación no encontrada en mapas globales');
        setDevices([]);
      }
    } catch (error) {
      console.error('Error loading devices from global maps:', error);
      setDevices([]);
    }
  }, [selectedRoom, isScrollingDashboard, currentScreen]);

  useEffect(() => {
    if (selectedRoom) {
      loadDevices();
    }
  }, [selectedRoom, loadDevices]);

  // Android TV / Samsung TV: refresh al entrar + foco mientras la habitación está abierta.
  useRoomAvSync(selectedRoom?.id);

  // Actualizar dispositivos cuando se actualiza mapaDispositivos (estructura:actualizada)
  // Esto asegura que devices tenga tipo_artefacto desde el inicio
  useEffect(() => {
    const handleEstructuraActualizada = () => {
      // Solo actualizar si hay una habitación seleccionada
      if (selectedRoom) {
        loadDevices();
      }
    };

    const unsubEstructura = eventBus.on('estructura:actualizada', handleEstructuraActualizada);
    
    return () => {
      unsubEstructura();
    };
  }, [selectedRoom, isScrollingDashboard, loadDevices]);

  useEffect(() => {
    const unsub = eventBus.on('sonos:updated', () => {
      setSonosUpdateTick((t) => t + 1);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = eventBus.on('samsung:updated', () => {
      setSamsungUpdateTick((t) => t + 1);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = eventBus.on('androidtv:updated', () => {
      setAndroidTvUpdateTick((t) => t + 1);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsubEvo = eventBus.on('evohome:updated', () => {
      setEvohomeUpdateTick((n) => n + 1);
    });
    const unsub = eventBus.on('lg:updated', () => {
      setLgUpdateTick((t) => t + 1);
    });
    const unsubPileta = eventBus.on('pileta:updated', () => {
      setPiletaUpdateTick((n) => n + 1);
    });
    return () => {
      unsub();
      unsubEvo();
      unsubPileta();
    };
  }, []);

  // SoftPhone: Socket.IO + SIP REGISTER cuando el usuario tiene permiso
  useEffect(() => {
    const user = userData || getUser();
    if (!token || (!canUsePhone(user) && !canAnswerDoorbell(user))) {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      const keepSipForIncoming =
        Boolean(stored && isStoredJwtAlive(stored) && user && (canUsePhone(user) || canAnswerDoorbell(user)))
        && (hasLoggedInDocumentSession() || isSoftphoneIncomingResume() || isSoftphoneAnswerDeepLink());
      if (keepSipForIncoming) {
        return;
      }
      void preload_SoftphoneClient().then(({ stopSoftphoneClient }) => {
        stopSoftphoneClient();
      });
      void preload_SipSoftphone().then(({ sipSoftphone }) => {
        void sipSoftphone.stop();
      });
      return;
    }

    let cancelled = false;
    const unlock = () => {
      void preload_SipSoftphone().then(({ sipSoftphone }) => {
        sipSoftphone.unlockAudio();
      });
    };
    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('touchstart', unlock, { passive: true });

    void preload_kickSoftphoneStart().then(({ kickSoftphoneStart }) => {
      if (cancelled) return;
      void kickSoftphoneStart();
    });

    return () => {
      cancelled = true;
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, [token, userData]);

  // SoftPhone: llamada entrante → video (timbre) o pantalla Teléfono
  useEffect(() => {
    const unsub = eventBus.on('softphone:incoming', (payload: {
      call_id?: string;
      kind?: string;
      from?: string;
      camera_id?: number | null;
    }) => {
      void preload_SipSoftphone().then(({ sipSoftphone }) => {
        if (sipSoftphone.blocksKind(payload?.kind || 'phone')) return;
        const user = userData || getUser();
        const kind = payload?.kind === 'doorbell' ? 'doorbell' : 'phone';
        if (kind === 'doorbell') {
          if (!canAnswerDoorbell(user)) return;
          if (isSoftphoneCallEndedGate()) return;
          const cameraId = payload?.camera_id || 23;
          if (screenBeforeDoorbellRef.current == null) {
            screenBeforeDoorbellRef.current = latestScreenRef.current;
          }
          markSoftphoneDoorbellSession();
          setDoorbellIsland(true);
          setPendingDoorbellCamera(cameraId);
          setCurrentScreen('softphone');
          setIsHomeActive(false);
          setIsInfoActive(false);
          setIsSettingsActive(false);
          try { eventBus.emit('softphone:open-doorbell', { cameraId }); } catch { /* ignore */ }
          return;
        }
        if (!canUsePhone(user)) return;
        clearSoftphoneDoorbellSession();
        clearPendingDoorbellCamera();
        setDoorbellIsland(false);
        setCurrentScreen('softphone');
        setIsHomeActive(false);
        setIsInfoActive(false);
        setIsSettingsActive(false);
        try { eventBus.emit('softphone:open-phone'); } catch { /* ignore */ }
      });
    });
    return () => unsub();
  }, [userData]);

  // SoftPhone: alguien más atendió / llamada cancelada → cerrar ring local y UI timbre.
  useEffect(() => {
    const onRemoteClear = (payload?: { reason?: string; by_user_id?: number; call_id?: string }) => {
      void import('./utils/pushRingSound').then(({ stopPushRingSound }) => {
        stopPushRingSound();
      });
      void preload_SipSoftphone().then(({ sipSoftphone }) => {
        const aborted = sipSoftphone.handleRemoteCleared(payload?.reason, payload);
        if (aborted) {
          eventBus.emit('softphone:doorbell-hangup');
          eventBus.emit('softphone:call-ended');
        }
      });
    };
    const unsub1 = eventBus.on('softphone:answered_elsewhere', onRemoteClear);
    const unsub2 = eventBus.on('softphone:ended', onRemoteClear);
    return () => {
      unsub1();
      unsub2();
    };
  }, []);

  const roomSonos = useMemo(() => {
    if (!selectedRoom) return [];
    void sonosUpdateTick;
    const user = userData || getUser();
    if (!canViewMusic(user)) return [];
    return getSonosByHabitacion(selectedRoom.id);
  }, [selectedRoom, sonosUpdateTick, userData]);

  const roomSamsung = useMemo(() => {
    if (!selectedRoom) return [];
    void samsungUpdateTick;
    const user = userData || getUser();
    return getSamsungByHabitacion(selectedRoom.id).filter((d) => {
      if (user?.role === 'admin') return true;
      if (d.device_kind === 'ac') return canViewAc(user);
      if (d.device_kind === 'tv') return canViewTv(user);
      return false;
    });
  }, [selectedRoom, samsungUpdateTick, userData]);

  const roomAndroidTv = useMemo(() => {
    if (!selectedRoom) return [];
    void androidtvUpdateTick;
    const user = userData || getUser();
    if (!canViewAndroidTv(user)) return [];
    return getAndroidTvByHabitacion(selectedRoom.id);
  }, [selectedRoom, androidtvUpdateTick, userData]);

  const roomLg = useMemo(() => {
    if (!selectedRoom) return [];
    void lgUpdateTick;
    const user = userData || getUser();
    return getLgByHabitacion(selectedRoom.id).filter((d) => {
      if (user?.role === 'admin') return true;
      if (d.ui_family === 'ac') return canViewAc(user);
      return false;
    });
  }, [selectedRoom, lgUpdateTick, userData]);

  const roomEvohome = useMemo(() => {
    if (!selectedRoom) return [];
    void evohomeUpdateTick;
    const user = userData || getUser();
    if (!canViewHeating(user)) return [];
    return getEvohomeByHabitacion(selectedRoom.id);
  }, [selectedRoom, evohomeUpdateTick, userData]);

  const roomPileta = useMemo(() => {
    if (!selectedRoom) return [];
    void piletaUpdateTick;
    const user = userData || getUser();
    if (!canViewPileta(user)) return [];
    return getPiletaByHabitacion(selectedRoom.id);
  }, [selectedRoom, piletaUpdateTick, userData]);

  const roomTotalDeviceCount =
    devices.length + roomSonos.length + roomSamsung.length + roomAndroidTv.length + roomLg.length + roomEvohome.length + roomPileta.length;

  // Modelos del filtro: Shelly de la vista + Sonos/Samsung/LG/Android TV/EvoHome/Raspberry de la habitación
  const gruposModelos = useMemo(() => collectModelFilterGroups({
    shellyTipos: devices.map((d: any) => d.tipo),
    sonosModelos: roomSonos.map((s) => s.modelo),
    samsungKinds: roomSamsung.map((d) => d.device_kind),
    lgDevices: roomLg.map((d) => ({ device_type: d.device_type, ui_family: d.ui_family })),
    androidTvCount: roomAndroidTv.length,
    evohomeCount: roomEvohome.length,
    piletaCount: roomPileta.length,
  }), [devices, roomSonos, roomSamsung, roomLg, roomAndroidTv.length, roomEvohome.length, roomPileta.length]);

  const handleSonosTransport = useCallback(async (
    speakerId: number,
    action: 'play' | 'pause' | 'previous' | 'next',
  ) => {
    try {
      const speaker = mapaSonos[speakerId];
      const runTransport = async () => {
        await sonosControl(speakerId, action);
        await refrescarSonos();
        setSonosUpdateTick((t) => t + 1);
        if (selectedSonosSpeaker?.id === speakerId) {
          const updated = mapaSonos[speakerId];
          if (updated) setSelectedSonosSpeaker(updated);
        }
      };
      if (!requirePinIfNeeded(speaker?.pin_required, speakerId, () => { void runTransport(); })) {
        return;
      }
      await runTransport();
    } catch {
      /* noop */
    }
  }, [selectedSonosSpeaker?.id]);

  const handleSonosFavoriteToggle = useCallback(async (speakerId: number) => {
    try {
      if (sonosFavorites.includes(speakerId)) {
        await removeFavorite(speakerId);
        setSonosFavorites((prev) => prev.filter((id) => id !== speakerId));
        setFavoriteSonosSpeakers((prev) => prev.filter((s) => s.id !== speakerId));
      } else {
        await addFavorite(speakerId);
        setSonosFavorites((prev) => [...prev, speakerId]);
        const speaker = mapaSonos[speakerId];
        if (speaker) {
          setFavoriteSonosSpeakers((prev) => [...prev, speaker]);
        }
      }
      eventBus.emit('favoritos:actualizado');
    } catch {
      /* noop */
    }
  }, [sonosFavorites]);

  useEffect(() => {
    const user = userData || getUser();
    if (!canViewMusic(user) || sonosFavorites.length === 0) {
      setFavoriteSonosSpeakers([]);
      return;
    }
    const speakers = sonosFavorites
      .map((id) => mapaSonos[id])
      .filter((s): s is SonosSpeaker => !!s);
    if (speakers.length > 0) {
      setFavoriteSonosSpeakers(speakers);
    }
  }, [sonosFavorites, sonosUpdateTick, userData]);

  const handleOpenSonosModal = useCallback((speaker: SonosSpeaker) => {
    closeAllDeviceModals();
    setSelectedSonosSpeaker(speaker);
    setSonosModalOpen(true);
  }, [closeAllDeviceModals]);

  const handleCloseSonosModal = useCallback(() => {
    setSonosModalOpen(false);
    setSelectedSonosSpeaker(null);
  }, []);

  const handleOpenAndroidTvModal = useCallback((device: AndroidTvDevice) => {
    closeAllDeviceModals();
    setSelectedAndroidTvDevice(device);
    setAndroidtvModalOpen(true);
  }, [closeAllDeviceModals]);

  const handleCloseAndroidTvModal = useCallback(() => {
    setAndroidtvModalOpen(false);
    setSelectedAndroidTvDevice(null);
  }, []);

  const handleAndroidTvUpdated = useCallback((device: AndroidTvDevice) => {
    patchAndroidTvDevice(device);
    setSelectedAndroidTvDevice(device);
    setAndroidTvUpdateTick((t) => t + 1);
  }, []);

  const handleOpenSamsungModal = useCallback((device: SamsungDevice) => {
    closeAllDeviceModals();
    setSelectedSamsungDevice(device);
    setSamsungModalOpen(true);
    // El historial / Back lo maneja useOverlayBackButton dentro de SamsungDeviceModal
  }, [closeAllDeviceModals]);

  const handleCloseSamsungModal = useCallback(() => {
    setSamsungModalOpen(false);
    setSelectedSamsungDevice(null);
  }, []);

  const handleSamsungUpdated = useCallback((device: SamsungDevice) => {
    upsertSamsungDevice(device);
    setSelectedSamsungDevice(device);
    setSamsungUpdateTick((t) => t + 1);
  }, []);

  const handleOpenEvohomeModal = useCallback((device: EvohomeDevice) => {
    closeAllDeviceModals();
    setSelectedEvohomeDevice(device);
    setEvohomeModalOpen(true);
  }, [closeAllDeviceModals]);

  const handleCloseEvohomeModal = useCallback(() => {
    setEvohomeModalOpen(false);
    setSelectedEvohomeDevice(null);
  }, []);

  const handleEvohomeUpdated = useCallback((device: EvohomeDevice) => {
    upsertEvohomeDevice(device);
    setSelectedEvohomeDevice(device);
    setEvohomeUpdateTick((n) => n + 1);
  }, []);

  const handleOpenPileta = useCallback((device: PiletaDevice) => {
    closeAllDeviceModals();
    const returnRoom = selectedRoom;
    const returnScreen: AppScreen =
      currentScreen === 'settings' || currentScreen === 'widgets'
        ? currentScreen
        : ((!isHorizontal && selectedRoom) ? 'devices' : 'dashboard');
    setPiletaReturnRoom(returnRoom);
    (window as any).__piletaReturnRoom = returnRoom;
    (window as any).__piletaReturnScreen = returnScreen;
    setPiletaPageTitle(
      (device.nombre || device.display_name || '').trim(),
    );
    upsertPiletaDevice(device);
    setCurrentScreen('pileta');
    window.history.pushState({ page: 'pileta' }, '', '');
  }, [closeAllDeviceModals, selectedRoom, isHorizontal, currentScreen]);

  const handleClosePileta = useCallback(() => {
    const returnRoom = piletaReturnRoom || ((window as any).__piletaReturnRoom as HabitacionConConsumo | null);
    const returnScreen = ((window as any).__piletaReturnScreen as AppScreen | undefined) || 'dashboard';
    if (returnRoom) setSelectedRoom(returnRoom);
    if (returnScreen === 'settings') {
      setIsSettingsActive(true);
      setIsHomeActive(false);
    }
    setCurrentScreen(returnScreen);
    setPiletaReturnRoom(null);
    setPiletaPageTitle('');
  }, [piletaReturnRoom]);

  const handleOpenLgModal = useCallback((device: LgDevice) => {
    closeAllDeviceModals();
    setSelectedLgDevice(device);
    setLgModalOpen(true);
  }, [closeAllDeviceModals]);

  const handleCloseLgModal = useCallback(() => {
    setLgModalOpen(false);
    setSelectedLgDevice(null);
  }, []);

  const handleLgUpdated = useCallback((device: LgDevice) => {
    upsertLgDevice(device);
    setSelectedLgDevice(device);
    setLgUpdateTick((t) => t + 1);
  }, []);
  
  // Aplicar actualizaciones pendientes cuando termine el scroll en Dashboard
  useEffect(() => {
    if (!isScrollingDashboard && pendingDashboardDeviceUpdatesRef.current && selectedRoom && currentScreen === 'dashboard') {
      pendingDashboardDeviceUpdatesRef.current = false;
      // Recargar dispositivos desde los mapas globales actualizados
      loadDevices();
    }
  }, [isScrollingDashboard, selectedRoom, currentScreen, loadDevices]);

  // CRÍTICO: Scroll manual en vista horizontal usando listeners nativos con capture: true (solución del documento 50)
  // SOLO para vista horizontal - NO afecta la vista vertical
  useEffect(() => {
    // Solo aplicar en vista horizontal cuando hay habitación seleccionada
    if (!isHorizontal || !selectedRoom || currentScreen !== 'dashboard') {
      console.log('[SCROLL DEBUG] useEffect NO se ejecuta:', { isHorizontal, selectedRoom: !!selectedRoom, currentScreen });
      return;
    }

    const container = dashboardContainerRef.current;
    if (!container) {
      console.log('[SCROLL DEBUG] Contenedor no existe');
      return;
    }
    
    console.log('[SCROLL DEBUG] Agregando listeners al contenedor:', container, {
      scrollHeight: container.scrollHeight,
      clientHeight: container.clientHeight,
      scrollTop: container.scrollTop,
      offsetHeight: container.offsetHeight,
      computedStyle: {
        height: window.getComputedStyle(container).height,
        maxHeight: window.getComputedStyle(container).maxHeight,
        overflowY: window.getComputedStyle(container).overflowY,
        flex: window.getComputedStyle(container).flex,
      },
      parentElement: container.parentElement ? {
        clientHeight: container.parentElement.clientHeight,
        scrollHeight: container.parentElement.scrollHeight,
        computedStyle: {
          height: window.getComputedStyle(container.parentElement).height,
          maxHeight: window.getComputedStyle(container.parentElement).maxHeight,
          overflowY: window.getComputedStyle(container.parentElement).overflowY,
          flex: window.getComputedStyle(container.parentElement).flex,
        }
      } : null
    });
    
    const handleTouchStart = (e: TouchEvent) => {
      // NO hacer preventDefault aquí - permitir que el scroll funcione
      if (e.touches[0]) {
        const touchX = e.touches[0].clientX;
        const touchY = e.touches[0].clientY;
        const elementAtPoint = document.elementFromPoint(touchX, touchY) as HTMLElement;
        
        dashboardTouchStartRef.current = {
          x: touchX,
          y: touchY,
          scrollTop: container.scrollTop,
          time: Date.now(),
          element: elementAtPoint || (e.target as HTMLElement)
        };
        dashboardIsScrollingRef.current = false;
        dashboardScrollVelocityRef.current = 0;
      }
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (dashboardTouchStartRef.current && e.touches[0] && container) {
        const deltaX = e.touches[0].clientX - dashboardTouchStartRef.current.x;
        const deltaY = e.touches[0].clientY - dashboardTouchStartRef.current.y;
        const absDeltaX = Math.abs(deltaX);
        const absDeltaY = Math.abs(deltaY);
        
        // Si hay movimiento significativo, es scroll
        if (absDeltaY > 2 || absDeltaX > 2) {
          dashboardIsScrollingRef.current = true;
          dashboardHasScrolledRef.current = true;
          
          // CRÍTICO: Si el movimiento es principalmente vertical, hacer scroll manual
          // Esto es necesario porque algunos elementos pueden estar bloqueando el scroll nativo
          if (absDeltaY > absDeltaX && absDeltaY > 5) {
            // CRÍTICO: Usar velocidad completa (1.0) para que el scroll sea más rápido
            // Almacenar velocidad para momentum
            const timeDelta = Date.now() - dashboardTouchStartRef.current.time;
            if (timeDelta > 0) {
              dashboardScrollVelocityRef.current = deltaY / timeDelta; // Velocidad en px/ms
            }
            
            // Hacer scroll manual del contenedor con velocidad completa
            const currentScrollTop = container.scrollTop;
            const newScrollTop = currentScrollTop - deltaY; // Invertir deltaY porque scroll es inverso
            container.scrollTop = Math.max(0, Math.min(container.scrollHeight - container.clientHeight, newScrollTop));
            
            // Actualizar la posición de referencia para el próximo movimiento
            dashboardTouchStartRef.current = {
              ...dashboardTouchStartRef.current,
              x: e.touches[0].clientX,
              y: e.touches[0].clientY,
              scrollTop: container.scrollTop,
              time: Date.now(),
            };
            
            // Prevenir el comportamiento por defecto para evitar scroll duplicado
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }
    };
    
    const handleTouchEnd = (e: TouchEvent) => {
      // CRÍTICO: Si hubo scroll, aplicar momentum/inercia para que se sienta más natural
      if (dashboardIsScrollingRef.current && dashboardScrollVelocityRef.current !== 0 && container) {
        // Aplicar momentum: continuar el scroll con deceleración
        const friction = 0.95; // Factor de fricción para deceleración
        let currentVelocity = dashboardScrollVelocityRef.current * 16; // Convertir a px por frame (asumiendo 60fps)
        
        const applyMomentum = () => {
          if (Math.abs(currentVelocity) > 0.5) {
            const currentScrollTop = container.scrollTop;
            const newScrollTop = currentScrollTop - currentVelocity;
            container.scrollTop = Math.max(0, Math.min(container.scrollHeight - container.clientHeight, newScrollTop));
            
            currentVelocity *= friction; // Aplicar fricción
            
            dashboardScrollAnimationFrameRef.current = requestAnimationFrame(applyMomentum);
          } else {
            dashboardScrollVelocityRef.current = 0;
            dashboardScrollAnimationFrameRef.current = null;
          }
        };
        
        // Cancelar cualquier animación previa
        if (dashboardScrollAnimationFrameRef.current) {
          cancelAnimationFrame(dashboardScrollAnimationFrameRef.current);
        }
        
        // Iniciar momentum
        dashboardScrollAnimationFrameRef.current = requestAnimationFrame(applyMomentum);
      }
      
      // Limpiar después de un delay
      setTimeout(() => {
        dashboardTouchStartRef.current = null;
        dashboardIsScrollingRef.current = false;
      }, 100);
    };
    
    // CRÍTICO: Manejar eventos wheel del mouse para scroll en desktop
    const handleWheel = (e: WheelEvent) => {
      console.log('[SCROLL DEBUG] Wheel event recibido:', {
        deltaY: e.deltaY,
        deltaX: e.deltaX,
        target: e.target,
        currentTarget: e.currentTarget,
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight,
        scrollTop: container.scrollTop
      });
      
      // Hacer scroll manual cuando se usa la rueda del mouse
      const currentScrollTop = container.scrollTop;
      const newScrollTop = currentScrollTop + e.deltaY;
      const clampedScrollTop = Math.max(0, Math.min(container.scrollHeight - container.clientHeight, newScrollTop));
      
      console.log('[SCROLL DEBUG] Haciendo scroll:', {
        currentScrollTop,
        newScrollTop,
        clampedScrollTop,
        deltaY: e.deltaY,
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight
      });
      
      // CRÍTICO: Prevenir el comportamiento por defecto para evitar scroll duplicado
      e.preventDefault();
      e.stopPropagation();
      
      container.scrollTop = clampedScrollTop;
      
      // Verificar que el scroll se aplicó correctamente
      console.log('[SCROLL DEBUG] Scroll aplicado:', {
        scrollTopDespues: container.scrollTop,
        clampedScrollTop
      });
      
      // Marcar que hubo scroll
      setIsScrollingDashboard(true);
      dashboardHasScrolledRef.current = true;
      
      // Limpiar el flag después de un delay
      if (dashboardScrollTimeoutRef.current) {
        clearTimeout(dashboardScrollTimeoutRef.current);
      }
      dashboardScrollTimeoutRef.current = setTimeout(() => {
        setIsScrollingDashboard(false);
        setTimeout(() => {
          dashboardHasScrolledRef.current = false;
        }, 50);
      }, 150);
    };
    
    // CRÍTICO: Usar capture: true para capturar eventos ANTES de que lleguen a los hijos
    // CRÍTICO: touchmove debe ser passive: false para poder hacer scroll manual cuando sea necesario
    container.addEventListener('touchstart', handleTouchStart, { capture: true, passive: true });
    container.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false });
    container.addEventListener('touchend', handleTouchEnd, { capture: true, passive: false });
    // CRÍTICO: Agregar listener para eventos wheel del mouse
    // CRÍTICO: passive: false para poder hacer scroll manual cuando sea necesario
    container.addEventListener('wheel', handleWheel, { passive: false });
    
    // DEBUG: Listener global para ver si los eventos wheel están llegando
    const globalWheelHandler = (e: WheelEvent) => {
      void e;
      void (container.contains(e.target as HTMLElement));
      // console.log('[SCROLL DEBUG] Wheel event global:', {
        // // // deltaY: e.deltaY,
        // // // target: target.tagName,
        // // // targetClass: target.className,
        // // // isOverContainer,
        // // // containerScrollHeight: container.scrollHeight,
        // // // containerClientHeight: container.clientHeight
      // // // });
    };
    document.addEventListener('wheel', globalWheelHandler, { passive: true });
    
    console.log('[SCROLL DEBUG] Listeners agregados correctamente');
    
    return () => {
      console.log('[SCROLL DEBUG] Limpiando listeners');
      container.removeEventListener('touchstart', handleTouchStart, { capture: true });
      container.removeEventListener('touchmove', handleTouchMove, { capture: true });
      container.removeEventListener('touchend', handleTouchEnd, { capture: true });
      container.removeEventListener('wheel', handleWheel);
      document.removeEventListener('wheel', globalWheelHandler);
      // Limpiar animación de momentum si existe
      if (dashboardScrollAnimationFrameRef.current) {
        cancelAnimationFrame(dashboardScrollAnimationFrameRef.current);
        dashboardScrollAnimationFrameRef.current = null;
      }
    };
  }, [isHorizontal, selectedRoom, currentScreen, isScrollingDashboard]);

  // Barra de tabs de tableros: scroll horizontal manual con el dedo.
  // En WKWebView/PWA el scroll horizontal nativo del scroller de MUI a veces no recibe
  // el gesto (mismo problema que la lista de habitaciones), por lo que movemos scrollLeft
  // manualmente y aplicamos inercia. Solo afecta a gestos predominantemente horizontales.
  useEffect(() => {
    if (currentScreen !== 'dashboard') {
      return;
    }
    const containerEl = boardTabsContainerRef.current;
    if (!containerEl) {
      return;
    }
    const scroller = containerEl.querySelector('.MuiTabs-scroller') as HTMLElement | null;
    if (!scroller) {
      return;
    }

    const stopMomentum = () => {
      if (boardTabsMomentumRef.current !== null) {
        cancelAnimationFrame(boardTabsMomentumRef.current);
        boardTabsMomentumRef.current = null;
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (!e.touches[0]) return;
      stopMomentum();
      boardTabsTouchRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        scrollLeft: scroller.scrollLeft,
        lastX: e.touches[0].clientX,
        time: Date.now(),
        vel: 0,
        moved: false,
      };
    };

    const handleTouchMove = (e: TouchEvent) => {
      const st = boardTabsTouchRef.current;
      if (!st || !e.touches[0]) return;
      const deltaX = e.touches[0].clientX - st.x;
      const deltaY = e.touches[0].clientY - st.y;
      // Solo tomamos control si el gesto es claramente horizontal.
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 3) {
        st.moved = true;
        const now = Date.now();
        const dt = Math.max(1, now - st.time);
        st.vel = (e.touches[0].clientX - st.lastX) / dt; // px/ms
        st.lastX = e.touches[0].clientX;
        st.time = now;

        const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        scroller.scrollLeft = Math.max(0, Math.min(maxScroll, st.scrollLeft - deltaX));
        // Evitar que el gesto horizontal dispare otros comportamientos (swipe entre tabs, etc.).
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleTouchEnd = () => {
      const st = boardTabsTouchRef.current;
      boardTabsTouchRef.current = null;
      if (!st || !st.moved) return;

      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      if (maxScroll <= 0) return;

      let velocity = st.vel * 16; // px por frame (~60fps)
      const friction = 0.95;
      const applyMomentum = () => {
        if (Math.abs(velocity) > 0.5) {
          scroller.scrollLeft = Math.max(0, Math.min(maxScroll, scroller.scrollLeft - velocity));
          velocity *= friction;
          boardTabsMomentumRef.current = requestAnimationFrame(applyMomentum);
        } else {
          boardTabsMomentumRef.current = null;
        }
      };
      stopMomentum();
      boardTabsMomentumRef.current = requestAnimationFrame(applyMomentum);
    };

    // passive: false para poder hacer preventDefault en gestos horizontales.
    scroller.addEventListener('touchstart', handleTouchStart, { passive: true });
    scroller.addEventListener('touchmove', handleTouchMove, { passive: false });
    scroller.addEventListener('touchend', handleTouchEnd, { passive: true });
    scroller.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      scroller.removeEventListener('touchstart', handleTouchStart);
      scroller.removeEventListener('touchmove', handleTouchMove);
      scroller.removeEventListener('touchend', handleTouchEnd);
      scroller.removeEventListener('touchcancel', handleTouchEnd);
      stopMomentum();
      boardTabsTouchRef.current = null;
    };
  }, [currentScreen, boards.length]);

  // Cuando la grilla visible no llega al borde inferior útil del panel, no debe haber scroll (.shc-room-list-content-pad).
  // scrollHeight del panel puede mentir si flex/min-height deja hueco vacío por debajo: comparamos getBoundingClientRect de la grid.
  useLayoutEffect(() => {
    if (currentScreen !== 'dashboard' || selectedRoom) {
      setDashRoomGridOverflows(false);
      return;
    }

    const recompute = () => {
      const pane = dashboardContentRef.current;
      const gridEl = dashboardRoomGridRef.current;
      if (
        !pane ||
        !gridEl ||
        currentScreenRef.current !== 'dashboard' ||
        selectedRoomRef.current
      ) {
        setDashRoomGridOverflows(false);
        return;
      }

      const cs =
        typeof window !== 'undefined' && pane.isConnected ? window.getComputedStyle(pane) : null;
      const borderBottom = cs ? Number.parseFloat(cs.borderBottomWidth) || 0 : 0;
      const tol = 8;
      const paneRect = pane.getBoundingClientRect();
      // Borde inferior del scrollport = caja de padding (visible), no restar paddingBottom:
      // restarlo subía el umbral y daba overflow con hueco vacío bajo la grilla.
      const scrollportBottomVp = paneRect.bottom - borderBottom;
      const gridBottomVp = gridEl.getBoundingClientRect().bottom;
      const gridExtendsBelowView = gridBottomVp > scrollportBottomVp + tol;
      const nativeCanScroll = pane.scrollHeight > pane.clientHeight + tol;
      // AND: si scrollHeight miente (flex/hueco fantasma) pero la grilla no llega al borde, no scrollear.
      const overflowsVertically = gridExtendsBelowView && nativeCanScroll;
      setDashRoomGridOverflows(overflowsVertically);
      if (!overflowsVertically && pane.scrollTop !== 0) pane.scrollTop = 0;
    };

    const schedule = () => {
      requestAnimationFrame(recompute);
    };

    const el = dashboardContentRef.current;
    window.addEventListener('resize', recompute);

    let ro: ResizeObserver | undefined;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(schedule);
      ro.observe(el);
      const gridPeek = dashboardRoomGridRef.current;
      if (gridPeek) ro.observe(gridPeek);
    }

    schedule();
    requestAnimationFrame(schedule);
    recompute();

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', recompute);
      setDashRoomGridOverflows(false);
    };
  }, [currentScreen, selectedRoom, rooms.length, selectedTab, isHorizontal]);

  // Lista de habitaciones (.shc-room-list-content-pad = dashboardContentRef): en móvil los hijos tapables
  // pueden impedir que el scroll nativo reciba el gesto; además listeners en el propio div a veces no reciben
  // la cadena de eventos igual que Safari/WKWebView. Document + capture permite preventDefault antes,
  // sin stopPropagation (no cortar swipe entre tableros). Ver doc 51 (Camera Tab, scroll manual).
  useLayoutEffect(() => {
    const active = currentScreen === 'dashboard' && !selectedRoom && dashRoomGridOverflows;
    let touchStart: { x: number; y: number; scrollTop: number; time: number } | null = null;
    let gestureVelocityPxPerMs = 0;
    let hadScrollGesture = false;
    let rafId: number | null = null;

    if (!active) {
      return;
    }

    const getScrollEl = (): HTMLElement | null =>
      dashboardContentRef.current instanceof HTMLElement ? dashboardContentRef.current : null;

    const markRoomsListScrolled = () => {
      roomsListHasScrolledRef.current = true;
      if (roomsListScrollFlagTimeoutRef.current) {
        clearTimeout(roomsListScrollFlagTimeoutRef.current);
      }
      roomsListScrollFlagTimeoutRef.current = setTimeout(() => {
        roomsListHasScrolledRef.current = false;
      }, 150);
    };

    const handleTouchStart = (e: TouchEvent) => {
      const container = getScrollEl();
      if (!container || !e.touches[0]) return;
      const n = e.target;
      if (!(n instanceof Node) || !container.contains(n)) return;
      touchStart = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        scrollTop: container.scrollTop,
        time: Date.now(),
      };
      gestureVelocityPxPerMs = 0;
      hadScrollGesture = false;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      const container = getScrollEl();
      if (!container || !touchStart || !e.touches[0]) return;

      const deltaX = e.touches[0].clientX - touchStart.x;
      const deltaY = e.touches[0].clientY - touchStart.y;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
      if (maxScroll <= 0) return;

      if (absDeltaY > 2 || absDeltaX > 2) {
        hadScrollGesture = true;
      }

      // Vertical (incluye diagonal apenas más vertical que horizontal): NO stopPropagation —
      // si no, algunos navegadores dejan el gesto muerto antes de llegar al resto de la vista.
      if (absDeltaY >= absDeltaX && absDeltaY > 4) {
        const timeDelta = Math.max(1, Date.now() - touchStart.time);
        gestureVelocityPxPerMs = deltaY / timeDelta;

        const nextTop = Math.max(0, Math.min(maxScroll, container.scrollTop - deltaY));
        container.scrollTop = nextTop;
        markRoomsListScrolled();

        touchStart = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          scrollTop: container.scrollTop,
          time: Date.now(),
        };

        e.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      const container = getScrollEl();
      if (!container) {
        touchStart = null;
        return;
      }
      const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
      if (hadScrollGesture && maxScroll > 0 && Math.abs(gestureVelocityPxPerMs) > 0.01) {
        const friction = 0.95;
        let currentVelocity = gestureVelocityPxPerMs * 16;

        const applyMomentum = () => {
          const c = getScrollEl();
          if (!c || c.scrollHeight <= c.clientHeight) {
            gestureVelocityPxPerMs = 0;
            rafId = null;
            return;
          }
          if (Math.abs(currentVelocity) > 0.5) {
            const hi = Math.max(0, c.scrollHeight - c.clientHeight);
            const nextTop = Math.max(0, Math.min(hi, c.scrollTop - currentVelocity));
            c.scrollTop = nextTop;
            markRoomsListScrolled();
            currentVelocity *= friction;
            rafId = requestAnimationFrame(applyMomentum);
          } else {
            gestureVelocityPxPerMs = 0;
            rafId = null;
          }
        };
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(applyMomentum);
      }

      touchStart = null;
      setTimeout(() => {
        hadScrollGesture = false;
      }, 80);
    };

    document.addEventListener('touchstart', handleTouchStart, { capture: true, passive: true });
    document.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false });
    document.addEventListener('touchend', handleTouchEnd, { capture: true, passive: true });
    document.addEventListener('touchcancel', handleTouchEnd, { capture: true, passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart, { capture: true });
      document.removeEventListener('touchmove', handleTouchMove, { capture: true });
      document.removeEventListener('touchend', handleTouchEnd, { capture: true });
      document.removeEventListener('touchcancel', handleTouchEnd, { capture: true });
      touchStart = null;
      gestureVelocityPxPerMs = 0;
      hadScrollGesture = false;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };
  }, [currentScreen, selectedRoom, dashRoomGridOverflows]);

  // Función para actualizar el estado de la UI desde los mapas globales
  // Usamos useRef para mantener referencias estables a currentScreen, selectedRoom y devices
  const currentScreenRefForUpdate = useRef(currentScreen);
  const selectedRoomRefForUpdate = useRef(selectedRoom);
  const devicesRefForUpdate = useRef(devices);
  
  useEffect(() => {
    currentScreenRefForUpdate.current = currentScreen;
  }, [currentScreen]);
  
  useEffect(() => {
    selectedRoomRefForUpdate.current = selectedRoom;
  }, [selectedRoom]);
  
  useEffect(() => {
    devicesRefForUpdate.current = devices;
  }, [devices]);
  
  const updateUIFromGlobalMaps = useCallback(() => {
    console.log('🔧 [UPDATE] updateUIFromGlobalMaps ejecutándose...');
    // Actualizar habitaciones con consumos desde los mapas globales usando función funcional
    // OPTIMIZACIÓN: Solo crear nuevo array si realmente hay cambios en el consumo
    setRooms(prevRooms => {
      let hasChanges = false;
      const newRooms = prevRooms.map(room => {
        const habitacionData = mapaHabitaciones[room.id];
        if (habitacionData) {
          // CRÍTICO: Redondear consumos a 1 decimal para evitar cambios por precisión float
          const currentConsumo = Math.round((room.consumo || 0) * 10) / 10;
          const newConsumo = Math.round((habitacionData.consumoTotal || 0) * 10) / 10;
          
          // Solo crear nuevo objeto si el consumo realmente cambió (con tolerancia de 0.1W)
          if (Math.abs(currentConsumo - newConsumo) >= 0.1) {
            hasChanges = true;
            console.log('🔧 [UPDATE] setRooms - cambio en habitación ' + room.id + ': ' + currentConsumo + ' -> ' + newConsumo);
            return {
              ...room,
              consumo: habitacionData.consumoTotal
            };
          }
          // Retornar objeto original si no hay cambios
          return room;
        }
        return room;
      });
      // Solo retornar nuevo array si hubo cambios, de lo contrario retornar el array original
      if (!hasChanges) {
        console.log('🔧 [UPDATE] setRooms - NO hay cambios, evitando actualización');
      }
      return hasChanges ? newRooms : prevRooms;
    });

    // Actualizar dispositivos si estamos en la pantalla de dispositivos O en Dashboard con habitación seleccionada
    // Usar refs para evitar dependencias
    // CRÍTICO: NO actualizar si estamos scrolleando en Dashboard (evita interrumpir momentum)
    const isDashboardWithRoom = currentScreenRefForUpdate.current === 'dashboard' && selectedRoomRefForUpdate.current;
    if (currentScreenRefForUpdate.current === 'devices' || isDashboardWithRoom) {
      // Si estamos scrolleando en Dashboard, NO actualizar ahora
      if (isScrollingDashboard && isDashboardWithRoom) {
        pendingDashboardDeviceUpdatesRef.current = true;
        return;
      }
      console.log('🔧 [App] Actualizando dispositivos para currentScreen = devices');
      let updatedDevices: DispositivoConConsumo[] = [];
      
      // Si estamos en Dashboard con habitación seleccionada, usar la misma lógica
      if (selectedRoomRefForUpdate.current) {
        console.log('🔧 [App] Habitación seleccionada:', selectedRoomRefForUpdate.current.id, selectedRoomRefForUpdate.current.nombre);
        // Si hay una habitación seleccionada, mostrar solo sus dispositivos
        const habitacionData = mapaHabitaciones[selectedRoomRefForUpdate.current.id];
        if (habitacionData) {
          // LOG: Verificar qué hay en mapaDispositivos antes de mapear
          const dispositivo10697Antes = mapaDispositivos[10697];
          if (dispositivo10697Antes) {
            console.log(`[MAP-DEVICES] updateUIFromGlobalMaps() - ANTES de mapear: mapaDispositivos[10697] tiene pin_required=${dispositivo10697Antes.pin_required}, tipo_artefacto=${dispositivo10697Antes.tipo_artefacto}`);
          }
          
          updatedDevices = habitacionData.dispositivos.map(id => {
            const dispositivo = mapaDispositivos[id];
            // LOG: Verificar qué se está obteniendo para dispositivo 10697
            if (id === 10697) {
              console.log(`[MAP-DEVICES] updateUIFromGlobalMaps() - Mapeando dispositivo 10697: pin_required=${dispositivo?.pin_required}, tipo_artefacto=${dispositivo?.tipo_artefacto}`);
            }
            return dispositivo;
          }).filter(Boolean);
          
          // LOG: Verificar qué hay en updatedDevices después de mapear
          const dispositivo10697Despues = updatedDevices.find(d => d.id === 10697);
          if (dispositivo10697Despues) {
            console.log(`[MAP-DEVICES] updateUIFromGlobalMaps() - DESPUÉS de mapear: updatedDevices[10697] tiene pin_required=${dispositivo10697Despues.pin_required}, tipo_artefacto=${dispositivo10697Despues.tipo_artefacto}`);
          }
          console.log('🔧 [App] Dispositivos encontrados en habitación:', updatedDevices.length);
        }
      } else {
        // Si NO hay habitación seleccionada, mostrar TODOS los dispositivos
        updatedDevices = Object.values(mapaDispositivos);
        console.log('🔧 [App] Mostrando todos los dispositivos:', updatedDevices.length);
      }
      
      if (updatedDevices) {
        // OPTIMIZACIÓN: Comparar si realmente hay cambios antes de actualizar devices
        // Usar ref para acceder al valor actual de devices dentro del useCallback
        const currentDevices = devicesRefForUpdate.current;
        
        // Comparar por cantidad de dispositivos
        const hasDeviceCountChange = currentDevices.length !== updatedDevices.length;
        
        // Comparar por IDs de dispositivos (ordenados)
        const currentDeviceIds = currentDevices.map(d => d.id).sort().join(',');
        const newDeviceIds = updatedDevices.map(d => d.id).sort().join(',');
        const hasDeviceIdsChange = currentDeviceIds !== newDeviceIds;
        
        // Comparar estados y consumos de dispositivos existentes
        const hasDeviceStateChange = currentDevices.some((device) => {
          const updatedDevice = updatedDevices.find(d => d.id === device.id);
          if (!updatedDevice) return true; // Dispositivo desapareció o apareció
          
          // Comparar estado (convertir a string para comparación segura)
          const currentState = String(device.estado || '');
          const newState = String(updatedDevice.estado || '');
          if (currentState !== newState) return true;
          
          // Comparar consumo con tolerancia (redondear a 1 decimal, tolerancia 0.1W)
          const currentConsumo = Math.round((device.consumo || 0) * 10) / 10;
          const newConsumo = Math.round((updatedDevice.consumo || 0) * 10) / 10;
          if (Math.abs(currentConsumo - newConsumo) >= 0.1) return true;
          
          // Comparar online status
          if (device.online !== updatedDevice.online) return true;
          
          // Comparar lights_brightness para DIMMER2
          if (device.tipo === 'DIMMER2' && device.lights_brightness !== updatedDevice.lights_brightness) {
            return true;
          }
          
          // Comparar cover_position para PLUS2PM
          if (device.tipo === 'PLUS2PM') {
            const currentCoverPosition = device.cover_position ?? device.metadata?.cover_position;
            const newCoverPosition = updatedDevice.cover_position ?? updatedDevice.metadata?.cover_position;
            if (currentCoverPosition !== newCoverPosition) {
              return true;
            }
          }
          
          return false;
        });
        
        // Solo actualizar si realmente hay cambios
        const hasDeviceChanges = hasDeviceCountChange || hasDeviceIdsChange || hasDeviceStateChange;
        
        if (hasDeviceChanges) {
          // Limpiar coverActions cuando el estado real del dispositivo cambie (fines de recorrido)
        setCoverActions(prev => {
          const newActions = { ...prev };
          console.log('🎯 [COVER] updateUIFromGlobalMaps - coverActions actuales:', prev);
          updatedDevices.forEach((device: DispositivoConConsumo) => {
            if (device.tipo === 'PLUS2PM') {
              console.log('🎯 [COVER] Dispositivo PLUS2PM:', { id: device.id, estado: device.estado, tipo: typeof device.estado, tieneAccion: !!prev[device.id] });
              // Solo limpiar si el dispositivo está realmente parado (open/closed/stopped)
              // Y si había una acción temporal pendiente
              if (prev[device.id] && (String(device.estado) === 'open' || String(device.estado) === 'closed' || String(device.estado) === 'stopped')) {
                console.log('🎯 [COVER] Limpiando coverActions por fin de recorrido:', { device_id: device.id, estado: device.estado });
                delete newActions[device.id];
              }
            }
          });
          console.log('🎯 [COVER] updateUIFromGlobalMaps - coverActions nuevos:', newActions);
          return newActions;
        });
        
        setDevices(updatedDevices);
        } else {
          console.log('🔧 [UPDATE] NO hay cambios en dispositivos - evitando setDevices');
        }
        // Si no hay cambios, no hacer nada (no actualizar devices, evitando re-renders innecesarios)
      }
    }
  }, [isScrollingDashboard]); // currentScreen/selectedRoom vía refs internos

  // OPTIMIZADO: Conectar WebSocket temprano si hay token disponible
  // Esto asegura que el WebSocket esté listo cuando el usuario haga clic en botones
  useEffect(() => {
    // Solo conectar si no estamos en proceso de login
    if (isLoggingIn) {
      return;
    }
    
    // Verificar si hay token existente para pasarlo al conectar
    const existingToken = localStorage.getItem('token');
    
    // OPTIMIZADO: Conectar WebSocket inmediatamente si hay token
    // Esto mejora la respuesta de los botones al cargar la página
    if (existingToken && !WebSocketService.isConnected()) {
      setWebsocketStatus('connecting');
      syncServerUrlWithCurrentOrigin();
      WebSocketService.connect(existingToken).catch((error) => {
        if (error.message !== 'Connection cancelled' && error.message !== 'No token provided') {
          // Reintentar en background sin pintar rojo permanente
          setWebsocketStatus('connecting');
          eventBus.emit('websocket:connecting');
          setTimeout(() => {
            WebSocketService.connect(existingToken).catch(() => {});
          }, 1000);
        }
      });
    } else if (!existingToken) {
      setWebsocketStatus('disconnected');
    } else if (WebSocketService.isConnected()) {
      // Si ya está conectado, verificar si está listo
      if (WebSocketService.isReady()) {
        // Si está listo, marcar como connected y emitir ready
        setWebsocketStatus('connected');
        (window as any).websocketStatus = 'connected';
        eventBus.emit('websocket:ready');
      } else {
        // Si está conectado pero no listo, marcar como connecting
        setWebsocketStatus('connecting');
        (window as any).websocketStatus = 'connecting';
        
        // Intentar inicializar y esperar a que esté listo
        WebSocketService.setupControlResponseListener();
        getListenerPool();
        
        // Esperar a que esté listo con timeout
        WebSocketService.waitUntilReady(2000).then((isReady) => {
          if (isReady) {
            setWebsocketStatus('connected');
            (window as any).websocketStatus = 'connected';
            eventBus.emit('websocket:ready');
          } else {
            // FALLBACK: Si no está listo después de 2 segundos pero está conectado, emitir de todas formas
            if (WebSocketService.isConnected()) {
              setWebsocketStatus('connected');
              (window as any).websocketStatus = 'connected';
              eventBus.emit('websocket:ready');
            }
          }
        });
      }
    }
    
    return () => {
      // No desconectar aquí, mantener conexión persistente durante toda la sesión
    };
  }, [isLoggingIn]); // Depender de isLoggingIn para evitar interferencias

  // useEffect para escuchar cuando el usuario es deshabilitado
  useEffect(() => {
    const handleUserDisabled = () => {
      markLoginNotice('userDisabled');
      handleLogout(false); // false = logout manual, no timeout
    };

    const handleTokenExpired = () => {
      markLoginNotice('tokenExpired');
      // Limpiar estado local inmediatamente
      setToken(null);
      setUserData(null);
      setLoading(false);
      // Expulsar al usuario si el token expiró
      handleLogout(false, 'Token expirado');
    };

    const { eventBus } = require('./services/EventBus');
    eventBus.on('user-disabled', handleUserDisabled);
    eventBus.on('token-expired', handleTokenExpired);
    
    // CRITICO: Limpiar caché de permisos cuando se actualizan permisos
    const handlePermissionsUpdated = async (data: { userId: number }) => {
      const user = getUser();
      if (user && user.id === data.userId) {
        setCachedUserPermissions(null);
        const { clearPermissionsCache } = await import('./services/permissionsService');
        clearPermissionsCache();
        try {
          const { getUserPermissions } = await import('./services/api');
          const perms = await getUserPermissions(data.userId);
          const updatedUser = {
            ...user,
            can_view_cameras: perms.can_view_cameras === true,
            can_use_ai: perms.can_use_ai === true,
            can_view_music: perms.can_view_music === true,
            can_view_ac: perms.can_view_ac === true,
            can_view_tv: perms.can_view_tv === true,
            can_view_android_tv: perms.can_view_android_tv === true,
            can_view_heating: perms.can_view_heating === true,
            can_view_pileta: perms.can_view_pileta === true,
            can_answer_doorbell: perms.can_answer_doorbell === true,
            can_use_phone: perms.can_use_phone === true,
            softphone_sip_extension: perms.softphone_sip_extension ?? null,
            has_softphone_sip_password: perms.has_softphone_sip_password === true,
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUserData(updatedUser);

          // Si el home guardado quedó sin permiso, esta sesión va a dashboard.
          // NO borrar home_page en BD: solo el usuario desde el menú puede cambiarla.
          const updatedUserHomePage = (updatedUser as any).home_page;
          const homeNowBlocked =
            (updatedUserHomePage === 'assistant' && perms.can_use_ai !== true) ||
            (updatedUserHomePage === 'video' && perms.can_view_cameras !== true);
          if (homeNowBlocked) {
            setCurrentScreen('dashboard');
            setIsHomeActive(true);
          }
        } catch {
          // Si falla el refresh, el usuario deberá re-login para ver flags actualizados
        }
      }
    };
    eventBus.on('permissions:updated', handlePermissionsUpdated);

    return () => {
      eventBus.off('user-disabled', handleUserDisabled);
      eventBus.off('token-expired', handleTokenExpired);
      eventBus.off('permissions:updated', handlePermissionsUpdated);
    };
    // handleLogout definido más abajo; re-suscribir en cada cambio de t
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // useEffect para inicializar el servicio de consumo (solo una vez)
  // NOTA: inicializarConsumoTiempoReal() también se llama en handleLogin después de refrescarEstructuraCompleta()
  // Este useEffect solo se ejecuta en casos de refresh de página donde no hay handleLogin
  useEffect(() => {
    if (token && initialLoadComplete && !isLoggingIn) {
      // Solo inicializar si ya se completó la carga inicial y no estamos en proceso de login
      // En handleLogin ya se llama a inicializarConsumoTiempoReal() después de refrescarEstructuraCompleta()
      // CRITICO: Pasar skipInitialRefresh=true para evitar una segunda carga completa innecesaria
      // Si handleLogin ya ejecutó refrescarEstructuraCompleta(), no volver a ejecutarlo aquí
      inicializarConsumoTiempoReal(true);
      
      // WebSocket ya está conectado, solo autenticar
      WebSocketService.authenticate(token);
    }
    
    return () => {
      // Limpiar el servicio de consumo en tiempo real
      limpiarRefrescoAutomatico();
    };
  }, [token, initialLoadComplete, isLoggingIn]); // Depender de initialLoadComplete para evitar ejecución duplicada

  // useEffect para actualizar selectedDevice cuando se refrescan los mapas globales
  useEffect(() => {
    const handleEstructuraActualizada = () => {
      if (selectedDevice && deviceModalOpen) {
        // Si hay un dispositivo seleccionado y el modal está abierto, actualizar con los datos frescos
        const dispositivoActualizado = mapaDispositivos[selectedDevice.id];
        if (dispositivoActualizado) {
          setSelectedDevice(dispositivoActualizado);
        }
      }
    };

    const unsubEstructura = eventBus.on('estructura:actualizada', handleEstructuraActualizada);
    
    return () => {
      unsubEstructura();
    };
  }, [selectedDevice, deviceModalOpen]);

  // useEffect para escuchar eventos de asignación de habitación
  useEffect(() => {
    const handleRoomAssigned = (data: any) => {
      console.log('🔧 [App] Evento room:assigned recibido:', data);
      
      // Actualizar mapaHabitaciones inmediatamente
      if (data.deviceIds && data.habitacion_id) {
        data.deviceIds.forEach((deviceId: number) => {
          if (mapaDispositivos[deviceId]) {
            // Actualizar el dispositivo en mapaDispositivos
            mapaDispositivos[deviceId].habitacion_id = data.habitacion_id;
            
            // Actualizar mapaHabitaciones
            if (mapaHabitaciones[data.habitacion_id]) {
              // Agregar el dispositivo a la nueva habitación si no está ya
              if (!mapaHabitaciones[data.habitacion_id].dispositivos.includes(deviceId)) {
                mapaHabitaciones[data.habitacion_id].dispositivos.push(deviceId);
              }
            }
            
            // Remover el dispositivo de la habitación anterior si existía
            Object.keys(mapaHabitaciones).forEach(habitacionId => {
              if (Number(habitacionId) !== data.habitacion_id) {
                const index = mapaHabitaciones[Number(habitacionId)].dispositivos.indexOf(deviceId);
                if (index > -1) {
                  mapaHabitaciones[Number(habitacionId)].dispositivos.splice(index, 1);
                }
              }
            });
          }
        });
      }
      
      // Forzar actualización inmediata de la UI
      updateUIFromGlobalMaps();
    };

    const unsubRoomAssigned = eventBus.on('room:assigned', handleRoomAssigned);
    
    return () => {
      unsubRoomAssigned();
    };
  }, [updateUIFromGlobalMaps]);

  // useEffect para manejar la actualización de la UI desde los mapas globales
  useEffect(() => {
    let updateInterval: NodeJS.Timeout | null = null;
    const UPDATE_INTERVAL_MS = 1000; // Máximo 1 segundo para telemetría en tiempo real
    
    // Solo activar el interval si NO estamos mostrando GroupsModal
    if (token && boards.length > 0 && !showGroupsModal) {
      console.log('🔧 [INTERVAL] Iniciando setInterval para updateUIFromGlobalMaps cada ' + UPDATE_INTERVAL_MS + 'ms');
      updateInterval = setInterval(() => {
        updateUIFromGlobalMaps();
      }, UPDATE_INTERVAL_MS);
    }
    
    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
    };
  }, [token, boards.length, showGroupsModal, updateUIFromGlobalMaps]);

  // useEffect para verificación inmediata de WebSocket al activar la PWA
  useEffect(() => {
    const verificarYReconectarWebSocket = async () => {
      // Durante login, solo handleLogin gestiona la conexión
      if (isLoggingInRef.current) return;
      if (!token) return;

      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        // Sesión ya cerrada (timeout/logout) pero token React obsoleto — sincronizar
        setToken(null);
        setWebsocketStatus('disconnected');
        return;
      }

      if (pwaWsReconnectLockRef.current) return;
      pwaWsReconnectLockRef.current = true;

      try {
        syncServerUrlWithCurrentOrigin();
        getListenerPool();

        setWebsocketStatus('connecting');
        (window as any).websocketStatus = 'connecting';
        eventBus.emit('websocket:connecting');

        if (!WebSocketService.isConnected()) {
          WebSocketService.disconnect({ silent: true });
          await WebSocketService.connect(storedToken);
        } else {
          WebSocketService.setupControlResponseListener();
        }

        const ready = await WebSocketService.waitUntilReady(5000);
        if (ready || WebSocketService.isConnected()) {
          setWebsocketStatus('connected');
          (window as any).websocketStatus = 'connected';
          eventBus.emit('websocket:ready');
        }

        eventBus.emit('estructura:refrescar');
      } catch {
        // Mantener connecting; el watchdog/health-check reintentará
        setWebsocketStatus('connecting');
        (window as any).websocketStatus = 'connecting';
        eventBus.emit('websocket:connecting');
        setTimeout(() => {
          WebSocketService.connect(storedToken).catch(() => {});
        }, 1000);
      } finally {
        pwaWsReconnectLockRef.current = false;
      }
    };

    const scheduleVerificarYReconectar = () => {
      if (pwaWsDebounceTimerRef.current) {
        clearTimeout(pwaWsDebounceTimerRef.current);
      }
      pwaWsDebounceTimerRef.current = setTimeout(() => {
        verificarYReconectarWebSocket();
      }, 300);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleVerificarYReconectar();
      }
    };

    const handleFocus = () => {
      scheduleVerificarYReconectar();
    };

    const handlePageShow = () => {
      scheduleVerificarYReconectar();
    };

    const handleOnline = () => {
      scheduleVerificarYReconectar();
    };

    const handlePageHide = () => {
      // La app está a punto de suspenderse; los timers se pausan automáticamente
    };

    const handleFreeze = () => {
      // La app se congela (Chrome/Android)
    };

    const handleResume = () => {
      scheduleVerificarYReconectar();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('online', handleOnline);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('freeze', handleFreeze);
    document.addEventListener('resume', handleResume);

    return () => {
      if (pwaWsDebounceTimerRef.current) {
        clearTimeout(pwaWsDebounceTimerRef.current);
        pwaWsDebounceTimerRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('freeze', handleFreeze);
      document.removeEventListener('resume', handleResume);
    };
  }, [token]);

  // Exponer estado global para que DeviceCard pueda acceder (sin re-suscribir listeners WS)
  useEffect(() => {
    (window as any).websocketStatus = websocketStatus;
  }, [websocketStatus]);

  // useEffect para escuchar eventos de WebSocket y actualizar el indicador visual
  useEffect(() => {
    // SOLUCIÓN 5: Escuchar 'websocket:ready' en lugar de 'websocket:reconnected'
    // Esto asegura que solo se marque como 'connected' cuando realmente esté listo
    const handleWebSocketReady = () => {
      // SOLUCIÓN 5: Solo marcar como 'connected' cuando se reciba 'websocket:ready'
      // Esto garantiza que setupControlResponseListener() y el pool de listeners estén listos
      setWebsocketStatus('connected');
      (window as any).websocketStatus = 'connected';
    };
    
    // Mantener handleWebSocketConnected para 'websocket:reconnected' pero solo para actualizar estado intermedio
    const handleWebSocketConnected = () => {
      // Cuando se recibe 'websocket:reconnected', marcar como 'connecting' (no 'connected' aún)
      // El estado 'connected' solo se establecerá cuando se reciba 'websocket:ready'
      setWebsocketStatus('connecting');
      (window as any).websocketStatus = 'connecting';
    };

    const handleWebSocketDisconnected = (reason: string) => {
      console.log('🔴 WebSocket desconectado - actualizando indicador:', reason);
      setWebsocketStatus('disconnected');
      (window as any).websocketStatus = 'disconnected';
    };

    const handleWebSocketError = (error: Error) => {
      console.log('⚠️ Error WebSocket - actualizando indicador:', error);
      setWebsocketStatus('disconnected');
      (window as any).websocketStatus = 'disconnected';
    };
    
    const handleWebSocketConnecting = () => {
      setWebsocketStatus('connecting');
      (window as any).websocketStatus = 'connecting';
    };

    // CRÍTICO: Manejar respuestas de control después de reconexión
    const handleDeviceControlResponse = (data: any) => {
      // SOLUCIÓN 2: Actualizar estado del dispositivo inmediatamente cuando llega confirmación exitosa
      if (data.success && data.device_id) {
        const device = mapaDispositivos[data.device_id];
        if (device) {
          // Calcular el nuevo estado
          const newState = data.new_state !== undefined ? data.new_state : !device.estado;
          
          // Registrar timestamp de control reciente para protección
          // Esto protege el dispositivo de ser sobrescrito por refrescarEstructuraCompleta
          // durante los próximos 3 segundos
          recentControlResponses.set(data.device_id, Date.now());
          
          // Limpiar el flag inmediatamente después de procesar device_update (no usar timer)
          // Se limpiará cuando llegue device_update del WebSocket
          
          // Actualizar el estado del dispositivo inmediatamente en mapaDispositivos
          mapaDispositivos[data.device_id] = {
            ...device,
            estado: newState,
            online: true,
            auth_required: device.auth_required ?? false,
            auth_configured: device.auth_configured ?? false,
            pin_required: device.pin_required ?? false
          };
          
          // Emitir evento device:update inmediatamente para actualizar la UI
          const updateEvent = {
            id: data.device_id,
            estado: newState,
            online: true,
            consumo: device.consumo,
            mac: device.mac,
            tipo: device.tipo,
            // Campos especiales según tipo
            ...(device.tipo === 'DIMMER2' && device.lights_brightness !== undefined ? { lights_brightness: device.lights_brightness } : {}),
            ...(device.tipo === 'PLUS2PM' ? { 
              cover_0_state: device.cover_0_state,
              metadata: device.metadata
            } : {})
          };
          
          eventBus.emit('device:update', updateEvent);
          
          // Solo en flanco del control: ON = auto-off; OFF = auto-on
          const timerCfg = device.timer_config;
          const turnedOn = isDevicePowerOn(newState);
          if (timerCfg?.enabled) {
            if (turnedOn && timerCfg.autoOffEnabled) {
              eventBus.emit('timer_activated', {
                device_id: data.device_id,
                timer_config: timerCfg,
                isActive: true,
                startTime: Date.now()
              });
            } else if (!turnedOn && timerCfg.autoOnEnabled) {
              eventBus.emit('timer_activated', {
                device_id: data.device_id,
                timer_config: timerCfg,
                isActive: true,
                startTime: Date.now()
              });
            } else {
              eventBus.emit('timer_deactivated', {
                device_id: data.device_id,
                isActive: false
              });
            }
          } else {
            eventBus.emit('timer_deactivated', {
              device_id: data.device_id,
              isActive: false
            });
          }
        }
      }
      
      // Manejar errores de permisos
      if (!data.success && data.error) {
        const errorMessage = data.error.toLowerCase();
        if (errorMessage.includes('permiso') || errorMessage.includes('permission') || errorMessage.includes('no tiene permisos') || data.status === 403) {
          // Mostrar mensaje de error de permisos
          setPermissionSnackbar({
            open: true,
            message: t('permissions.noPermission')
          });
          
          // Limpiar estado de confirmación
          if (data.device_id) {
            setConfirmingDevices(prev => {
              const newSet = new Set(prev);
              newSet.delete(data.device_id);
              return newSet;
            });
            (window as any).confirmingDevices.delete(data.device_id);
            
            // Mostrar error visual
            setDeviceErrors(prev => new Set(prev).add(data.device_id));
            setTimeout(() => {
              setDeviceErrors(prev => {
                const newErrors = new Set(prev);
                newErrors.delete(data.device_id);
                return newErrors;
              });
            }, 3000);
          }
          
          // Liberar listener
          if (data.device_id) {
            WebSocketService.releaseListener(data.device_id);
          }
          
          return; // Salir temprano, no procesar más
        }
      }
      
      // Limpiar estado de confirmación y error cuando llega respuesta del backend
      if (data.device_id) {
        // Obtener tipo de dispositivo para manejo especial de PLUS1
        const device = mapaDispositivos[data.device_id];
        const isPlus1 = device?.tipo === 'PLUS1';
        
        // LÓGICA ESPECIAL PARA PLUS1: Feedback extendido de 2 segundos
        if (isPlus1 && data.success) {
          // 1. Limpiar de confirmingDevices (quitar spinner)
          setConfirmingDevices(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.device_id);
            return newSet;
          });
          (window as any).confirmingDevices.delete(data.device_id);
          
          // 2. Agregar a confirmedDevices (mostrar botón blanco + anillo azul)
          setConfirmedDevices(prev => new Set(prev).add(data.device_id));
          
          // 3. Después de 2 segundos, limpiar confirmedDevices
          setTimeout(() => {
            setConfirmedDevices(prev => {
              const newSet = new Set(prev);
              newSet.delete(data.device_id);
              return newSet;
            });
          }, 2000);
        } else {
          // COMPORTAMIENTO NORMAL para otros dispositivos o si hay error
          setConfirmingDevices(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.device_id);
            return newSet;
          });
          (window as any).confirmingDevices.delete(data.device_id);
        }
        
        // Siempre limpiar errores
        setDeviceErrors(prev => {
          const newErrors = new Set(prev);
          newErrors.delete(data.device_id);
          return newErrors;
        });
        (window as any).deviceErrors.delete(data.device_id);
        
        // Establecer o limpiar coverActions cuando llega confirmación del backend
        setPendingCoverActions(prev => {
          const pendingAction = prev[data.device_id];
          if (pendingAction) {
            if (pendingAction === 'stop') {
              // Limpiar coverActions cuando se confirma el comando de stop
              setCoverActions(prev => {
                const newActions = { ...prev };
                delete newActions[data.device_id];
                return newActions;
              });
              // CRÍTICO: También limpiar en variables globales
              delete (window as any).coverActions[data.device_id];
            } else {
              // Solo establecer coverActions cuando se confirma comando de up/down Y success es true
              if (data.success) {
                setCoverActions(prev => ({ ...prev, [data.device_id]: pendingAction }));
                // CRÍTICO: También establecer en variables globales
                (window as any).coverActions[data.device_id] = pendingAction;
              }
            }
            // Limpiar la acción pendiente
            const newPending = { ...prev };
            delete newPending[data.device_id];
            return newPending;
          } else {
            return prev; // No cambiar el estado si no hay acción pendiente
          }
        });
        
        // Limpiar timeout
        setDeviceTimeouts(prev => {
          const newTimeouts = { ...prev };
          if (newTimeouts[data.device_id]) {
            clearTimeout(newTimeouts[data.device_id]);
            delete newTimeouts[data.device_id];
          }
          return newTimeouts;
        });
        
        // POOL DE LISTENERS: Liberar listener cuando llega respuesta
        WebSocketService.releaseListener(data.device_id);

        const auditPending = assistantAuditRef.current;
        if (auditPending && auditPending.device_id === data.device_id) {
          assistantAuditRef.current = null;
          const deviceName =
            auditPending.device_name ||
            mapaDispositivos[data.device_id]?.nombre ||
            `Dispositivo ${data.device_id}`;
          let resultMsg = '';
          if (data.success) {
            const rawState = data.new_state;
            const isOn =
              rawState === true ||
              rawState === 'on' ||
              rawState === 1 ||
              String(rawState).toLowerCase() === 'true';
            const dispInfo = mapaDispositivos[data.device_id] as
              | DispositivoConConsumo
              | undefined;
            const controlDevice = {
              tipo: dispInfo?.tipo ?? null,
              artifact: dispInfo?.tipo_artefacto ?? null,
              brightness:
                data.lights_brightness ?? dispInfo?.lights_brightness ?? null,
              coverPosition:
                data.cover_position ?? dispInfo?.cover_position ?? null,
              consumoW: data.consumo ?? dispInfo?.consumo ?? null,
            };
            resultMsg = formatAssistantControlSuccess(
              deviceName,
              auditPending.control_action,
              isOn,
              {
                requires_pin: auditPending.requires_pin,
                has_cameras: auditPending.has_cameras,
              },
              auditPending.idioma === 'en' ? 'en' : 'es',
              undefined,
              controlDevice
            );
          } else {
            const err =
              typeof data.error === 'string' && data.error.trim()
                ? data.error.trim()
                : '';
            resultMsg = err
              ? `No se pudo controlar «${deviceName}»: ${err}`
              : `No se pudo controlar «${deviceName}».`;
          }
          void auditAssistantControl({
            dispositivo_id: auditPending.device_id,
            tool_logica: auditPending.logical_tool,
            accion_pwa: auditPending.accion_pwa,
            accion_valor: auditPending.accion_valor,
            frase_usuario: auditPending.frase_usuario,
            exito: !!data.success,
            mensaje_resultado: resultMsg,
            idioma: auditPending.idioma,
            estado_nuevo: data.success ? data.new_state : null,
          }).catch(() => {});
          eventBus.emit('assistant:control_result', {
            device_id: data.device_id,
            success: !!data.success,
            message: resultMsg,
          });
        }
      }
    };

    // Suscribirse a eventos del WebSocket
    // SOLUCIÓN 5: Escuchar 'websocket:ready' para marcar como 'connected' solo cuando esté listo
    eventBus.on('websocket:ready', handleWebSocketReady);
    
    // Mantener listener de 'websocket:reconnected' para actualizar estado intermedio
    eventBus.on('websocket:reconnected', handleWebSocketConnected);
    eventBus.on('websocket:disconnected', handleWebSocketDisconnected);
    eventBus.on('websocket:error', handleWebSocketError);
    eventBus.on('websocket:connecting', handleWebSocketConnecting);
    eventBus.on('device_control_response', handleDeviceControlResponse);

    // Suscribirse a eventos de timer
    const handleTimerActivated = (data: any) => {
      console.log('🕐 Timer activado en dispositivo:', data.device_id);
      setDeviceTimerStates(prev => ({
        ...prev,
        [data.device_id]: {
          isTimerActive: true,
          timerStartTime: data.startTime || Date.now()
        }
      }));
    };

    const handleTimerDeactivated = (data: any) => {
      console.log('🛑 Timer desactivado en dispositivo:', data.device_id);
      setDeviceTimerStates(prev => ({
        ...prev,
        [data.device_id]: {
          isTimerActive: false,
          timerStartTime: undefined
        }
      }));
    };

    const handleSchedulesUpdated = (data: any) => {
      console.log('🔄 [APP] Configuraciones actualizadas:', data);
      
      // ACTUALIZAR INMEDIATAMENTE el estado devices con la nueva configuración
      setDevices(prev => prev.map(device => 
        device.id === data.id 
          ? { 
              ...device, 
              timer_config: data.timer_config,
              agenda_config: data.agenda_config
            }
          : device
      ));
      
      // Si había countdown corriendo, detenerlo al cambiar config.
      // Solo reiniciar auto-off si sigue ENCENDIDO (edición mid-timer).
      // Nunca arrancar auto-on solo por guardar: requiere flanco ON→OFF.
      if (deviceTimerStatesRef.current[data.id]?.isTimerActive) {
        setDeviceTimerStates(prev => ({
          ...prev,
          [data.id]: {
            isTimerActive: false,
            timerStartTime: undefined
          }
        }));
        
        setTimeout(() => {
          const device = mapaDispositivos[data.id];
          const cfg = data.timer_config;
          if (
            device &&
            cfg?.enabled &&
            cfg.autoOffEnabled &&
            isDevicePowerOn(device.estado)
          ) {
            eventBus.emit('timer_activated', {
              device_id: data.id,
              timer_config: cfg,
              isActive: true,
              startTime: Date.now()
            });
          }
        }, 50);
      }
      
      console.log('✅ [APP] Estado devices actualizado inmediatamente para dispositivo:', data.id);
    };

    eventBus.on('timer_activated', handleTimerActivated);
    eventBus.on('timer_deactivated', handleTimerDeactivated);
    eventBus.on('device:schedules_updated', handleSchedulesUpdated);
    // NOTA: handleDeviceControlResponse ya está registrado en la línea 4881, no duplicar aquí

    return () => {
      // SOLUCIÓN 5: Remover listener de 'websocket:ready'
      eventBus.off('websocket:ready', handleWebSocketReady);
      
      // Remover listener de 'websocket:reconnected'
      eventBus.off('websocket:reconnected', handleWebSocketConnected);
      eventBus.off('websocket:disconnected', handleWebSocketDisconnected);
      eventBus.off('websocket:error', handleWebSocketError);
      eventBus.off('websocket:connecting', handleWebSocketConnecting);
      eventBus.off('device_control_response', handleDeviceControlResponse);
      eventBus.off('timer_activated', handleTimerActivated);
      eventBus.off('timer_deactivated', handleTimerDeactivated);
      eventBus.off('device:schedules_updated', handleSchedulesUpdated);
    };
  }, [t]); // No re-suscribir en cada cambio de color del indicador WS

  // useEffect para limpiar WebSocket al desmontar
  useEffect(() => {
    return () => {
      WebSocketService.disconnect();
    };
  }, []);
  
  // ✅ FIX MEMORY LEAK: Limpieza periódica de cachés cada 60 segundos
  useEffect(() => {
    const cleanupInterval = window.setInterval(() => {
      // Limpiar cameraConfigCache si tiene más de 50 entradas
      if (cameraConfigCache.current.size > 50) {
        const entriesToDelete = cameraConfigCache.current.size - 50;
        let deleted = 0;
        for (const key of cameraConfigCache.current.keys()) {
          if (deleted >= entriesToDelete) break;
          cameraConfigCache.current.delete(key);
          deleted++;
        }
      }
      // Limpiar deviceCardWidthCache si tiene más de 100 entradas
      if (deviceCardWidthCache.current.size > 100) {
        const entriesToDelete = deviceCardWidthCache.current.size - 100;
        let deleted = 0;
        for (const key of deviceCardWidthCache.current.keys()) {
          if (deleted >= entriesToDelete) break;
          deviceCardWidthCache.current.delete(key);
          deleted++;
        }
      }
    }, 60000); // Limpiar cada 60 segundos
    
    return () => window.clearInterval(cleanupInterval);
  }, []);

  // useEffect para persistir estados de timers en localStorage
  useEffect(() => {
    try {
      localStorage.setItem('deviceTimerStates', JSON.stringify(deviceTimerStates));
      console.log('💾 [TIMER] Estados de timers guardados en localStorage');
    } catch (error) {
      console.error('❌ [TIMER] Error guardando estados de timers:', error);
    }
  }, [deviceTimerStates]);

  // Funciones de carga de datos
  // 🚀 Función para precargar todas las páginas en background
  // Esta función se ejecuta después de que la página de inicio esté visible
  // para precargar componentes y datos de todas las demás páginas
  const preloadAllPagesInBackground = async () => {
    try {
      console.log('🚀 [PRECARGA] Iniciando precarga de páginas en background...');
      
      // 1. Precargar componentes lazy en paralelo
      await preloadLazyComponents();
      
      // 2. Precargar datos necesarios para cada página
      // Precargar habitaciones de todos los tableros (excepto el primero que ya está cargado)
      if (boards.length > 1) {
        // Precargar habitaciones de los demás tableros en paralelo
        const otherBoards = boards.slice(1);
        await Promise.all(
          otherBoards.map(board => {
            // Solo precargar los datos, no actualizar el estado
            return ApiService.getBoardRooms(board.id).catch(() => {
              // Ignorar errores en precarga
            });
          })
        );
      }
      
      // 3. Si hay más tableros, precargar sus habitaciones también
      // Esto asegura que cuando el usuario cambie de tablero, los datos ya estén listos
      if (boards.length > 0) {
        // Precargar dispositivos de las habitaciones del primer tablero (si no están ya cargados)
        const firstBoardRooms = mapaTableros[boards[0]?.id]?.habitaciones || [];
        if (firstBoardRooms.length > 0) {
          // Precargar dispositivos de algunas habitaciones (no todas para no sobrecargar)
          const roomsToPreload = firstBoardRooms.slice(0, 3); // Precargar solo las primeras 3
          await Promise.all(
            roomsToPreload.map(roomId => {
              return ApiService.getRoomDevices(roomId).catch(() => {
                // Ignorar errores en precarga
              });
            })
          );
        }
      }
      
      console.log('✅ [PRECARGA] Precarga de páginas completada');
    } catch (error) {
      console.error('❌ [PRECARGA] Error en precarga:', error);
      // No mostrar error al usuario, es solo precarga en background
    }
  };

  // Esta función carga habitaciones de un tablero específico
  // Para usuarios normales, filtra habitaciones por permisos del usuario
  // ✅ OPTIMIZACIÓN: Declarar antes de loadBoards para evitar errores de hoisting
  // Obtiene (sin tocar el estado `rooms`) las habitaciones del tablero, ya filtradas por permisos.
  // Se usa tanto para renderizar la grilla como para resolver la habitación de inicio (home_room)
  // y para los diálogos de selección de página de inicio.
  const fetchRoomsForBoard = async (boardId: number): Promise<HabitacionConConsumo[]> => {
    try {
      // 🔧 SOLUCIÓN: Usar APIs directamente si los mapas no están disponibles
      const tableroData = mapaTableros[boardId];
      
      if (!tableroData || Object.keys(mapaHabitaciones).length === 0) {
        // Usar APIs directamente como fallback
        const habitacionesTablero = await ApiService.getBoardRooms(boardId);
        
        // CRITICO: Solo admin tiene acceso total sin permisos granulares
        if (userRole === 'admin') {
          return habitacionesTablero;
        }
        // Para usuarios normales y demo (ambos modos), filtrar por permisos usando caché
        let allowedRoomIds: number[] = [];
        if (cachedUserPermissions) {
          allowedRoomIds = cachedUserPermissions.allowedRoomIds;
        } else {
          const habitacionesPermitidas = await ApiService.getHabitaciones();
          allowedRoomIds = habitacionesPermitidas.map((h: any) => h.id);
        }
        return habitacionesTablero.filter((hab: any) => allowedRoomIds.includes(hab.id));
      }
      
      // Si los mapas están disponibles, usarlos (comportamiento optimizado)
      const habitacionesIds = Array.from(new Set(tableroData.habitaciones));
      
      const construirHabitaciones = (ids: number[]): HabitacionConConsumo[] =>
        ids.map(roomId => {
          const habitacionData = mapaHabitaciones[roomId];
          if (habitacionData) {
            return {
              ...habitacionData.datos,
              consumo: habitacionData.consumoTotal
            };
          }
          return null;
        }).filter((room): room is any => room !== null);
      
      // CRITICO: Solo admin tiene acceso total sin permisos granulares
      if (userRole === 'admin') {
        return construirHabitaciones(habitacionesIds);
      }
      // Para usuarios normales, filtrar por permisos can_view_room usando caché
      try {
        let allowedRoomIds: number[] = [];
        
        if (cachedUserPermissions) {
          allowedRoomIds = cachedUserPermissions.allowedRoomIds;
        } else {
          const user = getUser();
          if (!user || !user.id) {
            return [];
          }
          
          const userPermissions = await getUserPermissions(user.id);
          if (userPermissions.permissions && Array.isArray(userPermissions.permissions)) {
            allowedRoomIds = userPermissions.permissions
              .filter((p: any) => p.can_view_room)
              .map((p: any) => p.room_id);
          } else {
            allowedRoomIds = userPermissions.room_ids || [];
          }
          
          setCachedUserPermissions({
            allowedRoomIds,
            permissions: userPermissions.permissions,
            room_ids: userPermissions.room_ids
          });
        }
        
        return construirHabitaciones(habitacionesIds.filter(roomId => allowedRoomIds.includes(roomId)));
      } catch (error) {
        return construirHabitaciones(habitacionesIds);
      }
    } catch (error) {
      return [];
    }
  };

  const loadRoomsForBoard = async (boardId: number): Promise<HabitacionConConsumo[]> => {
    const habitaciones = await fetchRoomsForBoard(boardId);
    setRooms(habitaciones);
    return habitaciones;
  };

  const fetchRoomsForBoardRef = useRef(fetchRoomsForBoard);
  fetchRoomsForBoardRef.current = fetchRoomsForBoard;
  const loadRoomsForBoardRef = useRef(loadRoomsForBoard);
  loadRoomsForBoardRef.current = loadRoomsForBoard;

  // Guarda la selección de home (page + tab + room) en backend y estado, y navega a ella.
  // Centraliza la lógica para las opciones nuevas (Asistente y habitación dentro del tablero).
  // Único camino que puede poner home_page=null: el usuario elige "Ninguna" en el menú/diálogo.
  const saveHomeSelection = async (
    homePage: string | null,
    homeTab: number | null,
    homeRoom: number | null
  ) => {
    if (!userData?.id) return;
    try {
      await updateUserHomePage(userData.id, homePage, homeTab, homeRoom);
      setUserHomePage(homePage);
      setUserHomeTab(homeTab);
      setUserHomeRoom(homeRoom);
      localStorage.removeItem('user_chose_none_homepage_v2');
      const updatedUserData = { ...userData, home_page: homePage, home_tab: homeTab, home_room: homeRoom };
      localStorage.setItem('user', JSON.stringify(updatedUserData));
      setUserData(updatedUserData);

      // Cerrar diálogos
      setShowHomePageDialog(false);
      setShowHomePageMenu(false);
      setShowHomeTabDialog(false);
      setSelectedPageForTab(null);
      setSelectedBoardForTab(null);

      // Navegar a la página elegida
      if (homePage === 'dashboard' && homeTab !== null && homeTab !== undefined) {
        const boardIndex = boards.findIndex((b: any) => b.id === homeTab);
        const idx = boardIndex !== -1 ? boardIndex : 0;
        const boardId = boards[idx]?.id ?? homeTab;
        setCurrentScreen('dashboard');
        setSelectedTab(idx);
        roomsLoadedManuallyRef.current = true;
        const boardRooms = await fetchRoomsForBoard(boardId);
        setRooms(boardRooms);
        if (homeRoom !== null && homeRoom !== undefined) {
          const targetRoom = boardRooms.find((r: any) => r.id === homeRoom) || null;
          setSelectedRoom(targetRoom);
          setIsHomeActive(!targetRoom);
        } else {
          setSelectedRoom(null);
          setIsHomeActive(true);
        }
      } else if (homePage === 'assistant') {
        setSelectedRoom(null);
        setCurrentScreen('assistant');
        setIsHomeActive(false);
      } else if (homePage === 'widgets') {
        setSelectedRoom(null);
        setCurrentScreen('widgets');
        setIsHomeActive(false);
      } else if (homePage) {
        setSelectedRoom(null);
        setCurrentScreen(resolveAllowedScreen(homePage, userData as any));
        setIsHomeActive(false);
      }
    } catch (error) {
      console.error('Error al guardar página de inicio:', error);
    }
  };

  // Esta función ahora filtra tableros por permisos del usuario
  // - Administradores ven todos los tableros
  // - Usuarios normales solo ven tableros que contengan habitaciones permitidas
  // 🔧 RETORNA los boards cargados para poder usarlos inmediatamente
  const loadBoards = async (_tieneFavoritosParam?: boolean, skipInitialRoomLoad: boolean = false): Promise<any[]> => {
    try {
      // CRITICO: Solo admin tiene acceso total sin permisos granulares
      // Los usuarios demo (tanto en modo user como admin) necesitan permisos granulares explícitos (igual que usuarios normales)
      // La única diferencia es que demo en modo admin puede ver Settings
      if (userRole === 'admin') {
        // Para administradores, mostrar todos los tableros
        const boardsData = await ApiService.getBoards();
        
        console.log('[FAVORITOS] 📋 Admin - boardsData:', boardsData.length, 'tableros');
        
        // SOLUCIÓN CRÍTICA: Establecer boards siempre, pero NUNCA cargar habitaciones si skipInitialRoomLoad es true
        // Si skipInitialRoomLoad es true, significa que hay un homeTab y las habitaciones se cargarán después
        setBoards(boardsData);
        
        // SOLUCIÓN: Solo cargar habitaciones del primer tablero si NO se debe saltar Y no estamos en carga inicial
        // (skipInitialRoomLoad se usa cuando hay un homeTab definido y se cargará después)
        // IMPORTANTE: Limpiar habitaciones antes para evitar mostrar las del tab anterior
        if (boardsData.length > 0 && !skipInitialRoomLoad && !isInitialLoadInProgressRef.current) {
          setRooms([]); // Limpiar antes de cargar nuevas habitaciones
          await loadRoomsForBoard(boardsData[0].id);
        } else {
          // Si se debe saltar o estamos en carga inicial, asegurarse de que las habitaciones estén limpias
          setRooms([]);
        }
        
        return boardsData; // 🔧 Retornar los boards cargados
      } else {
        // Para usuarios normales y demo, filtrar tableros por permisos can_view_room
        // 1. Obtener todos los tableros y permisos granulares del usuario
        const user = getUser();
        if (!user || !user.id) {
          setBoards([]);
          return [];
        }
        
        // ✅ OPTIMIZACIÓN: Usar permisos cacheados si están disponibles
        let allowedRoomIds: number[] = [];
        let userPermissions: any = null;
        let todosLosTableros: any[] = [];
        
        if (cachedUserPermissions) {
          // Cargar tableros en paralelo con permisos cacheados
          todosLosTableros = await ApiService.getBoards();
          // Usar permisos cacheados (más rápido)
          allowedRoomIds = cachedUserPermissions.allowedRoomIds;
          userPermissions = {
            permissions: cachedUserPermissions.permissions,
            room_ids: cachedUserPermissions.room_ids
          };
        } else {
          // Si no hay caché, cargar permisos y tableros en paralelo (fallback)
          const [tablerosData, userPerms] = await Promise.all([
            ApiService.getBoards(),
            getUserPermissions(user.id)
          ]);
          todosLosTableros = tablerosData;
          userPermissions = userPerms;
          
          // Extraer room_ids donde tiene can_view_room
          if (userPermissions.permissions && Array.isArray(userPermissions.permissions)) {
            allowedRoomIds = userPermissions.permissions
              .filter((p: any) => p.can_view_room)
              .map((p: any) => p.room_id);
          } else {
            // Compatibilidad: si solo hay room_ids, asumir que tiene can_view_room
            allowedRoomIds = userPermissions.room_ids || [];
          }
          
          // Cachear permisos para próximas llamadas
          setCachedUserPermissions({
            allowedRoomIds,
            permissions: userPermissions.permissions,
            room_ids: userPermissions.room_ids
          });
        }
        
        // 3. Crear un mapa de habitaciones permitidas por ID para búsqueda rápida
        const habitacionesPermitidasMap = new Map();
        allowedRoomIds.forEach((roomId: number) => {
          habitacionesPermitidasMap.set(roomId, true);
        });
        
        // 3. Comprobar tablero por tablero cuáles tienen habitaciones permitidas
        // ⚡ OPTIMIZACIÓN: Hacer llamadas en paralelo en lugar de secuencial
        const tablerosConHabitaciones = await Promise.all(
          todosLosTableros.map(async (tablero: any) => {
          const habitacionesTablero = await ApiService.getBoardRooms(tablero.id);
            return { tablero, habitaciones: habitacionesTablero };
          })
        );
        
        const tablerosConPermiso = tablerosConHabitaciones
          .filter(({ tablero, habitaciones }) => 
            habitaciones.some((hab: any) => habitacionesPermitidasMap.has(hab.id))
          )
          .map(({ tablero }) => tablero);
        
        console.log('🔐 [PERMISOS] Tableros permitidos para usuario:', tablerosConPermiso.length, 'de', todosLosTableros.length);
        console.log('🔐 [PERMISOS] Tableros permitidos:', tablerosConPermiso.map((t: any) => t.nombre));
        console.log('🔐 [PERMISOS] Habitaciones permitidas:', habitacionesPermitidasMap.size);
        
        // SOLUCIÓN CRÍTICA: Establecer boards siempre, pero NUNCA cargar habitaciones si skipInitialRoomLoad es true
        // Si skipInitialRoomLoad es true, significa que hay un homeTab y las habitaciones se cargarán después
        setBoards(tablerosConPermiso);
        
        // SOLUCIÓN: Solo cargar habitaciones del primer tablero si NO se debe saltar Y no estamos en carga inicial
        // (skipInitialRoomLoad se usa cuando hay un homeTab definido y se cargará después)
        // IMPORTANTE: Limpiar habitaciones antes para evitar mostrar las del tab anterior
        if (tablerosConPermiso.length > 0 && !skipInitialRoomLoad && !isInitialLoadInProgressRef.current) {
          setRooms([]); // Limpiar antes de cargar nuevas habitaciones
          await loadRoomsForBoard(tablerosConPermiso[0].id);
        } else {
          // Si se debe saltar o estamos en carga inicial, asegurarse de que las habitaciones estén limpias
          setRooms([]);
        }
        
        return tablerosConPermiso; // 🔧 Retornar los boards cargados
      }
    } catch (error) {
      console.error('Error loading boards:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const loadBoardsRef = useRef(loadBoards);
  loadBoardsRef.current = loadBoards;

  const ensureHouseReady = useCallback(async () => {
    const storedToken = localStorage.getItem('token') || token;
    const user = userData || getUser();
    if (!storedToken || !user?.id) return;
    if (houseEnsureInFlightRef.current) return;
    houseEnsureInFlightRef.current = true;
    try {
      ApiService.setToken(storedToken);
      isInitialLoadInProgressRef.current = false;
      void connectWebSocketWithRetry(storedToken);
      await refrescarEstructuraCompleta();
      cargarMapasNoShelly();
      try {
        const [favoritesData, favoriteGroupsData] = await Promise.all([
          getUserFavorites(user.id, true),
          getUserFavoriteGroups(user.id, true),
        ]);
        setUserFavorites(favoritesData);
        setHasFavorites(favoritesData.length > 0 || favoriteGroupsData.length > 0);
        const favDevices = favoritesData
          .map((fav: { dispositivo?: any; device_id: number; orden?: number }) => {
            const dispositivo = fav.dispositivo;
            if (!dispositivo) return null;
            return {
              ...dispositivo,
              pin_required: dispositivo.pin_required ?? false,
              auth_required: dispositivo.auth_required ?? false,
              auth_configured: dispositivo.auth_configured ?? false,
              is_favorite: true,
              favorite_orden: fav.orden ?? 0,
            };
          })
          .filter(Boolean);
        setFavoriteDevices(favDevices as any);
      } catch {
        /* favoritos no bloquean tableros */
      }
      const homeTab = user.home_tab;
      const homePage = user.home_page;
      const skipInitial = homePage === 'dashboard' && homeTab != null;
      const loaded = await loadBoardsRef.current(false, skipInitial);
      if (homePage === 'dashboard' && homeTab != null && loaded.length) {
        const idx = loaded.findIndex((b: { id: number }) => b.id === homeTab);
        const useIdx = idx >= 0 ? idx : 0;
        setSelectedTab(useIdx);
        const boardRooms = await fetchRoomsForBoardRef.current(loaded[useIdx].id);
        setRooms(boardRooms);
        setSelectedRoom(null);
        homeTabAppliedRef.current = true;
        correctTabEstablishedRef.current = true;
        setHomeTabInitialized(true);
        roomsLoadedManuallyRef.current = true;
      } else if (loaded.length) {
        setSelectedTab(0);
        await loadRoomsForBoardRef.current(loaded[0].id);
      }
      inicializarConsumoTiempoReal(true);
      setInitialLoadComplete(true);
    } catch {
      /* ignore */
    } finally {
      houseEnsureInFlightRef.current = false;
      isInitialLoadInProgressRef.current = false;
    }
  }, [token, userData]);
  ensureHouseReadyRef.current = ensureHouseReady;

  // Documento nuevo o casa vacía tras una llamada: recargar mapas + tableros.
  useEffect(() => {
    if (!token || !userData?.id || isLoggingIn) return;
    if (doorbellIsland || isDoorbellIncomingContext()) return;
    const needHouse =
      currentScreen === 'dashboard'
      || currentScreen === 'devices'
      || currentScreen === 'favorites'
      || currentScreen === 'reports'
      || currentScreen === 'realtime';
    if (!needHouse) return;
    if (boards.length > 0) return;
    void ensureHouseReady();
  }, [token, userData?.id, boards.length, currentScreen, isLoggingIn, doorbellIsland, ensureHouseReady]);

  // useEffect para escuchar eventos de actualización de favoritos
  useEffect(() => {
    const handleFavoritosActualizados = async () => {
      // Recargar favoritos
      if (userData?.id) {
        try {
          const sonosFavPromise = canViewMusic(userData || getUser())
            ? Promise.all([
                getFavorites().catch(() => [] as number[]),
                getSonosFavoritesOrden().catch(() => new Map<number, number>()),
              ])
            : Promise.resolve([[] as number[], new Map<number, number>()] as const);
          const [favoritesData, favoriteGroups, sonosFavResult] = await Promise.all([
            getUserFavorites(userData.id, true),
            (async () => {
              const { getUserFavoriteGroups } = await import('./services/api');
              return getUserFavoriteGroups(userData.id, true);
            })(),
            sonosFavPromise,
          ]);
          const [sonosFavIds, sonosOrdenMap] = sonosFavResult;
          
          const tieneFavs = favoritesData.length > 0 || favoriteGroups.length > 0 || sonosFavIds.length > 0;
          
          setSonosFavorites(sonosFavIds);
          setSonosFavoriteOrden(Object.fromEntries(sonosOrdenMap));
          const favSonos = sonosFavIds
            .map((id) => mapaSonos[id])
            .filter((s): s is SonosSpeaker => !!s);
          setFavoriteSonosSpeakers(favSonos);
          
          setUserFavorites(favoritesData);
          setHasFavorites(tieneFavs);
          
          // Extraer dispositivos de los favoritos y ordenarlos
          const favDevices: DispositivoConConsumo[] = favoritesData
            .map(fav => {
              const dispositivo = fav.dispositivo;
              if (!dispositivo) return null;
              // Obtener dispositivo actualizado de mapaDispositivos para asegurar propiedades actualizadas como pin_required
              const dispositivoActualizado = mapaDispositivos[fav.device_id] || dispositivo;
              // Asegurar que pin_required, auth_required e is_favorite estén presentes y correctamente mapeados
              return {
                ...dispositivoActualizado,
                pin_required: dispositivoActualizado.pin_required ?? false,
                auth_required: dispositivoActualizado.auth_required ?? false,
                is_favorite: true,
                favorite_orden: fav.orden ?? 0,
              } as DispositivoConConsumo;
            })
            .filter((d): d is DispositivoConConsumo => d !== null)
            .sort((a, b) => {
              const aFav = favoritesData.find(f => f.device_id === a.id);
              const bFav = favoritesData.find(f => f.device_id === b.id);
              return (aFav?.orden || 0) - (bFav?.orden || 0);
            });
          setFavoriteDevices(favDevices);
          
          // Actualizar orden de habitaciones basado en el orden de dispositivos y Sonos
          const roomsInOrder: (number | string)[] = [];
          const seenRooms = new Set<string>();
          favDevices.forEach(device => {
            const roomId = String(device.habitacion_id || 'sin-habitacion');
            if (!seenRooms.has(roomId)) {
              roomsInOrder.push(roomId);
              seenRooms.add(roomId);
            }
          });
          favSonos.forEach((speaker) => {
            const roomId = String(speaker.habitacion_id || 'sin-habitacion');
            if (!seenRooms.has(roomId)) {
              roomsInOrder.push(roomId);
              seenRooms.add(roomId);
            }
          });
          setFavoriteRoomsOrder(roomsInOrder);
          
          // Recargar boards para actualizar tablero Favoritos, pasando el parámetro
          await loadBoardsRef.current(tieneFavs);
        } catch (error) {
          console.error('[FAVORITOS] Error recargando favoritos:', error);
        }
      }
    };
    
    const unsub = eventBus.on('favoritos:actualizado', handleFavoritosActualizados);
    
    return () => {
      unsub();
    };
  }, [userData]);

  // Funciones de control
  const handleTabPress = useCallback(async (index: number) => {
    console.log(`🎯 [TAB] handleTabPress llamado con index: ${index}`);
    console.log(`🎯 [TAB] boards disponibles:`, boards);
    console.log(`🎯 [TAB] selectedTab actual: ${selectedTab}`);
    
    // Resetear scroll del contenedor de contenido al cambiar de tablero
    if (dashboardContentRef.current) {
      dashboardContentRef.current.scrollTop = 0;
    }
    
    // Resetear habitación seleccionada al cambiar de tab para mostrar vista general
    setSelectedRoom(null);
    // Cerrar GroupsModal y overlays de dispositivo al cambiar de tablero
    setShowGroupsModal(false);
    setSelectedRoomForGroups(null);
    closeAllDeviceModals();
    setSelectedTab(index);
    if (boards[index]) {
      console.log(`🎯 [TAB] Cargando habitaciones para tablero: ${boards[index].id}`);
      await loadRoomsForBoardRef.current(boards[index].id);
    } else {
      console.log(`🎯 [TAB] No se encontró el board en el índice ${index}`);
    }
  }, [boards, selectedTab, closeAllDeviceModals]);

  // Funciones de callback para swipe horizontal
  const handleSwipeLeft = useCallback(() => {
    console.log('🔄 Swipe LEFT detectado');
    if (selectedTab < boards.length - 1) {
      console.log(`🔄 Navegando de tab ${selectedTab} a ${selectedTab + 1}`);
      // FORZAR MÁS HISTORIAL para que funcione el back button
      window.history.pushState({ page: 1 }, '', '');
      window.history.pushState({ page: 2 }, '', '');
      window.history.pushState({ page: 3 }, '', '');
      window.history.pushState({ page: 4 }, '', '');
      handleTabPress(selectedTab + 1);
    } else {
      console.log('🔄 Ya estás en el último tablero');
    }
  }, [selectedTab, boards.length, handleTabPress]);

  const handleSwipeRight = useCallback(() => {
    console.log('🔄 Swipe RIGHT detectado');
    if (selectedTab > 0) {
      console.log(`🔄 Navegando de tab ${selectedTab} a ${selectedTab - 1}`);
      // FORZAR MÁS HISTORIAL para que funcione el back button
      window.history.pushState({ page: 1 }, '', '');
      window.history.pushState({ page: 2 }, '', '');
      window.history.pushState({ page: 3 }, '', '');
      window.history.pushState({ page: 4 }, '', '');
      handleTabPress(selectedTab - 1);
    } else {
      console.log('🔄 Ya estás en el primer tablero');
    }
  }, [selectedTab, handleTabPress]);

  const addDebugLog = (message: string) => {
    setDebugLogs(prev => [...prev.slice(-4), `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  // Hook personalizado para swipe
  const swipeHandlers = useSwipeHorizontal(handleSwipeLeft, handleSwipeRight, addDebugLog);
  console.log('🔄 [SWIPE] swipeHandlers creados:', Object.keys(swipeHandlers));
  console.log('🔄 [SWIPE] currentScreen:', currentScreen);
  console.log('🔄 [SWIPE] Eventos aplicados:', currentScreen === 'dashboard' ? 'SÍ' : 'NO');

  // Función para controlar persianas (PLUS2PM en modo cover) - replicando exactamente la web
  const handleControlCover = useCallback(async (deviceId: number, action: 'up' | 'down' | 'stop') => {
    try {
      // CRITICO: Verificar si el usuario es demo ANTES de cualquier otra verificación
      // Los usuarios demo NO pueden operar dispositivos, incluso si tienen el PIN correcto
      const user = getUser();
      const isUserDemo = user ? isDemo(user) : false;
      if (isUserDemo) {
        // Mostrar mensaje de restricción
        setPermissionSnackbar({
          open: true,
          message: t('permissions.demoRestriction')
        });
        // Tracking para usuarios Demo
        const tracker = getDemoActivityTracker();
        tracker.trackClick(
          'device_cover_control',
          deviceId,
          `Dispositivo ${deviceId}`,
          {
            screen: currentScreen,
            roomId: null,
            deviceId: deviceId,
            attemptedAction: `control_cover_${action}`,
            blocked: true,
            blockReason: 'demo_user_no_control'
          }
        );
        return;
      }
      
      const dispositivo = mapaDispositivos[deviceId];
      const device = dispositivo || { id: deviceId };
      const deviceForPin = dispositivo ? { ...device, ...dispositivo } : device;
      
      // CRITICO: Verificación de seguridad adicional - si mapaDispositivos NO está cargado,
      // NO permitir operar dispositivos hasta que esté completamente cargado
      // Esto previene race conditions donde dispositivos se operan sin PIN durante la carga inicial
      if (!isMapaDispositivosCargado()) {
        // Esperar a que mapaDispositivos esté cargado antes de continuar
        const waitForMapaLoaded = () => {
          return new Promise<void>((resolve) => {
            if (isMapaDispositivosCargado()) {
              resolve();
              return;
            }
            const checkLoaded = () => {
              if (isMapaDispositivosCargado()) {
                eventBus.off('estructura:actualizada', checkLoaded);
                resolve();
              }
            };
            eventBus.on('estructura:actualizada', checkLoaded);
            // Timeout de seguridad: si después de 3 segundos no se carga, continuar pero con verificación estricta
            setTimeout(() => {
              eventBus.off('estructura:actualizada', checkLoaded);
              resolve();
            }, 3000);
          });
        };
        await waitForMapaLoaded();
        // Actualizar deviceForPin después de esperar
        const dispositivoActualizado = mapaDispositivos[deviceId];
        if (dispositivoActualizado) {
          Object.assign(deviceForPin, dispositivoActualizado);
        }
      }

      if (!(await ensureCanControlDevice(deviceForPin))) {
        return;
      }
      
      // CRITICO: Verificar PIN ANTES de cualquier otra verificacion o accion
      // NUNCA se debe saltar la verificación de PIN - es una cuestión de seguridad
      if ((window as any).checkPinRequired) {
        const requiresPin = (window as any).checkPinRequired(deviceForPin);
        if (requiresPin) {
          const pinValid = (window as any).isPinSessionValid && (window as any).isPinSessionValid();
          if (!pinValid) {
            await openFloatingCameraWindow(deviceForPin, 'control_cover', action);
            return;
          }
        }
      }
      
      // FEEDBACK VISUAL INMEDIATO - Activar estado de confirmación
      setConfirmingDevices(prev => new Set(prev).add(deviceId));
      

      // Guardar la acción para establecer/limpiar coverActions cuando llegue confirmación del backend
      console.log('🎯 [COVER] Guardando acción:', { deviceId, action });
      setPendingCoverActions(prev => ({ ...prev, [deviceId]: action }));
      
      // Configurar timeout para detectar falta de respuesta (8 segundos)
      const timeoutId = setTimeout(() => {
        // Verificar INMEDIATAMENTE si aún está esperando confirmación
        setConfirmingDevices(prev => {
          if (prev.has(deviceId)) {
            // Si aún está esperando, iniciar nuevo cronómetro de 1 segundo
            const secondTimeoutId = setTimeout(() => {
              setConfirmingDevices(prev2 => {
                if (prev2.has(deviceId)) {
                  // Si TAMPOCO llegó confirmación después del segundo adicional, mostrar error
                  setDeviceErrors(prev => new Set(prev).add(deviceId));
                  
                  setTimeout(() => {
                    setDeviceErrors(prev => {
                      const newErrors = new Set(prev);
                      newErrors.delete(deviceId);
                      return newErrors;
                    });
                  }, 3000); // Mostrar error por 3 segundos
                }
                // Eliminar del confirmingDevices
                const newSet = new Set(prev2);
                newSet.delete(deviceId);
                return newSet;
              });
            }, 1000); // Nuevo cronómetro de 1 segundo
            
            // Guardar el segundo timeout para poder cancelarlo si llega confirmación
            setDeviceTimeouts(prev => ({ ...prev, [deviceId]: secondTimeoutId }));
          }
          // Si ya no está esperando (llegó confirmación), no hacer nada más
          return prev;
        });
        
        // Limpiar el primer timeout
        setDeviceTimeouts(prev => {
          const newTimeouts = { ...prev };
          delete newTimeouts[deviceId];
          return newTimeouts;
        });
      }, 8000);
      
      // Guardar el timeout para poder cancelarlo si llega confirmación
      setDeviceTimeouts(prev => ({ ...prev, [deviceId]: timeoutId }));
      
      // MEJORA: Verificar si ya hay un listener asignado (por ejemplo, desde grupos)
      // Si ya está asignado, no asignar otro para evitar duplicados
      let listenerId = WebSocketService.getListenerByDevice(deviceId);
      if (listenerId === null) {
        // No hay listener asignado, asignar uno nuevo
        listenerId = WebSocketService.assignListener(deviceId);
      }
      
      if (listenerId === null) {
        // No hay listeners disponibles, mostrar error y abortar
        setConfirmingDevices(prev => {
          const newSet = new Set(prev);
          newSet.delete(deviceId);
          return newSet;
        });
        setDeviceErrors(prev => new Set(prev).add(deviceId));
        setTimeout(() => {
          setDeviceErrors(prev => {
            const newErrors = new Set(prev);
            newErrors.delete(deviceId);
            return newErrors;
          });
        }, 3000);
        // Limpiar timeout
        setDeviceTimeouts(prev => {
          const newTimeouts = { ...prev };
          if (newTimeouts[deviceId]) {
            clearTimeout(newTimeouts[deviceId]);
            delete newTimeouts[deviceId];
          }
          return newTimeouts;
        });
        return;
      }

      console.log(`🎯 [COVER] Controlando persiana ${deviceId} - acción: ${action}`);
      
      if (action === 'stop') {
        // Enviar comando de parada
        WebSocketService.sendDeviceControl(deviceId, 'stop');
        console.log(`✅ [COVER] Comando STOP enviado para persiana ${deviceId}`);
      } else {
        // Llamar a la API de control de persianas usando WebSocket directamente
        const channel = action === 'up' ? 0 : 1; // Canal 0 para subir, canal 1 para bajar
        WebSocketService.sendDeviceControl(deviceId, 'toggle', true, channel);
        console.log(`✅ [COVER] Comando enviado para persiana ${deviceId} - canal: ${channel}`);
      }
    } catch (error) {
      console.error(`❌ [COVER] Error al controlar persiana ${deviceId}:`, error);
      alert('Error: No se pudo controlar la persiana. Inténtalo de nuevo.');
      
      // En caso de error, limpiar estado de confirmación y error
      setConfirmingDevices(prev => {
        const newSet = new Set(prev);
        newSet.delete(deviceId);
        return newSet;
      });
      setDeviceErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(deviceId);
        return newErrors;
      });
      setCoverActions(prev => {
        const newActions = { ...prev };
        delete newActions[deviceId];
        return newActions;
      });
    }
  }, [t, currentScreen, openFloatingCameraWindow, ensureCanControlDevice]);

  const handleDeviceToggle = useCallback(async (device: any) => {
    bumpUsage('device', device?.id);
    // MEJORA: Obtener dispositivo actualizado desde mapaDispositivos
    const dispositivo = mapaDispositivos[device.id];
    const deviceForPin = dispositivo ? { ...device, ...dispositivo } : device;

    const user = getUser();
    const isUserDemo = user ? isDemo(user) : false;
    if (isUserDemo) {
      setConfirmingDevices(prev => {
        const newSet = new Set(prev);
        newSet.delete(device.id);
        return newSet;
      });
      setPermissionSnackbar({
        open: true,
        message: t('permissions.demoRestriction')
      });
      const tracker = getDemoActivityTracker();
      tracker.trackClick(
        'device_toggle_button',
        device.id,
        device.nombre || `Dispositivo ${device.id}`,
        {
          screen: currentScreen,
          roomId: device.habitacion_id,
          deviceId: device.id,
          attemptedAction: 'toggle_device',
          blocked: true,
          blockReason: 'demo_user_no_control'
        }
      );
      return;
    }

    if (!(await ensureCanControlDevice(deviceForPin))) {
      setConfirmingDevices(prev => {
        const newSet = new Set(prev);
        newSet.delete(device.id);
        return newSet;
      });
      return;
    }
    
    // MEJORA 3: Verificar si el WebSocket está completamente listo antes de continuar
    if (!WebSocketService.isReady()) {
      // Esperar hasta que esté listo (con timeout de seguridad de 3 segundos)
      const isReady = await WebSocketService.waitUntilReady(3000);
      if (!isReady) {
        // Si después de 3 segundos no está listo, mostrar error
        setDeviceErrors(prev => new Set(prev).add(device.id));
        setConfirmingDevices(prev => {
          const newSet = new Set(prev);
          newSet.delete(device.id);
          return newSet;
        });
        setTimeout(() => {
          setDeviceErrors(prev => {
            const newErrors = new Set(prev);
            newErrors.delete(device.id);
            return newErrors;
          });
        }, 3000);
        return;
      }
    }
    
    // MEJORA: Verificar disponibilidad de listeners ANTES de hacer verificaciones pesadas
    // Si no hay listeners disponibles, mostrar error inmediatamente
    if (!WebSocketService.hasAvailableListener()) {
      setDeviceErrors(prev => new Set(prev).add(device.id));
      setConfirmingDevices(prev => {
        const newSet = new Set(prev);
        newSet.delete(device.id);
        return newSet;
      });
      setTimeout(() => {
        setDeviceErrors(prev => {
          const newErrors = new Set(prev);
          newErrors.delete(device.id);
          return newErrors;
        });
      }, 3000);
      return;
    }
    
    // CRITICO: Verificar PIN PRIMERO, antes de cualquier otra cosa
    // CRITICO: Verificación de seguridad adicional - si mapaDispositivos NO está cargado,
    // NO permitir operar dispositivos hasta que esté completamente cargado
    // Esto previene race conditions donde dispositivos se operan sin PIN durante la carga inicial
    if (!isMapaDispositivosCargado()) {
      // Esperar a que mapaDispositivos esté cargado antes de continuar
      // Mostrar mensaje al usuario o simplemente esperar silenciosamente
      const waitForMapaLoaded = () => {
        return new Promise<void>((resolve) => {
          if (isMapaDispositivosCargado()) {
            resolve();
            return;
          }
          const checkLoaded = () => {
            if (isMapaDispositivosCargado()) {
              eventBus.off('estructura:actualizada', checkLoaded);
              resolve();
            }
          };
          eventBus.on('estructura:actualizada', checkLoaded);
          // Timeout de seguridad: si después de 3 segundos no se carga, continuar pero con verificación estricta
          setTimeout(() => {
            eventBus.off('estructura:actualizada', checkLoaded);
            resolve();
          }, 3000);
        });
      };
      await waitForMapaLoaded();
      // Actualizar deviceForPin después de esperar
      const dispositivoActualizado = mapaDispositivos[device.id];
      if (dispositivoActualizado) {
        Object.assign(deviceForPin, dispositivoActualizado);
      }
    }

    if (!(await ensureCanControlDevice(deviceForPin))) {
      setConfirmingDevices(prev => {
        const newSet = new Set(prev);
        newSet.delete(device.id);
        return newSet;
      });
      return;
    }
    
    // CRITICO: Verificar PIN ANTES de cualquier otra verificación o acción
    // NUNCA se debe saltar la verificación de PIN - es una cuestión de seguridad
    if ((window as any).checkPinRequired) {
      const requiresPin = (window as any).checkPinRequired(deviceForPin);
      if (requiresPin) {
        // Verificar si la sesión de PIN es válida
        const pinValid = (window as any).isPinSessionValid && (window as any).isPinSessionValid();
        if (!pinValid) {
          // Limpiar confirmingDevices antes de abrir el dialog de PIN
          setConfirmingDevices(prev => {
            const newSet = new Set(prev);
            newSet.delete(device.id);
            return newSet;
          });
          // FLUJO 1: Solo PIN (sin cámaras) - usar PinInputDialog directamente
          // FLUJO 3: PIN + cámaras - usar FloatingCameraWindow con PIN dialog
          // Verificar si tiene cámaras
          const cachedConfig = cameraConfigCache.current.get(device.id);
          const hasCameras = Boolean(cachedConfig?.cameraIds && cachedConfig.cameraIds.length > 0) || 
                            Boolean(dispositivo?.camera_ids && Array.isArray(dispositivo.camera_ids) && dispositivo.camera_ids.length > 0);
          
          if (!hasCameras) {
            // FLUJO 1: Solo PIN - usar requestPinForControl que maneja PinInputDialog
            (window as any).requestPinForControl(deviceForPin, 'toggle');
          } else {
            // FLUJO 3: PIN + cámaras - usar FloatingCameraWindow
            await openFloatingCameraWindow(deviceForPin, 'toggle');
          }
          return; // SALIR INMEDIATAMENTE si requiere PIN y no esta validado
        }
      }
    }
    
    // Tracking para usuarios Demo - registrar intento de control
    const tracker = getDemoActivityTracker();
    // user e isUserDemo ya están declarados arriba
    
    try {
      // Tracking: registrar intento de control (aunque sea Demo, registrar el intento)
      if (isUserDemo) {
        tracker.trackClick(
          'device_toggle_button',
          dispositivo?.id || device.id,
          dispositivo?.nombre || device.nombre || `Dispositivo ${device.id}`,
          {
            screen: currentScreen,
            roomId: dispositivo?.habitacion_id || device.habitacion_id,
            deviceId: dispositivo?.id || device.id,
            attemptedAction: 'toggle_device',
            blocked: isUserDemo, // Siempre bloqueado para Demo
            blockReason: isUserDemo ? 'demo_user_no_control' : undefined
          }
        );
      }
      
      // Obtener el estado actual del dispositivo
      const currentState = dispositivo ? dispositivo.estado : false;
      const newState = !currentState;
      
      // FLUJO 2: Solo cámaras (sin PIN) - abrir FloatingCameraWindow Y ejecutar comando en paralelo
      // FLUJO 3: PIN + cámaras - ya se manejó arriba (PIN verificado, cámaras presentes)
      // Verificar si tiene cámaras antes de abrir FloatingCameraWindow
      const cachedConfig = cameraConfigCache.current.get(device.id);
      const hasCameras = Boolean(cachedConfig?.cameraIds && cachedConfig.cameraIds.length > 0) || 
                        Boolean(dispositivo?.camera_ids && Array.isArray(dispositivo.camera_ids) && dispositivo.camera_ids.length > 0);
      
      // Verificar si requiere PIN (para distinguir FLUJO 2 vs FLUJO 3)
      const requiresPin = (window as any).checkPinRequired && (window as any).checkPinRequired(deviceForPin);
      
      // Verificar si la ventana flotante ya está abierta para este dispositivo
      const currentFloatingWindow = floatingCameraWindowRef.current;
      const isFloatingWindowOpen = currentFloatingWindow && 
                                   currentFloatingWindow.deviceId === device.id && 
                                   currentFloatingWindow.open === true;
      
      const willSendFromHere = !hasCameras || !requiresPin || isFloatingWindowOpen;
      
      // CRITICO: Activar confirmingDevices y asignar listener SOLO si vamos a enviar desde aquí
      if (willSendFromHere) {
        // MEJORA: confirmingDevices ya está activado desde DeviceModal
        // Solo asegurarnos de que esté activado aquí también (por si se llama desde otro lugar)
        setConfirmingDevices(prev => {
          const newSet = new Set(prev).add(device.id);
          return newSet;
        });
        
        // POOL DE LISTENERS: Asignar listener disponible SOLO si vamos a enviar desde aquí
        // MEJORA: Ya verificamos disponibilidad antes, pero verificamos de nuevo por seguridad
        const listenerId = WebSocketService.assignListener(device.id);
        if (listenerId === null) {
          // Limpiar confirmingDevices si no hay listener disponible
          setConfirmingDevices(prev => {
            const newSet = new Set(prev);
            newSet.delete(device.id);
            return newSet;
          });
          // Mostrar error visual
          setDeviceErrors(prev => new Set(prev).add(device.id));
          
          setTimeout(() => {
            setDeviceErrors(prev => {
              const newErrors = new Set(prev);
              newErrors.delete(device.id);
              return newErrors;
            });
          }, 3000);
          return;
        }
      }
      // Si willSendFromHere es false, NO limpiar confirmingDevices aquí
      // porque puede que FloatingCameraWindow lo necesite para mostrar el spinner
      // FloatingCameraWindow manejará su propio confirmingDevices
      
      if (hasCameras) {
        if (requiresPin) {
          if (isFloatingWindowOpen) {
            if (!willSendFromHere) {
              setConfirmingDevices(prev => {
                const newSet = new Set(prev).add(device.id);
                return newSet;
              });
              
              const listenerId = WebSocketService.assignListener(device.id);
              if (listenerId === null) {
                setConfirmingDevices(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(device.id);
                  return newSet;
                });
                setDeviceErrors(prev => new Set(prev).add(device.id));
                setTimeout(() => {
                  setDeviceErrors(prev => {
                    const newErrors = new Set(prev);
                    newErrors.delete(device.id);
                    return newErrors;
                  });
                }, 3000);
                return;
              }
            }
          } else {
            openFloatingCameraWindow(device, 'toggle').catch(() => {});
            return;
          }
        } else {
          openFloatingCameraWindow(device, 'toggle').catch(() => {});
        }
      }
      // Si NO tiene cámaras, NO abrir FloatingCameraWindow (FLUJO 1: solo PIN ya se manejó arriba)
      
      // Configurar timeout para detectar falta de respuesta (8 segundos)
      const timeoutId = setTimeout(() => {
        // Verificar INMEDIATAMENTE si aún está esperando confirmación
        setConfirmingDevices(prev => {
          if (prev.has(device.id)) {
            // Si aún está esperando, iniciar nuevo cronómetro de 1 segundo
            const secondTimeoutId = setTimeout(() => {
              setConfirmingDevices(prev2 => {
                if (prev2.has(device.id)) {
                  // Si TAMPOCO llegó confirmación después del segundo adicional, mostrar error
                  setDeviceErrors(prev => new Set(prev).add(device.id));
                  
                  setTimeout(() => {
                    setDeviceErrors(prev => {
                      const newErrors = new Set(prev);
                      newErrors.delete(device.id);
                      return newErrors;
                    });
                  }, 3000); // Mostrar error por 3 segundos
                }
                // Eliminar del confirmingDevices
                const newSet = new Set(prev2);
                newSet.delete(device.id);
                return newSet;
              });
            }, 1000); // Nuevo cronómetro de 1 segundo
            
            // Guardar el segundo timeout para poder cancelarlo si llega confirmación
            setDeviceTimeouts(prev => ({ ...prev, [device.id]: secondTimeoutId }));
          }
          // Si ya no está esperando (llegó confirmación), no hacer nada más
          return prev;
        });
        
        // Limpiar el primer timeout
        setDeviceTimeouts(prev => {
          const newTimeouts = { ...prev };
          delete newTimeouts[device.id];
          return newTimeouts;
        });
      }, 8000);
      
      // Guardar el timeout para poder cancelarlo si llega confirmación
      setDeviceTimeouts(prev => ({ ...prev, [device.id]: timeoutId }));
      
      console.log('🎯 [APP] Estado actual:', currentState, 'Nuevo estado:', newState);
      console.log('🎯 [APP] Llamando a WebSocketService.sendDeviceControl()');
      
      // Para DIMMER2, siempre usar toggle (no set_brightness)
      // El brillo se maneja por separado en el modal del dispositivo
      // CRITICO SEGURIDAD: Solo enviar comando si:
      // - NO tiene cámaras (FLUJO 1: solo PIN o sin PIN) → App.tsx envía
      // - O tiene cámaras pero NO requiere PIN (FLUJO 2: solo cámaras) → App.tsx envía
      // Si tiene cámaras Y requiere PIN (FLUJO 3), FloatingCameraWindow enviará el comando
      // NUNCA enviar aquí si hasCameras && requiresPin para evitar doble envío
      // 
      // Lógica de la condición:
      // - !hasCameras → true → envía (FLUJO 1)
      // - hasCameras && !requiresPin → true → envía (FLUJO 2: solo cámaras)
      // - hasCameras && requiresPin && !isFloatingWindowOpen → false → NO envía (FLUJO 3: primera vez, FloatingCameraWindow lo envía)
      // - hasCameras && requiresPin && isFloatingWindowOpen → true → envía (FLUJO 3: desde dentro con PIN validado)
      // 
      // Simplificar la condición:
      // Si NO tiene cámaras → envía
      // Si tiene cámaras pero NO requiere PIN → envía
      // Si tiene cámaras Y requiere PIN Y la ventana está abierta → envía (desde dentro)
      // Si tiene cámaras Y requiere PIN Y la ventana NO está abierta → NO envía (primera vez)
      // 
      const latestFloatingWindow = floatingCameraWindowRef.current;
      const currentIsFloatingWindowOpen = latestFloatingWindow && 
                                         latestFloatingWindow.deviceId === device.id && 
                                         latestFloatingWindow.open === true;
      const shouldSend = willSendFromHere || (!hasCameras || !requiresPin || currentIsFloatingWindowOpen);
      
      if (shouldSend) {
        // CRITICO: Verificar que el listener esté asignado
        // Si willSendFromHere es true, el listener ya está asignado arriba
        // Si willSendFromHere es false pero shouldSend es true, asignar listener ahora
        if (!willSendFromHere) {
          // Caso especial: willSendFromHere fue false pero shouldSend es true
          // Esto puede pasar si isFloatingWindowOpen cambió entre las dos verificaciones
          // Activar confirmingDevices y asignar listener
          setConfirmingDevices(prev => {
            const newSet = new Set(prev).add(device.id);
            return newSet;
          });
          
          const listenerId = WebSocketService.assignListener(device.id);
          if (listenerId === null) {
            // Limpiar confirmingDevices si no hay listener disponible
            setConfirmingDevices(prev => {
              const newSet = new Set(prev);
              newSet.delete(device.id);
              return newSet;
            });
            // Mostrar error visual
            setDeviceErrors(prev => new Set(prev).add(device.id));
            
            setTimeout(() => {
              setDeviceErrors(prev => {
                const newErrors = new Set(prev);
                newErrors.delete(device.id);
                return newErrors;
              });
            }, 3000);
            return;
          }
        }
        
        WebSocketService.sendDeviceControl(device.id, 'toggle', newState);
      }
      // Si hasCameras && requiresPin, el comando se enviará desde FloatingCameraWindow
      // La actualización del estado vendrá automáticamente via WebSocket
      // No necesitamos actualizar manualmente aquí
    } catch (error) {
      // En caso de error, limpiar estado de confirmación y error
      setConfirmingDevices(prev => {
        const newSet = new Set(prev);
        newSet.delete(device.id);
        return newSet;
      });
      setDeviceErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.add(device.id);
        return newErrors;
      });
      
      // POOL DE LISTENERS: Liberar listener en caso de error
      WebSocketService.releaseListener(device.id);
    }
  }, [openFloatingCameraWindow, t, currentScreen, ensureCanControlDevice]);

  // Función para ejecutar control pendiente después de verificar PIN
  const executePendingControl = useCallback(async () => {
    // Preferir ref (sync) sobre state (puede estar stale en el mismo tick que setPendingControl)
    const pending = pendingControlRef.current || pendingControl;
    if (!pending) {
      return;
    }
    
    const { device, action, value } = pending;
    
    // CRITICO: Verificar si el usuario es demo ANTES de ejecutar cualquier control
    // Los usuarios demo NO pueden operar dispositivos, incluso si tienen el PIN correcto
    const user = getUser();
    const isUserDemo = user ? isDemo(user) : false;
    if (isUserDemo) {
      // Limpiar pendingControl
      pendingControlRef.current = null;
      setPendingControl(null);
      // Limpiar confirmingDevices si estaba activo
      setConfirmingDevices(prev => {
        const newSet = new Set(prev);
        newSet.delete(device.id);
        return newSet;
      });
      // Mostrar mensaje de restricción
      setPermissionSnackbar({
        open: true,
        message: t('permissions.demoRestriction')
      });
      // Tracking para usuarios Demo
      const tracker = getDemoActivityTracker();
      tracker.trackClick(
        'device_control_after_pin',
        device.id,
        device.nombre || `Dispositivo ${device.id}`,
        {
          screen: currentScreen,
          roomId: device.habitacion_id,
          deviceId: device.id,
          attemptedAction: action,
          blocked: true,
          blockReason: 'demo_user_no_control_after_pin'
        }
      );
      return;
    }
    
    pendingControlRef.current = null;
    setPendingControl(null);

    // Sonos / Samsung (u otros): continuar acción tras verificar PIN
    if (action === 'resume' && typeof device?.__resume === 'function') {
      try {
        await device.__resume();
      } catch {
        /* noop */
      }
      return;
    }
    
    // MEJORA 3: Verificar si el WebSocket está completamente listo antes de continuar
    if (!WebSocketService.isReady()) {
      // Esperar hasta que esté listo (con timeout de seguridad de 3 segundos)
      const isReady = await WebSocketService.waitUntilReady(3000);
      if (!isReady) {
        // Si después de 3 segundos no está listo, mostrar error
        setDeviceErrors(prev => new Set(prev).add(device.id));
        setTimeout(() => {
          setDeviceErrors(prev => {
            const newErrors = new Set(prev);
            newErrors.delete(device.id);
            return newErrors;
          });
        }, 3000);
        return;
      }
    }
    
    // CRITICO: Obtener dispositivo actualizado desde mapaDispositivos para asegurar que tenga todos los campos correctos
    const dispositivoActualizado = mapaDispositivos[device.id] ? { ...device, ...mapaDispositivos[device.id] } : device;
    console.log('[App] executePendingControl: Dispositivo actualizado:', dispositivoActualizado);
    
    // No extendemos la sesión - expira en el tiempo absoluto definido
    
    // CRITICO: Ejecutar el control DIRECTAMENTE sin pasar por handleDeviceToggle
    // porque el PIN ya fue verificado. Si pasamos por handleDeviceToggle, volverá a verificar
    // el PIN y podría intentar abrir la ventana flotante innecesariamente.
    if (action === 'toggle' || action === 'set_power') {
      const audit = assistantAuditRef.current;
      const controlAction =
        audit?.device_id === dispositivoActualizado.id
          ? audit?.control_action
          : action === 'set_power'
          ? value === 'on' || value === true
            ? 'encender'
            : 'apagar'
          : undefined;
      const resolvedPlan = planPowerControl(
        dispositivoActualizado,
        action,
        value,
        controlAction
      );
      if (resolvedPlan.kind === 'noop' && resolvedPlan.noopReason) {
        const deviceName =
          dispositivoActualizado.nombre ||
          `Dispositivo ${dispositivoActualizado.id}`;
        const lang: 'es' | 'en' =
          audit?.idioma === 'en' ? 'en' : 'es';
        const msg = formatAssistantControlNoop(
          deviceName,
          resolvedPlan.noopReason,
          lang
        );
        if (audit && audit.device_id === dispositivoActualizado.id) {
          assistantAuditRef.current = null;
          void auditAssistantControl({
            dispositivo_id: audit.device_id,
            tool_logica: audit.logical_tool,
            accion_pwa: audit.accion_pwa,
            accion_valor: audit.accion_valor,
            frase_usuario: audit.frase_usuario,
            exito: true,
            mensaje_resultado: msg,
          }).catch(() => {});
        }
        eventBus.emit('assistant:control_result', {
          device_id: dispositivoActualizado.id,
          success: true,
          message: msg,
          noop: true,
        });
        return;
      }
      const newState = resolvedPlan.targetState!;

      // Marcar dispositivo como "confirmando" para mostrar feedback visual
      setConfirmingDevices(prev => new Set(prev).add(dispositivoActualizado.id));

      try {
        const listenerId = WebSocketService.assignListener(dispositivoActualizado.id);
        if (listenerId === null) {
          throw new Error('No hay listeners disponibles');
        }
        WebSocketService.sendDeviceControl(dispositivoActualizado.id, 'toggle', newState);
      } catch (error) {
        console.error('[App] executePendingControl: Error ejecutando toggle:', error);
        // Limpiar estado de confirmación en caso de error
        setConfirmingDevices(prev => {
          const newSet = new Set(prev);
          newSet.delete(dispositivoActualizado.id);
          return newSet;
        });
        setDeviceErrors(prev => new Set(prev).add(dispositivoActualizado.id));
        // Limpiar error después de 3 segundos
        setTimeout(() => {
          setDeviceErrors(prev => {
            const newErrors = new Set(prev);
            newErrors.delete(dispositivoActualizado.id);
            return newErrors;
          });
        }, 3000);
        // Liberar listener en caso de error
        WebSocketService.releaseListener(dispositivoActualizado.id);
      }
    } else if (action === 'brightness') {
      // MEJORA: Asignar listener ANTES de enviar comando para asegurar que esté listo
      const listenerId = WebSocketService.assignListener(device.id);
      if (listenerId !== null) {
        // Brightness se maneja directamente con WebSocket
        WebSocketService.sendDeviceControl(device.id, 'set_brightness', value);
      }
    } else if (action === 'control_cover') {
      // Usar la función local de control de covers
      handleControlCover(device.id, value);
    }
  }, [pendingControl, handleControlCover, t, currentScreen]);

  const handlePinVerified = useCallback(() => {
    // Cerrar dialog sin pasar por onClose (ese limpia pending; el resume lo necesita).
    setShowPinDialog(false);
    void executePendingControl();
  }, [executePendingControl]);

  // Función para agregar dispositivos a confirmingDevices (para grupos de pulsadores)
  const addToConfirmingDevices = useCallback((deviceIds: number[]) => {
    setConfirmingDevices(prev => {
      const newSet = new Set(prev);
      deviceIds.forEach(id => newSet.add(id));
      return newSet;
    });
    deviceIds.forEach(id => {
      if ((window as any).confirmingDevices) {
        (window as any).confirmingDevices.add(id);
      }
    });
  }, []);

  // Función para remover dispositivos de confirmingDevices (para manejo de errores)
  const removeFromConfirmingDevices = useCallback((deviceIds: number[]) => {
    setConfirmingDevices(prev => {
      const newSet = new Set(prev);
      deviceIds.forEach(id => newSet.delete(id));
      return newSet;
    });
    deviceIds.forEach(id => {
      if ((window as any).confirmingDevices) {
        (window as any).confirmingDevices.delete(id);
      }
    });
  }, []);

  // Exponer funciones de control después de que estén definidas
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).handleDeviceToggleFromGroup = handleDeviceToggle;
      (window as any).handleControlCoverFromGroup = handleControlCover;
      (window as any).addToConfirmingDevices = addToConfirmingDevices;
      (window as any).removeFromConfirmingDevices = removeFromConfirmingDevices;
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).handleDeviceToggleFromGroup;
        delete (window as any).handleControlCoverFromGroup;
        delete (window as any).addToConfirmingDevices;
        delete (window as any).removeFromConfirmingDevices;
      }
    };
  }, [handleDeviceToggle, handleControlCover, addToConfirmingDevices, removeFromConfirmingDevices]);

  // Función para limpiar mapas globales
  const limpiarMapasGlobales = () => {
    // Limpiar todos los mapas globales
    Object.keys(mapaTableros).forEach((k: string) => delete mapaTableros[Number(k)]);
    Object.keys(mapaHabitaciones).forEach((k: string) => delete mapaHabitaciones[Number(k)]);
    Object.keys(mapaDispositivos).forEach((k: string) => delete mapaDispositivos[Number(k)]);
    
    console.log('🧹 [LOGOUT] Mapas globales limpiados');
  };

  const handleLogout = async (isTimeout: boolean = false, timeoutReason: string = '') => {
    // Obtener datos del usuario antes de limpiar localStorage
    const userStr = localStorage.getItem('user');
    let userId: number | null = null;
    let token: string | null = null;
    try {
      if (userStr) {
        const user = JSON.parse(userStr);
        userId = user?.id || null;
      }
      token = localStorage.getItem('token');
    } catch (e) {
      // Ignorar error de parsing
    }
    
    // OPTIMIZACIÓN: Limpiar estado local INMEDIATAMENTE (no bloqueante)
    // Desconectar WebSocket
    WebSocketService.disconnect();
    setWebsocketStatus('disconnected');
    
    // Limpiar mapas globales
    limpiarMapasGlobales();
    stopSessionInactivity();
    
    // Limpiar localStorage
    localStorage.removeItem('token');
    clearLoginSessionFlags();
    localStorage.removeItem('user');
    localStorage.removeItem('login_timestamp');
    eventBus.emit('ai:policy-lookup-refresh');
    
    // Limpiar estado de React. El rol tiene que vaciarse: el monitor de
    // firmware sigue preguntando mientras userRole === 'admin', aunque el token ya no exista.
    setToken(null);
    setUserData(null);
    setUserRole('');
    setCurrentScreen('dashboard');
    setDemoViewMode(null);
    setDemoModeDialogOpen(false);
    setBoards([]);
    setRooms([]);
    setDevices([]);
    setSelectedTab(0);
    setSelectedRoom(null);
    // SOLUCIÓN: Resetear refs al hacer logout
    correctTabEstablishedRef.current = false;
    homeTabAppliedRef.current = false;
    roomsLoadedManuallyRef.current = false;
    isSettingCorrectTabRef.current = false;
    isInitialLoadInProgressRef.current = false;
    setShowUserMenu(false);
    setShowLogoutDialog(false);
    setInitialLoadComplete(false);
    // Cerrar snackbars de la sesión que termina (p. ej. aviso de inactividad a 5 min).
    // Si no, quedan open=true en estado y reaparecen apenas el usuario vuelve a loguearse.
    setPermissionSnackbar({ open: false, message: '' });
    
    // OPTIMIZACIÓN: Llamar al endpoint de logout SIN ESPERAR (fire-and-forget)
    // Esto permite que el usuario vea el cambio inmediatamente mientras el backend
    // registra la actividad en segundo plano
    if (token) {
      // Enviar petición sin await - no bloquea la UI
      axios.post(`${getApiBaseUrl()}/logout`, {
        is_timeout: isTimeout,
        timeout_reason: timeoutReason
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 3000 // Timeout corto para no bloquear si el servidor está lento
      }).catch(() => {
        // Ignorar errores silenciosamente - el logout local ya se completó
      });
    } else if (isTimeout && userId) {
      // Si es timeout y no hay token válido, usar endpoint alternativo
      axios.post(`${getApiBaseUrl()}/logout-timeout`, {
        user_id: userId,
        timeout_reason: timeoutReason
      }, {
        timeout: 3000
      }).catch(() => {
        // Ignorar errores silenciosamente
      });
    }
  };

  const confirmLogout = () => {
    handleLogout(false); // false = logout manual, no timeout
    setShowLogoutDialog(false);
  };

  // Función para ordenar dispositivos (como React Native)
  const ordenarDispositivos = useCallback((dispositivos: DispositivoConConsumo[], criterio: typeof sortCriterion) => {
    const dispositivosOrdenados = [...dispositivos];
    
    switch (criterio) {
      case 'personalizado':
        // Ordenar por el campo orden si existe
        return dispositivosOrdenados.sort((a, b) => {
          // Si ambos tienen orden definido, ordenar por ese campo
          if (a.orden !== undefined && b.orden !== undefined) {
            return a.orden - b.orden;
          }
          // Si solo uno tiene orden, el que tiene orden va primero
          if (a.orden !== undefined) return -1;
          if (b.orden !== undefined) return 1;
          // Si ninguno tiene orden, mantener el orden actual
          return 0;
        });
        
      case 'consumo-mayor':
        return dispositivosOrdenados.sort((a, b) => (b.consumo || 0) - (a.consumo || 0));
        
      case 'consumo-menor':
        return dispositivosOrdenados.sort((a, b) => (a.consumo || 0) - (b.consumo || 0));
        
      case 'estado':
        return dispositivosOrdenados.sort((a, b) => {
          // Lógica especial para PLUS2PM
          const isOnA = a.tipo === 'PLUS2PM'
            ? String(a.estado) === 'opening' || String(a.estado) === 'closing'
            : a.estado;
          const isOnB = b.tipo === 'PLUS2PM'
            ? String(b.estado) === 'opening' || String(b.estado) === 'closing'
            : b.estado;
          if (isOnA !== isOnB) {
            return isOnA ? 1 : -1; // Apagados primero, encendidos después
          }
          return a.nombre.localeCompare(b.nombre);
        });
        
      case 'defecto':
        return dispositivosOrdenados.sort((a, b) => {
          if (a.orden !== undefined && b.orden !== undefined) return a.orden - b.orden;
          if (a.orden !== undefined) return -1;
          if (b.orden !== undefined) return 1;
          return 0;
        });

      case 'artefacto':
        return dispositivosOrdenados.sort((a, b) => {
          const na = String((a as { tipo_artefacto?: string | null }).tipo_artefacto || '').trim();
          const nb = String((b as { tipo_artefacto?: string | null }).tipo_artefacto || '').trim();
          if (!na && nb) return 1;
          if (na && !nb) return -1;
          const byType = na.localeCompare(nb, 'es');
          if (byType !== 0) return byType;
          return a.nombre.localeCompare(b.nombre, 'es');
        });
        
      case 'nombre':
        return dispositivosOrdenados.sort((a, b) => a.nombre.localeCompare(b.nombre));
        
      default:
        return dispositivosOrdenados;
    }
  }, []);

  // Aplicar filtros (como React Native)
  const aplicarFiltros = useCallback((dispositivos: DispositivoConConsumo[]) => {
    // Performance: Evitar trabajo innecesario si no hay dispositivos
    if (!dispositivos.length) return [];
    
    let dispositivosFiltrados = [...dispositivos];
    
    // Ordenar primero
    dispositivosFiltrados = ordenarDispositivos(dispositivosFiltrados, sortCriterion);
    
    // Filtrar por estado online/offline (mutuamente excluyentes)
    if (filtroOnline && !filtroOffline) {
      dispositivosFiltrados = dispositivosFiltrados.filter(d => d.online);
    } else if (!filtroOnline && filtroOffline) {
      dispositivosFiltrados = dispositivosFiltrados.filter(d => !d.online);
    }
    
    // Filtrar por autenticación requerida
    if (filtroAuth) {
      dispositivosFiltrados = dispositivosFiltrados.filter(d => d.auth_required);
    }

    // Filtrar por autenticación ya configurada
    if (filtroAuthConfigured) {
      dispositivosFiltrados = dispositivosFiltrados.filter(d => d.auth_configured);
    }
    
    // Filtrar por PIN requerido
    if (filtroPin) {
      dispositivosFiltrados = dispositivosFiltrados.filter(d => Boolean(d.pin_required));
    }
    
    // Filtrar por cámara asignada
    if (filtroCamera) {
      dispositivosFiltrados = dispositivosFiltrados.filter(d => {
        const cameraIds = d.camera_ids;
        return cameraIds && Array.isArray(cameraIds) && cameraIds.length > 0;
      });
    }
    
    // Filtrar por estado encendido/apagado (mutuamente excluyentes)
    if (filtroEncendido && !filtroApagado) {
      dispositivosFiltrados = dispositivosFiltrados.filter(d => {
        if (d.tipo === 'PLUS2PM') {
          // Para PLUS2PM, considerar encendido solo cuando está en movimiento (opening o closing)
          return String(d.estado) === 'opening' || String(d.estado) === 'closing';
        }
        return d.estado;
      });
    } else if (!filtroEncendido && filtroApagado) {
      dispositivosFiltrados = dispositivosFiltrados.filter(d => {
        if (d.tipo === 'PLUS2PM') {
          // Para PLUS2PM, considerar apagado cuando no está en movimiento
          return String(d.estado) !== 'opening' && String(d.estado) !== 'closing';
        }
        return !d.estado;
      });
    }
    
    // Filtrar por consumo (mutuamente excluyentes)
    if (filtroConConsumo && !filtroSinConsumo) {
      dispositivosFiltrados = dispositivosFiltrados.filter(d => (d.consumo || 0) !== 0);
    } else if (!filtroConConsumo && filtroSinConsumo) {
      dispositivosFiltrados = dispositivosFiltrados.filter(d => (d.consumo || 0) === 0);
    }
    
    // Filtrar por modelo
    if (filtroModelos.length > 0) {
      dispositivosFiltrados = dispositivosFiltrados.filter(d => 
        d.tipo && filtroModelos.includes(d.tipo)
      );
    }
    
    // Filtrar por tipo de artefacto
    if (filtroArtefactos.length > 0) {
      dispositivosFiltrados = dispositivosFiltrados.filter(d => {
        const tipoArtefacto = d.artefacto_id ? mapaArtefactos[d.artefacto_id] : undefined;
        const pasa = (
          (filtroArtefactos.includes(t('deviceModal.undefined')) && !tipoArtefacto) ||
          (tipoArtefacto && filtroArtefactos.includes(tipoArtefacto))
        );
        return pasa;
      });
    }

    if (userRole === 'admin' && aiPolicy.ready && filtroAiPermitido) {
      dispositivosFiltrados = dispositivosFiltrados.filter((d) => {
        const p = aiPolicy.lookup[d.id];
        return Boolean(p && p.whitelisted && !p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiDenegado) {
      dispositivosFiltrados = dispositivosFiltrados.filter((d) => {
        const p = aiPolicy.lookup[d.id];
        return Boolean(p && p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiSinDefinir) {
      dispositivosFiltrados = dispositivosFiltrados.filter((d) => isAiPolicyUnset(aiPolicy.lookup[d.id]));
    }
    
    // Filtrar por texto de búsqueda
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      dispositivosFiltrados = dispositivosFiltrados.filter(d => 
        d.nombre.toLowerCase().includes(searchLower) ||
        d.tipo.toLowerCase().includes(searchLower)
      );
    }
    
    return dispositivosFiltrados;
  }, [sortCriterion, filtroModelos, filtroOnline, filtroOffline, filtroAuth, filtroAuthConfigured, filtroPin, filtroCamera, filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo, searchTerm, filtroArtefactos, ordenarDispositivos, userRole, aiPolicy.ready, aiPolicy.lookup, filtroAiPermitido, filtroAiDenegado, filtroAiSinDefinir, t, mapaArtefactos]);

  // MEMOIZACIÓN: Calcular dispositivos filtrados solo cuando cambian las dependencias
  const filteredAndSortedDevices = useMemo(() => {
    console.log('🔧 [MEMO] filteredAndSortedDevices recalculando - devices.length=' + devices.length);
    const resultado = aplicarFiltros(devices);
    return resultado;
  }, [devices, aplicarFiltros]);

  /** Parlantes Sonos de la habitación visibles según los mismos filtros que la grilla (como DeviceListView) */
  const filteredRoomSonos = useMemo(() => {
    if (!selectedRoom || roomSonos.length === 0) return [];

    const filtrosSoloShelly = isShellyExclusiveFilterActive({
      filtroAuth, filtroAuthConfigured, filtroCamera,
      filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo,
    });

    if (filtrosSoloShelly && !searchTerm.trim()) {
      return [];
    }

    let filtered = [...roomSonos];

    if (filtroModelos.length > 0) {
      filtered = filtered.filter((s) => matchesModelFilter(sonosModelKey(s.modelo), filtroModelos));
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((s) => s.nombre?.toLowerCase().includes(q));
    }
    if (filtroOnline) filtered = filtered.filter((s) => s.online);
    if (filtroOffline) filtered = filtered.filter((s) => !s.online);
    if (filtroPin) filtered = filtered.filter((s) => Boolean(s.pin_required));

    if (filtroArtefactos.length > 0) {
      filtered = filtered.filter((s) => {
        const tipoArtefacto = s.artefacto?.nombre
          ?? (s.artefacto_id ? mapaArtefactos[s.artefacto_id] : undefined);
        return (
          (filtroArtefactos.includes(t('deviceModal.undefined')) && !tipoArtefacto)
          || (tipoArtefacto && filtroArtefactos.includes(tipoArtefacto))
        );
      });
    }

    // Filtro IA (paridad con dispositivos): whitelist/blacklist por parlante
    if (userRole === 'admin' && aiPolicy.ready && filtroAiPermitido) {
      filtered = filtered.filter((s) => {
        const p = aiPolicy.sonosLookup[s.id];
        return Boolean(p && p.whitelisted && !p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiDenegado) {
      filtered = filtered.filter((s) => {
        const p = aiPolicy.sonosLookup[s.id];
        return Boolean(p && p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiSinDefinir) {
      filtered = filtered.filter((s) => isAiPolicyUnset(aiPolicy.sonosLookup[s.id]));
    }

    return filtered;
  }, [
    selectedRoom, roomSonos, searchTerm, filtroOnline, filtroOffline, filtroArtefactos,
    filtroModelos, filtroAuth, filtroAuthConfigured, filtroPin, filtroCamera,
    filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo,
    filtroAiPermitido, filtroAiDenegado, filtroAiSinDefinir, mapaArtefactos, t,
    userRole, aiPolicy.ready, aiPolicy.sonosLookup,
  ]);

  /** Dispositivos Samsung de la habitación visibles según filtros */
  const filteredRoomSamsung = useMemo(() => {
    if (!selectedRoom || roomSamsung.length === 0) return [];

    const filtrosSoloShelly = isShellyExclusiveFilterActive({
      filtroAuth, filtroAuthConfigured, filtroCamera,
      filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo,
    });

    if (filtrosSoloShelly && !searchTerm.trim()) {
      return [];
    }

    let filtered = [...roomSamsung];

    if (filtroModelos.length > 0) {
      filtered = filtered.filter((d) => matchesModelFilter(samsungModelKey(d.device_kind), filtroModelos));
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((d) => d.nombre?.toLowerCase().includes(q));
    }
    if (filtroOnline) filtered = filtered.filter((d) => d.online);
    if (filtroOffline) filtered = filtered.filter((d) => !d.online);
    if (filtroPin) filtered = filtered.filter((d) => Boolean(d.pin_required));

    if (filtroArtefactos.length > 0) {
      filtered = filtered.filter((d) => {
        const tipoArtefacto = d.artefacto?.nombre
          ?? (d.artefacto_id ? mapaArtefactos[d.artefacto_id] : undefined);
        return (
          (filtroArtefactos.includes(t('deviceModal.undefined')) && !tipoArtefacto)
          || (tipoArtefacto && filtroArtefactos.includes(tipoArtefacto))
        );
      });
    }

    // Filtro IA (paridad Shelly/Sonos): whitelist/blacklist por dispositivo Samsung
    if (userRole === 'admin' && aiPolicy.ready && filtroAiPermitido) {
      filtered = filtered.filter((d) => {
        const p = aiPolicy.samsungLookup[d.id];
        return Boolean(p && p.whitelisted && !p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiDenegado) {
      filtered = filtered.filter((d) => {
        const p = aiPolicy.samsungLookup[d.id];
        return Boolean(p && p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiSinDefinir) {
      filtered = filtered.filter((d) => isAiPolicyUnset(aiPolicy.samsungLookup[d.id]));
    }

    return filtered;
  }, [
    selectedRoom, roomSamsung, searchTerm, filtroOnline, filtroOffline, filtroArtefactos,
    filtroModelos, filtroAuth, filtroAuthConfigured, filtroPin, filtroCamera,
    filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo,
    filtroAiPermitido, filtroAiDenegado, filtroAiSinDefinir, mapaArtefactos, t,
    userRole, aiPolicy.ready, aiPolicy.samsungLookup,
  ]);


  const filteredRoomAndroidTv = useMemo(() => {
    if (!selectedRoom || roomAndroidTv.length === 0) return [];
    const filtrosSoloShelly = isShellyExclusiveFilterActive({
      filtroAuth, filtroAuthConfigured, filtroCamera,
      filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo,
    });
    if (filtrosSoloShelly && !searchTerm.trim()) return [];
    let filtered = [...roomAndroidTv];
    if (filtroModelos.length > 0) {
      filtered = filtered.filter(() => matchesModelFilter(androidTvModelKey(), filtroModelos));
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((d) => (d.nombre || '').toLowerCase().includes(q));
    }
    if (filtroOnline) filtered = filtered.filter((d) => d.online);
    if (filtroOffline) filtered = filtered.filter((d) => !d.online);
    if (filtroPin) filtered = filtered.filter((d) => Boolean(d.pin_required));
    if (filtroArtefactos.length > 0) {
      filtered = filtered.filter((d) => {
        const tipoArtefacto = d.artefacto?.nombre
          ?? (d.artefacto_id ? mapaArtefactos[d.artefacto_id] : undefined);
        return (
          (filtroArtefactos.includes(t('deviceModal.undefined')) && !tipoArtefacto)
          || (tipoArtefacto && filtroArtefactos.includes(tipoArtefacto))
        );
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiPermitido) {
      filtered = filtered.filter((d) => {
        const p = aiPolicy.androidtvLookup[d.id];
        return Boolean(p && p.whitelisted && !p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiDenegado) {
      filtered = filtered.filter((d) => {
        const p = aiPolicy.androidtvLookup[d.id];
        return Boolean(p && p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiSinDefinir) {
      filtered = filtered.filter((d) => isAiPolicyUnset(aiPolicy.androidtvLookup[d.id]));
    }
    void androidtvUpdateTick;
    return filtered;
  }, [
    selectedRoom, roomAndroidTv, searchTerm, filtroOnline, filtroOffline, filtroPin,
    filtroModelos, filtroAuth, filtroAuthConfigured, filtroCamera,
    filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo,
    filtroArtefactos, mapaArtefactos, t, androidtvUpdateTick,
    userRole, aiPolicy.ready, aiPolicy.androidtvLookup, filtroAiPermitido, filtroAiDenegado, filtroAiSinDefinir,
  ]);

  const filteredRoomLg = useMemo(() => {
    if (!selectedRoom || roomLg.length === 0) return [];
    const filtrosSoloShelly = isShellyExclusiveFilterActive({
      filtroAuth, filtroAuthConfigured, filtroCamera,
      filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo,
    });
    if (filtrosSoloShelly && !searchTerm.trim()) return [];
    let filtered = [...roomLg];
    if (filtroModelos.length > 0) {
      filtered = filtered.filter((d) =>
        matchesModelFilter(lgModelKey(d.device_type, d.ui_family), filtroModelos),
      );
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((d) => (d.nombre || '').toLowerCase().includes(q));
    }
    if (filtroOnline) filtered = filtered.filter((d) => d.online);
    if (filtroOffline) filtered = filtered.filter((d) => !d.online);
    if (filtroPin) filtered = filtered.filter((d) => Boolean(d.pin_required));
    if (filtroArtefactos.length > 0) {
      filtered = filtered.filter((d) => {
        const tipoArtefacto = d.artefacto?.nombre
          ?? (d.artefacto_id ? mapaArtefactos[d.artefacto_id] : undefined);
        return (
          (filtroArtefactos.includes(t('deviceModal.undefined')) && !tipoArtefacto)
          || (tipoArtefacto && filtroArtefactos.includes(tipoArtefacto))
        );
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiPermitido) {
      filtered = filtered.filter((d) => {
        const p = aiPolicy.lgLookup[d.id];
        return Boolean(p && p.whitelisted && !p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiDenegado) {
      filtered = filtered.filter((d) => {
        const p = aiPolicy.lgLookup[d.id];
        return Boolean(p && p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiSinDefinir) {
      filtered = filtered.filter((d) => isAiPolicyUnset(aiPolicy.lgLookup[d.id]));
    }
    void lgUpdateTick;
    return filtered;
  }, [
    selectedRoom, roomLg, searchTerm, filtroOnline, filtroOffline, filtroPin,
    filtroModelos, filtroAuth, filtroAuthConfigured, filtroCamera,
    filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo,
    filtroArtefactos, mapaArtefactos, t, lgUpdateTick,
    userRole, aiPolicy.ready, aiPolicy.lgLookup, filtroAiPermitido, filtroAiDenegado, filtroAiSinDefinir,
  ]);

  const filteredRoomEvohome = useMemo(() => {
    if (!selectedRoom || roomEvohome.length === 0) return [];
    const filtrosSoloShelly = isShellyExclusiveFilterActive({
      filtroAuth, filtroAuthConfigured, filtroCamera,
      filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo,
    });
    if (filtrosSoloShelly && !searchTerm.trim()) return [];
    let filtered = [...roomEvohome];
    if (filtroModelos.length > 0) {
      filtered = filtered.filter((d) =>
        matchesModelFilter(evohomeModelKey(d.model_type, d.zone_type), filtroModelos),
      );
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((d) => (d.nombre || '').toLowerCase().includes(q));
    }
    if (filtroOnline) filtered = filtered.filter((d) => d.online);
    if (filtroOffline) filtered = filtered.filter((d) => !d.online);
    if (filtroPin) filtered = filtered.filter((d) => Boolean(d.pin_required));
    if (filtroArtefactos.length > 0) {
      filtered = filtered.filter((d) => {
        const tipoArtefacto = d.artefacto?.nombre
          ?? (d.artefacto_id ? mapaArtefactos[d.artefacto_id] : undefined);
        return (
          (filtroArtefactos.includes(t('deviceModal.undefined')) && !tipoArtefacto)
          || (tipoArtefacto && filtroArtefactos.includes(tipoArtefacto))
        );
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiPermitido) {
      filtered = filtered.filter((d) => {
        const p = aiPolicy.evohomeLookup[d.id];
        return Boolean(p && p.whitelisted && !p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiDenegado) {
      filtered = filtered.filter((d) => {
        const p = aiPolicy.evohomeLookup[d.id];
        return Boolean(p && p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiSinDefinir) {
      filtered = filtered.filter((d) => isAiPolicyUnset(aiPolicy.evohomeLookup[d.id]));
    }
    void evohomeUpdateTick;
    return filtered;
  }, [
    selectedRoom, roomEvohome, searchTerm, filtroOnline, filtroOffline, filtroPin,
    filtroModelos, filtroAuth, filtroAuthConfigured, filtroCamera,
    filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo,
    filtroArtefactos, mapaArtefactos, t, evohomeUpdateTick,
    userRole, aiPolicy.ready, aiPolicy.evohomeLookup, filtroAiPermitido, filtroAiDenegado, filtroAiSinDefinir,
  ]);

  const filteredRoomPileta = useMemo(() => {
    if (!selectedRoom || roomPileta.length === 0) return [];
    const filtrosSoloShelly = isShellyExclusiveFilterActive({
      filtroAuth, filtroAuthConfigured, filtroCamera,
      filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo,
    });
    if (filtrosSoloShelly && !searchTerm.trim()) return [];
    let filtered = [...roomPileta];
    if (filtroModelos.length > 0) {
      filtered = filtered.filter(() => matchesModelFilter(piletaModelKey(), filtroModelos));
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((d) =>
        (d.nombre || d.display_name || '').toLowerCase().includes(q),
      );
    }
    if (filtroOnline) filtered = filtered.filter((d) => d.online);
    if (filtroOffline) filtered = filtered.filter((d) => !d.online);
    if (filtroArtefactos.length > 0) {
      filtered = filtered.filter((d) => {
        const tipoArtefacto = d.artefacto?.nombre
          ?? (d.artefacto_id ? mapaArtefactos[d.artefacto_id] : undefined);
        return (
          (filtroArtefactos.includes(t('deviceModal.undefined')) && !tipoArtefacto)
          || (tipoArtefacto && filtroArtefactos.includes(tipoArtefacto))
        );
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiPermitido) {
      filtered = filtered.filter((d) => {
        const p = aiPolicy.piletaLookup[d.id];
        return Boolean(p && p.whitelisted && !p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiDenegado) {
      filtered = filtered.filter((d) => {
        const p = aiPolicy.piletaLookup[d.id];
        return Boolean(p && p.blacklisted);
      });
    }
    if (userRole === 'admin' && aiPolicy.ready && filtroAiSinDefinir) {
      filtered = filtered.filter((d) => isAiPolicyUnset(aiPolicy.piletaLookup[d.id]));
    }
    void piletaUpdateTick;
    return filtered;
  }, [
    selectedRoom, roomPileta, searchTerm, filtroOnline, filtroOffline,
    filtroModelos, filtroAuth, filtroAuthConfigured, filtroCamera,
    filtroEncendido, filtroApagado, filtroConConsumo, filtroSinConsumo,
    filtroArtefactos, mapaArtefactos, t, piletaUpdateTick,
    userRole, aiPolicy.ready, aiPolicy.piletaLookup, filtroAiPermitido, filtroAiDenegado, filtroAiSinDefinir,
  ]);

  const filteredVisibleCount = filteredAndSortedDevices.length + filteredRoomSonos.length + filteredRoomSamsung.length + filteredRoomAndroidTv.length + filteredRoomLg.length + filteredRoomEvohome.length + filteredRoomPileta.length;
  
  // MEMOIZACIÓN: Memoizar el array de DeviceCard components para evitar re-renderizados innecesarios
  const memoizedDeviceCards = useMemo(() => {
    console.log('🔧 [MEMO] memoizedDeviceCards recalculando - cantidad=' + filteredAndSortedDevices.length);
    return filteredAndSortedDevices.map((device) => {
      const enrichedDevice = { ...device, ...(mapaDispositivos[device.id] || {}) };
      return {
        id: device.id,
        device: enrichedDevice,
        key: device.id
      };
    });
  }, [filteredAndSortedDevices]);

  const memoizedRoomGridItems = useMemo((): RoomGridItem[] => {
    void sonosUpdateTick;
    void samsungUpdateTick;
    void androidtvUpdateTick;
    void lgUpdateTick;
    void evohomeUpdateTick;
    void piletaUpdateTick;
    if (!selectedRoom) {
      return memoizedDeviceCards.map(({ id, device }) => ({
        kind: 'device' as const,
        id,
        device,
      }));
    }
    const userOrder = roomUserOrderRoomId === selectedRoom.id && roomUserOrder.size > 0
      ? roomUserOrder
      : null;
    const merged = sortRoomItems(
      collectRoomItems(
        filteredAndSortedDevices.map((d) => ({ ...d, ...(mapaDispositivos[d.id] || {}) })),
        filteredRoomSonos,
        filteredRoomSamsung,
        filteredRoomAndroidTv,
        filteredRoomLg,
        filteredRoomEvohome,
        filteredRoomPileta,
      ),
      sortCriterion,
      userOrder,
    );
    return merged.map((item) => {
      if (item.kind === 'device') {
        const card = memoizedDeviceCards.find((c) => c.id === item.data.id);
        return {
          kind: 'device' as const,
          id: item.data.id,
          device: card?.device || { ...item.data, ...(mapaDispositivos[item.data.id] || {}) },
        };
      }
      if (item.kind === 'sonos') {
        const speaker = mapaSonos[item.data.id] || item.data;
        return { kind: 'sonos' as const, id: item.data.id, speaker };
      }
      if (item.kind === 'samsung') {
        const samsungDevice = mapaSamsung[item.data.id] || item.data;
        return { kind: 'samsung' as const, id: item.data.id, device: samsungDevice };
      }
      if (item.kind === 'androidtv') {
        const atv = mapaAndroidTv[item.data.id] || item.data;
        return { kind: 'androidtv' as const, id: item.data.id, device: atv };
      }
      if (item.kind === 'evohome') {
        const evoDev = mapaEvohome[item.data.id] || item.data;
        return { kind: 'evohome' as const, id: item.data.id, device: evoDev };
      }
      if (item.kind === 'pileta') {
        const piletaDev = mapaPileta[item.data.id] || item.data;
        return { kind: 'pileta' as const, id: item.data.id, device: piletaDev };
      }
      const lgDev = mapaLg[item.data.id] || item.data;
      return { kind: 'lg' as const, id: item.data.id, device: lgDev };
    });
  }, [memoizedDeviceCards, filteredAndSortedDevices, selectedRoom, roomSonos, roomSamsung, roomAndroidTv, roomLg, roomEvohome, roomPileta, filteredRoomSonos, filteredRoomSamsung, filteredRoomAndroidTv, filteredRoomLg, filteredRoomEvohome, filteredRoomPileta, sonosUpdateTick, samsungUpdateTick, androidtvUpdateTick, lgUpdateTick, evohomeUpdateTick, piletaUpdateTick, sortCriterion, roomUserOrder, roomUserOrderRoomId]);

  const showSonosInGrid = Boolean(selectedRoom && roomSonos.length > 0);

  // Renderizado condicional del modal de grupos
  const groupsModalPortal = useMemo(() => {
    if (!showGroupsModal || !userData || !selectedRoomForGroups || typeof document === 'undefined') {
      return null;
    }
    const habitacion = mapaHabitaciones[selectedRoomForGroups];
    return createPortal(
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress sx={{ color: '#2391FF' }} />
        </Box>
      }>
      <GroupsModal
        key={`groups-modal-${userData.id}-${selectedRoomForGroups}`}
        isOpen={showGroupsModal}
        onClose={handleCloseGroupsModal}
        userId={userData.id}
        userRole={userRole}
        habitacionId={selectedRoomForGroups}
        nombreHabitacion={habitacion?.datos?.nombre || 'Habitación'}
        />
      </Suspense>,
      document.body
    );
  }, [showGroupsModal, userData, userRole, selectedRoomForGroups, handleCloseGroupsModal]);

  if (softphoneCallEnded) {
    return <SoftphoneCallEndedScreen onDismiss={dismissSoftphoneCallEndedToLogin} />;
  }

  if (!token) {
    if (incomingBootState === 'checking') {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            backgroundColor: '#000',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        />
      );
    }
    return (
      <>
        <LoginPage onLogin={handleLogin} />
      </>
    );
  }

  if (loading && !doorbellIsland) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#000',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Typography
          sx={{
            color: '#2391FF',
            fontSize: 18,
            fontWeight: 'bold'
          }}
        >
          {t('common.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}
    >
      {/* Debug logs en pantalla */}
      {/* {currentScreen === 'dashboard' && debugLogs.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            top: 10,
            right: 10,
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: '#fff',
            padding: 1,
            borderRadius: 1,
            fontSize: '12px',
            zIndex: 2000,
            maxWidth: 200
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 'bold', marginBottom: 0.5 }}>
            Debug Swipe:
          </Typography>
          {debugLogs.map((log, index) => (
            <Typography key={index} variant="caption" sx={{ display: 'block', marginBottom: 0.25 }}>
              {log}
            </Typography>
          ))}
        </Box>
      )} */}

      {/* Header principal - solo visible en dashboard */}
      {currentScreen === 'dashboard' && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingLeft: 2.5,
            paddingRight: (isHorizontal && selectedRoom) ? '10px' : 2.5, // Horizontal con habitación: 10px del borde derecho para el usuario
            paddingY: 0.75,
            backgroundColor: '#000',
            paddingTop: 1.5,
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', position: 'relative', flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', minHeight: '64px' }}>
              <Box
                component="img"
                src="/pwa/logo192.png"
                alt="SHC Logo"
                loading="eager"
                sx={{
                  height: '99.275px', // tamaño original
                  width: 'auto',
                  borderRadius: '12px',
                  border: 'none',
                  outline: 'none',
                  display: 'block',
                  objectFit: 'contain',
                  transform: 'scaleX(1.25)',
                  position: 'absolute',
                  // Original era top: -15px; solo se sube (mismo left/tamaño)
                  top: '-28px',
                  left: '-12px',
                }}
              />
            </Box>
          </Box>

          {/* Título: posición horizontal y tamaño originales; solo subido (marginTop original -7px) */}
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 'bold',
              color: '#fff',
              position: 'absolute',
              left: '117px',
              marginTop: '-20px',
              zIndex: 1,
            }}
          >
            Smart Home Control
          </Typography>

          {/* Fecha/hora a la izquierda; meteo centrado al ancho del header */}
          <Typography
            component="span"
            sx={{
              position: 'absolute',
              left: '8px',
              bottom: '6px',
              fontSize: '0.65rem',
              color: '#ccc',
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
              zIndex: 1,
            }}
          >
            {dashboardClockLabel}
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              bottom: '2px',
              transform: 'translateX(-50%)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WeatherChip size="sm" />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, marginTop: '-7px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <IconButton
                onClick={() => setShowUserMenu(!showUserMenu)}
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 15,
                  backgroundColor: '#333',
                  color: '#fff'
                }}
              >
                <AccountCircle />
              </IconButton>
              <Typography
                sx={{
                  fontSize: 10,
                  color: '#fff',
                  marginTop: 0.5,
                  textAlign: 'center',
                  lineHeight: 1
                }}
              >
                {username || 'Usuario'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Tabs - solo visible en dashboard */}
      {currentScreen === 'dashboard' && (
        <Box
          sx={{
            backgroundColor: '#000',
            paddingTop: 0,
            paddingBottom: 0,
            position: 'relative'
          }}
        >
          <Box
            ref={boardTabsContainerRef}
            sx={{ 
            paddingLeft: 0.625, 
            paddingRight: (isHorizontal && selectedRoom) ? 0 : 0.625, // Sin padding derecho cuando hay habitación seleccionada en horizontal (igual que FavoritesPage)
            paddingY: 0, 
            paddingTop: 0.5, 
            paddingBottom: 0 
          }}>
            <Tabs
              value={selectedTab}
              onChange={(_, newValue) => {
                console.log('🎯 [TABS] Click en tab:', newValue);
                handleTabPress(newValue);
              }}
              variant="scrollable"
              scrollButtons={false}
              allowScrollButtonsMobile
              sx={{
                marginBottom: -3,
                '& .MuiTabs-indicator': {
                  display: 'none'
                },
                '& .MuiTabs-scrollButtons': {
                  display: 'none'
                },
                '& .MuiTabs-scroller': {
                  overflowX: 'auto !important',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch',
                  touchAction: 'pan-x',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  }
                }
              }}
            >
              {boards.map((board, index) => (
                <Tab
                  key={board.id}
                  label={board.nombre.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                  sx={{
                    paddingX: 1,
                    paddingY: 0.5,
                    marginRight: 0.5,
                    borderRadius: 1,
                    fontSize: 12,
                    color: '#fff',
                    fontWeight: 'normal',
                    textTransform: 'capitalize',
                    minHeight: 'auto',
                    minWidth: 'auto',
                    '&.Mui-selected': {
                      backgroundColor: '#2391FF',
                      color: '#000',
                      fontWeight: 'bold'
                    }
                  }}
                />
              ))}
            </Tabs>
          </Box>
          {/* Línea azul pegada a los tabs */}
          <Box
            sx={{
              position: 'absolute',
              bottom: -0.5,
              left: 0,
              right: 0,
              height: 3,
              backgroundColor: '#2391FF'
            }}
          />
        </Box>
      )}

            {/* Contenido principal */}
      <Box
        ref={dashboardContentRef}
        className={
          currentScreen === 'dashboard' && !selectedRoom
            ? 'shc-room-list-content-pad'
            : undefined
        }
        onScroll={() => {
          if (currentScreen === 'dashboard' && !selectedRoom) {
            roomsListHasScrolledRef.current = true;
            if (roomsListScrollFlagTimeoutRef.current) {
              clearTimeout(roomsListScrollFlagTimeoutRef.current);
            }
            roomsListScrollFlagTimeoutRef.current = setTimeout(() => {
              roomsListHasScrolledRef.current = false;
            }, 150);
          }
        }}
        sx={{
          flex: 1,
          ...((currentScreen === 'widgets' || (currentScreen === 'dashboard' && selectedRoom && isHorizontal)) ? { display: 'flex', flexDirection: 'column' } : {}),
          paddingLeft: 0, // Base 0; margen horizontal vía .shc-room-list-content-pad (index.css) en vista de lista de habitaciones
          // Favoritos usa el mismo paddingRight que Audio/Clima/Cámaras para alinear el "?" del header
          paddingRight: (currentScreen === 'dashboard' && selectedRoom && isHorizontal) ? 0 : 1.25,
          paddingTop: (currentScreen === 'favorites' || currentScreen === 'softphone') ? 0 : (currentScreen === 'dashboard' && cameraWindowPaddingTop > 0 ? cameraWindowPaddingTop : -10),
          paddingBottom: (currentScreen === 'favorites' || currentScreen === 'video' || currentScreen === 'realtime' || currentScreen === 'reports' || currentScreen === 'assistant' || currentScreen === 'alarm' || currentScreen === 'music' || currentScreen === 'softphone' || currentScreen === 'climate' || currentScreen === 'widgets' || currentScreen === 'pileta') ? 0 : (currentScreen === 'dashboard' && selectedRoom && isHorizontal) ? 'calc(var(--shc-bottom-nav-height, 56px) + 5px)' : (isHorizontal ? 80 : 20), // Habitación horizontal: 5px sobre la barra inferior. Resto: reserva fija.
          transition: 'padding-top 0.3s ease-in-out', // Transición suave para el padding
          overscrollBehavior: 'none',
          // CRÍTICO: Cuando hay habitación seleccionada en Dashboard o estamos en Settings, el scroll debe estar en el contenedor hijo, NO aquí.
          overflowY:
            (
              currentScreen === 'favorites' ||
              currentScreen === 'realtime' ||
              currentScreen === 'reports' ||
              currentScreen === 'assistant' ||
              currentScreen === 'alarm' ||
              currentScreen === 'music' ||
              currentScreen === 'softphone' ||
              currentScreen === 'climate' ||
              currentScreen === 'widgets' ||
              currentScreen === 'pileta' ||
              currentScreen === 'settings' ||
              (currentScreen === 'devices' && selectedRoom && !isHorizontal) ||
              (currentScreen === 'dashboard' && selectedRoom) ||
              (currentScreen === 'dashboard' && !selectedRoom && !dashRoomGridOverflows)
            ) ? 'hidden' : 'auto',
          minHeight: 0, // Importante para que flex funcione correctamente
          WebkitOverflowScrolling:
            currentScreen === 'dashboard' && !selectedRoom && dashRoomGridOverflows ? 'touch' : 'auto',
          // CRÍTICO: Permitir scroll vertical cuando hay habitación seleccionada
          touchAction:
            currentScreen === 'widgets' || (currentScreen === 'dashboard' && (selectedRoom || dashRoomGridOverflows))
              ? 'pan-y'
              : undefined,
        }}
        onTouchStart={currentScreen === 'dashboard' && !selectedRoom ? swipeHandlers.handleSwipeStart : undefined}
      onTouchMove={currentScreen === 'dashboard' && !selectedRoom ? swipeHandlers.handleSwipeMove : undefined}
      onTouchEnd={currentScreen === 'dashboard' && !selectedRoom ? swipeHandlers.handleSwipeEnd : undefined}
      onMouseDown={currentScreen === 'dashboard' && !selectedRoom ? swipeHandlers.handleSwipeStart : undefined}
      onMouseMove={currentScreen === 'dashboard' && !selectedRoom ? swipeHandlers.handleSwipeMove : undefined}
      onMouseUp={currentScreen === 'dashboard' && !selectedRoom ? swipeHandlers.handleSwipeEnd : undefined}
      onMouseLeave={currentScreen === 'dashboard' && !selectedRoom ? swipeHandlers.handleSwipeCancel : undefined}
      >
        {currentScreen === 'realtime' ? (
          // Vista de Energía Solar
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
              <CircularProgress sx={{ color: '#2391FF' }} />
            </Box>
          }>
          <SolarEnergy 
            onBack={() => setCurrentScreen('dashboard')}
            initialTab={userHomePage === 'realtime' && userHomeTab !== null ? userHomeTab : undefined}
            demoViewMode={demoViewMode}
          />
          </Suspense>
        ) : currentScreen === 'reports' ? (
          // Vista de Reportes Históricos
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
              <CircularProgress sx={{ color: '#2391FF' }} />
            </Box>
          }>
          <HistoricalReports 
            onBack={() => setCurrentScreen('dashboard')}
            initialTab={userHomePage === 'reports' && userHomeTab !== null ? userHomeTab : undefined}
          />
          </Suspense>
        ) : currentScreen === 'settings' ? (
          // Vista de Settings
          <Suspense fallback={
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: 'calc(100vh - 300px)',
              width: '100%',
              height: '100%'
            }}>
              <CircularProgress sx={{ color: '#2391FF' }} />
            </Box>
          }>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SettingsPage 
              user={userData} 
              onBack={() => setCurrentScreen('dashboard')}
              todosArtefactos={todosArtefactos}
              mapaArtefactos={mapaArtefactos}
              onDeviceToggle={handleDeviceToggle}
              onControlCover={handleControlCover}
              confirmingDevices={confirmingDevices}
              deviceErrors={deviceErrors}
              coverActions={coverActions}
              onOpenPileta={handleOpenPileta}
            />
          </Box>
          </Suspense>
        ) : currentScreen === 'assistant' ? (
          // Hub unificado Asistente IA (Chat + Automatizaciones + Guía)
          <Box sx={{ flex: 1, overflow: 'hidden', height: '100%', minHeight: 0 }}>
            <Suspense fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress sx={{ color: '#2391FF' }} />
              </Box>
            }>
              <AssistantHubPage
                onBack={handleAssistantHubBack}
                initialTab={assistantHubTab}
                initialRuleId={deepLinkRuleId}
              />
            </Suspense>
          </Box>
        ) : currentScreen === 'favorites' ? (
          // Vista de Favoritos
          <Box sx={{ flex: 1, overflow: 'hidden', height: '100%', minHeight: 0 }}>
            <Suspense fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress sx={{ color: '#2391FF' }} />
              </Box>
            }>
            <FavoritesPage
              onBack={() => setCurrentScreen('dashboard')}
              favoriteDevices={favoriteDevices}
              onFavoriteDevicesChange={setFavoriteDevices}
              favoriteSonosSpeakers={favoriteSonosSpeakers}
              onFavoriteSonosSpeakersChange={setFavoriteSonosSpeakers}
              sonosFavorites={sonosFavorites}
              sonosFavoriteOrden={sonosFavoriteOrden}
              onSonosFavoriteToggle={handleSonosFavoriteToggle}
              onOpenSonosModal={handleOpenSonosModal}
              onSonosTransport={handleSonosTransport}
              onDeviceClick={handleDeviceClick}
              onDeviceToggle={handleDeviceToggle}
              onControlCover={handleControlCover}
              onDeviceImageClick={handleDeviceImageClick}
                    userRole={userRole}
              userId={userData?.id}
                    confirmingDevices={confirmingDevices}
                    confirmedDevices={confirmedDevices}
              deviceErrors={deviceErrors}
                    coverActions={coverActions}
                    deviceTimerStates={deviceTimerStates}
              favoriteRoomsOrder={favoriteRoomsOrder}
              onFavoriteRoomsOrderChange={setFavoriteRoomsOrder}
              todosArtefactos={todosArtefactos}
              mapaArtefactos={mapaArtefactos}
              initialView={userHomePage === 'favorites' && userHomeTab !== null ? userHomeTab : undefined}
            />
            </Suspense>
          </Box> ) : VideoModule && currentScreen === 'video' ? (
          <VideoModule.Screen onBack={() => {
            if (leaveSoftphoneQuickIfNeeded('dashboard')) return;
            setCurrentScreen('dashboard');
          }} demoViewMode={demoViewMode} />
        ) : currentScreen === 'alarm' ? (
          // Vista de Sistema de Alarma (en desarrollo)
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
              <CircularProgress sx={{ color: '#2391FF' }} />
            </Box>
          }>
            <AlarmSystem onBack={() => setCurrentScreen('dashboard')} />
          </Suspense>
        ) : currentScreen === 'music' ? (
          // Vista de Sistema de Música (en desarrollo)
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
              <CircularProgress sx={{ color: '#2391FF' }} />
            </Box>
          }>
            <MusicSystem onBack={() => setCurrentScreen('dashboard')} />
          </Suspense>
        ) : currentScreen === 'softphone' ? (
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
              <CircularProgress sx={{ color: '#2391FF' }} />
            </Box>
          }>
            <SoftphonePage onBack={() => {
              if (leaveSoftphoneQuickIfNeeded('dashboard')) return;
              setCurrentScreen('dashboard');
            }} />
          </Suspense>
        ) : currentScreen === 'climate' ? (
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
              <CircularProgress sx={{ color: '#2391FF' }} />
            </Box>
          }>
            <Box sx={{ flex: 1, height: '100%', minHeight: 0, overflow: 'hidden', width: '100%', maxWidth: '100%' }}>
              <ClimateSystem
                onBack={() => setCurrentScreen('dashboard')}
                onOpenEvohomeModal={handleOpenEvohomeModal}
                onOpenSamsungModal={handleOpenSamsungModal}
                onOpenLgModal={handleOpenLgModal}
              />
            </Box>
          </Suspense>
        ) : currentScreen === 'widgets' ? (
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
              <CircularProgress sx={{ color: '#2391FF' }} />
            </Box>
          }>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <WidgetsDashboard
              onBack={() => setCurrentScreen('dashboard')}
              favoriteDevices={favoriteDevices}
              onDeviceToggle={handleDeviceToggle}
              onDeviceBrightness={(device, value) => {
                const listenerId = WebSocketService.assignListener(device.id);
                if (listenerId !== null) {
                  WebSocketService.sendDeviceControl(device.id, 'set_brightness', value);
                }
              }}
              initialTab={userHomePage === 'widgets' && (userHomeTab === 0 || userHomeTab === 1) ? (userHomeTab as 0 | 1) : undefined}
              onNavigate={(screen, opts) => {
                if (screen === 'assistant' && opts?.draft) {
                  try {
                    sessionStorage.setItem('shc_assistant_draft', opts.draft);
                  } catch {
                    /* ignore */
                  }
                }
                if (opts?.roomId != null) {
                  const room = rooms.find((r: any) => r.id === opts.roomId) || Object.values(mapaHabitaciones).find((h: any) => h.datos?.id === opts.roomId);
                  if (room && (room as any).datos) {
                    setSelectedRoom({ id: (room as any).datos.id, nombre: (room as any).datos.nombre, ...((room as any).datos || {}) });
                    setCurrentScreen('dashboard');
                    return;
                  }
                }
                if (screen === 'pileta') {
                  const piletaDev = Object.values(mapaPileta)[0];
                  if (piletaDev) {
                    setPiletaPageTitle((piletaDev.nombre || piletaDev.display_name || '').trim());
                    upsertPiletaDevice(piletaDev);
                  }
                  (window as any).__piletaReturnScreen = 'widgets';
                }
                setCurrentScreen(resolveAllowedScreen(screen, userData || getUser()) as any);
              }}
            />
          </Box>
          </Suspense>
        ) : currentScreen === 'pileta' ? (
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
              <CircularProgress sx={{ color: '#2391FF' }} />
            </Box>
          }>
            <Box sx={{ flex: 1, height: '100%', minHeight: 0, overflow: 'hidden', width: '100%', maxWidth: '100%' }}>
              <PiletaPage
                pageTitle={piletaPageTitle}
                onBack={handleClosePileta}
              />
            </Box>
          </Suspense>
        ) : currentScreen === 'dashboard' ? (
          // Vista de Habitaciones + Dispositivos (si hay habitación seleccionada)
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isHorizontal ? 'row' : 'column', 
            flex: 1,
            minHeight: 0, // Permite que el flex item se encoja correctamente
            overflow: selectedRoom ? 'hidden' : 'visible',
            width: '100%', // Forzar 100% de ancho
            paddingRight: (isHorizontal && selectedRoom) ? 0 : undefined, // Sin padding cuando hay habitación seleccionada en horizontal
            boxSizing: 'border-box', // Incluir padding en el cálculo del ancho
            // CRÍTICO: Cuando hay habitación seleccionada, NO usar touchAction aquí - dejar que los eventos pasen directamente al contenedor scrollable
          }}>
            <Box
              sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              width: selectedRoom ? '100%' : 'auto', // Forzar 100% cuando hay habitación seleccionada en horizontal
              marginRight: (isHorizontal && selectedRoom) ? '406px' : (isHorizontal ? '391px' : 0), // Igual que Favoritos y Settings (406px) cuando hay habitación seleccionada
              paddingRight: (isHorizontal && selectedRoom) ? 0 : undefined, // Sin padding cuando hay habitación seleccionada en horizontal (igual que FavoritesPage)
              overflowX: 'hidden',
              minHeight: 0, // Crítico para que el flex item pueda hacer scroll
              overflowY: selectedRoom ? 'hidden' : 'visible',
              maxWidth: (isHorizontal && selectedRoom) ? 'none' : undefined, // Permitir que use todo el ancho disponible
              boxSizing: 'border-box', // Incluir padding en el cálculo del ancho
              // CRÍTICO: Cuando hay habitación seleccionada, NO usar touchAction aquí - dejar que los eventos pasen directamente al contenedor scrollable hijo
            }}
            >
              {/* Matriz de habitaciones */}
              <Box
                ref={dashboardRoomGridRef}
                className={isHorizontal && selectedRoom ? 'shc-room-matrix-inset' : undefined}
                sx={{ 
                display: 'grid', 
                gridTemplateColumns: isHorizontal ? `repeat(auto-fill, minmax(${ROOM_CARD_HEIGHT}px, ${ROOM_CARD_HEIGHT}px))` : 'repeat(2, 1fr)', 
                gap: isHorizontal ? '8px' : 1, 
                paddingTop: 1,
                paddingBottom: selectedRoom ? 1 : 2.5,
                justifyContent: 'flex-start',
                width: '100%',
                touchAction: dashRoomGridOverflows ? 'pan-y' : undefined,
              }}>
              {rooms.map((room) => (
              <Card
                key={room.id}
                sx={{
                  backgroundColor: '#333',
                  padding: 1,
                  borderRadius: 2,
                  height: ROOM_CARD_HEIGHT,
                  width: isHorizontal ? '100%' : 'auto', // 100% para que respete el ancho de la celda del grid
                  maxWidth: isHorizontal ? `${ROOM_CARD_HEIGHT}px` : 'none', // Máximo igual al alto
                  boxSizing: 'border-box', // Incluir padding en el ancho total
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center', // Centrar contenido horizontalmente
                  textAlign: 'center',
                  margin: isHorizontal ? 0 : 0.25, // Sin margin en horizontal, el gap del grid maneja la separación
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'visible',
                  // Permitir scroll en el padre overscroll igual que DeviceCard / doc 51
                  pointerEvents: 'none',
                  '&:hover': {
                    backgroundColor: '#444'
                  }
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 'bold',
                      color: '#fff',
                      marginBottom: 1
                    }}
                  >
                    {room.nombre}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center',  }}>
                    <Bolt
                      sx={{
                        fontSize: 14,
                        color: getColorForConsumo(room.consumo)
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 'bold',
                        marginLeft: 0.375,
                        color: getColorForConsumo(room.consumo)
                      }}
                    >
                      {formatConsumption(room.consumo || 0)}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (roomsListHasScrolledRef.current) return;

                    addDebugLog(`🏠 Click: ${room.nombre}`);

                    setPreventDeviceClicks(true);
                    setTimeout(() => {
                      setPreventDeviceClicks(false);
                    }, 300); // 300ms de protección

                    setSelectedRoom(room);
                    setShowGroupsModal(false);
                    setSelectedRoomForGroups(null);
                    closeAllDeviceModals();
                    setLoading(true);
                    setShowExitMessage(false);

                    for (let i = 0; i < 10; i++) {
                      window.history.pushState({ page: `devices-${i}` }, '', '');
                    }

                    if (!isHorizontal) {
                      setCurrentScreen('devices');
                    }

                    if (!isMapaDispositivosCargado()) {
                      const checkMapaLoaded = () => {
                        if (isMapaDispositivosCargado()) {
                          const habitacionData = mapaHabitaciones[room.id];
                          if (habitacionData) {
                            const devicesFromMap = habitacionData.dispositivos.map(id => mapaDispositivos[id]).filter(Boolean);
                            const enrichedDevices = devicesFromMap.map(device => {
                              const mapaDevice = mapaDispositivos[device.id];
                              return mapaDevice ? {
                                ...device,
                                ...mapaDevice,
                                pin_required: mapaDevice.pin_required ?? device.pin_required ?? false,
                                auth_required: mapaDevice.auth_required ?? device.auth_required ?? false,
                              } : device;
                            });
                            setDevices(enrichedDevices);
                            setLoading(false);
                            eventBus.off('estructura:actualizada', checkMapaLoaded);
                          } else {
                            ApiService.getRoomDevices(room.id)
                              .then(devicesData => {
                                const enrichedDevices = devicesData.map((device: any) => {
                                  const mapaDevice = mapaDispositivos[device.id];
                                  return mapaDevice ? {
                                    ...device,
                                    ...mapaDevice,
                                    pin_required: mapaDevice.pin_required ?? device.pin_required ?? false,
                                    auth_required: mapaDevice.auth_required ?? device.auth_required ?? false,
                                  } : {
                                    ...device,
                                    pin_required: device.pin_required ?? false,
                                    auth_required: device.auth_required ?? false,
                                  };
                                });
                                setDevices(enrichedDevices);
                                setLoading(false);
                                eventBus.off('estructura:actualizada', checkMapaLoaded);
                              })
                              .catch(error => {
                                void error;
                                setLoading(false);
                                eventBus.off('estructura:actualizada', checkMapaLoaded);
                              });
                          }
                        }
                      };
                      eventBus.on('estructura:actualizada', checkMapaLoaded);
                      setTimeout(() => {
                        if (!isMapaDispositivosCargado()) {
                          eventBus.off('estructura:actualizada', checkMapaLoaded);
                          ApiService.getRoomDevices(room.id)
                            .then(devicesData => {
                              const enrichedDevices = devicesData.map((device: any) => {
                                const mapaDevice = mapaDispositivos[device.id];
                                return mapaDevice ? {
                                  ...device,
                                  ...mapaDevice,
                                  pin_required: mapaDevice.pin_required ?? device.pin_required ?? false,
                                  auth_required: mapaDevice.auth_required ?? device.auth_required ?? false,
                                } : {
                                  ...device,
                                  pin_required: device.pin_required ?? false,
                                  auth_required: device.auth_required ?? false,
                                };
                              });
                              setDevices(enrichedDevices);
                              setLoading(false);
                            })
                            .catch(error => {
                              void error;
                              setLoading(false);
                            });
                        }
                      }, 5000);
                      return;
                    }

                    const habitacionData = mapaHabitaciones[room.id];
                    if (habitacionData) {
                      const devicesFromMap = habitacionData.dispositivos.map(id => mapaDispositivos[id]).filter(Boolean);
                      const enrichedDevices = devicesFromMap.map(device => {
                        const mapaDevice = mapaDispositivos[device.id];
                        return mapaDevice ? {
                          ...device,
                          ...mapaDevice,
                          pin_required: mapaDevice.pin_required ?? device.pin_required ?? false,
                          auth_required: mapaDevice.auth_required ?? device.auth_required ?? false,
                          camera_ids: mapaDevice.camera_ids || device.camera_ids || [],
                        } : device;
                      });
                      setDevices(enrichedDevices);
                      setLoading(false);
                    } else {
                      ApiService.getRoomDevices(room.id)
                        .then(devicesData => {
                          const enrichedDevices = devicesData.map((device: any) => {
                            const mapaDevice = mapaDispositivos[device.id];
                            return mapaDevice ? {
                              ...device,
                              ...mapaDevice,
                              pin_required: mapaDevice.pin_required ?? device.pin_required ?? false,
                              auth_required: mapaDevice.auth_required ?? device.auth_required ?? false,
                              camera_ids: mapaDevice.camera_ids || device.camera_ids || [],
                            } : {
                              ...device,
                              pin_required: device.pin_required ?? false,
                              auth_required: device.auth_required ?? false,
                              camera_ids: device.camera_ids || [],
                            };
                          });
                          setDevices(enrichedDevices);
                          setLoading(false);
                        })
                        .catch(error => {
                          void error;
                          setLoading(false);
                        });
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: 2,
                    pointerEvents: 'auto',
                    touchAction: dashRoomGridOverflows ? 'pan-y' : 'auto',
                    zIndex: 1,
                    cursor: 'pointer',
                  }}
                />
              </Card>
              ))}
              </Box>
              
              {/* Vista de dispositivos cuando hay habitación seleccionada - SOLO en horizontal */}
              {selectedRoom && isHorizontal && (
                <>
                  {/* Segunda barra: Consumo + Grupos */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      paddingLeft: 2.5,
                      paddingRight: 0, // Eliminado para aprovechar todo el espacio hasta el borde derecho
                      paddingTop: 0.5,
                      paddingBottom: 0.5,
                      marginTop: 1.5,
                      backgroundColor: '#000',
                      borderTop: '1px solid #2391FF',
                      borderBottom: '1px solid #2391FF',
                      position: 'sticky',
                      top: 0,
                      zIndex: 100
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: '#888',
                        fontWeight: 'normal',
                        marginTop: 0,
                        marginBottom: 0
                      }}
                    >
                      {devices.filter(d => (d.consumo || 0) > 0).length} {t('dashboard.consuming')} - {formatConsumption(devices.reduce((sum, d) => sum + (d.consumo || 0), 0))} Total
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.875, marginTop: 0, marginBottom: 0 }}>
                      <Typography
                        onClick={() => {
                          if (selectedRoom?.id) {
                            handleOpenGroupsModal(selectedRoom.id);
                          }
                        }}
                        sx={{
                          fontSize: 13,
                          color: showGroupsModal && selectedRoomForGroups === selectedRoom?.id ? '#2391FF' : '#fff',
                          fontWeight: showGroupsModal && selectedRoomForGroups === selectedRoom?.id ? 'bold' : 'normal',
                          marginTop: 0,
                          marginBottom: 0,
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: showGroupsModal && selectedRoomForGroups === selectedRoom?.id ? 'none' : 'underline'
                          }
                        }}
                      >
                        {t('common.groups')}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Tercera barra: Ordenar, Filtrar, Limpiar y Búsqueda */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: 2.5,
                      paddingRight: 0, // Eliminado para aprovechar todo el espacio hasta el borde derecho
                      paddingTop: 0.625,
                      paddingBottom: 0.625,
                      backgroundColor: '#000',
                      borderBottom: '1px solid #2391FF',
                      position: 'sticky',
                      top: 20,
                      zIndex: 100
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 0.75, marginLeft: -2 }}>
                      <Button
                        onClick={(event) => {
                          setSortAnchorEl(event.currentTarget);
                          for (let i = 0; i < 5; i++) {
                            window.history.pushState({ page: `sort-menu-${i}` }, '', '');
                          }
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.375,
                          color: sortCriterion !== 'personalizado' ? '#2391FF' : '#fff',
                          fontSize: 11,
                          fontWeight: sortCriterion !== 'personalizado' ? 'bold' : 'normal',
                          paddingX: 0.75,
                          paddingY: 0.25,
                          minWidth: 'auto'
                        }}
                      >
                        <Sort sx={{ fontSize: 14 }} />
                        {t('common.sort')}
                      </Button>
                      <Button
                        onClick={(event) => {
                          setFilterAnchorEl(event.currentTarget);
                          for (let i = 0; i < 5; i++) {
                            window.history.pushState({ page: `filter-menu-${i}` }, '', '');
                          }
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.375,
                          color: hayFiltrosActivos ? '#2391FF' : '#fff',
                          fontSize: 11,
                          fontWeight: hayFiltrosActivos ? 'bold' : 'normal',
                          paddingX: 0.75,
                          paddingY: 0.25,
                          minWidth: 'auto'
                        }}
                      >
                        <FilterList sx={{ fontSize: 14 }} />
                        {t('common.filter')}
                      </Button>
                      {/* Botón Limpiar siempre junto a FILTRAR en horizontal */}
                      {isHorizontal && (
                        <Button
                          onClick={limpiarFiltros}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.375,
                            color: hayFiltrosActivos ? '#FF5252' : '#fff',
                            fontSize: 11,
                            fontWeight: hayFiltrosActivos ? 'bold' : 'normal',
                            paddingX: 0.75,
                            paddingY: 0.25,
                            minWidth: 'auto'
                          }}
                        >
                          <Close sx={{ fontSize: 14 }} />
                          {t('common.clear')}
                        </Button>
                      )}
                    </Box>
                    
                    {/* Contador: sólo dispositivos que pasan el filtro (naranja), sin total */}
                    {hayFiltrosActivos && (
                      <Box
                        sx={{
                          flex: '0 0 auto',
                          display: 'flex',
                          alignItems: 'center',
                          paddingX: 1.25,
                          minWidth: 0
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 'bold',
                            color: '#FF8C00',
                            lineHeight: 1.2
                          }}
                        >
                          {filteredVisibleCount}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Botón Limpiar separado - solo cuando NO es horizontal */}
                    {!isHorizontal && (
                      <Button
                        onClick={limpiarFiltros}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.375,
                          color: hayFiltrosActivos ? '#FF5252' : '#fff',
                          fontSize: 11,
                          fontWeight: hayFiltrosActivos ? 'bold' : 'normal',
                          paddingX: 0.75,
                          paddingY: 0.25,
                          minWidth: 'auto'
                        }}
                      >
                        <Close sx={{ fontSize: 14 }} />
                        {t('common.clear')}
                      </Button>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 0.5, flexShrink: 0 }}>
                    <IconButton
                      onClick={() => setRoomReorderMode((on) => !on)}
                      aria-label={t('deviceList.sortOptions.custom')}
                      sx={{
                        color: roomReorderMode ? '#2391FF' : '#fff',
                        padding: '2px',
                      }}
                    >
                      <SwapVerticalCircleSharp sx={{ fontSize: '1.3rem' }} />
                    </IconButton>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: 2.5,
                        paddingX: 1.25,
                        paddingY: 0.25,
                        minWidth: 70,
                        height: 20,
                        border: '1px solid #555',
                        marginTop: -0.25,
                        flexShrink: 0
                      }}
                    >
                      <Search sx={{ marginRight: 0.5, color: '#fff', fontSize: 14 }} />
                      <TextField
                        placeholder={t('common.search')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        variant="standard"
                        sx={{
                          flex: 1,
                          '& .MuiInput-root': {
                            color: '#fff',
                            fontSize: 12,
                            paddingY: 0
                          },
                          '& .MuiInput-input': {
                            paddingY: 0
                          },
                          '& .MuiInput-underline:before': {
                            borderBottom: 'none'
                          },
                          '& .MuiInput-underline:after': {
                            borderBottom: 'none'
                          },
                          '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                            borderBottom: 'none'
                          }
                        }}
                      />
                    </Box>
                    </Box>
                  </Box>

                  {/* Lista de dispositivos - Grid en horizontal y vertical */}
                  <Box 
                    ref={dashboardContainerRef}
                    onScroll={() => {
                      setIsScrollingDashboard(true);
                      if (dashboardScrollTimeoutRef.current) {
                        clearTimeout(dashboardScrollTimeoutRef.current);
                      }
                      dashboardScrollTimeoutRef.current = setTimeout(() => {
                        setIsScrollingDashboard(false);
                      }, 150); // 150ms después del último scroll
                    }}
                    sx={{ 
                      // 4px + padding 1px del DeviceGrid = 5px bajo la línea azul
                      paddingTop: '4px',
                      // 4px + padding 1px del DeviceGrid = 5px del borde izquierdo de la pantalla
                      paddingLeft: '4px',
                      paddingBottom: 0, // CRÍTICO: Sin padding inferior para evitar línea gris
                      flex: 1,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      // CRÍTICO: En vista horizontal, NO usar maxHeight - usar solo flex: 1 para que el contenido pueda crecer
                      // El maxHeight estaba limitando el contenedor de manera que scrollHeight === clientHeight
                      ...(isHorizontal && selectedRoom ? {
                        // Vista horizontal: sin maxHeight para permitir scroll
                        minHeight: 0,
                        height: 0, // Forzar que use flex: 1
                      } : {
                        // Vista vertical: listado más alto (186 → 149: +37px vs original)
                        maxHeight: 'calc(100vh - 149px)',
                        minHeight: 0,
                      }),
                      overscrollBehavior: 'none',
                      WebkitOverflowScrolling: 'touch', // Suavizar scroll en iOS
                      minWidth: 0, // Permitir que el flex item se encoja correctamente
                      boxSizing: 'border-box',
                      width: '100%',
                      backgroundColor: '#000', // CRÍTICO: Fondo sólido igual que contenedor principal
                      // CRÍTICO: Eliminar cualquier borde o línea visible
                      border: 'none',
                      outline: 'none',
                      touchAction: 'pan-y',
                      willChange: 'scroll-position',
                      transform: 'translateZ(0)',
                      '&::-webkit-scrollbar': {
                        display: 'none', // Ocultar scrollbar en WebKit
                      },
                      scrollbarWidth: 'none', // Ocultar scrollbar en Firefox
                    }}>
                    <DeviceGrid
                      items={memoizedRoomGridItems}
                      reorderMode={roomReorderMode}
                      onReorder={stableRoomReorder}
                      userRole={userRole}
                      deviceErrors={deviceErrors}
                      confirmingDevices={confirmingDevices}
                      confirmedDevices={confirmedDevices}
                      coverActions={coverActions}
                      onDeviceClick={handleDeviceClick}
                      onDeviceToggle={handleDeviceToggle}
                      onControlCover={handleControlCover}
                      onDeviceImageClick={handleDeviceImageClick}
                      deviceTimerStates={deviceTimerStates}
                      dashboardNumColumns={dashboardNumColumns}
                      onOpenSonosModal={handleOpenSonosModal}
                      onSonosTransport={handleSonosTransport}
                      onOpenSamsungModal={handleOpenSamsungModal}
                    onOpenAndroidTvModal={handleOpenAndroidTvModal}
                    onAndroidTvUpdated={handleAndroidTvUpdated}
                      onSamsungUpdated={handleSamsungUpdated}
                      onOpenLgModal={handleOpenLgModal}
                      onLgUpdated={handleLgUpdated}
                      onOpenEvohomeModal={handleOpenEvohomeModal}
                      onEvohomeUpdated={handleEvohomeUpdated}
                      onOpenPileta={handleOpenPileta}
                      aiPolicyRevision={aiPolicy.revision}
                    />
                  </Box>
                </>
              )}
            </Box>
            {/* Panel lateral derecho en horizontal para dashboard */}
            {isHorizontal && (
              <>
                {/* DeviceListModal siempre visible (debajo) */}
                <Box sx={{
                  position: 'fixed',
                  right: '11px',
                  top: '120px',
                  bottom: '56px',
                  width: '380px',
                  zIndex: 1000,
                  backgroundColor: '#1a1a1a',
                  borderLeft: '2px solid #2391FF',
                  borderRight: '2px solid #2391FF',
                  borderTop: '2px solid #2391FF',
                  borderRadius: '16px 16px 0 0',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <DeviceListModal
                    isOpen={true}
                    onClose={() => setShowDeviceListModal(false)}
                    isAdmin={userRole === 'admin'}
                    selectedHabitacion={selectedRoom?.id || null}
                    roomMatrixView={selectedRoom ? true : false}
                    nombreHabitacion={selectedRoom?.nombre || ''}
                    isDashboardView={true}
                    isTodosTab={false}
                    isSettingsPage={false}
                    // Aplicar los mismos filtros que en modo vertical cuando está en Dashboard
                    hideOfflineAndTotal={true}
                    excludeArtifactTypes={['Tablero', 'Medidor de Consumo']}
                    includeSpecialRooms={true}
                    excludeSpecialRoomNames={['Conexion a Red']}
                  />
                </Box>
                
                {/* DeviceModal por delante cuando está abierto (zIndex mayor) */}
                {deviceModalOpen && selectedDevice && (
                  <Box sx={{
                    position: 'fixed',
                    right: '11px',
                    top: '120px',
                    bottom: '56px',
                    width: '380px',
                    zIndex: 1002, // Mayor que GroupsModal y DeviceListModal para aparecer por delante
                    backgroundColor: '#1a1a1a',
                    borderLeft: '2px solid #2391FF',
                    borderRight: '2px solid #2391FF',
                    borderTop: '2px solid #2391FF',
                    borderRadius: '16px 16px 0 0',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <DeviceModal
                      key={selectedDevice?.id || 'no-device'}
                      open={deviceModalOpen}
                      onClose={handleCloseDeviceModal}
                      onBack={handleCloseDeviceModal}
                      device={selectedDevice}
                      deviceTimerStates={deviceTimerStates}
                      onDeviceToggle={handleDeviceToggle}
                      onControlCover={handleControlCover}
                    />
                  </Box>
                )}
                
                {sonosModalOpen && selectedSonosSpeaker && (
                  <Box sx={{
                    position: 'fixed',
                    right: '11px',
                    top: '120px',
                    bottom: '56px',
                    width: '380px',
                    zIndex: 1002,
                    backgroundColor: '#1a1a1a',
                    borderLeft: '2px solid #2391FF',
                    borderRight: '2px solid #2391FF',
                    borderTop: '2px solid #2391FF',
                    borderRadius: '16px 16px 0 0',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <SonosSpeakerModal
                      open={sonosModalOpen}
                      speaker={selectedSonosSpeaker ? (mapaSonos[selectedSonosSpeaker.id] || selectedSonosSpeaker) : null}
                      canControl={selectedSonosSpeaker?.can_control !== false}
                      isFavorite={selectedSonosSpeaker ? sonosFavorites.includes(selectedSonosSpeaker.id) : false}
                      onFavoriteToggle={
                        showSonosInGrid && canViewMusic(userData || getUser())
                          ? handleSonosFavoriteToggle
                          : undefined
                      }
                      onClose={handleCloseSonosModal}
                    />
                  </Box>
                )}

                {androidtvModalOpen && selectedAndroidTvDevice && (
                  <Box sx={{
                    position: 'fixed',
                    right: '11px',
                    top: '120px',
                    bottom: '56px',
                    width: '380px',
                    zIndex: 1002,
                    backgroundColor: '#1a1a1a',
                    borderLeft: '2px solid #2391FF',
                    borderRight: '2px solid #2391FF',
                    borderTop: '2px solid #2391FF',
                    borderRadius: '16px 16px 0 0',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <AndroidTvDeviceModal
                      open={androidtvModalOpen}
                      device={mapaAndroidTv[selectedAndroidTvDevice.id] || selectedAndroidTvDevice}
                      onClose={handleCloseAndroidTvModal}
                      onUpdated={handleAndroidTvUpdated}
                    />
                  </Box>
                )}

                {samsungModalOpen && selectedSamsungDevice && (
                  <Box sx={{
                    position: 'fixed',
                    right: '11px',
                    top: '120px',
                    bottom: '56px',
                    width: '380px',
                    zIndex: 1002,
                    backgroundColor: '#1a1a1a',
                    borderLeft: '2px solid #2391FF',
                    borderRight: '2px solid #2391FF',
                    borderTop: '2px solid #2391FF',
                    borderRadius: '16px 16px 0 0',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <SamsungDeviceModal
                      open={samsungModalOpen}
                      device={mapaSamsung[selectedSamsungDevice.id] || selectedSamsungDevice}
                      onClose={handleCloseSamsungModal}
                      onUpdated={handleSamsungUpdated}
                    />
                  </Box>
                )}

                {evohomeModalOpen && selectedEvohomeDevice && (
                  <Box sx={{
                    position: 'fixed',
                    right: '11px',
                    top: '120px',
                    bottom: '56px',
                    width: '380px',
                    zIndex: 1002,
                    backgroundColor: '#1a1a1a',
                    borderLeft: '2px solid #2391FF',
                    borderRight: '2px solid #2391FF',
                    borderTop: '2px solid #2391FF',
                    borderRadius: '16px 16px 0 0',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <EvohomeDeviceModal
                      open={evohomeModalOpen}
                      device={mapaEvohome[selectedEvohomeDevice.id] || selectedEvohomeDevice}
                      onClose={handleCloseEvohomeModal}
                      onUpdated={handleEvohomeUpdated}
                    />
                  </Box>
                )}
                {lgModalOpen && selectedLgDevice && (
                  <Box sx={{
                    position: 'fixed',
                    right: '11px',
                    top: '120px',
                    bottom: '56px',
                    width: '380px',
                    zIndex: 1002,
                    backgroundColor: '#1a1a1a',
                    borderLeft: '2px solid #2391FF',
                    borderRight: '2px solid #2391FF',
                    borderTop: '2px solid #2391FF',
                    borderRadius: '16px 16px 0 0',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <LgDeviceModal
                      open={lgModalOpen}
                      device={mapaLg[selectedLgDevice.id] || selectedLgDevice}
                      onClose={handleCloseLgModal}
                      onUpdated={handleLgUpdated}
                    />
                  </Box>
                )}
                
                {/* GroupsModal por delante cuando está abierto (zIndex mayor) - solo si DeviceModal no está abierto */}
                {!deviceModalOpen && showGroupsModal && selectedRoomForGroups && userData && (
                  <Box sx={{
                    position: 'fixed',
                    right: '11px',
                    top: '120px',
                    bottom: '56px',
                    width: '380px',
                    zIndex: 1001, // Mayor que DeviceListModal para aparecer por delante
                    backgroundColor: '#1a1a1a',
                    borderLeft: '2px solid #2391FF',
                    borderRight: '2px solid #2391FF',
                    borderTop: '2px solid #2391FF',
                    borderRadius: '16px 16px 0 0',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <Suspense fallback={
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                        <CircularProgress sx={{ color: '#2391FF' }} />
                      </Box>
                    }>
                      <GroupsModal
                        key={`groups-modal-${userData.id}-${selectedRoomForGroups}`}
                        isOpen={showGroupsModal}
                        onClose={handleCloseGroupsModal}
                        userId={userData.id}
                        userRole={userRole}
                        habitacionId={selectedRoomForGroups}
                        nombreHabitacion={mapaHabitaciones[selectedRoomForGroups]?.datos?.nombre || 'Habitación'}
                      />
                    </Suspense>
                  </Box>
                )}
              </>
            )}
          </Box>
        ) : currentScreen === 'devices' && selectedRoom && !isHorizontal ? (
          // Vista de dispositivos en vertical (comportamiento original)
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            flex: 1, 
            minHeight: 0,
            overflow: 'hidden'
          }}>
            {selectedRoom && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingX: 0.5,
                  paddingY: 0.125,
                  backgroundColor: '#000',
                  borderBottom: '1px solid #2391FF',
                  paddingTop: 1.25,
                  position: 'sticky',
                  top: 0,
                  zIndex: 100,
                  flexShrink: 0
                }}
              >
                <IconButton
                  onClick={() => {
                    setSelectedRoom(null);
                    setCurrentScreen('dashboard');
                  }}
                  sx={{
                    paddingX: 0.25,
                    paddingY: 0.625,
                    color: '#2391FF',
                    '& svg': { fontSize: 18 },
                  }}
                >
                  <ArrowBackIosNew />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 500,
                      color: '#ccc',
                      marginLeft: 0.625,
                      whiteSpace: 'nowrap',
                      letterSpacing: '-0.02em',
                      lineHeight: 1,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {dashboardClockLabel}
                  </Typography>
                </IconButton>
                <Box sx={{ flex: 1, alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 'bold',
                      color: '#fff',
                      textAlign: 'center'
                    }}
                  >
                    {selectedRoom.nombre}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: '#888',
                    fontWeight: 'normal'
                  }}
                >
                  ({roomTotalDeviceCount} {t('dashboard.devices')})
                </Typography>
              </Box>
            )}

            {/* Segunda barra: Consumo + Grupos */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                paddingX: 2.5,
                paddingTop: -0.5,
                paddingBottom: 0.5,
                backgroundColor: '#000',
                borderBottom: '1px solid #2391FF',
                position: 'sticky',
                top: selectedRoom ? 60 : 0,
                zIndex: 100,
                flexShrink: 0
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: '#888',
                  fontWeight: 'normal',
                  marginTop: -1,
                  marginBottom: 0
                }}
              >
                {devices.filter(d => (d.consumo || 0) > 0).length} {t('dashboard.consuming')} - {formatConsumption(devices.reduce((sum, d) => sum + (d.consumo || 0), 0))} Total
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.875, marginTop: -1, marginBottom: 0 }}>
                <Typography
                  onClick={() => {
                    if (selectedRoom?.id) {
                      handleOpenGroupsModal(selectedRoom.id);
                    }
                  }}
                  sx={{
                    fontSize: 13,
                    color: showGroupsModal && selectedRoomForGroups === selectedRoom?.id ? '#2391FF' : '#fff',
                    fontWeight: showGroupsModal && selectedRoomForGroups === selectedRoom?.id ? 'bold' : 'normal',
                    marginTop: 0,
                    marginBottom: 0,
                    cursor: 'pointer',
                    textDecoration: 'none', // Nunca subrayado
                    '&:hover': {
                      textDecoration: 'none' // Nunca subrayado en hover
                    }
                  }}
                >
                  {t('common.groups')}
                </Typography>
              </Box>
            </Box>

            {/* Tercera barra: Ordenar, Filtrar, Limpiar y Búsqueda */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingX: 2.5,
                paddingTop: 0.625,
                paddingBottom: 0.625,
                backgroundColor: '#000',
                borderBottom: '1px solid #2391FF',
                position: 'sticky',
                top: selectedRoom ? 80 : 20,
                zIndex: 100,
                flexShrink: 0,
                // CRÍTICO: Asegurar que el header sticky NO bloquee eventos de touch del contenedor scrollable
                pointerEvents: 'auto', // Permitir eventos en el header
                // CRÍTICO: Los elementos dentro del header (botones, inputs) deben poder recibir eventos
                // pero el área vacía del header NO debe bloquear eventos que pasen al contenedor scrollable
              }}
            >
              <Box sx={{ display: 'flex', gap: 0.75, marginLeft: -2 }}>
                <Button
                  onClick={(event) => {
                    setSortAnchorEl(event.currentTarget);
                    for (let i = 0; i < 5; i++) {
                      window.history.pushState({ page: `sort-menu-${i}` }, '', '');
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.375,
                    color: sortCriterion !== 'personalizado' ? '#2391FF' : '#fff',
                    fontSize: 11,
                    fontWeight: sortCriterion !== 'personalizado' ? 'bold' : 'normal',
                    paddingX: 0.75,
                    paddingY: 0.25,
                    minWidth: 'auto'
                  }}
                >
                  <Sort sx={{ fontSize: 14 }} />
                  {t('common.sort')}
                </Button>
                <Button
                  onClick={(event) => {
                    setFilterAnchorEl(event.currentTarget);
                    for (let i = 0; i < 5; i++) {
                      window.history.pushState({ page: `filter-menu-${i}` }, '', '');
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.375,
                    color: hayFiltrosActivos ? '#2391FF' : '#fff',
                    fontSize: 11,
                    fontWeight: hayFiltrosActivos ? 'bold' : 'normal',
                    paddingX: 0.75,
                    paddingY: 0.25,
                    minWidth: 'auto'
                  }}
                >
                  <FilterList sx={{ fontSize: 14 }} />
                  {t('common.filter')}
                </Button>
              </Box>
              
              {hayFiltrosActivos && (
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 'bold',
                    color: '#FF8C00',
                    lineHeight: 1.2,
                    position: 'absolute',
                    left: '48%',
                    transform: 'translateX(-50%)',
                    zIndex: 1
                  }}
                >
                  {filteredVisibleCount}
                </Typography>
              )}
              
              <Button
                onClick={limpiarFiltros}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.375,
                  color: hayFiltrosActivos ? '#FF5252' : '#fff',
                  fontSize: 11,
                  fontWeight: hayFiltrosActivos ? 'bold' : 'normal',
                  paddingX: 0.75,
                  paddingY: 0.25,
                  minWidth: 'auto',
                  marginLeft: 5.5
                }}
              >
                <Close sx={{ fontSize: 14 }} />
                {t('common.clear')}
              </Button>

              <IconButton
                onClick={() => setRoomReorderMode((on) => !on)}
                aria-label={t('deviceList.sortOptions.custom')}
                sx={{
                  color: roomReorderMode ? '#2391FF' : '#fff',
                  padding: '2px',
                  marginLeft: 0.5,
                }}
              >
                <SwapVerticalCircleSharp sx={{ fontSize: '1.3rem' }} />
              </IconButton>
              
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: 2.5,
                  paddingX: 1.25,
                  paddingY: 0.25,
                  minWidth: 70,
                  height: 20,
                  border: '1px solid #555',
                  marginLeft: 1,
                  marginTop: -0.25
                }}
              >
                <Search sx={{ marginRight: 0.5, color: '#fff', fontSize: 14 }} />
                <TextField
                  placeholder={t('common.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  variant="standard"
                  sx={{
                    flex: 1,
                    '& .MuiInput-root': {
                      color: '#fff',
                      fontSize: 12,
                      paddingY: 0
                    },
                    '& .MuiInput-input': {
                      paddingY: 0
                    },
                    '& .MuiInput-underline:before': {
                      borderBottom: 'none'
                    },
                    '& .MuiInput-underline:after': {
                      borderBottom: 'none'
                    },
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                      borderBottom: 'none'
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Lista de dispositivos - Grid flexible en vertical */}
            <Box 
              ref={dashboardContainerRef}
              onScroll={() => {
                setIsScrollingDashboard(true);
                // CRÍTICO: Marcar que hubo scroll para que DeviceCard no ejecute tap
                dashboardHasScrolledRef.current = true;
                if (dashboardScrollTimeoutRef.current) {
                  clearTimeout(dashboardScrollTimeoutRef.current);
                }
                dashboardScrollTimeoutRef.current = setTimeout(() => {
                  setIsScrollingDashboard(false);
                  // CRÍTICO: Limpiar el flag de scroll después de un delay para permitir nuevos taps
                  setTimeout(() => {
                    dashboardHasScrolledRef.current = false;
                  }, 50);
                }, 150); // 150ms después del último scroll
              }}
              // CRÍTICO: Detectar taps en móviles usando touchend directamente en el contenedor scrollable
              // Esto NO interfiere con el scroll porque el evento ya pasó
              onTouchEnd={(e) => {
                // Solo procesar si NO hubo scroll
                if (!dashboardHasScrolledRef.current && dashboardTouchStartRef.current && e.changedTouches[0]) {
                  const timeSinceStart = Date.now() - dashboardTouchStartRef.current.time;
                  const deltaX = Math.abs(e.changedTouches[0].clientX - dashboardTouchStartRef.current.x);
                  const deltaY = Math.abs(e.changedTouches[0].clientY - dashboardTouchStartRef.current.y);
                  
                  // Solo ejecutar si fue un tap claro (sin movimiento significativo)
                  if (timeSinceStart < 300 && deltaX < 10 && deltaY < 10) {
                    // CRÍTICO: Usar el elemento guardado en touchStart primero
                    // Si no funciona, intentar con elementFromPoint
                    const startElement = dashboardTouchStartRef.current.element;
                    let deviceCard: HTMLElement | null = null;
                    
                    if (startElement) {
                      deviceCard = startElement.closest('[data-device-id]') as HTMLElement;
                    }
                    
                    // Si no encontramos el DeviceCard, intentar con elementFromPoint
                    if (!deviceCard) {
                      const touchX = e.changedTouches[0].clientX;
                      const touchY = e.changedTouches[0].clientY;
                      const elementAtPoint = document.elementFromPoint(touchX, touchY) as HTMLElement;
                      if (elementAtPoint) {
                        deviceCard = elementAtPoint.closest('[data-device-id]') as HTMLElement;
                      }
                    }
                    
                    if (deviceCard) {
                      // Verificar que NO sea un botón
                      const isButton = startElement?.closest('button') !== null || 
                                       startElement?.closest('.MuiIconButton-root') !== null ||
                                       startElement?.closest('.MuiButton-root') !== null ||
                                       deviceCard.querySelector('button') !== null ||
                                       deviceCard.querySelector('.MuiIconButton-root') !== null ||
                                       deviceCard.querySelector('.MuiButton-root') !== null;
                      
                      if (!isButton) {
                        const deviceId = deviceCard.getAttribute('data-device-id');
                        if (deviceId) {
                          // Buscar el dispositivo y ejecutar handleDeviceClick
                          const deviceCardData = memoizedDeviceCards.find((d: any) => d.id.toString() === deviceId);
                          if (deviceCardData && deviceCardData.device) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeviceClick(deviceCardData.device);
                          }
                        }
                      }
                    }
                  }
                }
                
                // Limpiar después de un delay
                setTimeout(() => {
                  dashboardTouchStartRef.current = null;
                  dashboardHasScrolledRef.current = false;
                }, 100);
              }}
              onTouchStart={(e) => {
                // Guardar coordenadas iniciales para detectar taps
                if (e.touches[0]) {
                  // CRÍTICO: Usar elementFromPoint para encontrar el elemento real tocado
                  // Esto funciona incluso con pointer-events: none porque buscamos el elemento visualmente
                  const touchX = e.touches[0].clientX;
                  const touchY = e.touches[0].clientY;
                  const elementAtPoint = document.elementFromPoint(touchX, touchY) as HTMLElement;
                  
                  dashboardTouchStartRef.current = {
                    x: touchX,
                    y: touchY,
                    time: Date.now(),
                    element: elementAtPoint || (e.target as HTMLElement)
                  };
                  dashboardHasScrolledRef.current = false;
                }
                // NO hacer preventDefault - permitir que el scroll funcione
              }}
              onTouchMove={(e) => {
                // Detectar si hay movimiento (scroll)
                // CRÍTICO: Marcar como scroll inmediatamente si hay cualquier movimiento
                // Esto evita que el tap se ejecute cuando hay scroll
                if (dashboardTouchStartRef.current && e.touches[0]) {
                  const deltaX = Math.abs(e.touches[0].clientX - dashboardTouchStartRef.current.x);
                  const deltaY = Math.abs(e.touches[0].clientY - dashboardTouchStartRef.current.y);
                  // CRÍTICO: Usar umbral más bajo (2px) para detectar scroll más rápido
                  if (deltaY > 2 || deltaX > 2) {
                    dashboardHasScrolledRef.current = true;
                  }
                }
                // NO hacer preventDefault - permitir que el scroll funcione
              }}
              sx={{ 
                // 2.75 clearance sticky + ~5px (rowGap) bajo la línea azul
                paddingTop: 3.375,
                paddingBottom: 0, // CRÍTICO: Sin padding inferior para evitar línea gris
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden', // CRÍTICO: No permitir scroll horizontal
                // CRÍTICO: En vista horizontal, NO usar maxHeight - usar solo flex: 1 para que el contenido pueda crecer
                // El maxHeight estaba limitando el contenedor de manera que scrollHeight === clientHeight
                ...(isHorizontal && selectedRoom ? {
                  // Vista horizontal: sin maxHeight para permitir scroll
                  minHeight: 0,
                  height: 0, // Forzar que use flex: 1
                } : {
                  // Vista vertical: listado más alto (186 → 149: +37px vs original)
                  maxHeight: 'calc(100vh - 149px)', // +2px respecto al ajuste anterior (151)
                  minHeight: 0,
                }),
                overscrollBehavior: 'none',
                WebkitOverflowScrolling: 'touch', // Suavizar scroll en iOS
                boxSizing: 'border-box',
                minWidth: 0, // Permitir que el flex item se encoja correctamente
                width: '100%',
                backgroundColor: '#000', // CRÍTICO: Fondo sólido igual que contenedor principal
                // CRÍTICO: Eliminar cualquier borde o línea visible
                border: 'none',
                outline: 'none',
                // CRÍTICO: Propiedades para scroll suave y momentum en móviles
                touchAction: 'pan-y', // Permitir solo scroll vertical
                willChange: 'scroll-position', // Optimización para scroll
                transform: 'translateZ(0)', // Aceleración por hardware
                '&::-webkit-scrollbar': {
                  display: 'none', // Ocultar scrollbar en WebKit
                },
                scrollbarWidth: 'none', // Ocultar scrollbar en Firefox
              }}>
              <DeviceGrid
                items={memoizedRoomGridItems}
                reorderMode={roomReorderMode}
                onReorder={stableRoomReorder}
                userRole={userRole}
                deviceErrors={deviceErrors}
                confirmingDevices={confirmingDevices}
                confirmedDevices={confirmedDevices}
                coverActions={coverActions}
                onDeviceClick={handleDeviceClick}
                onDeviceToggle={handleDeviceToggle}
                onControlCover={handleControlCover}
                onDeviceImageClick={handleDeviceImageClick}
                deviceTimerStates={deviceTimerStates}
                dashboardNumColumns={dashboardNumColumns}
                onOpenSonosModal={handleOpenSonosModal}
                onSonosTransport={handleSonosTransport}
                onOpenSamsungModal={handleOpenSamsungModal}
                    onOpenAndroidTvModal={handleOpenAndroidTvModal}
                    onAndroidTvUpdated={handleAndroidTvUpdated}
                onSamsungUpdated={handleSamsungUpdated}
                onOpenLgModal={handleOpenLgModal}
                onLgUpdated={handleLgUpdated}
                onOpenEvohomeModal={handleOpenEvohomeModal}
                onEvohomeUpdated={handleEvohomeUpdated}
                onOpenPileta={handleOpenPileta}
                aiPolicyRevision={aiPolicy.revision}
              />
            </Box>
          </Box>
        ) : null}
      </Box>

      {/* Navegación inferior — oculta en isla de llamada (quick-answer o timbre) */}
      {!softphoneQuickSession && !doorbellIsland && (
      <BottomNavBar
        currentScreen={currentScreen}
        isHomeActive={isHomeActive}
        isInfoActive={isInfoActive}
        isSettingsActive={isSettingsActive}
        isHorizontal={isHorizontal}
        canViewSettings={canViewSettings}
        videoModuleAvailable={Boolean(VideoModule)}
        user={userData || getUser()}
        websocketStatus={websocketStatus}
        onNavigate={handleBottomNavNavigate}
        onInfoClick={handleInfoClick}
      />
      )}

      {/* Menú de usuario */}
      {showUserMenu && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            // Por encima del panel de dispositivo en horizontal (zIndex 1002)
            zIndex: 1200
          }}
        >
          <Box
            onClick={() => setShowUserMenu(false)}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 80,
              right: 0,
              backgroundColor: '#333',
              borderRadius: 1,
              padding: 1.5,
              width: 220,
              minHeight: 40,
              boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
              zIndex: 1001
            }}
          >
            {canViewSettings && (
              <Button
                onClick={() => {
                  setShowUserMenu(false);
                  handleSettingsClick();
                }}
                sx={{
                  paddingY: 1.2,
                  paddingX: 1.5,
                  borderRadius: 1,
                  height: 'auto',
                  minHeight: 36,
                  width: '100%',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  color: '#2391FF',
                  fontSize: 14,
                  textTransform: 'none',
                  marginBottom: 0.5,
                  whiteSpace: 'nowrap',
                  textAlign: 'left'
                }}
              >
                <SettingsIcon sx={{ fontSize: 18, marginRight: 1, verticalAlign: 'middle' }} />
                {t('homePage.settings')}
              </Button>
            )}
            <Button
              onClick={() => {
                setShowUserMenu(false);
                setShowHomePageMenu(true);
              }}
              sx={{
                paddingY: 1.2,
                paddingX: 1.5,
                borderRadius: 1,
                height: 'auto',
                minHeight: 36,
                width: '100%',
                justifyContent: 'flex-start',
                alignItems: 'center',
                color: '#2391FF',
                fontSize: 14,
                textTransform: 'none',
                marginBottom: 0.5,
                whiteSpace: 'nowrap',
                textAlign: 'left'
              }}
            >
              <HomeIcon sx={{ fontSize: 18, marginRight: 1, verticalAlign: 'middle' }} />
              {t('homePage.change')}
            </Button>
            <Button
              onClick={() => {
                setShowUserMenu(false);
                openPWAInstallInstructions();
              }}
              sx={{
                paddingY: 1.2,
                paddingX: 1.5,
                borderRadius: 1,
                height: 'auto',
                minHeight: 36,
                width: '100%',
                justifyContent: 'flex-start',
                alignItems: 'center',
                color: '#2391FF',
                fontSize: 14,
                textTransform: 'none',
                marginBottom: 0.5,
                whiteSpace: 'nowrap',
                textAlign: 'left'
              }}
            >
              <GetApp sx={{ fontSize: 18, marginRight: 1, verticalAlign: 'middle' }} />
              {t('homePage.installApp')}
            </Button>
            <Button
              onClick={() => handleLogout()}
              sx={{
                paddingY: 1.2,
                paddingX: 1.5,
                borderRadius: 1,
                height: 'auto',
                minHeight: 36,
                width: '100%',
                justifyContent: 'flex-start',
                alignItems: 'center',
                color: '#2391FF',
                fontSize: 14,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                textAlign: 'left'
              }}
            >
              <LogoutIcon sx={{ fontSize: 18, marginRight: 1, verticalAlign: 'middle' }} />
              {t('common.logout')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Menú de ordenamiento */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={() => setSortAnchorEl(null)}
        PaperProps={{
          style: {
            backgroundColor: '#333',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            borderRadius: '8px'
          }
        }}
      >
        <MenuItem 
          onClick={() => applyRoomSortCriterion('defecto')}
          selected={sortCriterion === 'defecto'}
          sx={{ 
            fontSize: '0.85rem',
            padding: '4px 16px',
            '&.Mui-selected': { 
              backgroundColor: 'rgba(35, 145, 255, 0.1)', 
              color: '#2391FF'
            },
            '&:hover': {
              backgroundColor: 'rgba(35, 145, 255, 0.05)'
            }
          }}
        >
          {t('deviceList.sortOptions.byDefault')}
        </MenuItem>
        <MenuItem 
          onClick={() => applyRoomSortCriterion('personalizado')}
          selected={sortCriterion === 'personalizado'}
          sx={{ 
            fontSize: '0.85rem',
            padding: '4px 16px',
            '&.Mui-selected': { 
              backgroundColor: 'rgba(35, 145, 255, 0.1)', 
              color: '#2391FF'
            },
            '&:hover': {
              backgroundColor: 'rgba(35, 145, 255, 0.05)'
            }
          }}
        >
          {t('deviceList.sortOptions.custom')}
        </MenuItem>
        <MenuItem 
          onClick={() => applyRoomSortCriterion('consumo-mayor')}
          selected={sortCriterion === 'consumo-mayor'}
          sx={{ 
            fontSize: '0.85rem',
            padding: '4px 16px',
            '&.Mui-selected': { 
              backgroundColor: 'rgba(35, 145, 255, 0.1)', 
              color: '#2391FF'
            },
            '&:hover': {
              backgroundColor: 'rgba(35, 145, 255, 0.05)'
            }
          }}
        >
          {t('deviceList.sortOptions.highestConsumption')}
        </MenuItem>
        <MenuItem 
          onClick={() => applyRoomSortCriterion('consumo-menor')}
          selected={sortCriterion === 'consumo-menor'}
          sx={{ 
            fontSize: '0.85rem',
            padding: '4px 16px',
            '&.Mui-selected': { 
              backgroundColor: 'rgba(35, 145, 255, 0.1)', 
              color: '#2391FF'
            },
            '&:hover': {
              backgroundColor: 'rgba(35, 145, 255, 0.05)'
            }
          }}
        >
          {t('deviceList.sortOptions.lowestConsumption')}
        </MenuItem>
        <MenuItem 
          onClick={() => applyRoomSortCriterion('estado')}
          selected={sortCriterion === 'estado'}
          sx={{ 
            fontSize: '0.85rem',
            padding: '4px 16px',
            '&.Mui-selected': { 
              backgroundColor: 'rgba(35, 145, 255, 0.1)', 
              color: '#2391FF'
            },
            '&:hover': {
              backgroundColor: 'rgba(35, 145, 255, 0.05)'
            }
          }}
        >
          {t('deviceList.sortOptions.byStatus')}
        </MenuItem>
        <MenuItem 
          onClick={() => applyRoomSortCriterion('artefacto')}
          selected={sortCriterion === 'artefacto'}
          sx={{ 
            fontSize: '0.85rem',
            padding: '4px 16px',
            '&.Mui-selected': { 
              backgroundColor: 'rgba(35, 145, 255, 0.1)', 
              color: '#2391FF'
            },
            '&:hover': {
              backgroundColor: 'rgba(35, 145, 255, 0.05)'
            }
          }}
        >
          {t('deviceList.sortOptions.byArtifact')}
        </MenuItem>
        <MenuItem 
          onClick={() => applyRoomSortCriterion('nombre')}
          selected={sortCriterion === 'nombre'}
          sx={{ 
            fontSize: '0.85rem',
            padding: '4px 16px',
            '&.Mui-selected': { 
              backgroundColor: 'rgba(35, 145, 255, 0.1)', 
              color: '#2391FF'
            },
            '&:hover': {
              backgroundColor: 'rgba(35, 145, 255, 0.05)'
            }
          }}
        >
          {t('deviceList.sortOptions.byName')}
        </MenuItem>
      </Menu>

      {/* Menú de filtros */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        PaperProps={{
          style: {
            backgroundColor: '#333',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            borderRadius: '8px',
            width: '350px',
            maxHeight: '625px'
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #444', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ color: '#2391FF', fontSize: '16px', fontWeight: 'bold', mb: 1 }}>
            {t('deviceList.filterTitle')}
          </Typography>
          <IconButton
            onClick={() => setFilterAnchorEl(null)}
            sx={{
              color: '#999',
              padding: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(35, 145, 255, 0.1)',
                color: '#2391FF',
              },
            }}
          >
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        
        {/* Sección Artefactos */}
        <Box sx={{ p: 2 }}>
          <Typography sx={{ color: '#2391FF', fontSize: '14px', fontWeight: 'bold', mb: 1 }}>
            {t('deviceList.appliances')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {todosArtefactos
              .sort((a, b) => {
                // "Sin Definir" va al final
                if (a === t('deviceModal.undefined')) return 1;
                if (b === t('deviceModal.undefined')) return -1;
                return a.localeCompare(b);
              })
              .map((artefacto) => (
              <Button
                key={artefacto}
                variant={filtroArtefactos.includes(artefacto) ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  if (filtroArtefactos.includes(artefacto)) {
                    setFiltroArtefactos(filtroArtefactos.filter(a => a !== artefacto));
                  } else {
                    setFiltroArtefactos([...filtroArtefactos, artefacto]);
                  }
                }}
                sx={{
                  fontSize: '0.75rem',
                  padding: '2px 8px',
                  minWidth: 'auto',
                  backgroundColor: filtroArtefactos.includes(artefacto) ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                  color: filtroArtefactos.includes(artefacto) ? '#2391FF' : '#fff',
                  borderColor: filtroArtefactos.includes(artefacto) ? '#2391FF' : '#555',
                  '&:hover': {
                    backgroundColor: 'rgba(35, 145, 255, 0.05)'
                  }
                }}
              >
                {artefacto}
              </Button>
            ))}
          </Box>
        </Box>
        
        {/* Sección Estado */}
        <Box sx={{ p: 2 }}>
          <Typography sx={{ color: '#2391FF', fontSize: '14px', fontWeight: 'bold', mb: 1 }}>
            {t('deviceList.status')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant={filtroOnline ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setFiltroOnline(!filtroOnline);
                if (!filtroOnline) setFiltroOffline(false);
              }}
              sx={{
                fontSize: '0.75rem',
                padding: '2px 8px',
                minWidth: 'auto',
                backgroundColor: filtroOnline ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                color: filtroOnline ? '#2391FF' : '#fff',
                borderColor: filtroOnline ? '#2391FF' : '#555',
                '&:hover': {
                  backgroundColor: 'rgba(35, 145, 255, 0.05)'
                }
              }}
            >
              {t('devices.online')}
            </Button>
            <Button
              variant={filtroOffline ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setFiltroOffline(!filtroOffline);
                if (!filtroOffline) setFiltroOnline(false);
              }}
              sx={{
                fontSize: '0.75rem',
                padding: '2px 8px',
                minWidth: 'auto',
                backgroundColor: filtroOffline ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                color: filtroOffline ? '#2391FF' : '#fff',
                borderColor: filtroOffline ? '#2391FF' : '#555',
                '&:hover': {
                  backgroundColor: 'rgba(35, 145, 255, 0.05)'
                }
              }}
            >
              {t('devices.offline')}
            </Button>
            <Button
              variant={filtroAuth ? 'contained' : 'outlined'}
              size="small"
              onClick={() => { setFiltroAuth(!filtroAuth); if (!filtroAuth) setFiltroAuthConfigured(false); }}
              sx={{
                fontSize: '0.75rem',
                padding: '2px 8px',
                minWidth: 'auto',
                backgroundColor: filtroAuth ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                color: filtroAuth ? '#2391FF' : '#fff',
                borderColor: filtroAuth ? '#2391FF' : '#555',
                '&:hover': {
                  backgroundColor: 'rgba(35, 145, 255, 0.05)'
                }
              }}
            >
              AUTH_Required
            </Button>
            <Button
              variant={filtroAuthConfigured ? 'contained' : 'outlined'}
              size="small"
              onClick={() => { setFiltroAuthConfigured(!filtroAuthConfigured); if (!filtroAuthConfigured) setFiltroAuth(false); }}
              sx={{
                fontSize: '0.75rem',
                padding: '2px 8px',
                minWidth: 'auto',
                backgroundColor: filtroAuthConfigured ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                color: filtroAuthConfigured ? '#2391FF' : '#fff',
                borderColor: filtroAuthConfigured ? '#2391FF' : '#555',
                '&:hover': {
                  backgroundColor: 'rgba(35, 145, 255, 0.05)'
                }
              }}
            >
              AUTH_Configured
            </Button>
            <Button
              variant={filtroPin ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setFiltroPin(!filtroPin)}
              sx={{
                fontSize: '0.75rem',
                padding: '2px 8px',
                minWidth: 'auto',
                backgroundColor: filtroPin ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                color: filtroPin ? '#2391FF' : '#fff',
                borderColor: filtroPin ? '#2391FF' : '#555',
                '&:hover': {
                  backgroundColor: 'rgba(35, 145, 255, 0.05)'
                }
              }}
            >
              {t('devices.pin')}
            </Button>
            <Button
              variant={filtroCamera ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setFiltroCamera(!filtroCamera)}
              sx={{
                fontSize: '0.75rem',
                padding: '2px 8px',
                minWidth: 'auto',
                backgroundColor: filtroCamera ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                color: filtroCamera ? '#2391FF' : '#fff',
                borderColor: filtroCamera ? '#2391FF' : '#555',
                '&:hover': {
                  backgroundColor: 'rgba(35, 145, 255, 0.05)'
                }
              }}
            >
              {t('devices.camera')}
            </Button>
            {userRole === 'admin' && (
              <>
                <Button
                  variant={filtroAiPermitido ? 'contained' : 'outlined'}
                  size="small"
                  disabled={!aiPolicy.ready}
                  onClick={() => {
                    const next = !filtroAiPermitido;
                    setFiltroAiPermitido(next);
                    if (next) {
                      setFiltroAiDenegado(false);
                      setFiltroAiSinDefinir(false);
                    }
                  }}
                  sx={{
                    fontSize: '0.75rem',
                    padding: '2px 8px',
                    minWidth: 'auto',
                    backgroundColor: filtroAiPermitido ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                    color: filtroAiPermitido ? '#2391FF' : '#fff',
                    borderColor: filtroAiPermitido ? '#2391FF' : '#555',
                    '&:hover': {
                      backgroundColor: 'rgba(35, 145, 255, 0.05)'
                    }
                  }}
                >
                  {t('deviceList.aiPolicyAllowed')}
                </Button>
                <Button
                  variant={filtroAiDenegado ? 'contained' : 'outlined'}
                  size="small"
                  disabled={!aiPolicy.ready}
                  onClick={() => {
                    const next = !filtroAiDenegado;
                    setFiltroAiDenegado(next);
                    if (next) {
                      setFiltroAiPermitido(false);
                      setFiltroAiSinDefinir(false);
                    }
                  }}
                  sx={{
                    fontSize: '0.75rem',
                    padding: '2px 8px',
                    minWidth: 'auto',
                    backgroundColor: filtroAiDenegado ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                    color: filtroAiDenegado ? '#2391FF' : '#fff',
                    borderColor: filtroAiDenegado ? '#2391FF' : '#555',
                    '&:hover': {
                      backgroundColor: 'rgba(35, 145, 255, 0.05)'
                    }
                  }}
                >
                  {t('deviceList.aiPolicyDenied')}
                </Button>
                <Button
                  variant={filtroAiSinDefinir ? 'contained' : 'outlined'}
                  size="small"
                  disabled={!aiPolicy.ready}
                  onClick={() => {
                    const next = !filtroAiSinDefinir;
                    setFiltroAiSinDefinir(next);
                    if (next) {
                      setFiltroAiPermitido(false);
                      setFiltroAiDenegado(false);
                    }
                  }}
                  sx={{
                    fontSize: '0.75rem',
                    padding: '2px 8px',
                    minWidth: 'auto',
                    backgroundColor: filtroAiSinDefinir ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                    color: filtroAiSinDefinir ? '#2391FF' : '#fff',
                    borderColor: filtroAiSinDefinir ? '#2391FF' : '#555',
                    '&:hover': {
                      backgroundColor: 'rgba(35, 145, 255, 0.05)'
                    }
                  }}
                >
                  {t('deviceList.aiPolicyUnset')}
                </Button>
              </>
            )}
            <Button
              variant={filtroEncendido ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setFiltroEncendido(!filtroEncendido);
                if (!filtroEncendido) setFiltroApagado(false);
              }}
              sx={{
                fontSize: '0.75rem',
                padding: '2px 8px',
                minWidth: 'auto',
                backgroundColor: filtroEncendido ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                color: filtroEncendido ? '#2391FF' : '#fff',
                borderColor: filtroEncendido ? '#2391FF' : '#555',
                '&:hover': {
                  backgroundColor: 'rgba(35, 145, 255, 0.05)'
                }
              }}
            >
              {t('devices.on')}
            </Button>
            <Button
              variant={filtroApagado ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setFiltroApagado(!filtroApagado);
                if (!filtroApagado) setFiltroEncendido(false);
              }}
              sx={{
                fontSize: '0.75rem',
                padding: '2px 8px',
                minWidth: 'auto',
                backgroundColor: filtroApagado ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                color: filtroApagado ? '#2391FF' : '#fff',
                borderColor: filtroApagado ? '#2391FF' : '#555',
                '&:hover': {
                  backgroundColor: 'rgba(35, 145, 255, 0.05)'
                }
              }}
            >
              {t('devices.off')}
            </Button>
            <Button
              variant={filtroConConsumo ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setFiltroConConsumo(!filtroConConsumo);
                if (!filtroConConsumo) setFiltroSinConsumo(false);
              }}
              sx={{
                fontSize: '0.75rem',
                padding: '2px 8px',
                minWidth: 'auto',
                backgroundColor: filtroConConsumo ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                color: filtroConConsumo ? '#2391FF' : '#fff',
                borderColor: filtroConConsumo ? '#2391FF' : '#555',
                '&:hover': {
                  backgroundColor: 'rgba(35, 145, 255, 0.05)'
                }
              }}
            >
              {t('devices.withConsumption')}
            </Button>
            <Button
              variant={filtroSinConsumo ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setFiltroSinConsumo(!filtroSinConsumo);
                if (!filtroSinConsumo) setFiltroConConsumo(false);
              }}
              sx={{
                fontSize: '0.75rem',
                padding: '2px 8px',
                minWidth: 'auto',
                backgroundColor: filtroSinConsumo ? 'rgba(35, 145, 255, 0.1)' : 'transparent',
                color: filtroSinConsumo ? '#2391FF' : '#fff',
                borderColor: filtroSinConsumo ? '#2391FF' : '#555',
                '&:hover': {
                  backgroundColor: 'rgba(35, 145, 255, 0.05)'
                }
              }}
            >
              {t('deviceList.noConsumption')}
            </Button>
          </Box>
        </Box>
        
        <ModelFilterByManufacturer
          title={t('deviceList.models')}
          groups={gruposModelos}
          selected={filtroModelos}
          onChange={setFiltroModelos}
        />
      </Menu>

      {/* Modal de selección de página de inicio (bloqueante, solo si no tiene definida) */}
      {showHomePageDialog && (
        <Box sx={HOME_PAGE_DIALOG_OVERLAY_SX}>
          <Box sx={HOME_PAGE_DIALOG_CARD_SX}>
            <Typography
              sx={{
                color: '#2391FF',
                fontSize: 17,
                fontWeight: 'bold',
                marginBottom: 1,
                textAlign: 'center',
                flexShrink: 0,
              }}
            >
              {t('homePage.selectTitle')}
            </Typography>
            <Typography
              sx={{
                color: '#fff',
                fontSize: 12,
                marginBottom: 1.25,
                textAlign: 'center',
                lineHeight: 1.4,
                flexShrink: 0,
              }}
            >
              {t('homePage.selectDescription')}
            </Typography>
            <Box sx={HOME_PAGE_DIALOG_LIST_SX}>
              <Button
                onClick={() => {
                  setSelectedPageForTab('dashboard');
                  setSelectedBoardForTab(null);
                  setShowHomePageDialog(false);
                  setShowHomeTabDialog(true);
                }}
                sx={{
                  backgroundColor: '#2391FF',
                  color: '#fff',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { backgroundColor: '#1a7ad9' }
                }}
              >
                {t('homePage.options.dashboard')}
              </Button>
              <Button
                onClick={() => {
                  setSelectedPageForTab('widgets');
                  setShowHomePageDialog(false);
                  setShowHomeTabDialog(true);
                }}
                sx={{
                  backgroundColor: '#2391FF',
                  color: '#fff',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { backgroundColor: '#1a7ad9' }
                }}
              >
                {t('homePage.options.widgets')}
              </Button>
              <Button
                onClick={() => {
                  setSelectedPageForTab('favorites');
                  setShowHomePageDialog(false);
                  setShowHomeTabDialog(true);
                }}
                sx={{
                  backgroundColor: '#2391FF',
                  color: '#fff',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { backgroundColor: '#1a7ad9' }
                }}
              >
                {t('homePage.options.favorites')}
              </Button>
              <Button
                onClick={() => {
                  setSelectedPageForTab('reports');
                  setShowHomePageDialog(false);
                  setShowHomeTabDialog(true);
                }}
                sx={{
                  backgroundColor: '#2391FF',
                  color: '#fff',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { backgroundColor: '#1a7ad9' }
                }}
              >
                {t('homePage.options.reports')}
              </Button>
              {SOLAR_INSTALLED && canViewSolar(userData) && <Button
                onClick={() => {
                  setSelectedPageForTab('realtime');
                  setShowHomePageDialog(false);
                  setShowHomeTabDialog(true);
                }}
                sx={{
                  backgroundColor: '#2391FF',
                  color: '#fff',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { backgroundColor: '#1a7ad9' }
                }}
              >
                {t('homePage.options.realtime')}
              </Button>}
              {CAMERAS_INSTALLED && canViewCameras(userData) && <Button
                onClick={() => {
                  setSelectedPageForTab('video');
                  setShowHomePageDialog(false);
                  setShowHomeTabDialog(true);
                }}
                sx={{
                  backgroundColor: '#2391FF',
                  color: '#fff',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { backgroundColor: '#1a7ad9' }
                }}
              >
                {t('homePage.options.video')}
              </Button>}
              {MUSIC_INSTALLED && canViewMusic(userData) && (
                <HomeChoiceButton
                  label={t('homePage.options.music')}
                  onClick={() => saveHomeSelection('music', null, null)}
                />
              )}
              <HomeChoiceButton
                label={t('homePage.options.alarm')}
                onClick={() => saveHomeSelection('alarm', null, null)}
              />
              {(AIR_INSTALLED || HEAT_INSTALLED) && (canViewAc(userData) || canViewHeating(userData)) && (
                <HomeChoiceButton
                  label={t('homePage.options.climate')}
                  onClick={() => saveHomeSelection('climate', null, null)}
                />
              )}
              {PHONE_INSTALLED && (canUsePhone(userData) || canAnswerDoorbell(userData)) && (
                <HomeChoiceButton
                  label={t('homePage.options.phone')}
                  onClick={() => saveHomeSelection('softphone', null, null)}
                />
              )}
              {ASSISTANT_INSTALLED && <Button
                  onClick={() => {
                    // El Asistente IA no tiene sub-tabs: se guarda directamente
                    saveHomeSelection('assistant', null, null);
                  }}
                  sx={{
                    backgroundColor: '#2391FF',
                    color: '#fff',
                    ...HOME_PAGE_OPTION_BTN_SX,
                    '&:hover': { backgroundColor: '#1a7ad9' }
                  }}
                >
                  {t('homePage.options.assistant')}
                </Button>}
              <Button
                onClick={async () => {
                  if (userData?.id) {
                    try {
                      // Guardar null en home_page y home_tab
                      await updateUserHomePage(userData.id, null, null);
                      setUserHomePage(null);
                      setUserHomeTab(null);
                      setUserHomeRoom(null);
                      // Marcar que el usuario eligió "Ninguna" explícitamente
                      localStorage.setItem('user_chose_none_homepage_v2', 'true');
                      // Establecer dashboard con el primer tablero si existe
                      if (boards.length > 0) {
                        setCurrentScreen('dashboard');
                        setIsHomeActive(true);
                        setSelectedTab(0);
                        await loadRoomsForBoard(boards[0].id);
                      } else {
                        setCurrentScreen('dashboard');
                        setIsHomeActive(true);
                      }
                      setShowHomePageDialog(false);
                      // Actualizar userData en localStorage
                      const updatedUserData = { ...userData, home_page: null, home_tab: null, home_room: null };
                      localStorage.setItem('user', JSON.stringify(updatedUserData));
                      setUserData(updatedUserData);
                    } catch (error) {
                      console.error('Error al guardar página de inicio:', error);
                    }
                  }
                }}
                sx={{
                  backgroundColor: '#2391FF',
                  color: '#fff',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { backgroundColor: '#1a7ad9' }
                }}
              >
                {t('homePage.options.none')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Modal para cambiar página de inicio desde menú de cuenta */}
      {showHomePageMenu && (
        <Box
          sx={{ ...HOME_PAGE_DIALOG_OVERLAY_SX, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setShowHomePageMenu(false)}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={HOME_PAGE_DIALOG_CARD_SX}
          >
            <Typography
              sx={{
                color: '#2391FF',
                fontSize: 17,
                fontWeight: 'bold',
                marginBottom: 1,
                textAlign: 'center',
                flexShrink: 0,
              }}
            >
              {t('homePage.changeTitle')}
            </Typography>
            <Typography
              sx={{
                color: '#fff',
                fontSize: 12,
                marginBottom: 1.25,
                textAlign: 'center',
                lineHeight: 1.4,
                flexShrink: 0,
              }}
            >
              {t('homePage.changeDescription')}
            </Typography>
            <Box sx={HOME_PAGE_DIALOG_LIST_SX}>
              <Button
                onClick={() => {
                  setSelectedPageForTab('dashboard');
                  setSelectedBoardForTab(null);
                  setShowHomePageMenu(false);
                  setShowHomeTabDialog(true);
                }}
                variant={userHomePage === 'dashboard' ? 'contained' : 'outlined'}
                sx={{
                  backgroundColor: userHomePage === 'dashboard' ? '#2391FF' : 'transparent',
                  color: userHomePage === 'dashboard' ? '#fff' : '#2391FF',
                  borderColor: '#2391FF',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { 
                    backgroundColor: userHomePage === 'dashboard' ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                    color: userHomePage === 'dashboard' ? '#fff' : '#2391FF'
                  }
                }}
              >
                {t('homePage.options.dashboard')} {userHomePage === 'dashboard' && '✓'}
              </Button>
              <Button
                onClick={() => {
                  setSelectedPageForTab('widgets');
                  setShowHomePageMenu(false);
                  setShowHomeTabDialog(true);
                }}
                variant={userHomePage === 'widgets' ? 'contained' : 'outlined'}
                sx={{
                  backgroundColor: userHomePage === 'widgets' ? '#2391FF' : 'transparent',
                  color: userHomePage === 'widgets' ? '#fff' : '#2391FF',
                  borderColor: '#2391FF',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': {
                    backgroundColor: userHomePage === 'widgets' ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                    color: userHomePage === 'widgets' ? '#fff' : '#2391FF'
                  }
                }}
              >
                {t('homePage.options.widgets')} {userHomePage === 'widgets' && '✓'}
              </Button>
              <Button
                onClick={() => {
                  setSelectedPageForTab('favorites');
                  setShowHomePageMenu(false);
                  setShowHomeTabDialog(true);
                }}
                variant={userHomePage === 'favorites' ? 'contained' : 'outlined'}
                sx={{
                  backgroundColor: userHomePage === 'favorites' ? '#2391FF' : 'transparent',
                  color: userHomePage === 'favorites' ? '#fff' : '#2391FF',
                  borderColor: '#2391FF',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { 
                    backgroundColor: userHomePage === 'favorites' ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                    color: userHomePage === 'favorites' ? '#fff' : '#2391FF'
                  }
                }}
              >
                {t('homePage.options.favorites')} {userHomePage === 'favorites' && '✓'}
              </Button>
              <Button
                onClick={() => {
                  setSelectedPageForTab('reports');
                  setShowHomePageMenu(false);
                  setShowHomeTabDialog(true);
                }}
                variant={userHomePage === 'reports' ? 'contained' : 'outlined'}
                sx={{
                  backgroundColor: userHomePage === 'reports' ? '#2391FF' : 'transparent',
                  color: userHomePage === 'reports' ? '#fff' : '#2391FF',
                  borderColor: '#2391FF',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { 
                    backgroundColor: userHomePage === 'reports' ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                    color: userHomePage === 'reports' ? '#fff' : '#2391FF'
                  }
                }}
              >
                {t('homePage.options.reports')} {userHomePage === 'reports' && '✓'}
              </Button>
              {SOLAR_INSTALLED && canViewSolar(userData) && <Button
                onClick={() => {
                  setSelectedPageForTab('realtime');
                  setShowHomePageMenu(false);
                  setShowHomeTabDialog(true);
                }}
                variant={userHomePage === 'realtime' ? 'contained' : 'outlined'}
                sx={{
                  backgroundColor: userHomePage === 'realtime' ? '#2391FF' : 'transparent',
                  color: userHomePage === 'realtime' ? '#fff' : '#2391FF',
                  borderColor: '#2391FF',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { 
                    backgroundColor: userHomePage === 'realtime' ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                    color: userHomePage === 'realtime' ? '#fff' : '#2391FF'
                  }
                }}
              >
                {t('homePage.options.realtime')} {userHomePage === 'realtime' && '✓'}
              </Button>}
              {CAMERAS_INSTALLED && canViewCameras(userData) && <Button
                onClick={() => {
                  setSelectedPageForTab('video');
                  setShowHomePageMenu(false);
                  setShowHomeTabDialog(true);
                }}
                variant={userHomePage === 'video' ? 'contained' : 'outlined'}
                sx={{
                  backgroundColor: userHomePage === 'video' ? '#2391FF' : 'transparent',
                  color: userHomePage === 'video' ? '#fff' : '#2391FF',
                  borderColor: '#2391FF',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { 
                    backgroundColor: userHomePage === 'video' ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                    color: userHomePage === 'video' ? '#fff' : '#2391FF'
                  }
                }}
              >
                {t('homePage.options.video')} {userHomePage === 'video' && '✓'}
              </Button>}
              {MUSIC_INSTALLED && canViewMusic(userData) && (
                <HomeChoiceButton
                  outlined
                  selected={userHomePage === 'music'}
                  label={t('homePage.options.music')}
                  onClick={() => saveHomeSelection('music', null, null)}
                />
              )}
              <HomeChoiceButton
                outlined
                selected={userHomePage === 'alarm'}
                label={t('homePage.options.alarm')}
                onClick={() => saveHomeSelection('alarm', null, null)}
              />
              {(AIR_INSTALLED || HEAT_INSTALLED) && (canViewAc(userData) || canViewHeating(userData)) && (
                <HomeChoiceButton
                  outlined
                  selected={userHomePage === 'climate'}
                  label={t('homePage.options.climate')}
                  onClick={() => saveHomeSelection('climate', null, null)}
                />
              )}
              {PHONE_INSTALLED && (canUsePhone(userData) || canAnswerDoorbell(userData)) && (
                <HomeChoiceButton
                  outlined
                  selected={userHomePage === 'softphone'}
                  label={t('homePage.options.phone')}
                  onClick={() => saveHomeSelection('softphone', null, null)}
                />
              )}
              {ASSISTANT_INSTALLED && <Button
                  onClick={() => {
                    // El Asistente IA no tiene sub-tabs: se guarda directamente
                    saveHomeSelection('assistant', null, null);
                  }}
                  variant={userHomePage === 'assistant' ? 'contained' : 'outlined'}
                  sx={{
                    backgroundColor: userHomePage === 'assistant' ? '#2391FF' : 'transparent',
                    color: userHomePage === 'assistant' ? '#fff' : '#2391FF',
                    borderColor: '#2391FF',
                    ...HOME_PAGE_OPTION_BTN_SX,
                    '&:hover': {
                      backgroundColor: userHomePage === 'assistant' ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                      color: userHomePage === 'assistant' ? '#fff' : '#2391FF'
                    }
                  }}
                >
                  {t('homePage.options.assistant')} {userHomePage === 'assistant' && '✓'}
                </Button>}
              <Button
                onClick={async () => {
                  if (userData?.id) {
                    try {
                      // Guardar null en home_page y home_tab
                      await updateUserHomePage(userData.id, null, null);
                      setUserHomePage(null);
                      setUserHomeTab(null);
                      setUserHomeRoom(null);
                      // Marcar que el usuario eligió "Ninguna" explícitamente
                      localStorage.setItem('user_chose_none_homepage_v2', 'true');
                      // Establecer dashboard con el primer tablero si existe
                      if (boards.length > 0) {
                        setCurrentScreen('dashboard');
                        setIsHomeActive(true);
                        setSelectedTab(0);
                        await loadRoomsForBoard(boards[0].id);
                      } else {
                        setCurrentScreen('dashboard');
                        setIsHomeActive(true);
                      }
                      setShowHomePageMenu(false);
                      // Actualizar userData en localStorage
                      const updatedUserData = { ...userData, home_page: null, home_tab: null, home_room: null };
                      localStorage.setItem('user', JSON.stringify(updatedUserData));
                      setUserData(updatedUserData);
                    } catch (error) {
                      console.error('Error al guardar página de inicio:', error);
                    }
                  }
                }}
                variant={userHomePage === null ? 'contained' : 'outlined'}
                sx={{
                  backgroundColor: userHomePage === null ? '#2391FF' : 'transparent',
                  color: userHomePage === null ? '#fff' : '#2391FF',
                  borderColor: '#2391FF',
                  ...HOME_PAGE_OPTION_BTN_SX,
                  '&:hover': { 
                    backgroundColor: userHomePage === null ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                    color: userHomePage === null ? '#fff' : '#2391FF'
                  }
                }}
              >
                {t('homePage.options.none')} {userHomePage === null && '✓'}
              </Button>
            </Box>
            <Button
              onClick={() => setShowHomePageMenu(false)}
              sx={{
                marginTop: 1,
                width: '100%',
                color: '#fff',
                borderColor: '#555',
                flexShrink: 0,
                ...HOME_PAGE_OPTION_BTN_SX,
                '&:hover': { borderColor: '#777' }
              }}
              variant="outlined"
            >
              {t('common.cancel')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Modal para seleccionar tab de inicio */}
      {showHomeTabDialog && selectedPageForTab && (
        <Box
          sx={HOME_PAGE_DIALOG_OVERLAY_SX}
          onClick={() => {
            setShowHomeTabDialog(false);
            setSelectedPageForTab(null);
            setSelectedBoardForTab(null);
            // Volver al modal anterior si venía del diálogo inicial
            if (!userHomePage) {
              setShowHomePageDialog(true);
            }
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={HOME_PAGE_DIALOG_CARD_SX}
          >
            <Typography
              sx={{
                color: '#2391FF',
                fontSize: 17,
                fontWeight: 'bold',
                marginBottom: 1,
                textAlign: 'center',
                flexShrink: 0,
              }}
            >
              {selectedPageForTab === 'dashboard' 
                ? (selectedBoardForTab !== null ? t('homePage.tabs.dashboard.roomTitle') : t('homePage.tabs.dashboard.title'))
                : t('homePage.selectTabTitle')}
            </Typography>
            <Typography
              sx={{
                color: '#fff',
                fontSize: 12,
                marginBottom: 1.25,
                textAlign: 'center',
                lineHeight: 1.4,
                flexShrink: 0,
              }}
            >
              {selectedPageForTab === 'dashboard'
                ? (selectedBoardForTab !== null ? t('homePage.tabs.dashboard.roomDescription') : t('homePage.tabs.dashboard.description'))
                : t('homePage.selectTabDescription')}
            </Typography>
            <Box sx={HOME_PAGE_DIALOG_LIST_SX}>
              {selectedPageForTab === 'widgets' && (
                <>
                  <Button
                    onClick={() => saveHomeSelection('widgets', 0, null)}
                    variant={userHomePage === 'widgets' && userHomeTab === 0 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomePage === 'widgets' && userHomeTab === 0 ? '#2391FF' : 'transparent',
                      color: userHomePage === 'widgets' && userHomeTab === 0 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                    }}
                  >
                    {t('homePage.tabs.widgets.0')} {userHomePage === 'widgets' && userHomeTab === 0 && '✓'}
                  </Button>
                  <Button
                    onClick={() => saveHomeSelection('widgets', 1, null)}
                    variant={userHomePage === 'widgets' && userHomeTab === 1 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomePage === 'widgets' && userHomeTab === 1 ? '#2391FF' : 'transparent',
                      color: userHomePage === 'widgets' && userHomeTab === 1 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                    }}
                  >
                    {t('homePage.tabs.widgets.1')} {userHomePage === 'widgets' && userHomeTab === 1 && '✓'}
                  </Button>
                </>
              )}
              {selectedPageForTab === 'dashboard' && boards.length > 0 && selectedBoardForTab === null && (
                <>
                  {boards.map((board) => (
                    <Button
                      key={board.id}
                      onClick={async () => {
                        // Paso 1 -> Paso 2: elegir una habitación de este tablero (o todo el tablero)
                        setSelectedBoardForTab(board.id);
                        setHomeTabDialogRooms([]);
                        setHomeTabDialogRoomsLoading(true);
                        try {
                          const r = await fetchRoomsForBoard(board.id);
                          setHomeTabDialogRooms(r);
                        } catch (error) {
                          setHomeTabDialogRooms([]);
                        } finally {
                          setHomeTabDialogRoomsLoading(false);
                        }
                      }}
                      variant={userHomeTab === board.id ? 'contained' : 'outlined'}
                      sx={{
                        backgroundColor: userHomeTab === board.id ? '#2391FF' : 'transparent',
                        color: userHomeTab === board.id ? '#fff' : '#2391FF',
                        borderColor: '#2391FF',
                        ...HOME_PAGE_OPTION_BTN_SX,
                        '&:hover': { 
                          backgroundColor: userHomeTab === board.id ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                          color: userHomeTab === board.id ? '#fff' : '#2391FF'
                        }
                      }}
                    >
                      {board.nombre} {userHomeTab === board.id && '✓'}
                    </Button>
                  ))}
                </>
              )}
              {selectedPageForTab === 'dashboard' && selectedBoardForTab !== null && (
                <>
                  {/* Opción: todo el tablero (grilla de habitaciones) */}
                  <Button
                    onClick={() => saveHomeSelection('dashboard', selectedBoardForTab, null)}
                    variant={(userHomeTab === selectedBoardForTab && (userHomeRoom === null || userHomeRoom === undefined)) ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: (userHomeTab === selectedBoardForTab && (userHomeRoom === null || userHomeRoom === undefined)) ? '#2391FF' : 'transparent',
                      color: (userHomeTab === selectedBoardForTab && (userHomeRoom === null || userHomeRoom === undefined)) ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': {
                        backgroundColor: (userHomeTab === selectedBoardForTab && (userHomeRoom === null || userHomeRoom === undefined)) ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                        color: (userHomeTab === selectedBoardForTab && (userHomeRoom === null || userHomeRoom === undefined)) ? '#fff' : '#2391FF'
                      }
                    }}
                  >
                    {t('homePage.tabs.dashboard.wholeBoard')} {(userHomeTab === selectedBoardForTab && (userHomeRoom === null || userHomeRoom === undefined)) && '✓'}
                  </Button>
                  {homeTabDialogRoomsLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', paddingY: 0.75 }}>
                      <CircularProgress size={22} sx={{ color: '#2391FF' }} />
                    </Box>
                  )}
                  {!homeTabDialogRoomsLoading && homeTabDialogRooms.map((room) => (
                    <Button
                      key={room.id}
                      onClick={() => saveHomeSelection('dashboard', selectedBoardForTab, room.id)}
                      variant={(userHomeTab === selectedBoardForTab && userHomeRoom === room.id) ? 'contained' : 'outlined'}
                      sx={{
                        backgroundColor: (userHomeTab === selectedBoardForTab && userHomeRoom === room.id) ? '#2391FF' : 'transparent',
                        color: (userHomeTab === selectedBoardForTab && userHomeRoom === room.id) ? '#fff' : '#2391FF',
                        borderColor: '#2391FF',
                        ...HOME_PAGE_OPTION_BTN_SX,
                        '&:hover': {
                          backgroundColor: (userHomeTab === selectedBoardForTab && userHomeRoom === room.id) ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                          color: (userHomeTab === selectedBoardForTab && userHomeRoom === room.id) ? '#fff' : '#2391FF'
                        }
                      }}
                    >
                      {room.nombre} {(userHomeTab === selectedBoardForTab && userHomeRoom === room.id) && '✓'}
                    </Button>
                  ))}
                  <Button
                    onClick={() => {
                      setSelectedBoardForTab(null);
                      setHomeTabDialogRooms([]);
                    }}
                    sx={{
                      marginTop: 0.35,
                      color: '#fff',
                      borderColor: '#555',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': { borderColor: '#777' }
                    }}
                    variant="outlined"
                  >
                    {t('homePage.tabs.dashboard.backToBoards')}
                  </Button>
                </>
              )}
              {selectedPageForTab === 'reports' && (
                <>
                  <Button
                    onClick={async () => {
                      if (userData?.id) {
                        try {
                          await updateUserHomePage(userData.id, 'reports', 0);
                          setUserHomePage('reports');
                          setUserHomeTab(0);
                          setCurrentScreen('reports');
                          setIsHomeActive(false);
                          setShowHomeTabDialog(false);
                          setSelectedPageForTab(null);
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Actualizar userData en localStorage
                          const updatedUserData = { ...userData, home_page: 'reports', home_tab: 0 };
                          localStorage.setItem('user', JSON.stringify(updatedUserData));
                          setUserData(updatedUserData);
                        } catch (error) {
                          console.error('Error al guardar página de inicio:', error);
                        }
                      }
                    }}
                    variant={userHomeTab === 0 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomeTab === 0 ? '#2391FF' : 'transparent',
                      color: userHomeTab === 0 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': { 
                        backgroundColor: userHomeTab === 0 ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                        color: userHomeTab === 0 ? '#fff' : '#2391FF'
                      }
                    }}
                  >
                    {t('homePage.tabs.reports.0')} {userHomeTab === 0 && '✓'}
                  </Button>
                  <Button
                    onClick={async () => {
                      if (userData?.id) {
                        try {
                          await updateUserHomePage(userData.id, 'reports', 1);
                          setUserHomePage('reports');
                          setUserHomeTab(1);
                          setCurrentScreen('reports');
                          setIsHomeActive(false);
                          setShowHomeTabDialog(false);
                          setSelectedPageForTab(null);
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Actualizar userData en localStorage
                          const updatedUserData = { ...userData, home_page: 'reports', home_tab: 1 };
                          localStorage.setItem('user', JSON.stringify(updatedUserData));
                          setUserData(updatedUserData);
                        } catch (error) {
                          console.error('Error al guardar página de inicio:', error);
                        }
                      }
                    }}
                    variant={userHomeTab === 1 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomeTab === 1 ? '#2391FF' : 'transparent',
                      color: userHomeTab === 1 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': { 
                        backgroundColor: userHomeTab === 1 ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                        color: userHomeTab === 1 ? '#fff' : '#2391FF'
                      }
                    }}
                  >
                    {t('homePage.tabs.reports.1')} {userHomeTab === 1 && '✓'}
                  </Button>
                  <Button
                    onClick={async () => {
                      if (userData?.id) {
                        try {
                          await updateUserHomePage(userData.id, 'reports', 2);
                          setUserHomePage('reports');
                          setUserHomeTab(2);
                          setCurrentScreen('reports');
                          setIsHomeActive(false);
                          setShowHomeTabDialog(false);
                          setSelectedPageForTab(null);
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Actualizar userData en localStorage
                          const updatedUserData = { ...userData, home_page: 'reports', home_tab: 2 };
                          localStorage.setItem('user', JSON.stringify(updatedUserData));
                          setUserData(updatedUserData);
                        } catch (error) {
                          console.error('Error al guardar página de inicio:', error);
                        }
                      }
                    }}
                    variant={userHomeTab === 2 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomeTab === 2 ? '#2391FF' : 'transparent',
                      color: userHomeTab === 2 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': { 
                        backgroundColor: userHomeTab === 2 ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                        color: userHomeTab === 2 ? '#fff' : '#2391FF'
                      }
                    }}
                  >
                    {t('homePage.tabs.reports.2')} {userHomeTab === 2 && '✓'}
                  </Button>
                </>
              )}
              {selectedPageForTab === 'favorites' && (
                <>
                  <Button
                    onClick={async () => {
                      if (userData?.id) {
                        try {
                          await updateUserHomePage(userData.id, 'favorites', 0);
                          setUserHomePage('favorites');
                          setUserHomeTab(0);
                          setCurrentScreen('favorites');
                          setIsHomeActive(false);
                          setShowHomeTabDialog(false);
                          setSelectedPageForTab(null);
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Actualizar userData en localStorage
                          const updatedUserData = { ...userData, home_page: 'favorites', home_tab: 0 };
                          localStorage.setItem('user', JSON.stringify(updatedUserData));
                          setUserData(updatedUserData);
                        } catch (error) {
                          console.error('Error al guardar página de inicio:', error);
                        }
                      }
                    }}
                    variant={userHomeTab === 0 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomeTab === 0 ? '#2391FF' : 'transparent',
                      color: userHomeTab === 0 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': { 
                        backgroundColor: userHomeTab === 0 ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                        color: userHomeTab === 0 ? '#fff' : '#2391FF'
                      }
                    }}
                  >
                    {t('homePage.tabs.favorites.0')} {userHomeTab === 0 && '✓'}
                  </Button>
                  <Button
                    onClick={async () => {
                      if (userData?.id) {
                        try {
                          await updateUserHomePage(userData.id, 'favorites', 1);
                          setUserHomePage('favorites');
                          setUserHomeTab(1);
                          setCurrentScreen('favorites');
                          setIsHomeActive(false);
                          setShowHomeTabDialog(false);
                          setSelectedPageForTab(null);
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Actualizar userData en localStorage
                          const updatedUserData = { ...userData, home_page: 'favorites', home_tab: 1 };
                          localStorage.setItem('user', JSON.stringify(updatedUserData));
                          setUserData(updatedUserData);
                        } catch (error) {
                          console.error('Error al guardar página de inicio:', error);
                        }
                      }
                    }}
                    variant={userHomeTab === 1 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomeTab === 1 ? '#2391FF' : 'transparent',
                      color: userHomeTab === 1 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': { 
                        backgroundColor: userHomeTab === 1 ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                        color: userHomeTab === 1 ? '#fff' : '#2391FF'
                      }
                    }}
                  >
                    {t('homePage.tabs.favorites.1')} {userHomeTab === 1 && '✓'}
                  </Button>
                  <Button
                    onClick={async () => {
                      if (userData?.id) {
                        try {
                          await updateUserHomePage(userData.id, 'favorites', 2);
                          setUserHomePage('favorites');
                          setUserHomeTab(2);
                          setCurrentScreen('favorites');
                          setIsHomeActive(false);
                          setShowHomeTabDialog(false);
                          setSelectedPageForTab(null);
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Actualizar userData en localStorage
                          const updatedUserData = { ...userData, home_page: 'favorites', home_tab: 2 };
                          localStorage.setItem('user', JSON.stringify(updatedUserData));
                          setUserData(updatedUserData);
                        } catch (error) {
                          console.error('Error al guardar página de inicio:', error);
                        }
                      }
                    }}
                    variant={userHomeTab === 2 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomeTab === 2 ? '#2391FF' : 'transparent',
                      color: userHomeTab === 2 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': { 
                        backgroundColor: userHomeTab === 2 ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                        color: userHomeTab === 2 ? '#fff' : '#2391FF'
                      }
                    }}
                  >
                    {t('homePage.tabs.favorites.2')} {userHomeTab === 2 && '✓'}
                  </Button>
                  <Button
                    onClick={async () => {
                      if (userData?.id) {
                        try {
                          await updateUserHomePage(userData.id, 'favorites', 3);
                          setUserHomePage('favorites');
                          setUserHomeTab(3);
                          setCurrentScreen('favorites');
                          setIsHomeActive(false);
                          setShowHomeTabDialog(false);
                          setSelectedPageForTab(null);
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Actualizar userData en localStorage
                          const updatedUserData = { ...userData, home_page: 'favorites', home_tab: 3 };
                          localStorage.setItem('user', JSON.stringify(updatedUserData));
                          setUserData(updatedUserData);
                        } catch (error) {
                          console.error('Error al guardar página de inicio:', error);
                        }
                      }
                    }}
                    variant={userHomeTab === 3 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomeTab === 3 ? '#2391FF' : 'transparent',
                      color: userHomeTab === 3 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': { 
                        backgroundColor: userHomeTab === 3 ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                        color: userHomeTab === 3 ? '#fff' : '#2391FF'
                      }
                    }}
                  >
                    {t('homePage.tabs.favorites.3')} {userHomeTab === 3 && '✓'}
                  </Button>
                </>
              )}
              {selectedPageForTab === 'realtime' && (
                <>
                  <Button
                    onClick={async () => {
                      if (userData?.id) {
                        try {
                          await updateUserHomePage(userData.id, 'realtime', 0);
                          setUserHomePage('realtime');
                          setUserHomeTab(0);
                          setCurrentScreen('realtime');
                          setIsHomeActive(false);
                          setShowHomeTabDialog(false);
                          setSelectedPageForTab(null);
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Actualizar userData en localStorage
                          const updatedUserData = { ...userData, home_page: 'realtime', home_tab: 0 };
                          localStorage.setItem('user', JSON.stringify(updatedUserData));
                          setUserData(updatedUserData);
                        } catch (error) {
                          console.error('Error al guardar página de inicio:', error);
                        }
                      }
                    }}
                    variant={userHomeTab === 0 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomeTab === 0 ? '#2391FF' : 'transparent',
                      color: userHomeTab === 0 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': { 
                        backgroundColor: userHomeTab === 0 ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                        color: userHomeTab === 0 ? '#fff' : '#2391FF'
                      }
                    }}
                  >
                    {t('homePage.tabs.realtime.0')} {userHomeTab === 0 && '✓'}
                  </Button>
                  <Button
                    onClick={async () => {
                      if (userData?.id) {
                        try {
                          await updateUserHomePage(userData.id, 'realtime', 1);
                          setUserHomePage('realtime');
                          setUserHomeTab(1);
                          setCurrentScreen('realtime');
                          setIsHomeActive(false);
                          setShowHomeTabDialog(false);
                          setSelectedPageForTab(null);
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Actualizar userData en localStorage
                          const updatedUserData = { ...userData, home_page: 'realtime', home_tab: 1 };
                          localStorage.setItem('user', JSON.stringify(updatedUserData));
                          setUserData(updatedUserData);
                        } catch (error) {
                          console.error('Error al guardar página de inicio:', error);
                        }
                      }
                    }}
                    variant={userHomeTab === 1 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomeTab === 1 ? '#2391FF' : 'transparent',
                      color: userHomeTab === 1 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': { 
                        backgroundColor: userHomeTab === 1 ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                        color: userHomeTab === 1 ? '#fff' : '#2391FF'
                      }
                    }}
                  >
                    {t('homePage.tabs.realtime.1')} {userHomeTab === 1 && '✓'}
                  </Button>
                  <Button
                    onClick={async () => {
                      if (userData?.id) {
                        try {
                          await updateUserHomePage(userData.id, 'realtime', 2);
                          setUserHomePage('realtime');
                          setUserHomeTab(2);
                          setCurrentScreen('realtime');
                          setIsHomeActive(false);
                          setShowHomeTabDialog(false);
                          setSelectedPageForTab(null);
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Actualizar userData en localStorage
                          const updatedUserData = { ...userData, home_page: 'realtime', home_tab: 2 };
                          localStorage.setItem('user', JSON.stringify(updatedUserData));
                          setUserData(updatedUserData);
                        } catch (error) {
                          console.error('Error al guardar página de inicio:', error);
                        }
                      }
                    }}
                    variant={userHomeTab === 2 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomeTab === 2 ? '#2391FF' : 'transparent',
                      color: userHomeTab === 2 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': { 
                        backgroundColor: userHomeTab === 2 ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                        color: userHomeTab === 2 ? '#fff' : '#2391FF'
                      }
                    }}
                  >
                    {t('homePage.tabs.realtime.2')} {userHomeTab === 2 && '✓'}
                  </Button>
                </>
              )}
              {selectedPageForTab === 'video' && (
                <>
                  <Button
                    onClick={async () => {
                      if (userData?.id) {
                        try {
                          await updateUserHomePage(userData.id, 'video', 0);
                          setUserHomePage('video');
                          setUserHomeTab(0);
                          setCurrentScreen('video');
                          setIsHomeActive(false);
                          setShowHomeTabDialog(false);
                          setSelectedPageForTab(null);
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Eliminar flag de "Ninguna" si existe
                          localStorage.removeItem('user_chose_none_homepage_v2');
                          // Actualizar userData en localStorage
                          const updatedUserData = { ...userData, home_page: 'video', home_tab: 0 };
                          localStorage.setItem('user', JSON.stringify(updatedUserData));
                          setUserData(updatedUserData);
                        } catch (error) {
                          console.error('Error al guardar página de inicio:', error);
                        }
                      }
                    }}
                    variant={userHomeTab === 0 ? 'contained' : 'outlined'}
                    sx={{
                      backgroundColor: userHomeTab === 0 ? '#2391FF' : 'transparent',
                      color: userHomeTab === 0 ? '#fff' : '#2391FF',
                      borderColor: '#2391FF',
                      ...HOME_PAGE_OPTION_BTN_SX,
                      '&:hover': { 
                        backgroundColor: userHomeTab === 0 ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                        color: userHomeTab === 0 ? '#fff' : '#2391FF'
                      }
                    }}
                  >
                    {t('homePage.tabs.video.0')} {userHomeTab === 0 && '✓'}
                  </Button>
                  {userRole === 'admin' && (
                    <Button
                      onClick={async () => {
                        if (userData?.id) {
                          try {
                            await updateUserHomePage(userData.id, 'video', 1);
                            setUserHomePage('video');
                            setUserHomeTab(1);
                            setCurrentScreen('video');
                            setIsHomeActive(false);
                            setShowHomeTabDialog(false);
                            setSelectedPageForTab(null);
                            // Actualizar userData en localStorage
                            const updatedUserData = { ...userData, home_page: 'video', home_tab: 1 };
                            localStorage.setItem('user', JSON.stringify(updatedUserData));
                            setUserData(updatedUserData);
                          } catch (error) {
                            console.error('Error al guardar página de inicio:', error);
                          }
                        }
                      }}
                      variant={userHomeTab === 1 ? 'contained' : 'outlined'}
                      sx={{
                        backgroundColor: userHomeTab === 1 ? '#2391FF' : 'transparent',
                        color: userHomeTab === 1 ? '#fff' : '#2391FF',
                        borderColor: '#2391FF',
                        ...HOME_PAGE_OPTION_BTN_SX,
                        '&:hover': { 
                          backgroundColor: userHomeTab === 1 ? '#1a7ad9' : 'rgba(35, 145, 255, 0.1)',
                          color: userHomeTab === 1 ? '#fff' : '#2391FF'
                        }
                      }}
                    >
                      {t('homePage.tabs.video.1')} {userHomeTab === 1 && '✓'}
                    </Button>
                  )}
                </>
              )}
            </Box>
            <Button
              onClick={() => {
                // Si estamos eligiendo habitación, volver a la lista de tableros
                if (selectedPageForTab === 'dashboard' && selectedBoardForTab !== null) {
                  setSelectedBoardForTab(null);
                  setHomeTabDialogRooms([]);
                  return;
                }
                setShowHomeTabDialog(false);
                setSelectedPageForTab(null);
                setSelectedBoardForTab(null);
                // Volver al modal anterior si venía del diálogo inicial
                if (!userHomePage) {
                  setShowHomePageDialog(true);
                }
              }}
              sx={{
                marginTop: 1,
                width: '100%',
                color: '#fff',
                borderColor: '#555',
                flexShrink: 0,
                ...HOME_PAGE_OPTION_BTN_SX,
                '&:hover': { borderColor: '#777' }
              }}
              variant="outlined"
            >
              {t('homePage.back')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Diálogo de confirmación de logout */}
      {showLogoutDialog && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            
            alignItems: 'center',
            zIndex: 1200 // Modal crítico (confirmación de logout)
          }}
        >
          <Box
            sx={{
              backgroundColor: '#333',
              borderRadius: 1.25,
              padding: 2.5,
              minWidth: 300,
              margin: 2.5
            }}
          >
            <Typography
              sx={{
                color: '#2391FF',
                fontSize: 18,
                fontWeight: 'bold',
                marginBottom: 1
              }}
            >
              {t('common.logoutConfirm')}
            </Typography>
            <Typography
              sx={{
                color: '#fff',
                fontSize: 14,
                marginBottom: 2.5
              }}
            >
              {t('common.logoutQuestion')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.25
              }}
            >
              <Button
                onClick={() => setShowLogoutDialog(false)}
                sx={{
                  paddingY: 1,
                  paddingX: 2,
                  borderRadius: 1,
                  color: '#2391FF',
                  fontSize: 14,
                  fontWeight: 'bold'
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={confirmLogout}
                sx={{
                  paddingY: 1,
                  paddingX: 2,
                  borderRadius: 1,
                  backgroundColor: '#2391FF',
                  color: '#000',
                  fontSize: 14,
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#1976d2'
                  }
                }}
              >
                {t('common.confirm')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Selección de vista para usuarios DEMO */}
      <Dialog
        open={isDemoUser && demoModeDialogOpen}
        onClose={handleDemoDialogClose}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            backgroundColor: '#1E1E1E',
            color: '#fff',
            borderRadius: 2,
            width: '90%',
            maxWidth: 420,
            border: '1px solid rgba(35, 145, 255, 0.4)'
          }
        }}
      >
        <DialogTitle sx={{ color: '#2391FF', fontWeight: 'bold' }}>
          {t('demo.selectView')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#d9d9d9', fontSize: 14, lineHeight: 1.4, mb: 2 }} dangerouslySetInnerHTML={{ __html: t('demo.description') }} />
          <Typography sx={{ color: '#ffa726', fontSize: 13, lineHeight: 1.4, mb: 2, fontWeight: 500, bgcolor: 'rgba(255, 167, 38, 0.1)', p: 1.5, borderRadius: 1, border: '1px solid rgba(255, 167, 38, 0.3)' }}>
            {t('demo.autoDisconnect')}
          </Typography>
          <Typography sx={{ color: '#888', fontSize: 12 }}>
            {t('demo.changeView')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 3, pb: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleDemoModeSelect('user')}
            sx={{
              borderColor: '#2391FF',
              color: '#2391FF',
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            {t('demo.viewAsUser')}
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleDemoModeSelect('admin')}
            sx={{
              backgroundColor: '#2391FF',
              color: '#000',
              textTransform: 'none',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#1a7ad9'
              }
            }}
          >
            {t('demo.viewAsAdmin')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de DeviceList - solo en vertical */}
      {!isHorizontal && (
        <DeviceListModal
          isOpen={showDeviceListModal}
          onClose={() => {
            setShowDeviceListModal(false);
            setIsInfoActive(false);
          }}
          isAdmin={userRole === 'admin'}
          selectedHabitacion={
            currentScreen === 'settings' ? null : 
            (currentScreen === 'realtime' || currentScreen === 'reports' || currentScreen === 'favorites' || currentScreen === 'assistant' || currentScreen === 'automations' || currentScreen === 'alarm' || currentScreen === 'music' || currentScreen === 'softphone' || currentScreen === 'video' || currentScreen === 'climate' || currentScreen === 'widgets') ? null : 
            (selectedRoom?.id || null)
          }
          roomMatrixView={
            currentScreen === 'settings' ? true : 
            (currentScreen === 'realtime' || currentScreen === 'reports' || currentScreen === 'favorites' || currentScreen === 'assistant' || currentScreen === 'automations' || currentScreen === 'alarm' || currentScreen === 'music' || currentScreen === 'softphone' || currentScreen === 'video' || currentScreen === 'climate' || currentScreen === 'widgets') ? true : 
            true
          }
          nombreHabitacion={currentScreen === 'settings' ? '' : (selectedRoom?.nombre || '')}
          isDashboardView={currentScreen === 'settings' ? false : true}
          isTodosTab={false}
          isSettingsPage={currentScreen === 'settings'}
          onOfflineClick={currentScreen === 'settings' ? () => {} : undefined}
          onTotalClick={currentScreen === 'settings' ? () => {} : undefined}
          // Aplicar los mismos filtros que en energía solar cuando está en Dashboard (no en Settings)
          hideOfflineAndTotal={currentScreen !== 'settings'}
          excludeArtifactTypes={currentScreen !== 'settings' ? ['Tablero', 'Medidor de Consumo'] : []}
          includeSpecialRooms={currentScreen !== 'settings'}
          excludeSpecialRoomNames={currentScreen !== 'settings' ? ['Conexion a Red'] : []}
        />
      )}

      {/* Modal de dispositivo - solo en vertical */}
      {!isHorizontal && (
        <DeviceModal
          key={selectedDevice?.id || 'no-device'}
          open={deviceModalOpen}
          onClose={handleCloseDeviceModal}
          onBack={handleCloseDeviceModal}
          device={selectedDevice}
          deviceTimerStates={deviceTimerStates}
          onDeviceToggle={handleDeviceToggle}
          onControlCover={handleControlCover}
        />
      )}

      {!isHorizontal && (
        <SonosSpeakerModal
          open={sonosModalOpen}
          speaker={selectedSonosSpeaker ? (mapaSonos[selectedSonosSpeaker.id] || selectedSonosSpeaker) : null}
          canControl={selectedSonosSpeaker?.can_control !== false}
          isFavorite={selectedSonosSpeaker ? sonosFavorites.includes(selectedSonosSpeaker.id) : false}
          onFavoriteToggle={
            showSonosInGrid && canViewMusic(userData || getUser())
              ? handleSonosFavoriteToggle
              : undefined
          }
          onClose={handleCloseSonosModal}
        />
      )}

      {!isHorizontal && selectedAndroidTvDevice && (
        <AndroidTvDeviceModal
          open={androidtvModalOpen}
          device={mapaAndroidTv[selectedAndroidTvDevice.id] || selectedAndroidTvDevice}
          onClose={handleCloseAndroidTvModal}
          onUpdated={handleAndroidTvUpdated}
        />
      )}

      {!isHorizontal && selectedSamsungDevice && (
        <SamsungDeviceModal
          open={samsungModalOpen}
          device={mapaSamsung[selectedSamsungDevice.id] || selectedSamsungDevice}
          onClose={handleCloseSamsungModal}
          onUpdated={handleSamsungUpdated}
        />
      )}

      {!isHorizontal && selectedEvohomeDevice && (
        <EvohomeDeviceModal
          open={evohomeModalOpen}
          device={mapaEvohome[selectedEvohomeDevice.id] || selectedEvohomeDevice}
          onClose={handleCloseEvohomeModal}
          onUpdated={handleEvohomeUpdated}
        />
      )}
      {!isHorizontal && selectedLgDevice && (
        <LgDeviceModal
          open={lgModalOpen}
          device={mapaLg[selectedLgDevice.id] || selectedLgDevice}
          onClose={handleCloseLgModal}
          onUpdated={handleLgUpdated}
        />
      )}

      {/* Modales clima en landscape (panel lateral, igual que vista habitación) */}
      {isHorizontal && currentScreen === 'climate' && samsungModalOpen && selectedSamsungDevice && (
        <Box sx={{
          position: 'fixed',
          right: '11px',
          top: '120px',
          bottom: '56px',
          width: '380px',
          zIndex: 1002,
          backgroundColor: '#1a1a1a',
          borderLeft: '2px solid #2391FF',
          borderRight: '2px solid #2391FF',
          borderTop: '2px solid #2391FF',
          borderRadius: '16px 16px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <SamsungDeviceModal
            open={samsungModalOpen}
            device={mapaSamsung[selectedSamsungDevice.id] || selectedSamsungDevice}
            onClose={handleCloseSamsungModal}
            onUpdated={handleSamsungUpdated}
          />
        </Box>
      )}
      {isHorizontal && currentScreen === 'climate' && evohomeModalOpen && selectedEvohomeDevice && (
        <Box sx={{
          position: 'fixed',
          right: '11px',
          top: '120px',
          bottom: '56px',
          width: '380px',
          zIndex: 1002,
          backgroundColor: '#1a1a1a',
          borderLeft: '2px solid #2391FF',
          borderRight: '2px solid #2391FF',
          borderTop: '2px solid #2391FF',
          borderRadius: '16px 16px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <EvohomeDeviceModal
            open={evohomeModalOpen}
            device={mapaEvohome[selectedEvohomeDevice.id] || selectedEvohomeDevice}
            onClose={handleCloseEvohomeModal}
            onUpdated={handleEvohomeUpdated}
          />
        </Box>
      )}
      {isHorizontal && currentScreen === 'climate' && lgModalOpen && selectedLgDevice && (
        <Box sx={{
          position: 'fixed',
          right: '11px',
          top: '120px',
          bottom: '56px',
          width: '380px',
          zIndex: 1002,
          backgroundColor: '#1a1a1a',
          borderLeft: '2px solid #2391FF',
          borderRight: '2px solid #2391FF',
          borderTop: '2px solid #2391FF',
          borderRadius: '16px 16px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <LgDeviceModal
            open={lgModalOpen}
            device={mapaLg[selectedLgDevice.id] || selectedLgDevice}
            onClose={handleCloseLgModal}
            onUpdated={handleLgUpdated}
          />
        </Box>
      )}

      {/* Mensaje de salida */}

      {/* Dialog de PIN para control de dispositivos - Solo si NO hay ventana flotante abierta */}
      {/* El PIN dialog dentro de FloatingCameraWindow se maneja internamente */}
      {!floatingCameraWindow && (
        <PinInputDialog
          open={showPinDialog}
          onClose={() => {
            // Cancelar / dismiss: limpiar pending. Éxito usa handlePinVerified (no onClose).
            setShowPinDialog(false);
            pendingControlRef.current = null;
            setPendingControl(null);
          }}
          onPinVerified={handlePinVerified}
          deviceId={pendingControl?.device?.id}
          onShowRestriction={() => {
            setPermissionSnackbar({
              open: true,
              message: t('permissions.demoRestriction')
            });
          }}
        />
      )}
      
      {/* Ventana flotante de cámaras */}
      {floatingCameraWindow && (() => {
        // CRITICO: Obtener dispositivo actualizado desde mapaDispositivos para asegurar que tenga pin_required correcto
        const dispositivoCompleto = floatingCameraWindow.deviceId 
          ? (mapaDispositivos[floatingCameraWindow.deviceId] || floatingCameraWindow.device)
          : floatingCameraWindow.device;
        
        // Asegurar que el dispositivo tenga todos los campos correctos, especialmente pin_required
        const deviceWithPin = dispositivoCompleto ? {
          ...dispositivoCompleto,
          ...(floatingCameraWindow.deviceId && mapaDispositivos[floatingCameraWindow.deviceId] 
            ? mapaDispositivos[floatingCameraWindow.deviceId] 
            : {})
        } : dispositivoCompleto;
        
        return (
          <FloatingCameraWindow
            open={floatingCameraWindow.open}
            cameraIds={floatingCameraWindow.cameraIds}
            displayDuration={floatingCameraWindow.displayDuration}
            deviceName={floatingCameraWindow.deviceName}
            deviceId={floatingCameraWindow.deviceId}
            device={deviceWithPin}
            originalCardWidth={floatingCameraWindow.originalCardWidth}
            requiresPin={floatingCameraWindow.requiresPin}
            pendingAction={floatingCameraWindow.pendingAction}
            pendingActionValue={floatingCameraWindow.pendingActionValue}
            controlAction={floatingCameraWindow.controlAction}
            onClose={() => {
              setFloatingCameraWindow(null);
              setPendingControl(null);
              setShowPinDialog(false);
              setCameraWindowPaddingTop(0);
            }}
            onPinVerified={handlePinVerified}
            userRole={userRole}
            deviceErrors={deviceErrors}
            confirmingDevices={confirmingDevices}
            confirmedDevices={confirmedDevices}
            coverActions={coverActions}
            onDeviceClick={handleDeviceClick}
            onDeviceToggle={handleDeviceToggle}
            onControlCover={handleControlCover}
            onDeviceImageClick={handleDeviceImageClick}
            deviceTimerStates={deviceTimerStates}
            isDemo={getUser() ? isDemo(getUser()!) : false}
            onShowRestriction={() => {
              setPermissionSnackbar({
                open: true,
                message: t('permissions.demoRestriction')
              });
            }}
          />
        );
      })()}

      <ExitMessage show={showExitMessage} />

      {/* Banner de instalación PWA */}
      <PWAInstallPrompt />

      {/* Los íconos PNG ahora están en el backend y se cargan dinámicamente */}

      {/* Modal de Grupos de Dispositivos - Renderizado en portal para aislar de re-renders */}
      {groupsModalPortal}

      {/* Snackbar para mensajes de permisos */}
      <Snackbar
        open={Boolean(firmwareMonitor.justFinished)}
        autoHideDuration={8000}
        onClose={firmwareMonitor.clearJustFinished}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '16px !important', maxWidth: 'calc(100vw - 32px)', zIndex: 10001 }}
      >
        <Alert
          onClose={firmwareMonitor.clearJustFinished}
          severity={firmwareMonitor.justFinished && firmwareMonitor.justFinished.failed > 0 ? 'warning' : 'success'}
          sx={{ width: '100%' }}
        >
          {firmwareMonitor.justFinished
            ? t('settings.firmware.progressDone', {
                ok: firmwareMonitor.justFinished.ok,
                failed: firmwareMonitor.justFinished.failed,
                skipped: firmwareMonitor.justFinished.skipped,
              })
            : ''}
        </Alert>
      </Snackbar>
      <Snackbar
        open={permissionSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setPermissionSnackbar({ ...permissionSnackbar, open: false })}
        {...getDemoRestrictionSnackbarProps()}
      >
        <Alert
          onClose={() => setPermissionSnackbar({ ...permissionSnackbar, open: false })}
          severity="error"
          sx={{ width: '100%' }}
        >
          {permissionSnackbar.message}
        </Alert>
      </Snackbar>
      <Snackbar
        open={automationPushSnack.open}
        autoHideDuration={8000}
        onClose={() => setAutomationPushSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '16px !important', maxWidth: 'calc(100vw - 32px)', zIndex: 10001 }}
      >
        <Alert
          onClose={() => setAutomationPushSnack((s) => ({ ...s, open: false }))}
          severity="info"
          sx={{ width: '100%', whiteSpace: 'pre-line' }}
        >
          <strong>{automationPushSnack.title}</strong>
          {automationPushSnack.body ? (
            <>
              <br />
              {automationPushSnack.body}
            </>
          ) : null}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default App;
